import React, { useState, useEffect, useMemo, useRef } from 'react';
// SCMS SchoolAdmin Component - Updated 2026-08-25
import { API_BASE_URL } from '../config.js';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { Document, Packer, Paragraph, Table, TableCell, TableRow, WidthType, AlignmentType, TextRun } from 'docx';
import { saveAs } from 'file-saver';
import SecureAccountSettings from './SecureAccountSettings';
import MarksCardRenderer from './MarksCardRenderer';
import CertificateRenderer from './CertificateRenderer';
import SchoolCertificatesManager from './admin/certificates/SchoolCertificatesManager';
import brandingService from '../services/BrandingService';
import SmartAlertsManagement from './admin/SmartAlertsManagement';
import PtmManagement from './admin/PtmManagement';
import IDCardManagement from './admin/IDCardManagement';
import VehicleManagement from './admin/VehicleManagement';
import LibraryManagement from './admin/LibraryManagement';
import SettingsManagement from './admin/SettingsManagement';
import StorageManagement from './admin/StorageManagement.jsx';
import AnalyticsManagement from './admin/AnalyticsManagement';
import AdmissionReports from './admin/AdmissionReports';
import PaymentVerifications from './admin/PaymentVerifications.jsx';
import FeeManagementWorkspace from './admin/fees/FeeManagementWorkspace.jsx';
import FeeClearanceCertificateModal from './admin/fees/FeeClearanceCertificateModal.jsx';
import StudentMasterList from './admin/StudentMasterList';
import SubjectManagement from './admin/SubjectManagement';
import TimetableManager from './timetable/TimetableManager.jsx';
import CreateClassTimetableModal from './timetable/CreateClassTimetableModal.jsx';
import HomeworkAssignmentsManager from './assignments/HomeworkAssignmentsManager.jsx';
import CreateTestModal from './admin/CreateTestModal';
import ClassManagement from './admin/ClassManagement';
import AcademicStructureManagement from './admin/AcademicStructureManagement';
import PucStructureManagement from './admin/PucStructureManagement';
import Examinations from './admin/examinations/Examinations';
import AssignSubjectsModal from './admin/AssignSubjectsModal';
import ResetStudentPasswordModal from './admin/ResetStudentPasswordModal';
import SmartAttendanceStudio from './admin/SmartAttendanceStudio';
import CommunicationCenterStudio from './admin/CommunicationCenterStudio';
import SchoolSidebar from './sidebar/SchoolSidebar';
import subUserService from '../services/SubUserService';
import identifierService from '../services/IdentifierService';
import TeacherDirectory from './teachers/TeacherDirectory';
import TeacherProfileDrawer from './teachers/TeacherProfileDrawer';
import { getNormalizedInstitutionType, getInstitutionTerminology } from '../config/institutionConfig';
import { 
  studentService, 
  teacherService, 
  departmentService, 
  feeService,
  classService,
  subjectService,
  timetableService,
  subjectAssignmentService,
  feeReminderService,
  hostelService,
  vehicleService,
  reportCardService,
  dashboardService
} from '../services/index.js';
import smartAttendanceService from '../services/SmartAttendanceService';

// Helper to format attendance time to real Indian Standard Time (IST - 12-hour AM/PM)
const formatIndianTime = (rawTime) => {
  if (!rawTime || rawTime === '—' || rawTime === 'Recorded' || rawTime === 'null' || rawTime === 'undefined') {
    return rawTime || '—';
  }
  const str = String(rawTime).trim();
  if (!str) return '—';

  // If already formatted with AM/PM (e.g. "09:50:52 AM" or "09:50 AM"), return as is
  if (str.toUpperCase().includes('AM') || str.toUpperCase().includes('PM')) {
    return str;
  }

  // If it's a full ISO or date-time string (e.g. "2026-09-26T04:20:52Z" or "2026-09-26 04:20:52")
  if (str.includes('T') || (str.includes('-') && str.includes(':'))) {
    try {
      const d = new Date(str.endsWith('Z') || str.includes('+') ? str : (str + 'Z'));
      if (!isNaN(d.getTime())) {
        return d.toLocaleTimeString('en-IN', {
          timeZone: 'Asia/Kolkata',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
      }
    } catch (_) {}
  }

  // If it's in "HH:mm:ss" or "HH:mm" 24h format from UTC server (e.g. "04:20:52", "11:41:14")
  const parts = str.split(':');
  if (parts.length >= 2) {
    try {
      const h = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      const s = parts.length > 2 ? parseInt(parts[2], 10) : 0;
      if (!isNaN(h) && !isNaN(m)) {
        // Convert UTC server time to Indian Standard Time (UTC + 5:30)
        let totalMinutes = h * 60 + m + (5 * 60 + 30);
        let istSeconds = isNaN(s) ? 0 : s;
        let istHour = Math.floor(totalMinutes / 60) % 24;
        let istMin = totalMinutes % 60;

        const ampm = istHour >= 12 ? 'PM' : 'AM';
        const displayHour = istHour % 12 || 12;
        const pad = (n) => String(n).padStart(2, '0');
        return `${pad(displayHour)}:${pad(istMin)}:${pad(istSeconds)} ${ampm}`;
      }
    } catch (_) {}
  }

  return str;
};


// Helper to resolve student profile photo URL
const resolveStudentPhotoUrl = (student) => {
  if (!student) return null;
  const photo = student.photo || student.photoUrl || student.studentPhoto || student.image || student.imageUrl;
  if (!photo || typeof photo !== 'string' || photo.trim() === '') return null;
  
  if (photo.startsWith('data:') || photo.startsWith('blob:') || photo.startsWith('http://') || photo.startsWith('https://')) {
    return photo;
  }
  
  const cleanPath = photo.startsWith('/') ? photo : `/${photo}`;
  return `${API_BASE_URL}${cleanPath}`;
};

// Helper for date formatting: Jan 1, 2020
const formatDisplayDate = (dateStr) => {
  if (!dateStr || dateStr === 'N/A' || dateStr === '—') return 'Not provided';
  try {
    const parts = String(dateStr).split('T')[0].split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const monthIdx = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      if (day >= 1 && day <= 31 && monthIdx >= 0 && monthIdx < 12) {
        return `${monthNames[monthIdx]} ${day}, ${year}`;
      }
    }
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    return dateStr;
  } catch (e) {
    return dateStr;
  }
};

// Helper for phone formatting: Not provided if < 7 digits or missing
const formatPhoneNumber = (phone) => {
  if (!phone || String(phone).trim() === '' || String(phone).trim() === 'N/A' || String(phone).trim() === '—') {
    return 'Not provided';
  }
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length < 7) {
    return 'Not provided';
  }
  return String(phone).trim();
};

// Helper for Academic Year formatting: 2026–27
const formatAcademicYear = (year) => {
  if (!year || String(year).trim() === '' || String(year).trim() === '—' || String(year).trim() === 'N/A') {
    return '2026–27';
  }
  const str = String(year).trim();
  if (str.includes('–') || (str.includes('-') && str.length > 5)) return str;
  const y = parseInt(str, 10);
  if (!isNaN(y)) {
    return `${y}–${(y + 1).toString().slice(-2)}`;
  }
  return str;
};

// Helper for Aadhaar masking: XXXX XXXX 1234
const formatMaskedAadhaar = (aadhaar) => {
  if (!aadhaar || String(aadhaar).trim() === '' || aadhaar === 'N/A' || aadhaar === '—') return 'Not provided';
  const digits = String(aadhaar).replace(/\D/g, '');
  if (digits.length >= 4) {
    return `XXXX XXXX ${digits.slice(-4)}`;
  }
  return 'Not provided';
};

// Helper for missing values
const displayValue = (val, fallback = 'Not provided') => {
  if (val === null || val === undefined || String(val).trim() === '' || String(val).trim() === 'N/A' || String(val).trim() === '—') {
    return fallback;
  }
  return String(val).trim();
};

// Student Photo Avatar Component with Error Fallback and Big Screen Lightbox Support
const StudentPhotoAvatar = ({ student, size = 80, style = {}, onClick, disablePreview = false }) => {
  const [hasError, setHasError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const photoUrl = useMemo(() => resolveStudentPhotoUrl(student), [student]);
  const initials = (student?.name || 'S').trim().charAt(0).toUpperCase();

  useEffect(() => {
    setHasError(false);
  }, [student?.studentId || student?.id || photoUrl]);

  const handleClick = (e) => {
    e.stopPropagation();
    if (onClick) {
      onClick(e);
      return;
    }
    if (!disablePreview) {
      window.dispatchEvent(new CustomEvent('open-photo-preview', {
        detail: {
          imageUrl: photoUrl || '',
          title: student?.name || 'Student Profile Photo',
          subtitle: `${student?.studentId || student?.id || ''} • Class ${student?.className || ''} ${student?.section ? `(Sec ${student?.section})` : ''}`,
          tag: (student?.active === true || student?.status === 'Active') ? 'Active' : 'Inactive',
          initials: initials
        }
      }));
    }
  };

  const cursorStyle = !disablePreview || onClick ? 'pointer' : 'default';

  if (photoUrl && !hasError) {
    return (
      <div
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title="Click to view full size photo"
        style={{
          position: 'relative',
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: '50%',
          flexShrink: 0,
          cursor: cursorStyle,
          display: 'inline-block'
        }}
      >
        <img
          src={photoUrl}
          alt={student?.name || 'Student Photo'}
          onError={() => setHasError(true)}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            objectFit: 'cover',
            border: isHovered ? '3px solid #2563eb' : '3px solid #ffffff',
            boxShadow: isHovered ? '0 4px 14px rgba(37, 99, 235, 0.35)' : '0 2px 8px rgba(0,0,0,0.08)',
            background: '#f1f5f9',
            transition: 'all 0.2s ease',
            transform: isHovered ? 'scale(1.05)' : 'scale(1)',
            ...style
          }}
        />
        {isHovered && (
          <div style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background: 'rgba(15, 23, 42, 0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontSize: `${Math.max(12, Math.round(size * 0.3))}px`,
            pointerEvents: 'none'
          }}>
            🔍
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title="Click to view full profile details"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '50%',
        background: isHovered ? 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)' : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
        color: '#ffffff',
        fontWeight: '700',
        fontSize: `${Math.max(12, Math.round(size * 0.42))}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: isHovered ? '3px solid #2563eb' : '3px solid #ffffff',
        boxShadow: isHovered ? '0 4px 14px rgba(37, 99, 235, 0.35)' : '0 2px 8px rgba(0,0,0,0.08)',
        flexShrink: 0,
        cursor: cursorStyle,
        transition: 'all 0.2s ease',
        transform: isHovered ? 'scale(1.05)' : 'scale(1)',
        ...style
      }}
    >
      {initials}
    </div>
  );
};


const SchoolAdmin = ({ onLogout, orgData }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const institutionType = 'SCHOOL';
  const terminology = getInstitutionTerminology('SCHOOL');
  const isDegreeCollege = false;
  const isPuc = false;
  const isSchool = true;
  const isCollege = false;

  const getGreetingMessage = () => {
    const hrs = new Date().getHours();
    if (hrs < 12) return 'Good Morning';
    if (hrs < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getTodayFormatted = () => {
    const today = new Date();
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    
    const dayName = days[today.getDay()];
    const dateNum = today.getDate();
    const monthName = months[today.getMonth()];
    const yearNum = today.getFullYear();
    
    return `${dateNum} ${monthName} ${yearNum}, ${dayName}`;
  };

  // RBAC: Role & Permissions for sidebar filtering
  const [userRole, setUserRole] = useState(() => {
    const isSub = orgData?.userType === 'SUB_USER' || localStorage.getItem('userType') === 'SUB_USER';
    if (isSub) return orgData?.role || localStorage.getItem('userRole') || 'STAFF';
    return orgData?.role || (localStorage.getItem('userRole') === 'COLLEGE_ADMIN' ? 'SCHOOL_ADMIN' : (localStorage.getItem('userRole') || 'SCHOOL_ADMIN'));
  });
  const [userPermissions, setUserPermissions] = useState([]);
  
  // Certificates states
  const [certificatesActiveTab, setCertificatesActiveTab] = useState('issue'); // issue, templates, history
  const [certTemplateName, setCertTemplateName] = useState('Classic Formal');
  const [certPrimaryColor, setCertPrimaryColor] = useState('#1e3a8a');
  const [certSecondaryColor, setCertSecondaryColor] = useState('#f59e0b');
  const [certTemplateContent, setCertTemplateContent] = useState('');
  const [certSelectedStudent, setCertSelectedStudent] = useState(null);
  const [certType, setCertType] = useState('STUDY_CERTIFICATE');
  const [issuedCertificatesList, setIssuedCertificatesList] = useState([]);
  const [certSearchQuery, setCertSearchQuery] = useState('');
  const [certLoading, setCertLoading] = useState(false);
  const [certIssueDate, setCertIssueDate] = useState(new Date().toISOString().split('T')[0]);
  const [certFilterClass, setCertFilterClass] = useState('');
  const [certFilterSection, setCertFilterSection] = useState('');
  const [certFilterSearch, setCertFilterSearch] = useState('');
  const [brandingData, setBrandingData] = useState(null);

  const [bulkStudentRows, setBulkStudentRows] = useState([
    { studentId: '', name: '', dob: '', gender: '', phone: '', email: '', class: '', section: '', aadhaarNumber: '', address: '', password: '' }
  ]);
  const [students, setStudents] = useState([]);
  const [resetPasswordStudent, setResetPasswordStudent] = useState(null);
  const [teachers, setTeachers] = useState([]);

  const birthdayStudents = useMemo(() => {
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentDate = today.getDate();
    return (students || []).filter(s => {
      if (!s) return false;
      const dobVal = s.dob || s.dateOfBirth;
      if (!dobVal) return false;
      
      let m = 0, d = 0;
      if (dobVal.includes('-')) {
        const parts = dobVal.split('-');
        if (parts.length === 3) {
          if (parts[0].length === 4) {
            m = parseInt(parts[1], 10);
            d = parseInt(parts[2], 10);
          } else {
            m = parseInt(parts[1], 10);
            d = parseInt(parts[0], 10);
          }
        }
      }
      return m === currentMonth && d === currentDate;
    });
  }, [students]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    academicYear: '',
    class: '',
    section: ''
  });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // 🔔 Header Notification Drawer & Dropdown State
  const notificationRef = useRef(null);
  const [showNotificationMenu, setShowNotificationMenu] = useState(false);
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [notificationFilterTab, setNotificationFilterTab] = useState('ALL');
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  const formatRelativeTime = (isoString) => {
    if (!isoString) return 'Just now';
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return 'Just now';
      const now = new Date();
      const diffMs = now - d;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays}d ago`;
      return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
    } catch (e) {
      return 'Just now';
    }
  };

  const getSchoolEmailDomain = () => {
    try {
      const adminEmail = orgData?.email || localStorage.getItem('email');
      if (adminEmail && adminEmail.includes('@')) {
        const host = adminEmail.split('@')[1]?.toLowerCase();
        if (host && !['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'icloud.com'].includes(host)) {
          return host;
        }
      }
      const rawSchool = orgData?.orgName || orgData?.institutionName || orgData?.name || localStorage.getItem('orgName') || orgData?.orgId || localStorage.getItem('orgId') || 'school';
      const cleanSchool = rawSchool.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 15);
      return cleanSchool ? `${cleanSchool}.edu` : 'school.edu';
    } catch (e) {
      return 'school.edu';
    }
  };

  const getDisplayStudentEmail = (email) => {
    if (!email) return 'N/A';
    if (email.endsWith('@sharada.edu') || email.endsWith('@sharadaschool.edu')) {
      const domain = getSchoolEmailDomain();
      if (!domain.includes('sharada')) {
        const prefix = email.split('@')[0];
        return `${prefix}@${domain}`;
      }
    }
    return email;
  };

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotificationMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAdminNotifications = async () => {
    const orgId = orgData?.orgId || localStorage.getItem('orgId') || 'DEFAULT';
    setLoadingNotifications(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/notifications/admin/${encodeURIComponent(orgId)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'ngrok-skip-browser-warning': 'true'
        }
      });
      let dbNotifs = [];
      if (res.ok) {
        const data = await res.json();
        if (data && data.success && Array.isArray(data.notifications)) {
          dbNotifs = data.notifications;
        }
      }

      // Generate real-time system alerts based on current state
      const generatedNotifs = [];

      // 1. Fee Balance Alert
      const studentsWithPendingFees = (students || []).filter(s => {
        const amt = Number(s.pendingFee || s.dueFee || s.balanceFee || 0);
        return amt > 0;
      });
      if (studentsWithPendingFees.length > 0) {
        const totalPending = studentsWithPendingFees.reduce((acc, s) => acc + Number(s.pendingFee || s.dueFee || s.balanceFee || 0), 0);
        generatedNotifs.push({
          id: 'sys-fee-alert',
          title: 'Fee Payment Balance Alert',
          message: `${studentsWithPendingFees.length} student(s) have pending fees totaling ₹${totalPending.toLocaleString('en-IN')}`,
          type: 'FEE_ALERT',
          category: 'ALERTS',
          status: 'UNREAD',
          createdAt: new Date().toISOString(),
          icon: '💰'
        });
      }

      // 2. Birthday Today Alert
      if (birthdayStudents && birthdayStudents.length > 0) {
        generatedNotifs.push({
          id: 'sys-bday-alert',
          title: 'Birthday Today 🎉',
          message: `Today is the birthday of ${birthdayStudents.map(s => s.name).join(', ')}!`,
          type: 'SMART_ALERT',
          category: 'ACTIVITIES',
          status: 'UNREAD',
          createdAt: new Date().toISOString(),
          icon: '🎂'
        });
      }

      // 3. Faculty Overview
      if (teachers && teachers.length > 0) {
        generatedNotifs.push({
          id: 'sys-teacher-alert',
          title: 'Active Faculty Members',
          message: `${teachers.length} active teacher records registered in portal.`,
          type: 'ACTIVITY',
          category: 'ACTIVITIES',
          status: 'READ',
          createdAt: new Date(Date.now() - 3600000).toISOString(),
          icon: '🎓'
        });
      }

      // 4. Low Attendance Alert
      const lowAttendanceCount = (students || []).filter(s => (s.attendancePercentage || s.attendanceRate || 100) < 75).length;
      if (lowAttendanceCount > 0) {
        generatedNotifs.push({
          id: 'sys-att-alert',
          title: 'Low Attendance Notice',
          message: `${lowAttendanceCount} student(s) have attendance rate below 75% threshold.`,
          type: 'SMART_ALERT',
          category: 'ALERTS',
          status: 'UNREAD',
          createdAt: new Date(Date.now() - 1800000).toISOString(),
          icon: '⚠️'
        });
      }

      const dbIds = new Set(dbNotifs.map(n => String(n.id)));
      const combined = [
        ...dbNotifs.map(n => ({
          ...n,
          category: (n.type === 'FEE_ALERT' || n.type === 'SMART_ALERT' || n.type === 'ALERT') ? 'ALERTS' : 'ACTIVITIES',
          icon: n.type === 'FEE_ALERT' ? '💰' : (n.type === 'SMART_ALERT' ? '⚠️' : (n.type === 'PTM_BOOKING' || n.type === 'APPOINTMENT_BOOKED' ? '📅' : '🔔'))
        })),
        ...generatedNotifs.filter(g => !dbIds.has(String(g.id)))
      ];

      setAdminNotifications(combined);
    } catch (err) {
      console.error('Error fetching admin notifications:', err);
    } finally {
      setLoadingNotifications(false);
    }
  };

  useEffect(() => {
    const fetchCoreSchoolData = async () => {
      const orgId = orgData?.orgId || localStorage.getItem('orgId') || 'SCH-02';
      try {
        const studentRes = await studentService.getStudents(orgId);
        if (Array.isArray(studentRes)) {
          setStudents(studentRes);
        } else if (studentRes && Array.isArray(studentRes.students)) {
          setStudents(studentRes.students);
        } else if (studentRes && Array.isArray(studentRes.data)) {
          setStudents(studentRes.data);
        }
      } catch (e) {
        console.error('Error fetching students in SchoolAdmin:', e);
      }

      try {
        const teacherRes = await teacherService.getAllTeachers(orgId);
        const incoming = Array.isArray(teacherRes) ? teacherRes : (teacherRes?.teachers || teacherRes?.data || []);
        if (Array.isArray(incoming) && incoming.length > 0) {
          setTeachers(prev => {
            if (!prev || prev.length === 0) return incoming;
            const prevMap = new Map(prev.map(t => [String(t.teacherId || t.id || t.teacherNumericId), t]));
            return incoming.map(inc => {
              const key = String(inc.teacherId || inc.id || inc.teacherNumericId);
              const old = prevMap.get(key);
              return {
                ...(old || {}),
                ...inc,
                photoUrl: inc.photoUrl || inc.photo || old?.photoUrl || old?.photo || '',
                photo: inc.photo || inc.photoUrl || old?.photo || old?.photoUrl || '',
                canUpdatePhoto: inc.canUpdatePhoto !== undefined ? inc.canUpdatePhoto : (old?.canUpdatePhoto ?? false),
                subjects: (inc.subjects && inc.subjects.length > 0) ? inc.subjects : (old?.subjects || [])
              };
            });
          });
        }
      } catch (e) {
        console.error('Error fetching teachers in SchoolAdmin:', e);
      }
    };

    fetchCoreSchoolData();
  }, [orgData?.orgId]);

  useEffect(() => {
    fetchAdminNotifications();
  }, [students?.length, teachers?.length, orgData?.orgId]);

  const unreadNotifCount = useMemo(() => {
    return adminNotifications.filter(n => n.status !== 'READ').length;
  }, [adminNotifications]);

  const handleMarkNotificationAsRead = async (notifId) => {
    setAdminNotifications(prev => prev.map(n => String(n.id) === String(notifId) ? { ...n, status: 'READ' } : n));
    if (typeof notifId === 'number' || (typeof notifId === 'string' && !notifId.startsWith('sys-'))) {
      try {
        await fetch(`${API_BASE_URL}/api/notifications/${notifId}/read`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'ngrok-skip-browser-warning': 'true'
          }
        });
      } catch (e) {
        console.error('Error updating read status:', e);
      }
    }
  };

  const handleMarkAllNotificationsAsRead = async () => {
    const orgId = orgData?.orgId || localStorage.getItem('orgId') || 'DEFAULT';
    setAdminNotifications(prev => prev.map(n => ({ ...n, status: 'READ' })));
    try {
      await fetch(`${API_BASE_URL}/api/notifications/admin/${encodeURIComponent(orgId)}/mark-read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'ngrok-skip-browser-warning': 'true'
        }
      });
    } catch (e) {
      console.error('Error marking all notifications as read:', e);
    }
  };

  const filteredNotificationsList = useMemo(() => {
    if (notificationFilterTab === 'ALERTS') {
      return adminNotifications.filter(n => n.category === 'ALERTS' || n.type === 'FEE_ALERT' || n.type === 'SMART_ALERT');
    }
    if (notificationFilterTab === 'ACTIVITIES') {
      return adminNotifications.filter(n => n.category === 'ACTIVITIES' || n.type === 'ACTIVITY');
    }
    return adminNotifications;
  }, [adminNotifications, notificationFilterTab]);

  // Load permissions for the current user (for sidebar RBAC)
  useEffect(() => {
    const role = orgData?.role || localStorage.getItem('userRole');
    if (role) setUserRole(role);
    // Only fetch module permissions for non-admin users
    const isSub = orgData?.userType === 'SUB_USER' || localStorage.getItem('userType') === 'SUB_USER';
    if (isSub || (role && role !== 'COLLEGE_ADMIN' && role !== 'SUPER_ADMIN')) {
      subUserService.getMyPermissions().then(res => {
        if (res && res.success && res.permissions) {
          setUserPermissions(res.permissions);
        } else if (res && res.success && res.screens) {
          setUserPermissions(res.screens);
        }
      }).catch(err => console.error('Failed to load permissions:', err));
    }
  }, [orgData?.role, orgData?.userType]);
  const [showFinancePasswordModal, setShowFinancePasswordModal] = useState(false);
  const [financePassword, setFinancePassword] = useState('');
  const [recentActivities, setRecentActivities] = useState([]);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [draftToast, setDraftToast] = useState(null);

  // Bulk Import State
  const [bulkImportMode, setBulkImportMode] = useState('manual'); // 'manual' | 'excel'
  const [bulkPreviewFilter, setBulkPreviewFilter] = useState('all'); // 'all' | 'errors'
  const [bulkFileError, setBulkFileError] = useState(null);
  const [bulkSuccessResult, setBulkSuccessResult] = useState(null);

  // Teacher Profile Drawer State
  const [selectedTeacherForDrawer, setSelectedTeacherForDrawer] = useState(null);

  // Bulk Promotion Modal State
  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [promotionSourceClass, setPromotionSourceClass] = useState('LKG');
  const [promotionTargetClass, setPromotionTargetClass] = useState('UKG');
  const [promotionSourceYear, setPromotionSourceYear] = useState('2026-27');
  const [promotionTargetYear, setPromotionTargetYear] = useState('2027-28');
  const [promotionPreviewData, setPromotionPreviewData] = useState(null);
  const [promotionLoading, setPromotionLoading] = useState(false);
  const [promotionExecuting, setPromotionExecuting] = useState(false);
  const [promotionRemarks, setPromotionRemarks] = useState('');

  const fetchPromotionPreview = async (currentClass) => {
    try {
      setPromotionLoading(true);
      const res = await studentService.getBulkPromotionPreview(currentClass);
      if (res) {
        setPromotionPreviewData(res);
      }
    } catch (err) {
      console.error('Promotion preview error:', err);
    } finally {
      setPromotionLoading(false);
    }
  };

  const executePromotion = async () => {
    try {
      setPromotionExecuting(true);
      const res = await studentService.executeBulkPromotion(promotionSourceClass, promotionRemarks);
      if (res && res.success) {
        if (showNotification) showNotification('success', 'Bulk Promotion Complete', res.message || 'Students promoted successfully');
        setShowPromotionModal(false);
        if (refreshStudentData) refreshStudentData();
      } else {
        if (showNotification) showNotification('error', 'Promotion Failed', res?.error || 'Failed to promote students');
      }
    } catch (err) {
      if (showNotification) showNotification('error', 'Error', 'Failed to execute promotion');
    } finally {
      setPromotionExecuting(false);
    }
  };

  // Auto-restore saved admission draft on modal open if authenticated key exists
  useEffect(() => {
    const userId = localStorage.getItem('userId') || localStorage.getItem('user_id');
    if (showModal && !editingItem && orgData?.id && userId) {
      const draftKey = `erp_student_admission_draft_${orgData.id}_${userId}`;
      const savedDraft = localStorage.getItem(draftKey);
      if (savedDraft) {
        try {
          const parsed = JSON.parse(savedDraft);
          setStudentFormData(parsed);
          setDraftToast('Restored saved admission draft!');
          setTimeout(() => setDraftToast(null), 4000);
        } catch (e) {
          console.error('Failed to parse saved admission draft:', e);
        }
      }
    }
  }, [showModal, editingItem, orgData?.id]);

  // Teacher search state
  const [teacherSearchTerm, setTeacherSearchTerm] = useState('');

  // Track loaded data to prevent duplicate API calls
  const [dataLoaded, setDataLoaded] = useState({
    students: false,
    teachers: false,
    classes: false,
    subjects: false,
    fees: false
  });
  const [loadingFees, setLoadingFees] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);

  // Fee management state
  const [fees, setFees] = useState([]);
  const [feeStructures, setFeeStructures] = useState([]);
  const [feeFilters, setFeeFilters] = useState({
    academicYear: '',
    class: '',
    section: '',
    paymentStatus: '',
    dueAmountFilter: '',
    feeStructureId: ''
  });
  const [feeSearchTerm, setFeeSearchTerm] = useState('');
  const [feeCustomMinDueAmount, setFeeCustomMinDueAmount] = useState('');
  const [feeCustomMaxDueAmount, setFeeCustomMaxDueAmount] = useState('');
  const [feeShowAdvancedFilters, setFeeShowAdvancedFilters] = useState(false);
  const [feeCurrentPage, setFeeCurrentPage] = useState(1);
  const [feePageSize, setFeePageSize] = useState(20);

  // Fee Alert / Notification Modal state
  const [showFeeAlertModal, setShowFeeAlertModal] = useState(false);
  const [alertTargetStudents, setAlertTargetStudents] = useState([]);
  const [alertCustomTitle, setAlertCustomTitle] = useState('Fee Reminder Alert 📢');
  const [alertCustomMessage, setAlertCustomMessage] = useState('');
  const [isSendingAlert, setIsSendingAlert] = useState(false);

    const handleExportFeeExcel = (filteredList) => {
    try {
      const dataToExport = filteredList.map(student => {
        const fee = fees.find(f => f.studentId === student.studentId);
        const total = fee?.totalFees != null ? Number(fee.totalFees) : ((fee?.items && fee.items.length > 0) ? fee.items.reduce((s, i) => s + (i.netAmount || i.originalAmount || 0), 0) : 0);
        const paid = fee?.paidFees != null ? Number(fee.paidFees) : 0;
        const concession = fee?.concessionAmount != null ? Number(fee.concessionAmount) : 0;
        const penalty = fee?.penaltyAmount != null ? Number(fee.penaltyAmount) : 0;
        const outstanding = fee?.pendingFee != null ? Number(fee.pendingFee) : Math.max(0, total - concession + penalty - paid);
        
        let status = 'Not Assigned';
        if (fee && (fee.feeStructureId || fee.totalFees > 0 || (fee.items && fee.items.length > 0))) {
          if (outstanding === 0 || fee.status === 'PAID') status = 'PAID';
          else if (paid > 0) status = 'PARTIAL';
          else status = 'UNPAID';
        }

        return {
          'Student ID': student.studentId,
          'Student Name': student.name,
          'Class': student.className || '—',
          'Section': student.section ? `Sec ${student.section}` : '—',
          'Assigned Structure': fee?.feeStructureName || 'Not Assigned',
          'Total Fee (₹)': total != null ? total : 0,
          'Paid Amount (₹)': paid != null ? paid : 0,
          'Outstanding Due (₹)': outstanding != null ? outstanding : 0,
          'Payment Status': status
        };
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      XLSX.utils.book_append_sheet(wb, ws, 'Fee Dues Report');
      XLSX.writeFile(wb, `Fee_Report_${orgData?.orgName || 'School'}_${new Date().toISOString().slice(0,10)}.xlsx`);
      setDraftToast('✅ Fee Dues Excel report downloaded successfully!');
      setTimeout(() => setDraftToast(null), 3000);
    } catch (err) {
      alert('❌ Failed to export Excel: ' + err.message);
    }
  };

  const handleExportFeePDF = (filteredList) => {
    try {
      const doc = new jsPDF('p', 'pt', 'a4');
      const orgName = orgData?.orgName || localStorage.getItem('orgName') || 'School Admin';
      doc.setFontSize(18);
      doc.setTextColor(15, 23, 42);
      doc.text(`${orgName} - Fee Dues Report`, 40, 40);

      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139);
      doc.text(`Generated on: ${new Date().toLocaleString('en-IN')} | Total Records: ${filteredList.length}`, 40, 58);

      let startY = 80;
      doc.setFontSize(10);
      doc.setFont('Helvetica', 'bold');
      doc.setFillColor(241, 245, 249);
      doc.rect(40, startY - 12, 515, 20, 'F');
      doc.text('Student ID', 45, startY);
      doc.text('Name', 130, startY);
      doc.text('Class', 250, startY);
      doc.text('Total (₹)', 330, startY);
      doc.text('Paid (₹)', 410, startY);
      doc.text('Due (₹)', 480, startY);

      startY += 15;
      doc.setFont('Helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(30, 41, 59);

      let totalDuesSum = 0;
      filteredList.forEach((s) => {
        if (startY > 780) {
          doc.addPage();
          startY = 40;
        }
        const f = fees.find(item => item.studentId === s.studentId);
        const netSum = (f?.items && f.items.length > 0) ? f.items.reduce((acc, item) => acc + (item.netAmount || item.originalAmount || 0), 0) : (f?.totalFees || 0);
        const paidSum = (f?.items && f.items.length > 0) ? f.items.reduce((acc, item) => acc + (item.paidAmount || 0), 0) : (f?.paidFees || 0);
        const rem = (netSum != null && paidSum != null) ? Math.max(0, netSum - paidSum) : (f?.pendingFee || 0);
        totalDuesSum += rem;

        doc.text(String(s.studentId || ''), 45, startY);
        doc.text(String(s.name || '').substring(0, 20), 130, startY);
        doc.text(`${s.className || ''} ${s.section || ''}`, 250, startY);
        doc.text(String(netSum || 0), 330, startY);
        doc.text(String(paidSum || 0), 410, startY);
        doc.text(String(rem || 0), 480, startY);
        startY += 18;
      });

      doc.setFont('Helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(220, 38, 38);
      doc.text(`Total Outstanding Dues: ₹${totalDuesSum.toLocaleString('en-IN')}`, 40, Math.min(startY + 20, 800));

      doc.save(`Fee_Report_${orgName}_${new Date().toISOString().slice(0,10)}.pdf`);
      setDraftToast('✅ Fee Dues PDF report downloaded successfully!');
      setTimeout(() => setDraftToast(null), 3000);
    } catch (err) {
      alert('❌ Failed to export PDF: ' + err.message);
    }
  };

  const handleSendFeeAlerts = async () => {
    if (!alertTargetStudents || alertTargetStudents.length === 0) return;
    setIsSendingAlert(true);
    const targetOrgId = orgData?.orgId || localStorage.getItem('orgId') || 'DEFAULT';
    try {
      const studentIds = alertTargetStudents.map(s => s.studentId);
      const data = await feeService.sendReminderAlert({
        orgId: targetOrgId,
        studentIds,
        customTitle: alertCustomTitle,
        customMessage: alertCustomMessage
      });
      if (data && data.success) {
        setDraftToast(`✅ ${data.message || `Fee alert sent to ${studentIds.length} parent(s)!`}`);
        setTimeout(() => setDraftToast(null), 4000);
        setShowFeeAlertModal(false);
      } else {
        alert(`❌ ${data?.error || 'Failed to send fee alert'}`);
      }
    } catch (err) {
      alert(`❌ ${err.message || 'Error sending fee alert'}`);
    } finally {
      setIsSendingAlert(false);
    }
  };
  
  // Create / Edit structure template modal state
  const [showCreateStructureModal, setShowCreateStructureModal] = useState(false);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [structureFormData, setStructureFormData] = useState({
    name: '',
    academicYear: '2026-27',
    classId: '',
    className: '',
    totalAmount: 0,
    items: [{ feeType: 'Tuition Fee', amount: '' }],
    installments: [{ installmentName: 'Term 1', amount: '', dueDate: '' }]
  });

  // Assign structure modal state
  const [showAssignStructureModal, setShowAssignStructureModal] = useState(false);
  const [assignPreview, setAssignPreview] = useState(null);
  const [assignFormData, setAssignFormData] = useState({
    feeStructureId: '',
    className: '',
    section: '',
    overwrite: false
  });

  // Fee collection workspace state
  const [selectedStudentForCollection, setSelectedStudentForCollection] = useState(null);
  const [collectionSearchTerm, setCollectionSearchTerm] = useState('');
  const [collectionStudentList, setCollectionStudentList] = useState([]);
  const [collectionFeeRecord, setCollectionFeeRecord] = useState(null);
  const [collectionPayments, setCollectionPayments] = useState([]);
  
  // Payment transaction state
  const [selectedInstallmentId, setSelectedInstallmentId] = useState('');
  const [selectedFeeItemId, setSelectedFeeItemId] = useState('');
  const [componentPaymentAllocations, setComponentPaymentAllocations] = useState({});
  const [autoFineSettings, setAutoFineSettings] = useState({ enableAutoOverdueFine: false, overdueFineType: 'PER_DAY', graceDays: 5, overdueFineAmount: 20, maximumFine: 500 });
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('CASH');
  const [paymentRefNumber, setPaymentRefNumber] = useState('');
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [isGeneratingReceiptPdf, setIsGeneratingReceiptPdf] = useState(false);

  // Payment history log modal state
  const [selectedStudentForHistory, setSelectedStudentForHistory] = useState(null);
  const [historyPayments, setHistoryPayments] = useState([]);
  const [loadingPaymentHistory, setLoadingPaymentHistory] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyComponentFilter, setHistoryComponentFilter] = useState('ALL');
  const [historyTimeFilter, setHistoryTimeFilter] = useState('ALL');

  // Concession & Penalty state
  const [showConcessionModal, setShowConcessionModal] = useState(false);
  const [concessionFormData, setConcessionFormData] = useState({ amount: '', reason: '' });
  const [showPenaltyModal, setShowPenaltyModal] = useState(false);
  const [penaltyFormData, setPenaltyFormData] = useState({ amount: '', reason: '' });

  // Fee reminder state
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderTimeRemaining, setReminderTimeRemaining] = useState(null);
  const [reminderFormData, setReminderFormData] = useState({
    class: '',
    section: ''
  });

  // Fee Clearance Certificate trigger after final payment
  const [clearedPaymentPrompt, setClearedPaymentPrompt] = useState(null);
  const [showAdminClearanceModal, setShowAdminClearanceModal] = useState(false);
  const [adminClearanceStudent, setAdminClearanceStudent] = useState(null);
  const [adminClearanceFee, setAdminClearanceFee] = useState(null);

  const loadBrandingData = async () => {
    try {
      const res = await brandingService.getBranding();
      if (res && res.success && res.branding) {
        setBrandingData(res.branding);
      }
    } catch (err) {
      console.error('Error loading branding data in SchoolAdmin:', err);
    }
  };

  useEffect(() => {
    loadBrandingData();
  }, [orgData]);

  // Big screen photo preview modal state
  const [previewPhotoModal, setPreviewPhotoModal] = useState({
    isOpen: false,
    imageUrl: '',
    title: '',
    subtitle: '',
    tag: '',
    initials: ''
  });

  useEffect(() => {
    const handleOpenPhotoPreview = (e) => {
      if (e.detail) {
        setPreviewPhotoModal({
          isOpen: true,
          imageUrl: e.detail.imageUrl || '',
          title: e.detail.title || 'Profile Photo',
          subtitle: e.detail.subtitle || '',
          tag: e.detail.tag || '',
          initials: e.detail.initials || ''
        });
      }
    };
    window.addEventListener('open-photo-preview', handleOpenPhotoPreview);
    return () => {
      window.removeEventListener('open-photo-preview', handleOpenPhotoPreview);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && previewPhotoModal.isOpen) {
        setPreviewPhotoModal({ isOpen: false, imageUrl: '', title: '', subtitle: '', tag: '', initials: '' });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewPhotoModal.isOpen]);


  // Academic management state
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [timetables, setTimetables] = useState([]);
  const [academicActiveTab, setAcademicActiveTab] = useState('classes');
  const [academicsMenuExpanded, setAcademicsMenuExpanded] = useState(true);
  const [studentsMenuExpanded, setStudentsMenuExpanded] = useState(true);
  const [attendanceSubTab, setAttendanceSubTab] = useState('student'); // Default to student record tab
  const [reportType, setReportType] = useState('student'); // 'student' | 'teacher'
  const [reportFromDate, setReportFromDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [reportToDate, setReportToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [reportAcademicYear, setReportAcademicYear] = useState('2026-27');
  const [reportClass, setReportClass] = useState('All');
  const [reportSection, setReportSection] = useState('All');
  const [reportDepartment, setReportDepartment] = useState('All');
  const [reportDesignation, setReportDesignation] = useState('All');
  const [reportSearchText, setReportSearchText] = useState('');
  const [reportMinPct, setReportMinPct] = useState('');
  const [teacherAttendanceRecords, setTeacherAttendanceRecords] = useState([]);
  const [loadingTeacherRecords, setLoadingTeacherRecords] = useState(false);
  const [studentAttendanceReportData, setStudentAttendanceReportData] = useState(null);
  const [loadingStudentReport, setLoadingStudentReport] = useState(false);
  const [studentReportError, setStudentReportError] = useState(null);
  const [studentReportRetryCount, setStudentReportRetryCount] = useState(0);

  // Attendance Tracker & Day-Wise & Class-Wise States
  const [trackerDate, setTrackerDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [trackerStatusFilter, setTrackerStatusFilter] = useState('ALL'); // 'ALL' | 'TAKEN' | 'PENDING'
  const [trackerSearch, setTrackerSearch] = useState('');
  const [trackerData, setTrackerData] = useState(null);
  const [loadingTracker, setLoadingTracker] = useState(false);
  const [trackerError, setTrackerError] = useState(null);

  const [dayWiseDate, setDayWiseDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [dayWiseClass, setDayWiseClass] = useState('All');
  const [dayWiseSection, setDayWiseSection] = useState('All');
  const [dayWiseStatusFilter, setDayWiseStatusFilter] = useState('All');
  const [dayWiseSearch, setDayWiseSearch] = useState('');
  const [dayWiseData, setDayWiseData] = useState(null);
  const [loadingDayWise, setLoadingDayWise] = useState(false);
  const [dayWiseError, setDayWiseError] = useState(null);

  const [classWiseData, setClassWiseData] = useState(null);
  const [loadingClassWise, setLoadingClassWise] = useState(false);
  const [classWiseError, setClassWiseError] = useState(null);
  const [classWiseSearch, setClassWiseSearch] = useState('');

  useEffect(() => {
    if (orgData) {
      const activeYr = orgData.academicYear || orgData.activeAcademicYear;
      if (activeYr) {
        setReportAcademicYear(activeYr);
      }
    }
  }, [orgData]);
  const [studentAttendanceMode, setStudentAttendanceMode] = useState('mark'); // 'mark' | 'history'
  const [attendanceDate, setAttendanceDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [attendanceClass, setAttendanceClass] = useState('6th Standard');
  const [attendanceSection, setAttendanceSection] = useState('A');
  const [attendanceSession, setAttendanceSession] = useState('Full Day'); // 'Full Day' | 'Morning' | 'Afternoon'
  const [attendanceAcademicYear, setAttendanceAcademicYear] = useState('2026-27');
  const [markAttendanceList, setMarkAttendanceList] = useState([]);
  const [selectedHistoryStudentId, setSelectedHistoryStudentId] = useState('');
  const [savedAttendanceDb, setSavedAttendanceDb] = useState({});
  const [isEditingExisting, setIsEditingExisting] = useState(false);
  const [historyAttendanceList, setHistoryAttendanceList] = useState([]);
  const [historySummary, setHistorySummary] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historySearchText, setHistorySearchText] = useState('');
  const [isHistoryDropdownOpen, setIsHistoryDropdownOpen] = useState(false);
  const [historyClass, setHistoryClass] = useState('6th Standard');
  const [historySection, setHistorySection] = useState('A');
  const [historyDateRangePreset, setHistoryDateRangePreset] = useState('last_30_days');

  // Fetch student attendance report records (Genuine DB-Driven)
  useEffect(() => {
    if (activeTab !== 'attendance' || attendanceSubTab !== 'summary' || reportType !== 'student') {
      return;
    }

    console.log("Fetching student attendance report. Dates:", reportFromDate, "to", reportToDate, "Class:", reportClass, "Sec:", reportSection, "AY:", reportAcademicYear);
    setLoadingStudentReport(true);
    setStudentReportError(null);
    smartAttendanceService.getReport(reportFromDate, reportToDate, reportClass, reportSection, reportAcademicYear)
      .then(res => {
        console.log("Student Attendance Report response received:", res);
        setStudentAttendanceReportData(res || { students: [], summary: {} });
      })
      .catch(err => {
        console.error("Error fetching student attendance report:", err);
        setStudentReportError(err?.message || 'Unable to load attendance report.');
        showNotification('error', 'Error', 'Failed to load student attendance report');
      })
      .finally(() => {
        setLoadingStudentReport(false);
      });
  }, [activeTab, attendanceSubTab, reportType, reportFromDate, reportToDate, reportClass, reportSection, reportAcademicYear, studentReportRetryCount]);

  // Fetch teacher attendance report records
  useEffect(() => {
    if (activeTab !== 'attendance' || attendanceSubTab !== 'summary' || reportType !== 'teacher') {
      return;
    }

    console.log("Fetching teacher attendance report. Dates:", reportFromDate, "to", reportToDate);
    setLoadingTeacherRecords(true);
    teacherService.getTeacherAttendanceReport(reportFromDate, reportToDate)
      .then(res => {
        console.log("Teacher Report response received:", res);
        setTeacherAttendanceRecords(Array.isArray(res) ? res : []);
      })
      .catch(err => {
        console.error("Error fetching teacher report records:", err);
        showNotification('error', 'Error', 'Failed to load teacher attendance report');
      })
      .finally(() => {
        setLoadingTeacherRecords(false);
      });
  }, [activeTab, attendanceSubTab, reportType, reportFromDate, reportToDate]);

  // Fetch Teacher Attendance Tracker (Today / Selected Date)
  useEffect(() => {
    if (activeTab !== 'attendance' || attendanceSubTab !== 'summary' || reportType !== 'tracker') {
      return;
    }
    setLoadingTracker(true);
    setTrackerError(null);
    smartAttendanceService.getTodayTeacherTracker(trackerDate, reportAcademicYear)
      .then(res => {
        setTrackerData(res || { classes: [] });
      })
      .catch(err => {
        console.error("Error fetching attendance tracker:", err);
        setTrackerError(err?.message || "Failed to load attendance tracker");
      })
      .finally(() => setLoadingTracker(false));
  }, [activeTab, attendanceSubTab, reportType, trackerDate, reportAcademicYear]);

  // Fetch Day-Wise Attendance
  useEffect(() => {
    if (activeTab !== 'attendance' || attendanceSubTab !== 'summary' || reportType !== 'daywise') {
      return;
    }
    setLoadingDayWise(true);
    setDayWiseError(null);
    smartAttendanceService.getDayWiseAttendance(dayWiseDate, dayWiseClass, dayWiseSection, reportAcademicYear)
      .then(res => {
        setDayWiseData(res || { students: [], summary: {} });
      })
      .catch(err => {
        console.error("Error fetching day-wise attendance:", err);
        setDayWiseError(err?.message || "Failed to load day-wise attendance");
      })
      .finally(() => setLoadingDayWise(false));
  }, [activeTab, attendanceSubTab, reportType, dayWiseDate, dayWiseClass, dayWiseSection, reportAcademicYear]);

  // Fetch Class-Wise Attendance Comparison
  useEffect(() => {
    if (activeTab !== 'attendance' || attendanceSubTab !== 'summary' || reportType !== 'classwise') {
      return;
    }
    setLoadingClassWise(true);
    setClassWiseError(null);
    smartAttendanceService.getClassWiseAttendance(reportFromDate, reportToDate, reportAcademicYear)
      .then(res => {
        setClassWiseData(res || { classes: [] });
      })
      .catch(err => {
        console.error("Error fetching class-wise attendance:", err);
        setClassWiseError(err?.message || "Failed to load class-wise attendance");
      })
      .finally(() => setLoadingClassWise(false));
  }, [activeTab, attendanceSubTab, reportType, reportFromDate, reportToDate, reportAcademicYear]);

  useEffect(() => {
    console.log("Report state debug - reportType:", reportType, "teachers in state:", teachers);
  }, [reportType, teachers]);

  // Teacher Daily Attendance States
  const [teacherAttendanceDate, setTeacherAttendanceDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [teacherAttendanceList, setTeacherAttendanceList] = useState([]);
  const [isLoadingTeacherAttendance, setIsLoadingTeacherAttendance] = useState(false);
  const [savedTeacherAttendanceDb, setSavedTeacherAttendanceDb] = useState({});

  // Fetch and sync daily teacher attendance
  useEffect(() => {
    if (activeTab !== 'attendance' || attendanceSubTab !== 'teacher_attendance' || !teachers || teachers.length === 0) {
      return;
    }

    setIsLoadingTeacherAttendance(true);
    teacherService.getTeacherAttendanceByDate(teacherAttendanceDate)
      .then(res => {
        const records = Array.isArray(res) ? res : [];
        const mappedList = teachers.map(teacher => {
          const matched = records.find(r => String(r.teacherId) === String(teacher.teacherId));
          return {
            teacherId: String(teacher.teacherId),
            name: teacher.teacherName || teacher.name || 'Unknown Teacher',
            department: teacher.departmentName || teacher.department || 'General',
            designation: teacher.designation || 'Faculty',
            status: matched ? matched.status : 'PRESENT',
            checkIn: matched && matched.checkIn ? matched.checkIn.substring(0, 5) : '09:00',
            checkOut: matched && matched.checkOut ? matched.checkOut.substring(0, 5) : '17:00',
            remarks: matched ? matched.remarks || '' : ''
          };
        });

        setTeacherAttendanceList(mappedList);
        setSavedTeacherAttendanceDb(prev => ({
          ...prev,
          [teacherAttendanceDate]: records.length > 0 ? 'saved' : 'not_saved'
        }));
      })
      .catch(err => {
        console.error("Error loading teacher attendance:", err);
        const defaultList = teachers.map(teacher => ({
          teacherId: String(teacher.teacherId),
          name: teacher.teacherName || teacher.name || 'Unknown Teacher',
          department: teacher.departmentName || teacher.department || 'General',
          designation: teacher.designation || 'Faculty',
          status: 'PRESENT',
          checkIn: '09:00',
          checkOut: '17:00',
          remarks: ''
        }));
        setTeacherAttendanceList(defaultList);
      })
      .finally(() => {
        setIsLoadingTeacherAttendance(false);
      });
  }, [teacherAttendanceDate, teachers, activeTab, attendanceSubTab]);
  const [historyStartDate, setHistoryStartDate] = useState(() => {
    const today = new Date();
    today.setDate(today.getDate() - 30);
    return today.toISOString().split('T')[0];
  });
  const [historyEndDate, setHistoryEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Utility to compute start/end dates for range presets
  const calculatePresetDates = (preset) => {
    const today = new Date();
    let start = new Date();
    let end = new Date();

    if (preset === 'last_30_days') {
      start.setDate(today.getDate() - 30);
    } else if (preset === 'this_month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = today; // Current day of month
    } else if (preset === 'prev_month') {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (preset === 'this_academic_year') {
      const currentYear = today.getFullYear();
      if (today.getMonth() >= 5) {
        start = new Date(currentYear, 5, 1);
        end = today;
      } else {
        start = new Date(currentYear - 1, 5, 1);
        end = today;
      }
    }
    
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  // Synchronize history dates when preset changes
  useEffect(() => {
    if (historyDateRangePreset !== 'custom') {
      const { start, end } = calculatePresetDates(historyDateRangePreset);
      setHistoryStartDate(start);
      setHistoryEndDate(end);
    }
  }, [historyDateRangePreset]);

  // Reset selected history student when class or section filters change
  useEffect(() => {
    setSelectedHistoryStudentId('');
    setHistorySearchText('');
    setHistoryAttendanceList([]);
    setHistorySummary(null);
  }, [historyClass, historySection]);

  // Helper to format student search option display label
  const getStudentDisplayName = (st) => {
    if (!st) return '';
    const roll = st.rollNumber ? `${st.rollNumber} - ` : '';
    const cls = st.className || st.class || '';
    const sec = st.section ? `-${st.section}` : '';
    return `${roll}${st.name || st.studentName} (${cls}${sec})`;
  };

  // Synchronize historySearchText when selectedHistoryStudentId or students changes
  useEffect(() => {
    const selected = students.find(st => String(st.studentId || st.id) === String(selectedHistoryStudentId));
    if (selected) {
      setHistorySearchText(getStudentDisplayName(selected));
    } else {
      setHistorySearchText('');
    }
  }, [selectedHistoryStudentId, students]);

  // Reset editing mode when any filter config changes
  useEffect(() => {
    setIsEditingExisting(false);
  }, [attendanceClass, attendanceSection, attendanceDate, attendanceSession]);

  // Fetch real-time student attendance history when selectedHistoryStudentId or dates change
  useEffect(() => {
    if (!selectedHistoryStudentId) {
      setHistoryAttendanceList([]);
      setHistorySummary(null);
      return;
    }

    setLoadingHistory(true);
    studentService.getStudentAttendanceHistory(selectedHistoryStudentId, historyStartDate, historyEndDate)
      .then(res => {
        if (res && res.success) {
          setHistoryAttendanceList(res.attendance || []);
          setHistorySummary(res.summary || null);
        } else {
          setHistoryAttendanceList([]);
          setHistorySummary(null);
        }
      })
      .catch(err => {
        console.error("Error fetching student history:", err);
        setHistoryAttendanceList([]);
        setHistorySummary(null);
      })
      .finally(() => {
        setLoadingHistory(false);
      });
  }, [selectedHistoryStudentId]);

  // Automatically update markAttendanceList and check if already saved in DB
  useEffect(() => {
    const classFiltered = students.filter(st => {
      const stClass = st.className || st.class || '';
      const stSection = st.section || '';
      return stClass.toLowerCase().trim() === attendanceClass.toLowerCase().trim() &&
             stSection.toLowerCase().trim() === attendanceSection.toLowerCase().trim();
    });

    const stateKey = `${attendanceClass}_${attendanceSection}_${attendanceDate}_${attendanceSession}`;

    // Fetch database records for this date to check if already marked
    teacherService.getTeacherMarkedStudentAttendance(attendanceDate)
      .then(res => {
        if (res && res.success && Array.isArray(res.attendance)) {
          // Filter records matching className and section
          const dbRecords = res.attendance.filter(a => 
            a.className && a.className.toLowerCase().trim() === attendanceClass.toLowerCase().trim() &&
            (a.section || '').toLowerCase().trim() === attendanceSection.toLowerCase().trim()
          );

          if (dbRecords.length > 0) {
            // Map status enums back to dropdown format
            const mapEnumToStatus = (statusEnum) => {
              if (statusEnum === 'PRESENT') return 'Present';
              if (statusEnum === 'ABSENT') return 'Absent';
              if (statusEnum === 'LATE') return 'Late';
              if (statusEnum === 'HALF_DAY') return 'Half Day';
              if (statusEnum === 'LEAVE') return 'Leave';
              return statusEnum;
            };

            const mappedList = classFiltered.map((st, idx) => {
              const studentId = st.studentId || st.id || `idx-${idx}`;
              const dbRec = dbRecords.find(r => String(r.studentId) === String(studentId));
              return {
                studentId: studentId,
                rollNumber: st.rollNumber || String(idx + 1).padStart(2, '0'),
                name: st.name || st.studentName,
                status: dbRec ? mapEnumToStatus(dbRec.status) : 'Present',
                remarks: dbRec ? (dbRec.remarks || '') : ''
              };
            });

            // Find marked timestamp from any record
            const firstRec = dbRecords[0];
            let timeStr = 'Saved';
            if (firstRec.markedAt) {
              try {
                if (Array.isArray(firstRec.markedAt)) {
                  const [y, m, d, hh, mm] = firstRec.markedAt;
                  const ampm = hh >= 12 ? 'PM' : 'AM';
                  const displayHour = hh % 12 || 12;
                  timeStr = `${displayHour}:${String(mm).padStart(2, '0')} ${ampm}`;
                } else {
                  const markedDate = new Date(firstRec.markedAt);
                  timeStr = markedDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                }
              } catch (e) {
                timeStr = 'Saved';
              }
            }

            setSavedAttendanceDb(prev => ({
              ...prev,
              [stateKey]: {
                status: 'saved',
                time: timeStr,
                records: mappedList
              }
            }));
            setMarkAttendanceList(mappedList);
          } else {
            // Initialize with default
            const initialList = classFiltered.map((st, idx) => ({
              studentId: st.studentId || st.id || `idx-${idx}`,
              rollNumber: st.rollNumber || String(idx + 1).padStart(2, '0'),
              name: st.name || st.studentName,
              status: 'Present',
              remarks: ''
            }));
            setSavedAttendanceDb(prev => ({
              ...prev,
              [stateKey]: { status: 'not_marked' }
            }));
            setMarkAttendanceList(initialList);
          }
        } else {
          // Initialize default
          const initialList = classFiltered.map((st, idx) => ({
            studentId: st.studentId || st.id || `idx-${idx}`,
            rollNumber: st.rollNumber || String(idx + 1).padStart(2, '0'),
            name: st.name || st.studentName,
            status: 'Present',
            remarks: ''
          }));
          setSavedAttendanceDb(prev => ({
            ...prev,
            [stateKey]: { status: 'not_marked' }
          }));
          setMarkAttendanceList(initialList);
        }
      })
      .catch(err => {
        console.error("Error fetching marked attendance:", err);
        const initialList = classFiltered.map((st, idx) => ({
          studentId: st.studentId || st.id || `idx-${idx}`,
          rollNumber: st.rollNumber || String(idx + 1).padStart(2, '0'),
          name: st.name || st.studentName,
          status: 'Present',
          remarks: ''
        }));
        setMarkAttendanceList(initialList);
      });
  }, [students, attendanceClass, attendanceSection, attendanceDate, attendanceSession]);

  const updateStudentStatus = (studentId, status) => {
    setMarkAttendanceList(prev => prev.map(item => item.studentId === studentId ? { ...item, status } : item));
  };

  const updateStudentRemarks = (studentId, remarks) => {
    setMarkAttendanceList(prev => prev.map(item => item.studentId === studentId ? { ...item, remarks } : item));
  };

  const markAllPresent = () => {
    setMarkAttendanceList(prev => prev.map(item => ({ ...item, status: 'Present' })));
  };

  const counts = useMemo(() => {
    let present = 0, absent = 0, late = 0, halfDay = 0, leave = 0;
    markAttendanceList.forEach(item => {
      if (item.status === 'Present') present++;
      else if (item.status === 'Absent') absent++;
      else if (item.status === 'Late') late++;
      else if (item.status === 'Half Day') halfDay++;
      else if (item.status === 'Leave') leave++;
    });
    return { present, absent, late, halfDay, leave };
  }, [markAttendanceList]);

  const [attendanceMenuExpanded, setAttendanceMenuExpanded] = useState(true);
  const [reportsSubTab, setReportsSubTab] = useState('admission_register');
  const [reportsMenuExpanded, setReportsMenuExpanded] = useState(true);
  const [feesSubTab, setFeesSubTab] = useState('management');
  const [feesMenuExpanded, setFeesMenuExpanded] = useState(true);
  const [libraryActiveTab, setLibraryActiveTab] = useState('inventory');
  const [libraryMenuExpanded, setLibraryMenuExpanded] = useState(true);
  const [showAcademicModal, setShowAcademicModal] = useState(false);
  const [showCreateTimetableModal, setShowCreateTimetableModal] = useState(false);
  const [timetableInitialParams, setTimetableInitialParams] = useState(null);
  const [monthlyAttendancePatterns, setMonthlyAttendancePatterns] = useState({});
  const [monthlyEditMode, setMonthlyEditMode] = useState(false);
  const [editingMonthlyRowId, setEditingMonthlyRowId] = useState(null);
  const [monthlySearchQuery, setMonthlySearchQuery] = useState('');
  const [absentFilterClass, setAbsentFilterClass] = useState('All Classes');
  const [absentFilterSection, setAbsentFilterSection] = useState('All Sections');
  const [showTeacherModal, setShowTeacherModal] = useState(false);
  const [showTeacherPassword, setShowTeacherPassword] = useState(false);
  const [showTeacherDetailsModal, setShowTeacherDetailsModal] = useState(false);
  const [editingAcademicItem, setEditingAcademicItem] = useState(null);
  const [editingTeacher, setEditingTeacher] = useState(null);
  
  // Exams & Marks state
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [testResults, setTestResults] = useState([]);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testForm, setTestForm] = useState({
    title: '',
    description: '',
    className: '',
    section: '',
    subjectName: '',
    testDate: '',
    maxMarks: '',
    instructions: '',
    teacherId: ''
  });
  const [resultsLoading, setResultsLoading] = useState(false);
  const [examFilters, setExamFilters] = useState({
    subject: '',
    teacher: '',
    className: '',
    section: '',
    date: ''
  });
  const [editingTest, setEditingTest] = useState(null);
  const [isEnteringMarks, setIsEnteringMarks] = useState(false);
  const [editingMarkStudentId, setEditingMarkStudentId] = useState(null);
  const [editMarkForm, setEditMarkForm] = useState({
    marksObtained: '',
    remarks: ''
  });
  const [academicSearchQuery, setAcademicSearchQuery] = useState('');

  // Timetable selection state
  const [selectedTimetableClass, setSelectedTimetableClass] = useState('');
  const [selectedTimetableSection, setSelectedTimetableSection] = useState('');
  const [currentTimetable, setCurrentTimetable] = useState(null);

  // Report Card / Marks Card State
  const [reportCards, setReportCards] = useState([]);
  const [gradingSchemes, setGradingSchemes] = useState([]);
  const [selectedStudentForRC, setSelectedStudentForRC] = useState('');
  const [rcAcademicYear, setRcAcademicYear] = useState('2025-26');
  const [rcSemester, setRcSemester] = useState('ALL');
  const [rcTerm, setRcTerm] = useState('Final Exam');
  const [rcGenFilterClass, setRcGenFilterClass] = useState('');
  const [rcGenFilterSection, setRcGenFilterSection] = useState('');
  const [rcGenFilterSearch, setRcGenFilterSearch] = useState('');
  const [selectedReportCardForView, setSelectedReportCardForView] = useState(null);
  const [rcEntriesForView, setRcEntriesForView] = useState([]);
  const [rcBrandingForView, setRcBrandingForView] = useState({});
  const [rcOrgInfoForView, setRcOrgInfoForView] = useState({});
  const [rcGradingScaleForView, setRcGradingScaleForView] = useState([]);
  const [showRCPreviewModal, setShowRCPreviewModal] = useState(false);
  const [showRCEditRemarksModal, setShowRCEditRemarksModal] = useState(false);
  const [editingRC, setEditingRC] = useState(null);
  const [rcClassRemarksInput, setRcClassRemarksInput] = useState('');
  const [rcPrincipalRemarksInput, setRcPrincipalRemarksInput] = useState('');
  const [isGeneratingRC, setIsGeneratingRC] = useState(false);
  const [rcSelectedIds, setRcSelectedIds] = useState(new Set());
  const [rcTemplate, setRcTemplate] = useState('classic');
  const [rcFilters, setRcFilters] = useState({
    className: '',
    academicYear: '2025-26',
    semester: 'ALL',
    term: 'Final Exam'
  });
  const [marksCardsActiveSubTab, setMarksCardsActiveSubTab] = useState('manage');
  const [bulkProgress, setBulkProgress] = useState({ generating: false, current: 0, total: 0 });

  const loadReportCards = async () => {
    try {
      const params = {};
      if (rcFilters.className) params.className = rcFilters.className;
      if (rcFilters.academicYear) params.academicYear = rcFilters.academicYear;
      if (rcFilters.semester) params.semester = rcFilters.semester;
      if (rcFilters.term) params.term = rcFilters.term;

      const data = await reportCardService.listByOrg(params);
      setReportCards(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load report cards:', err);
    }
  };

  const loadGradingSchemes = async () => {
    try {
      const data = await reportCardService.getGradingSchemes();
      if (Array.isArray(data) && data.length > 0) {
        setGradingSchemes(data);
      } else {
        setGradingSchemes([
          { grade: 'A+', minPercentage: 90.0, maxPercentage: 100.0, passStatus: 'PASS' },
          { grade: 'A', minPercentage: 80.0, maxPercentage: 89.99, passStatus: 'PASS' },
          { grade: 'B', minPercentage: 70.0, maxPercentage: 79.99, passStatus: 'PASS' },
          { grade: 'C', minPercentage: 60.0, maxPercentage: 69.99, passStatus: 'PASS' },
          { grade: 'D', minPercentage: 50.0, maxPercentage: 59.99, passStatus: 'PASS' },
          { grade: 'E', minPercentage: 40.0, maxPercentage: 49.99, passStatus: 'PASS' },
          { grade: 'F', minPercentage: 0.0, maxPercentage: 39.99, passStatus: 'FAIL' }
        ]);
      }
    } catch (err) {
      console.error('Failed to load grading schemes:', err);
    }
  };

  const handleSaveGradingSchemes = async (schemesToSave) => {
    try {
      const res = await reportCardService.saveGradingSchemes(schemesToSave);
      if (res && res.success) {
        alert('Grading schemes saved successfully');
        loadGradingSchemes();
      } else {
        alert('Failed to save grading schemes: ' + (res?.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleGenerateReportCard = async () => {
    if (!selectedStudentForRC) {
      alert('Please select a student');
      return;
    }
    setIsGeneratingRC(true);
    try {
      const res = await reportCardService.generateReportCard({
        studentId: selectedStudentForRC,
        academicYear: rcAcademicYear,
        semester: rcSemester,
        term: rcTerm
      });
      if (res && res.success) {
        alert(res.message || 'Report card generated successfully');
        loadReportCards();
        setSelectedStudentForRC('');
      } else {
        alert('Failed to generate report card: ' + (res?.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setIsGeneratingRC(false);
    }
  };

  const handleUpdateRCRemarks = async () => {
    if (!editingRC) return;
    try {
      const res = await reportCardService.updateRemarks(editingRC.id, rcClassRemarksInput, rcPrincipalRemarksInput);
      if (res && res.success) {
        alert('Remarks updated successfully');
        setShowRCEditRemarksModal(false);
        setEditingRC(null);
        loadReportCards();
      } else {
        alert('Failed to update remarks: ' + (res?.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleUpdateRCStatus = async (id, status) => {
    try {
      const res = await reportCardService.updateStatus(id, status);
      if (res && res.success) {
        alert(`Report card status updated to ${status}`);
        loadReportCards();
      } else {
        alert('Failed to update status: ' + (res?.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleDeleteReportCard = async (id) => {
    if (!window.confirm('Are you sure you want to delete this report card?')) return;
    try {
      const res = await reportCardService.deleteReportCard(id);
      if (res && res.success) {
        alert('Report card deleted successfully');
        loadReportCards();
      } else {
        alert('Failed to delete report card: ' + (res?.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleViewReportCard = async (rc) => {
    try {
      const details = await reportCardService.getReportCardById(rc.id);
      setSelectedReportCardForView(details.reportCard);
      setRcEntriesForView(details.entries);
      setRcBrandingForView(details.branding || {});
      setRcOrgInfoForView(details.organizationInfo || {});
      setRcGradingScaleForView(details.gradingScale || []);
      setShowRCPreviewModal(true);
    } catch (err) {
      alert('Failed to fetch report card details: ' + err.message);
    }
  };

  const handleDownloadRCPDF = async (rc, entries) => {
    try {
      const container = document.createElement('div');
      container.style.cssText = 'position:fixed;top:-9999px;left:-9999px;z-index:-1;';
      document.body.appendChild(container);

      const { createRoot } = await import('react-dom/client');
      const el = document.createElement('div');
      container.appendChild(el);

      const root = createRoot(el);
      await new Promise(resolve => {
        root.render(
          <MarksCardRenderer 
            reportCard={rc} 
            entries={entries} 
            branding={rcBrandingForView}
            institutionName={rcOrgInfoForView?.orgName || orgData?.orgName}
            institutionAddress={rcOrgInfoForView?.address || orgData?.address}
            id="single-rc-download"
            template={rcTemplate}
            gradingScale={rcGradingScaleForView}
          />
        );
        setTimeout(resolve, 300);
      });

      const captureEl = document.getElementById('single-rc-download');
      const canvas = await html2canvas(captureEl, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      pdf.addImage(imgData, 'PNG', 0, 0, 210, 297);
      pdf.save(`${rc.student?.name || 'student'}_marks_card.pdf`);

      root.unmount();
      container.removeChild(el);
      document.body.removeChild(container);
      alert('PDF downloaded successfully');
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('Failed to generate PDF: ' + err.message);
    }
  };

  const handleBulkDownloadRCPDF = async () => {
    if (rcSelectedIds.size === 0) {
      alert('Please select at least one report card.');
      return;
    }
    const selectedCards = reportCards.filter(rc => rcSelectedIds.has(rc.id));
    
    setBulkProgress({ generating: true, current: 0, total: selectedCards.length });
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const container = document.createElement('div');
      container.style.cssText = 'position:fixed;top:-9999px;left:-9999px;z-index:-1;';
      document.body.appendChild(container);
      
      const { createRoot } = await import('react-dom/client');
      
      for (let i = 0; i < selectedCards.length; i++) {
        const rc = selectedCards[i];
        setBulkProgress(prev => ({ ...prev, current: i + 1 }));
        
        const details = await reportCardService.getReportCardById(rc.id);
        
        const el = document.createElement('div');
        container.appendChild(el);
        
        const root = createRoot(el);
        await new Promise(resolve => {
          root.render(
            <MarksCardRenderer 
              reportCard={details.reportCard} 
              entries={details.entries} 
              branding={details.branding || {}}
              institutionName={details.organizationInfo?.orgName || orgData?.orgName}
              institutionAddress={details.organizationInfo?.address || orgData?.address}
              id={`bulk-rc-${rc.id}`}
              template={rcTemplate}
              gradingScale={details.gradingScale || []}
            />
          );
          setTimeout(resolve, 300);
        });
        
        const captureEl = document.getElementById(`bulk-rc-${rc.id}`);
        const canvas = await html2canvas(captureEl, { scale: 2, useCORS: true });
        const imgData = canvas.toDataURL('image/png');
        
        if (i > 0) {
          pdf.addPage();
        }
        pdf.addImage(imgData, 'PNG', 0, 0, 210, 297);
        
        root.unmount();
        container.removeChild(el);
      }
      
      document.body.removeChild(container);
      pdf.save(`Bulk_Report_Cards_${new Date().toISOString().slice(0,10)}.pdf`);
      setBulkProgress({ generating: false, current: 0, total: 0 });
      alert('Combined report cards PDF has been generated.');
    } catch (err) {
      console.error('Error generating bulk PDF:', err);
      alert('Error generating bulk PDF: ' + err.message);
      setBulkProgress({ generating: false, current: 0, total: 0 });
    }
  };

  useEffect(() => {
    if (activeTab === 'marks-cards') {
      loadReportCards();
      loadGradingSchemes();
    }
  }, [activeTab, rcFilters.className, rcFilters.academicYear, rcFilters.semester, rcFilters.term]);

  const [timetableLoading, setTimetableLoading] = useState(false);
  
  // Subject assignment state
  const [subjectAssignments, setSubjectAssignments] = useState([]);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);

  // Secure account settings state
  const [showSecureAccountSettings, setShowSecureAccountSettings] = useState(false);

  // Hostel management state
  const [hostels, setHostels] = useState([]);
  const [hostelAllocations, setHostelAllocations] = useState([]);
  const [showHostelModal, setShowHostelModal] = useState(false);
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [hostelLoading, setHostelLoading] = useState(false);
  const [selectedHostel, setSelectedHostel] = useState(null);
  const [selectedHostelRooms, setSelectedHostelRooms] = useState([]);
  const [hostelRoomsLoading, setHostelRoomsLoading] = useState(false);
  const [editingHostel, setEditingHostel] = useState(null);
  const [editingRoom, setEditingRoom] = useState(null);
  const [hostelFormData, setHostelFormData] = useState({
    hostelName: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    capacity: '',
    warden: '',
    phone: '',
    email: '',
    totalRooms: '',
    bedsPerRoom: ''
  });
  const [roomFormData, setRoomFormData] = useState({
    roomNumber: '',
    capacity: '',
    type: 'shared'
  });
  const [allocationFormData, setAllocationFormData] = useState({
    studentId: '',
    roomId: '',
    allocationDate: '',
    checkoutDate: ''
  });
  const [maintenanceFormData, setMaintenanceFormData] = useState({
    description: '',
    type: 'repair',
    priority: 'normal',
    status: 'pending'
  });

  // Vehicle management state
  const [vehicles, setVehicles] = useState([]);
  const [vehicleRoutes, setVehicleRoutes] = useState([]);
  const [vehicleAssignments, setVehicleAssignments] = useState([]);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [showRouteModal, setShowRouteModal] = useState(false);
  const [showVehicleAssignmentModal, setShowVehicleAssignmentModal] = useState(false);
  const [showVehicleMaintenanceModal, setShowVehicleMaintenanceModal] = useState(false);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleFormData, setVehicleFormData] = useState({
    vehicleNumber: '',
    vehicleType: 'bus',
    capacity: '',
    driverName: '',
    driverPhone: '',
    registrationNumber: '',
    insuranceExpiry: '',
    status: 'active'
  });
  const [routeFormData, setRouteFormData] = useState({
    routeName: '',
    startPoint: '',
    endPoint: '',
    distance: '',
    estimatedTime: '',
    vehicleId: ''
  });
  const [vehicleAssignmentFormData, setVehicleAssignmentFormData] = useState({
    studentId: '',
    vehicleId: '',
    routeId: '',
    pickupPoint: '',
    dropPoint: '',
    startDate: ''
  });
  const [vehicleMaintenanceFormData, setVehicleMaintenanceFormData] = useState({
    description: '',
    type: 'service',
    cost: '',
    date: '',
    nextDueDate: ''
  });

  // Form data states
  const [classFormData, setClassFormData] = useState({
    className: '',
    section: '',
    maxCapacity: '40',
    classTeacherName: '',
    roomNumber: '',
    shift: 'General Shift',
    academicYear: '2026-2027',
    status: 'ACTIVE'
  });

  const [teacherFormData, setTeacherFormData] = useState({
    teacherId: '',
    teacherName: '',
    dob: '',
    experience: '',
    phoneNumber: '',
    address: '',
    designation: '',
    gmail: '',
    qualifications: '',
    degree: '',
    specialization: '',
    password: '',
    status: 'ACTIVE',
    photo: '',
    photoUrl: '',
    subjects: [] // Array to hold selected subjects
  });

  const [studentFormData, setStudentFormData] = useState({
    studentId: '',
    name: '',
    photo: '',
    dob: '',
    gender: '',
    phone: '',
    email: '',
    class: '',
    section: '',
    admissionDate: new Date().toISOString().split('T')[0],
    admissionYear: new Date().getFullYear().toString(),
    aadhaarNumber: '',
    address: '',
    parentName: '',
    parentPhone: '',
    password: '',
    status: 'Active'
  });

  const [subjectFormData, setSubjectFormData] = useState({
    subjectName: '',
    subjectCode: '',
    classLevel: '',
    type: 'Core',
    status: 'ACTIVE',
    departmentName: '',
    theoryMarks: 80,
    practicalMarks: 20
  });

  const [subjectFormErrors, setSubjectFormErrors] = useState({});

  const [timetableFormData, setTimetableFormData] = useState({
    orgId: '',
    academicYear: new Date().getFullYear().toString(),
    className: '',
    section: '',
    schedule: {
      Monday: {},
      Tuesday: {},
      Wednesday: {},
      Thursday: {},
      Friday: {},
      Saturday: {}
    }
  });

  // Time slots state
  const [timeSlots, setTimeSlots] = useState(['09:00-10:00', '10:00-11:00', '11:00-12:00', '12:00-13:00', '13:00-14:00', '14:00-15:00', '15:00-16:00']);
  const [showCustomTimeModal, setShowCustomTimeModal] = useState(false);
  const [customTimeInput, setCustomTimeInput] = useState('');

  // Promote student state
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [promoteStudent, setPromoteStudent] = useState(null);
  const [promoteToClass, setPromoteToClass] = useState('');

  // Subject Assignment state
  const [studentSubjects, setStudentSubjects] = useState([]);
  const [expandedStudents, setExpandedStudents] = useState(new Set());
  const [expandedFeeStudents, setExpandedFeeStudents] = useState(new Set());
  const [assignmentFormData, setAssignmentFormData] = useState({
    classId: '',
    subjectIds: [],
    academicYear: new Date().getFullYear().toString()
  });

  // ✏️ Edit Fee & Immutable Audit Trail States
  const [showEditFeeModal, setShowEditFeeModal] = useState(false);
  const [selectedStudentForFeeEdit, setSelectedStudentForFeeEdit] = useState(null);
  const [selectedFeeForEdit, setSelectedFeeForEdit] = useState(null);
  const [feeEditActiveTab, setFeeEditActiveTab] = useState('details'); // 'details' | 'history'
  const [feeEditHistoryList, setFeeEditHistoryList] = useState([]);
  const [feeEditHistoryLoading, setFeeEditHistoryLoading] = useState(false);
  const [editFeeFormData, setEditFeeFormData] = useState({
    feeStructureId: '',
    feeStructureName: '',
    type: 'tuition',
    totalFees: '',
    concessionAmount: '',
    concessionReason: '',
    penaltyAmount: 0,
    dueDate: '',
    reason: '',
    items: [{ feeType: 'Tuition Fee', amount: '' }],
    installments: [{ installmentName: 'Term 1', amount: '', dueDate: '' }]
  });

  // 🔍 Fee Correction Confirmation Dialog States
  const [showConfirmCorrectionModal, setShowConfirmCorrectionModal] = useState(false);
  const [correctionReasonPreset, setCorrectionReasonPreset] = useState('Amount entered incorrectly');
  const [correctionReasonCustom, setCorrectionReasonCustom] = useState('');
  const [isCorrectingFee, setIsCorrectingFee] = useState(false);

  // 💰 Edit Cash Payment Transaction States (Admin Only)
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  const [selectedTxForEdit, setSelectedTxForEdit] = useState(null);
  const [selectedStudentForPaymentEdit, setSelectedStudentForPaymentEdit] = useState(null);
  const [editPaymentFormData, setEditPaymentFormData] = useState({
    newPaidAmount: '',
    reasonPreset: 'Amount entered incorrectly (Typo correction)',
    reasonCustom: '',
    notes: ''
  });
  const [isEditingPayment, setIsEditingPayment] = useState(false);

  // Download functionality state
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadConfig, setDownloadConfig] = useState({
    dataType: '', // 'students', 'teachers', 'subjects', 'classes', 'fees'
    format: 'csv', // 'csv', 'excel', 'pdf'
    filters: {},
    includeFields: []
  });

  // Student List Redesign state variables
  const [studentPage, setStudentPage] = useState(1);
  const [studentPageSize, setStudentPageSize] = useState(20);
  const [activeStudentRowMenu, setActiveStudentRowMenu] = useState(null);
  const [selectedStudentForDrawer, setSelectedStudentForDrawer] = useState(null);
  const [drawerActiveTab, setDrawerActiveTab] = useState('overview');

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.student-action-dropdown-container')) {
        setActiveStudentRowMenu(null);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setActiveStudentRowMenu(null);
        setSelectedStudentForDrawer(null);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    setStudentPage(1);
  }, [searchTerm, filters.class, filters.section, filters.academicYear]);

  // Load initial data
  useEffect(() => {
    // Load initial data on component mount for dashboard
    if (orgData?.orgId) {
      loadDashboardSummary();
      loadStudents();
      loadTeachers();
      loadClasses(false);
      loadSubjects();
      loadFees();
      loadTests();
    }
  }, [orgData?.orgId]);

  useEffect(() => {
    if (activeTab === 'students' && !dataLoaded.students) {
      loadStudents();
      setDataLoaded(prev => ({ ...prev, students: true }));
    } else if (activeTab === 'teachers' && !dataLoaded.teachers) {
      loadTeachers();
      setDataLoaded(prev => ({ ...prev, teachers: true }));
    } else if (activeTab === 'academics' && !dataLoaded.classes) {
      loadClasses();
      setDataLoaded(prev => ({ ...prev, classes: true }));
    } else if (activeTab === 'academics' && !dataLoaded.subjects) {
      // Don't load subjects here since they're loaded on mount
      setDataLoaded(prev => ({ ...prev, subjects: true }));
    } else if (activeTab === 'fees') {
      if (!dataLoaded.fees || fees.length === 0) {
        loadFees();
        setDataLoaded(prev => ({ ...prev, fees: true }));
      }
      if (students.length === 0 && !loadingStudents && !dataLoaded.students) {
        loadStudents();
      }
    }
  }, [activeTab]);

  // Load student subjects when assignments tab is opened
  useEffect(() => {
    if (activeTab === 'academics' && academicActiveTab === 'assignments') {
      loadStudentSubjects();
    }
  }, [activeTab, academicActiveTab]);

  // Browser back button functionality
  useEffect(() => {
    const handleBrowserBack = (event) => {
      // Prevent default browser back behavior
      event.preventDefault();
      // Reload page to go back to institution selector
      window.location.reload();
    };

    // Add event listener for browser back button
    window.addEventListener('popstate', handleBrowserBack);

    // Push current state to history to enable back button
    window.history.pushState({ page: 'admin' }, 'Admin Dashboard', window.location.href);

    // Cleanup function
    return () => {
      window.removeEventListener('popstate', handleBrowserBack);
    };
  }, []);

  const loadStudents = async () => {
    try {
      setLoading(true);
      setLoadingStudents(true);
      const targetOrgId = orgData?.orgId || localStorage.getItem('orgId');
      const response = await studentService.getStudents(targetOrgId);
      if (response && Array.isArray(response)) {
        setStudents(response);
      } else if (response && response.students && Array.isArray(response.students)) {
        setStudents(response.students);
      } else {
        setStudents([]);
      }
    } catch (error) {
      console.error('Error loading students:', error);
      showNotification('error', 'Error', 'Failed to load students');
    } finally {
      setLoading(false);
      setLoadingStudents(false);
    }
  };

  const loadTeachers = async () => {
    try {
      setLoading(true);
      console.log('Loading teachers for orgId:', orgData?.orgId);
      const response = await teacherService.getTeachersByOrg(orgData?.orgId);
      console.log('Teachers response:', response);
      
      const incoming = Array.isArray(response)
        ? response.filter(Boolean)
        : (response?.teachers?.filter(Boolean) || response?.data?.filter(Boolean) || []);
      
      if (incoming.length > 0) {
        setTeachers(prev => {
          if (!prev || prev.length === 0) return incoming;
          const prevMap = new Map(prev.map(t => [String(t.teacherId || t.id || t.teacherNumericId), t]));
          return incoming.map(inc => {
            const key = String(inc.teacherId || inc.id || inc.teacherNumericId);
            const old = prevMap.get(key);
            return {
              ...(old || {}),
              ...inc,
              photoUrl: inc.photoUrl || inc.photo || old?.photoUrl || old?.photo || '',
              photo: inc.photo || inc.photoUrl || old?.photo || old?.photoUrl || '',
              canUpdatePhoto: inc.canUpdatePhoto !== undefined ? inc.canUpdatePhoto : (old?.canUpdatePhoto ?? false),
              subjects: (inc.subjects && inc.subjects.length > 0) ? inc.subjects : (old?.subjects || [])
            };
          });
        });
      }
    } catch (error) {
      console.error('Error loading teachers:', error);
      showNotification('error', 'Error', 'Failed to load teachers');
    } finally {
      setLoading(false);
    }
  };

  const loadClasses = async (showLoader = true) => {
    try {
      if (showLoader) {
        setLoading(true);
      }
      const response = await classService.getAllClasses();
      if (response && response.classes && Array.isArray(response.classes)) {
        setClasses(response.classes);
      } else if (Array.isArray(response)) {
        setClasses(response);
      } else {
        setClasses([]);
      }
    } catch (error) {
      console.error('Error loading classes:', error);
      showNotification('error', 'Error', 'Failed to load classes');
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };

  const loadSubjects = async () => {
    try {
      setLoading(true);
      console.log('Loading subjects for orgId:', orgData?.orgId);
      const response = await subjectService.getSubjectsByOrg(orgData?.orgId);
      console.log('Subjects response:', response);
      
      if (Array.isArray(response)) {
        // Direct array response from getSubjectsByOrg
        console.log('Setting subjects from array response:', response.length, 'subjects');
        setSubjects(response.filter(Boolean));
      } else if (response && response.subjects && Array.isArray(response.subjects)) {
        // Object with subjects property
        console.log('Setting subjects from object response:', response.subjects.length, 'subjects');
        setSubjects(response.subjects.filter(Boolean));
      } else if (response && Array.isArray(response.data)) {
        // Object with data property
        console.log('Setting subjects from data property:', response.data.length, 'subjects');
        setSubjects(response.data.filter(Boolean));
      } else {
        console.warn('Unexpected response format:', response);
        setSubjects([]);
      }
    } catch (error) {
      console.error('Error loading subjects:', error);
      showNotification('error', 'Error', 'Failed to load subjects');
      setSubjects([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFeeStructures = async () => {
    try {
      const response = await feeService.getFeeStructures();
      if (Array.isArray(response)) {
        setFeeStructures(response);
      } else if (response && response.data && Array.isArray(response.data)) {
        setFeeStructures(response.data);
      } else if (response && response.structures && Array.isArray(response.structures)) {
        setFeeStructures(response.structures);
      } else {
        setFeeStructures([]);
      }
    } catch (error) {
      console.error('Error loading fee structures:', error);
      setFeeStructures([]);
    }
  };

  const loadFees = async (overrideAcademicYear) => {
    try {
      setLoading(true);
      setLoadingFees(true);
      const targetOrg = orgData?.orgId || localStorage.getItem('orgId');
      const targetYear = overrideAcademicYear || filters?.academicYear || feeFilters?.academicYear || '';
      const response = await feeService.getFeesByOrg(targetOrg, targetYear);
      if (response && response.fees && Array.isArray(response.fees)) {
        setFees(response.fees);
      } else if (Array.isArray(response)) {
        setFees(response);
      } else {
        setFees([]);
      }
      await loadFeeStructures();
    } catch (error) {
      console.error('Error loading fees:', error);
      showNotification('error', 'Error', 'Failed to load fees');
    } finally {
      setLoading(false);
      setLoadingFees(false);
    }
  };

  useEffect(() => {
    if (showAssignStructureModal || showCreateStructureModal) {
      loadFeeStructures();
    }
  }, [showAssignStructureModal, showCreateStructureModal]);

  useEffect(() => {
    if (showAssignStructureModal && assignFormData.feeStructureId && assignFormData.className) {
      feeService.previewAssignFeeStructure(assignFormData)
        .then(preview => setAssignPreview(preview))
        .catch(err => console.error('Failed to generate preview:', err));
    } else {
      setAssignPreview(null);
    }
  }, [showAssignStructureModal, assignFormData.feeStructureId, assignFormData.className, assignFormData.section]);

  const handleOpenEditFeeModal = async (student, fee) => {
    setSelectedStudentForFeeEdit(student);
    setSelectedFeeForEdit(fee || null);
    setFeeEditActiveTab('details');

    const defaultStructureId = fee?.feeStructureId || '';
    const defaultStructureName = fee?.feeStructureName || (fee?.type && fee?.type !== 'tuition' ? fee.type : '');
    const defaultTotal = fee && fee.totalFees != null ? fee.totalFees : '';
    const defaultConcession = fee && fee.concessionAmount != null ? fee.concessionAmount : '';
    const defaultConcessionReason = fee?.concessionReason || '';
    const defaultPenalty = fee && fee.penaltyAmount != null ? fee.penaltyAmount : 0;
    const defaultDueDate = fee?.dueDate ? (typeof fee.dueDate === 'string' ? fee.dueDate.split('T')[0] : '') : '';

    const defaultItems = (fee?.items && fee.items.length > 0)
      ? fee.items.map(it => ({ feeType: it.feeType, amount: it.originalAmount || it.amount || it.netAmount || '' }))
      : [{ feeType: 'Tuition Fee', amount: defaultTotal || '' }];

    const defaultInstallments = (fee?.installments && fee.installments.length > 0)
      ? fee.installments.map(inst => ({
          installmentName: inst.installmentName,
          amount: inst.amount || '',
          dueDate: inst.dueDate ? (typeof inst.dueDate === 'string' ? inst.dueDate.split('T')[0] : '') : ''
        }))
      : [{ installmentName: 'Term 1', amount: defaultTotal || '', dueDate: defaultDueDate || '' }];

    setEditFeeFormData({
      studentId: student.studentId,
      orgId: orgData?.orgId || student.orgId,
      academicYear: fee?.academicYear || filters.academicYear || assignmentFormData.academicYear || '2026-2027',
      feeStructureId: defaultStructureId,
      feeStructureName: defaultStructureName,
      type: fee?.type || 'tuition',
      totalFees: defaultTotal,
      concessionAmount: defaultConcession,
      concessionReason: defaultConcessionReason,
      penaltyAmount: defaultPenalty,
      dueDate: defaultDueDate,
      reason: '',
      items: defaultItems,
      installments: defaultInstallments
    });

    setShowEditFeeModal(true);

    // Fetch immutable audit history
    setFeeEditHistoryLoading(true);
    try {
      const feeIdOrStudent = fee?.feeId || student.studentId;
      const history = await feeService.getFeeHistory(feeIdOrStudent, orgData?.orgId);
      setFeeEditHistoryList(Array.isArray(history) ? history : []);
    } catch (err) {
      console.error('Failed to load fee edit history:', err);
      setFeeEditHistoryList([]);
    } finally {
      setFeeEditHistoryLoading(false);
    }
  };

  const handleSaveEditFee = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    const parsedTotal = parseFloat(editFeeFormData.totalFees);
    if (isNaN(parsedTotal) || parsedTotal < 0) {
      showNotification('error', 'Invalid Amount', 'Please enter a valid non-negative fee amount.');
      return;
    }

    const currentPaid = selectedFeeForEdit?.paidFees || 0;
    if (selectedFeeForEdit && parsedTotal < currentPaid) {
      showNotification('error', 'Payment Protection Alert', `This fee cannot be reduced below the amount already paid (₹${currentPaid.toLocaleString('en-IN')}). Please use the appropriate payment correction/refund process.`);
      return;
    }

    // If editing an existing fee, trigger the explicit 2-step confirmation modal
    if (selectedFeeForEdit) {
      setCorrectionReasonPreset('Amount entered incorrectly');
      setCorrectionReasonCustom(editFeeFormData.reason || '');
      setShowConfirmCorrectionModal(true);
      return;
    }

    // Otherwise, direct assignment for new fee
    await handleExecuteFeeCorrection(editFeeFormData.reason || 'Initial fee assignment');
  };

  const handleExecuteFeeCorrection = async (overrideReason) => {
    const finalReason = overrideReason || (
      correctionReasonPreset === 'Other'
        ? (correctionReasonCustom.trim() || 'Amount entered incorrectly')
        : (correctionReasonPreset + (correctionReasonCustom.trim() ? ': ' + correctionReasonCustom.trim() : ''))
    );

    if (!finalReason || !finalReason.trim()) {
      showNotification('warning', 'Reason Required', 'Please select or provide a reason for this fee correction for audit records.');
      return;
    }

    setIsCorrectingFee(true);
    setLoading(true);
    try {
      const payload = {
        studentId: selectedStudentForFeeEdit?.studentId,
        orgId: orgData?.orgId || selectedStudentForFeeEdit?.orgId,
        academicYear: editFeeFormData.academicYear || filters.academicYear || '2026-2027',
        feeStructureId: editFeeFormData.feeStructureId || null,
        feeStructureName: editFeeFormData.feeStructureName || null,
        type: editFeeFormData.type || 'tuition',
        totalFees: parseFloat(editFeeFormData.totalFees) || 0,
        concessionAmount: parseFloat(editFeeFormData.concessionAmount) || 0,
        concessionReason: editFeeFormData.concessionReason || '',
        penaltyAmount: parseFloat(editFeeFormData.penaltyAmount) || 0,
        dueDate: editFeeFormData.dueDate || null,
        reason: finalReason.trim(),
        items: (editFeeFormData.items || []).filter(it => it.feeType && it.amount).map(it => ({
          feeType: it.feeType,
          amount: parseFloat(it.amount) || 0
        })),
        installments: (editFeeFormData.installments || []).filter(inst => inst.installmentName && inst.amount).map(inst => ({
          installmentName: inst.installmentName,
          amount: parseFloat(inst.amount) || 0,
          dueDate: inst.dueDate || null
        }))
      };

      const targetKey = selectedFeeForEdit?.feeId || selectedStudentForFeeEdit?.studentId;
      const res = await feeService.editFee(targetKey, payload);

      if (res && (res.success || res.fee)) {
        const oldVal = res.oldTotalFee != null ? Number(res.oldTotalFee) : (selectedFeeForEdit?.totalFees || 0);
        const newVal = res.newTotalFee != null ? Number(res.newTotalFee) : (parseFloat(editFeeFormData.totalFees) || 0);
        const diff = res.difference != null ? Number(res.difference) : (newVal - oldVal);
        const diffStr = (diff >= 0 ? '+' : '') + '₹' + diff.toLocaleString('en-IN');

        showNotification(
          'success',
          selectedFeeForEdit ? 'Fee Corrected Successfully' : 'Fee Assigned',
          selectedFeeForEdit
            ? `Previous: ₹${oldVal.toLocaleString('en-IN')} → New: ₹${newVal.toLocaleString('en-IN')} (Diff: ${diffStr}). Audit trail logged.`
            : `Student fee assigned successfully.`
        );

        setShowConfirmCorrectionModal(false);
        setShowEditFeeModal(false);
        await loadFees();
      } else {
        showNotification('error', 'Operation Failed', res?.error || res?.message || 'Failed to save fee record.');
      }
    } catch (err) {
      console.error('Error saving fee correction:', err);
      showNotification('error', 'Correction Failed', err?.message || 'Failed to save fee edits.');
    } finally {
      setIsCorrectingFee(false);
      setLoading(false);
    }
  };

  const handleOpenEditPaymentModal = (tx, student) => {
    // Role verification: Admin only
    const userRoleStr = (userRole || '').toUpperCase();
    const isAdminUser = userRoleStr === 'SCHOOL_ADMIN' || userRoleStr === 'SUPER_ADMIN' || userRoleStr === 'COLLEGE_ADMIN' || userRoleStr === 'ADMIN';
    if (!isAdminUser) {
      showNotification('error', 'Admin Access Only', 'Only administrators have authorization to edit recorded payment amounts.');
      return;
    }

    setSelectedTxForEdit(tx);
    setSelectedStudentForPaymentEdit(student || selectedStudentForCollection);
    setEditPaymentFormData({
      newPaidAmount: tx?.paidAmount != null ? String(tx.paidAmount) : '',
      reasonPreset: 'Amount entered incorrectly (Typo correction)',
      reasonCustom: '',
      notes: ''
    });
    setShowEditPaymentModal(true);
  };

  const handleSaveEditedPayment = async () => {
    if (!selectedTxForEdit) return;

    const newAmtNum = parseFloat(editPaymentFormData.newPaidAmount);
    if (isNaN(newAmtNum) || newAmtNum <= 0) {
      showNotification('warning', 'Invalid Amount', 'Please enter a valid payment amount greater than ₹0.');
      return;
    }

    const oldAmtNum = parseFloat(selectedTxForEdit.paidAmount || 0);
    if (newAmtNum === oldAmtNum) {
      showNotification('info', 'No Change', 'The new amount is identical to the current payment amount.');
      return;
    }

    const finalReason = editPaymentFormData.reasonPreset === 'Other'
      ? (editPaymentFormData.reasonCustom.trim() || 'Typo / Amount correction')
      : (editPaymentFormData.reasonPreset + (editPaymentFormData.reasonCustom.trim() ? ': ' + editPaymentFormData.reasonCustom.trim() : ''));

    if (!finalReason || !finalReason.trim()) {
      showNotification('warning', 'Reason Required', 'Please select or provide a reason for editing this payment amount.');
      return;
    }

    setIsEditingPayment(true);
    try {
      const txId = selectedTxForEdit.transactionId || selectedTxForEdit.id;
      const payload = {
        newPaidAmount: newAmtNum,
        reason: finalReason.trim(),
        notes: editPaymentFormData.notes ? editPaymentFormData.notes.trim() : ''
      };

      const res = await feeService.editPaymentTransaction(txId, payload);
      if (res && res.success) {
        showNotification(
          'success',
          'Payment Updated',
          `Payment amount corrected from ₹${oldAmtNum.toLocaleString('en-IN')} to ₹${newAmtNum.toLocaleString('en-IN')}. Student fee balances updated.`
        );
        setShowEditPaymentModal(false);
        setSelectedTxForEdit(null);
        
        // Refresh fee data & transactions
        await loadFees();
        if (selectedStudentForCollection?.studentId) {
          const freshFees = await feeService.getFeeByStudentAndOrg(selectedStudentForCollection.studentId, orgData?.orgId);
          if (freshFees && freshFees.length > 0) {
            setCollectionFeeRecord(freshFees[0]);
          }
          const txs = await feeService.getTransactionHistory(selectedStudentForCollection.studentId, orgData?.orgId);
          setCollectionPayments(txs || []);
        }
        if (selectedStudentForHistory?.studentId) {
          const freshHistory = await feeService.getTransactionHistory(selectedStudentForHistory.studentId, orgData?.orgId);
          setHistoryPayments(freshHistory || []);
        }
        if (selectedStudentForFeeEdit?.studentId) {
          const targetKey = selectedFeeForEdit?.feeId || selectedStudentForFeeEdit?.studentId;
          const freshFeeHistory = await feeService.getFeeHistory(targetKey, orgData?.orgId);
          setFeeEditHistoryList(freshFeeHistory || []);
        }
      } else {
        showNotification('error', 'Update Failed', res?.error || res?.message || 'Failed to update payment amount.');
      }
    } catch (err) {
      console.error('Error updating payment transaction:', err);
      showNotification('error', 'Update Failed', err?.message || 'Failed to update payment amount.');
    } finally {
      setIsEditingPayment(false);
    }
  };

  const loadTests = async () => {
    try {
      const response = await reportCardService.getTests();
      if (response && response.success && Array.isArray(response.tests)) {
        setTests(response.tests);
      } else {
        setTests([]);
      }
    } catch (error) {
      console.error('Error loading tests:', error);
      setTests([]);
    }
  };

  const loadDashboardSummary = async () => {
    try {
      setDashboardLoading(true);
      const res = await dashboardService.getDashboardSummary(orgData?.orgId);
      if (res) {
        setDashboardSummary(res);
      }
    } catch (error) {
      console.error('Error loading dashboard summary:', error);
    } finally {
      setDashboardLoading(false);
    }
  };

  const handleSaveTest = async (e) => {
    if (e) e.preventDefault();
    try {
      setLoading(true);
      const orgId = orgData?.orgId || 'SCHOOL';
      
      const payload = {
        title: testForm.title,
        description: testForm.description,
        className: testForm.className,
        section: testForm.section,
        subjectName: testForm.subjectName || '',
        testDate: testForm.testDate,
        maxMarks: parseInt(testForm.maxMarks),
        instructions: testForm.instructions,
        teacherId: testForm.teacherId || 'ADMIN',
        adminEmail: orgData?.email || 'admin@school.edu'
      };

      let result;
      if (editingTest) {
        result = await reportCardService.updateTest(editingTest.testId, payload);
      } else {
        result = await reportCardService.createTest(payload);
      }

      if (result && result.success) {
        showNotification('success', editingTest ? 'Test Updated' : 'Test Created', `Test "${testForm.title}" has been saved successfully.`);
        setShowTestModal(false);
        setEditingTest(null);
        setTestForm({
          title: '',
          description: '',
          className: '',
          section: '',
          subjectName: '',
          testDate: '',
          maxMarks: '',
          instructions: '',
          teacherId: ''
        });
        await loadTests();
      } else {
        showNotification('error', 'Save Failed', result.error || 'Failed to save test');
      }
    } catch (error) {
      console.error('Error saving test:', error);
      showNotification('error', 'Save Failed', error.message || 'An error occurred while saving the test');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTest = async (testId, title) => {
    if (window.confirm(`Are you sure you want to delete the test "${title}"? This will also delete all associated marks.`)) {
      try {
        setLoading(true);
        const result = await reportCardService.deleteTest(testId);
        if (result && result.success) {
          showNotification('success', 'Test Deleted', `Test "${title}" has been deleted.`);
          await loadTests();
          if (selectedTest?.testId === testId) {
            setSelectedTest(null);
            setTestResults([]);
          }
        } else {
          showNotification('error', 'Delete Failed', result.error || 'Failed to delete test');
        }
      } catch (error) {
        console.error('Error deleting test:', error);
        showNotification('error', 'Delete Failed', error.message || 'Failed to delete test');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleViewTestResults = async (test) => {
    setSelectedTest(test);
    setTestResults([]);
    setIsEnteringMarks(true);
    setResultsLoading(true);
    try {
      const response = await reportCardService.getTestResults(test.testId);
      if (response && response.success && Array.isArray(response.results)) {
        setTestResults(response.results);
      } else {
        setTestResults([]);
      }
    } catch (error) {
      console.error('Failed to load test results:', error);
      showNotification('error', 'Fetch Failed', 'Could not load test results');
    } finally {
      setResultsLoading(false);
    }
  };

  const handleSaveStudentMark = async (studentId) => {
    try {
      setResultsLoading(true);
      const marksObtained = parseInt(editMarkForm.marksObtained);
      if (isNaN(marksObtained) || marksObtained < 0 || marksObtained > selectedTest.maxMarks) {
        alert(`Please enter marks between 0 and ${selectedTest.maxMarks}`);
        setResultsLoading(false);
        return;
      }

      const resultsData = [{
        studentId: studentId,
        marksObtained: marksObtained,
        remarks: editMarkForm.remarks || ''
      }];

      const response = await reportCardService.uploadTestResults(selectedTest.testId, resultsData);
      if (response && response.success) {
        showNotification('success', 'Marks Saved', 'Student marks updated successfully.');
        setEditingMarkStudentId(null);
        const resultsRes = await reportCardService.getTestResults(selectedTest.testId);
        if (resultsRes && resultsRes.success && Array.isArray(resultsRes.results)) {
          setTestResults(resultsRes.results);
        }
      } else {
        showNotification('error', 'Save Failed', response.error || 'Failed to save marks');
      }
    } catch (error) {
      console.error('Failed to save student marks:', error);
      showNotification('error', 'Save Failed', error.message || 'Failed to save marks');
    } finally {
      setResultsLoading(false);
    }
  };

  const extractErrorMessage = (error, fallback = 'An unexpected error occurred. Please try again.') => {
    if (!error) return fallback;

    const possibleMessage = (() => {
      if (typeof error === 'string') return error;
      if (error?.response?.data?.error) return error.response.data.error;
      if (error?.response?.data?.message) return error.response.data.message;
      if (error?.response?.error) return error.response.error;
      if (error?.response?.message) return error.response.message;
      if (error?.message) {
        const match = error.message.match(/HTTP\s\d+:\s*(.*)$/i);
        if (match && match[1]) {
          const payload = match[1].trim();
          try {
            const parsed = JSON.parse(payload);
            if (parsed?.error) return parsed.error;
            if (parsed?.message) return parsed.message;
            if (typeof parsed === 'string') return parsed;
          } catch (parseError) {
            // not JSON, fallback to plain text
            if (payload) return payload;
          }
        }
        return error.message;
      }
      return null;
    })();

    if (!possibleMessage) return fallback;

    const cleaned = String(possibleMessage).replace(/^"|"$/g, '').trim();
    return cleaned || fallback;
  };

  const buildStudentPayload = (studentData) => {
    const payload = { ...studentData };

    payload.className = studentData.className || studentData.class || '';
    payload.section = studentData.section || '';
    payload.rollNo = studentData.rollNo || studentData.rollNumber || '';
    payload.rollNumber = payload.rollNo;

    // Convert numeric & boolean fields for Spring Boot backend
    payload.parentsAnnualIncome = studentData.parentsAnnualIncome ? parseFloat(studentData.parentsAnnualIncome) : null;
    payload.numberOfDependents = studentData.numberOfDependents ? parseInt(studentData.numberOfDependents, 10) : null;
    payload.elderBrothersCount = studentData.elderBrothersCount ? parseInt(studentData.elderBrothersCount, 10) : 0;
    payload.youngerBrothersCount = studentData.youngerBrothersCount ? parseInt(studentData.youngerBrothersCount, 10) : 0;
    payload.elderSistersCount = studentData.elderSistersCount ? parseInt(studentData.elderSistersCount, 10) : 0;
    payload.youngerSistersCount = studentData.youngerSistersCount ? parseInt(studentData.youngerSistersCount, 10) : 0;
    payload.tcProduced = studentData.tcProduced === 'Yes' || studentData.tcProduced === true;

    if (payload.tcProduced === false) {
      payload.tcNumber = null;
      payload.tcDate = null;
    }

    // For SCHOOL institution type, map section to courseName and set classId
    if (orgData?.institutionType === 'SCHOOL') {
      payload.courseName = studentData.section || '';

      // Find the classId from classOptions based on the selected class name
      const selectedClass = classOptions.find(cls => cls.label === payload.className);
      payload.classId = selectedClass ? selectedClass.id : null;

      payload.departmentName = null;
      payload.degree = null;
      payload.semester = null;
      // Keep payload.section so it persists in student entity
    }

    delete payload.class;

    payload.phone = studentData.phone || studentData.parentPhone || studentData.fatherPhone || studentData.guardianPhone || '';
    payload.parentPhone = studentData.parentPhone || studentData.fatherPhone || studentData.phone || '';
    payload.fatherPhone = studentData.fatherPhone || studentData.parentPhone || studentData.phone || '';
    payload.fatherName = studentData.fatherName || studentData.parentName || '';
    payload.motherName = studentData.motherName || '';
    payload.admissionDate = studentData.admissionDate || new Date().toISOString().split('T')[0];

    if (!payload.institutionType) {
      payload.institutionType = orgData?.institutionType || 'School';
    }

    if (!payload.password || payload.password.trim() === '') {
      payload.password = orgData?.defaultStudentPassword || 
                         localStorage.getItem(`erp_default_pwd_${orgData?.orgId || 'default'}`) || 
                         localStorage.getItem('erp_default_pwd_default') || 
                         'Student@123';
    }
    if (!payload.email || payload.email.trim() === '') {
      const cleanName = (payload.name || 'student').toLowerCase().replace(/[^a-z0-9]/g, '');
      const domain = getSchoolEmailDomain();
      payload.email = `${cleanName}${Date.now().toString().slice(-4)}@${domain}`;
    }

    return payload;
  };

  const showNotification = (type, title, message) => {
    setNotification({ type, title, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleStudentPhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        showNotification('error', 'File Too Large', 'Photo size should be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setStudentFormData(prev => ({
          ...prev,
          photo: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTeacherPhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        showNotification('error', 'Invalid File Format', 'Please upload a PNG, JPG, or WEBP image.');
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        showNotification('error', 'File Too Large', 'Photo size should be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setTeacherFormData(prev => ({
          ...prev,
          photo: reader.result,
          photoUrl: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFinancePasswordSubmit = (e) => {
    e.preventDefault();
    if (!financePassword || !financePassword.trim()) {
      showNotification('error', 'Error', 'Please enter the finance password to continue');
      return;
    }
    showNotification('success', 'Access Granted', 'Finance dashboard access granted (demo)');
    setShowFinancePasswordModal(false);
    setFinancePassword('');
  };

  const handleOpenFinancePasswordModal = () => {
    setFinancePassword('');
    setShowFinancePasswordModal(true);
  };

  const handleCloseFinancePasswordModal = () => {
    setShowFinancePasswordModal(false);
    setFinancePassword('');
  };

  const getActionButtonStyle = (variant = 'primary') => {
    const palette = {
      primary: {
        bg: '#5b63ff',
        bgHover: '#4c52ee'
      },
      danger: {
        bg: '#ff5757',
        bgHover: '#ff3838'
      }
    };

    const { bg } = palette[variant] || palette.primary;

    return {
      background: bg,
      color: '#ffffff',
      border: 'none',
      borderRadius: '5px',
      padding: '4px 10px',
      fontSize: '11px',
      fontWeight: 600,
      letterSpacing: '0.2px',
      cursor: 'pointer',
      transition: 'all 0.15s ease',
      boxShadow: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      marginRight: '4px'
    };
  };

  const handleActionButtonHover = (event, isHover, variant = 'primary') => {
    if (!event?.currentTarget) return;
    const target = event.currentTarget;

    requestAnimationFrame(() => {
      target.style.transform = isHover ? 'scale(1.02)' : 'scale(1)';
      target.style.boxShadow = isHover
        ? '0 2px 6px rgba(0, 0, 0, 0.15)'
        : 'none';
    });
  };

  // Dashboard statistics
  const getDashboardStats = () => {
    const totalStudents = students.length;
    
    // Check both 'active' boolean property and 'status' string property for compatibility
    const activeStudents = students.filter(s => {
      return s.active === true || s.status === 'Active';
    }).length;
    
    const inactiveStudents = students.filter(s => {
      return s.active === false || s.status === 'Inactive' || s.status === 'Registered';
    }).length;
    
    const totalTeachers = teachers.length;
    
    return {
      totalStudents,
      activeStudents,
      inactiveStudents,
      totalTeachers
    };
  };

  // Auto convert DOB date to words helper
  const convertDobToWords = (dobString) => {
    if (!dobString) return '';
    const parts = dobString.split('-');
    if (parts.length !== 3) return dobString;
    const year = parseInt(parts[0], 10);
    const monthIdx = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);

    const dayNames = ["", "First", "Second", "Third", "Fourth", "Fifth", "Sixth", "Seventh", "Eighth", "Ninth", "Tenth", "Eleventh", "Twelfth", "Thirteenth", "Fourteenth", "Fifteenth", "Sixteenth", "Seventeenth", "Eighteenth", "Nineteenth", "Twentieth", "Twenty-First", "Twenty-Second", "Twenty-Third", "Twenty-Fourth", "Twenty-Fifth", "Twenty-Sixth", "Twenty-Seventh", "Twenty-Eighth", "Twenty-Ninth", "Thirtieth", "Thirty-First"];
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    
    if (day >= 1 && day <= 31 && monthIdx >= 0 && monthIdx < 12) {
      return `${dayNames[day] || day} ${monthNames[monthIdx]} ${year}`;
    }
    return dobString;
  };

  // Format time ago helper function
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  // Add recent activity function
  const addRecentActivity = (icon, description, details = '') => {
    const activity = {
      id: Date.now(),
      icon,
      description,
      details,
      timestamp: new Date().toISOString()
    };
    
    setRecentActivities(prev => [activity, ...prev].slice(0, 20)); // Keep only last 20 activities
  };

  // Helper functions for student management
  const openModal = (type) => {
    setEditingItem(null);
    setShowModal(true);
  };

  const refreshStudentData = async () => {
    await loadStudents();
    showNotification('success', 'Refreshed', 'Student data refreshed successfully');
  };

  const handleStudentSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isSubmitting || loading) return;
    setLoading(true);
    setIsSubmitting(true);
    try {
      if (editingItem) {
        const studentId = editingItem.studentId;
        const updateData = buildStudentPayload({ ...studentFormData });
        if (!studentFormData.password || studentFormData.password.trim() === '') {
          delete updateData.password;
        }
        console.log('📤 Updating student with data:', updateData);
        const updateResponse = await studentService.updateStudent(studentId, updateData);
        const updated = updateResponse?.student || updateResponse?.data || updateResponse;
        if (updated && (updated.studentId || updated.id)) {
          setStudents(prev => prev.map(s => s.studentId === studentId ? { ...s, ...updated } : s));
        }
        showNotification('success', 'Success', 'Student updated successfully!');
        
        addRecentActivity('✏️', `Student updated: ${updated?.name || studentFormData.name || studentId}`, `Student ID: ${updated?.studentId || studentId}`);
      } else {
        const createData = buildStudentPayload({ ...studentFormData });
        console.log('📤 Creating student with data:', createData);
        const createResponse = await studentService.createStudent(createData);
        const created = createResponse?.student || createResponse?.data || createResponse;
        if (created && (created.studentId || created.id)) {
          setStudents(prev => [...prev, created]);
        }
        showNotification('success', 'Success', 'Student created successfully!');
        
        addRecentActivity('👨‍🎓', `New student added: ${created?.name || studentFormData.name}`, `Student ID: ${created?.studentId || ''}`);
      }

      const currentUserId = localStorage.getItem('userId') || localStorage.getItem('user_id');
      if (orgData?.id && currentUserId) {
        localStorage.removeItem(`erp_student_admission_draft_${orgData.id}_${currentUserId}`);
      }

      setShowModal(false);
      setEditingItem(null);
      setValidationErrors({});
      setStudentFormData({
        studentId: '',
        name: '',
        rollNumber: '',
        photo: '',
        dob: '',
        gender: '',
        bloodGroup: '',
        phone: '',
        email: '',
        class: '',
        section: '',
        admissionYear: new Date().getFullYear().toString(),
        aadhaarNumber: '',
        address: '',
        fatherName: '',
        motherName: '',
        parentName: '',
        parentPhone: '',
        emergencyPhone: '',
        hostelOpted: 'No',
        transportOpted: 'No',
        password: '',
        status: 'Active'
      });
      await loadStudents();
    } catch (error) {
      console.error('Error saving student:', error);
      const errorMessage = extractErrorMessage(error, 'Failed to save student. Please try again.');
      setDraftToast(`⚠️ ${errorMessage}`);
      showNotification('error', 'Registration Error', errorMessage);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  // --- Bulk Import Helper Functions ---
  const createEmptyBulkStudentRow = () => ({
    name: '',
    dob: '',
    gender: 'Boy',
    class: '',
    section: '',
    parentPhone: '',
    admissionDate: new Date().toISOString().slice(0, 10),
    satsNo: '',
    penNo: ''
  });

  const openBulkAddModal = () => {
    setBulkImportMode('manual');
    setBulkPreviewFilter('all');
    setBulkFileError(null);
    setBulkSuccessResult(null);
    setBulkStudentRows([createEmptyBulkStudentRow()]);
    setShowBulkAddModal(true);
  };

  const addMoreBulkRows = (count = 1) => {
    const newRows = Array.from({ length: count }, () => createEmptyBulkStudentRow());
    setBulkStudentRows(prev => [...prev, ...newRows]);
  };

  const updateBulkRow = (index, field, value) => {
    setBulkStudentRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeBulkRow = (index) => {
    if (bulkStudentRows.length <= 1) return;
    setBulkStudentRows(prev => prev.filter((_, i) => i !== index));
  };

  // Helper to normalize Excel Dates safely
  const normalizeExcelDate = (val) => {
    if (!val) return '';
    if (val instanceof Date && !isNaN(val.getTime())) {
      return val.toISOString().slice(0, 10);
    }
    if (typeof val === 'number') {
      const dateObj = XLSX.SSF ? XLSX.SSF.parse_date_code(val) : null;
      if (dateObj) {
        const y = dateObj.y;
        const m = String(dateObj.m).padStart(2, '0');
        const d = String(dateObj.d).padStart(2, '0');
        return `${y}-${m}-${d}`;
      }
    }
    const strVal = String(val).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(strVal)) return strVal;
    const ddmmyyyy = strVal.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
    if (ddmmyyyy) {
      const d = ddmmyyyy[1].padStart(2, '0');
      const m = ddmmyyyy[2].padStart(2, '0');
      const y = ddmmyyyy[3];
      return `${y}-${m}-${d}`;
    }
    return strVal;
  };

  // Helper to normalize Phone / Identifier Strings
  const normalizeStringValue = (val) => {
    if (val === null || val === undefined) return '';
    return String(val).trim();
  };

  const normalizePhone = (val) => {
    const raw = normalizeStringValue(val);
    const digits = raw.replace(/\D/g, '');
    return digits.length >= 10 ? digits.slice(-10) : digits;
  };

  // Pre-validate single student row
  const validateBulkStudentRow = (row, index, allRows = [], existingStudentsList = []) => {
    const errors = [];
    
    // Completely empty row check
    const isCompletelyEmpty = !row.name && !row.dob && !row.class && !row.parentPhone && !row.satsNo && !row.penNo;
    if (isCompletelyEmpty) {
      return { isCompletelyEmpty: true, errors: [] };
    }

    if (!row.name || !row.name.trim()) {
      errors.push('Student Name is required');
    }
    if (!row.dob || !row.dob.trim()) {
      errors.push('Date of Birth is required');
    }
    if (!row.gender) {
      errors.push('Gender is required');
    }
    if (!row.class) {
      errors.push('Class is required');
    } else {
      const validClass = classOptions.some(c => c.label.toLowerCase() === row.class.trim().toLowerCase());
      if (!validClass) {
        errors.push(`Class '${row.class}' does not exist in academic structure`);
      }
    }

    if (row.section && sectionOptions.length > 0) {
      const validSection = sectionOptions.some(s => String(s).toLowerCase() === String(row.section).trim().toLowerCase());
      if (!validSection) {
        errors.push(`Section '${row.section}' invalid for selected Class`);
      }
    }

    const cleanPhone = normalizePhone(row.parentPhone);
    if (!cleanPhone || cleanPhone.length !== 10) {
      errors.push('Primary Parent Phone must contain a valid 10-digit mobile number');
    }

    // Duplicate detection in batch
    if (row.satsNo && row.satsNo.trim()) {
      const dupInBatch = allRows.filter((r, i) => i !== index && r.satsNo && r.satsNo.trim().toLowerCase() === row.satsNo.trim().toLowerCase());
      if (dupInBatch.length > 0) errors.push(`Duplicate SATS No '${row.satsNo}' within batch file`);
      
      const dupInDB = (existingStudentsList || []).some(s => s.satsNo && String(s.satsNo).trim().toLowerCase() === row.satsNo.trim().toLowerCase());
      if (dupInDB) errors.push(`SATS No '${row.satsNo}' already belongs to an existing student in database`);
    }

    if (row.penNo && row.penNo.trim()) {
      const dupInBatch = allRows.filter((r, i) => i !== index && r.penNo && r.penNo.trim().toLowerCase() === row.penNo.trim().toLowerCase());
      if (dupInBatch.length > 0) errors.push(`Duplicate PEN No '${row.penNo}' within batch file`);

      const dupInDB = (existingStudentsList || []).some(s => s.penNo && String(s.penNo).trim().toLowerCase() === row.penNo.trim().toLowerCase());
      if (dupInDB) errors.push(`PEN No '${row.penNo}' already belongs to an existing student in database`);
    }

    return { isCompletelyEmpty: false, errors };
  };

  // Generate official Excel template
  const handleDownloadBulkTemplate = () => {
    const templateData = [
      {
        'Student Name': 'Rahul Kumar',
        'Date of Birth': '2015-08-15',
        'Gender': 'Boy',
        'Class': classOptions.length > 0 ? classOptions[0].label : 'Class 1',
        'Section': 'A',
        'Parent Phone': '9876543210',
        'Admission Date': new Date().toISOString().slice(0, 10),
        'SATS No': 'SATS1001',
        'PEN No': 'PEN1001'
      },
      {
        'Student Name': 'Priya Sharma',
        'Date of Birth': '2016-04-20',
        'Gender': 'Girl',
        'Class': classOptions.length > 0 ? classOptions[0].label : 'Class 1',
        'Section': 'B',
        'Parent Phone': '9876543211',
        'Admission Date': new Date().toISOString().slice(0, 10),
        'SATS No': 'SATS1002',
        'PEN No': 'PEN1002'
      }
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(templateData);
    XLSX.utils.book_append_sheet(wb, ws, 'Student Admission Template');
    XLSX.writeFile(wb, 'Official_Student_Admission_Template.xlsx');
  };

  // File parsing logic with Header Validation and 500 Row / 5MB Limits
  const handleExcelFileUpload = (file) => {
    if (!file) return;
    setBulkFileError(null);

    // Limit File Size to 5MB
    if (file.size > 5 * 1024 * 1024) {
      setBulkFileError('File size exceeds 5 MB limit. Please upload a smaller file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rawJson = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        if (rawJson.length === 0) {
          setBulkFileError('Uploaded file contains no data rows.');
          return;
        }

        // Limit maximum rows to 500
        if (rawJson.length > 500) {
          setBulkFileError(`File contains ${rawJson.length} rows. Maximum allowed is 500 rows per batch.`);
          return;
        }

        // Header mapping check
        const firstRow = rawJson[0];
        const normalizedKeys = Object.keys(firstRow).map(k => k.trim().toLowerCase());
        const hasName = normalizedKeys.some(k => k.includes('name'));
        const hasClass = normalizedKeys.some(k => k.includes('class'));
        const hasPhone = normalizedKeys.some(k => k.includes('phone') || k.includes('mobile'));

        if (!hasName || !hasClass || !hasPhone) {
          setBulkFileError('Unable to import file: Required headers missing! Required columns: Student Name, Class, Parent Phone.');
          return;
        }

        // Normalize rows and ignore completely empty rows
        const parsedRows = [];
        for (const rowObj of rawJson) {
          const findKey = (candidates) => {
            const key = Object.keys(rowObj).find(k => candidates.includes(k.trim().toLowerCase()));
            return key ? rowObj[key] : '';
          };

          const name = normalizeStringValue(findKey(['student name', 'name', 'full name', 'student_name'])).toUpperCase();
          const dob = normalizeExcelDate(findKey(['date of birth', 'dob', 'birth date']));
          const genderRaw = normalizeStringValue(findKey(['gender', 'sex']));
          const gender = genderRaw.toLowerCase().startsWith('g') || genderRaw.toLowerCase() === 'female' ? 'Girl' : (genderRaw.toLowerCase().startsWith('b') || genderRaw.toLowerCase() === 'male' ? 'Boy' : 'Boy');
          const className = normalizeStringValue(findKey(['class', 'standard', 'grade']));
          const section = normalizeStringValue(findKey(['section', 'sec']));
          const parentPhone = normalizePhone(findKey(['parent phone', 'phone', 'mobile', 'parent_phone', 'contact']));
          const admissionDate = normalizeExcelDate(findKey(['admission date', 'admission_date'])) || new Date().toISOString().slice(0, 10);
          const satsNo = normalizeStringValue(findKey(['sats no', 'sats', 'sats_no']));
          const penNo = normalizeStringValue(findKey(['pen no', 'pen', 'pen_no']));

          // Skip completely empty rows
          if (!name && !dob && !className && !parentPhone && !satsNo && !penNo) continue;

          parsedRows.push({
            name,
            dob,
            gender,
            class: className,
            section,
            parentPhone,
            admissionDate,
            satsNo,
            penNo
          });
        }

        if (parsedRows.length === 0) {
          setBulkFileError('All rows in the uploaded spreadsheet were completely empty.');
          return;
        }

        setBulkStudentRows(parsedRows);
        setBulkImportMode('excel');
      } catch (err) {
        console.error('Error reading spreadsheet:', err);
        setBulkFileError('Failed to parse spreadsheet file. Please check file format and try again.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleBulkStudentSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isSubmitting || loading) return;

    // Filter out completely empty rows and validate all rows
    const nonEmptyRows = bulkStudentRows.filter(row => {
      const res = validateBulkStudentRow(row, 0, [], students);
      return !res.isCompletelyEmpty;
    });

    if (nonEmptyRows.length === 0) {
      showNotification('error', 'Error', 'Please enter at least one student row before importing');
      return;
    }

    // Check if any row has validation errors
    let totalErrors = 0;
    for (let i = 0; i < nonEmptyRows.length; i++) {
      const res = validateBulkStudentRow(nonEmptyRows[i], i, nonEmptyRows, students);
      if (res.errors.length > 0) totalErrors++;
    }

    if (totalErrors > 0) {
      showNotification('error', 'Validation Failed', `Please resolve all ${totalErrors} invalid student row(s) before importing.`);
      return;
    }

    setLoading(true);
    setIsSubmitting(true);
    try {
      let successCount = 0;
      let failCount = 0;
      const errors = [];
      const createdStudents = [];

      for (const student of nonEmptyRows) {
        try {
          const studentData = buildStudentPayload({
            ...student,
            phone: student.phone || student.parentPhone,
            fatherPhone: student.parentPhone,
            admissionYear: orgData?.academicYear || new Date().getFullYear().toString(),
            status: 'Active'
          });
          const created = await studentService.createStudent(studentData);
          createdStudents.push(created);
          successCount++;
        } catch (error) {
          failCount++;
          const errorMessage = extractErrorMessage(error, 'Failed to create');
          errors.push(`${student.name || 'Student'}: ${errorMessage}`);
        }
      }

      if (successCount > 0) {
        await loadStudents();
        addRecentActivity('📊', `${successCount} students imported in bulk`, `Successfully created ${successCount} student records`);
        
        setBulkSuccessResult({
          totalCount: successCount,
          students: createdStudents
        });
      } else {
        showNotification('error', 'Import Failed', errors.join(' | ') || 'Failed to import student records.');
      }
    } catch (error) {
      console.error('Error in bulk upload:', error);
      const errorMessage = extractErrorMessage(error, 'Bulk upload failed. Please try again.');
      showNotification('error', 'Error', errorMessage);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      academicYear: '',
      class: '',
      section: ''
    });
    setSearchTerm('');
  };

  const getUniqueValues = (field) => {
    const values = students.map(student => {
      if (field === 'class') return student.className;
      if (field === 'section') return student.section;
      if (field === 'admissionYear') return student.admissionYear;
      return student[field];
    }).filter(value => value && value !== '');
    return [...new Set(values)].sort();
  };

  // Academic management handlers
  const handleClassSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const matchedTeacher = (teachers || []).find(t => t.teacherName === classFormData.classTeacherName || t.name === classFormData.classTeacherName);
      const teacherIdVal = matchedTeacher ? (matchedTeacher.teacherId || matchedTeacher.id) : undefined;

      if (editingAcademicItem) {
        // Edit existing class



      const updateData = {
          className: classFormData.className,
          section: classFormData.section,
          maxCapacity: parseInt(classFormData.maxCapacity) || 40,
          classTeacherName: classFormData.classTeacherName,
          classTeacherId: teacherIdVal,
          roomNumber: classFormData.roomNumber || '',
          shift: classFormData.shift || 'General Shift',
          academicYear: classFormData.academicYear || '2026-2027',
          status: classFormData.status,
          organizationId: orgData?.orgId
        };

        const classTargetId = editingAcademicItem.classId || editingAcademicItem.id;
        const updatedClass = await classService.updateClass(classTargetId, updateData);
        
        setClasses(prev => prev.map(cls => 
          (cls.classId || cls.id) === classTargetId ? (updatedClass || { ...cls, ...updateData }) : cls
        ));
        showNotification('success', 'Success', 'Class updated successfully!');
      } else {
        // Add new class using API
        const newClassData = {
          className: classFormData.className,
          section: classFormData.section,
          maxCapacity: parseInt(classFormData.maxCapacity) || 40,
          classTeacherName: classFormData.classTeacherName,
          classTeacherId: teacherIdVal,
          roomNumber: classFormData.roomNumber || '',
          shift: classFormData.shift || 'General Shift',
          academicYear: classFormData.academicYear || '2026-2027',
          status: classFormData.status || 'ACTIVE',
          organizationId: orgData?.orgId
        };

        const res = await classService.createClass(newClassData);
        const createdClassObj = res?.data || res?.class || res;
        
        if (createdClassObj) {
          setClasses(prev => [...prev, createdClassObj]);
        }
        
        // Reload classes to ensure UI updates with complete data
        try {
          const response = orgData?.orgId 
            ? await classService.getClassesByOrg(orgData.orgId)
            : await classService.getAllClasses();

          const classList = response?.data || response?.classes || (Array.isArray(response) ? response : null);
          if (Array.isArray(classList) && classList.length > 0) {
            setClasses(classList);
          }
        } catch (reloadErr) {
          console.warn('Could not reload classes list:', reloadErr);
        }

        showNotification('success', 'Success', 'Class created successfully!');
        
        // Add recent activity
        addRecentActivity('🏫', `New class added: ${createdClassObj?.className || classFormData.className}`, `Section ${createdClassObj?.section || classFormData.section}`);
      }

      setShowAcademicModal(false);
      setEditingAcademicItem(null);
      setClassFormData({
        className: '',
        section: '',
        maxCapacity: '40',
        classTeacherName: '',
        roomNumber: '',
        shift: 'General Shift',
        academicYear: '2026-2027',
        status: 'ACTIVE'
      });
    } catch (error) {
      console.error('Error saving class:', error);
      showNotification('error', 'Error', 'Failed to save class. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubjectSubmit = async (e) => {
    e.preventDefault();
    
    // Inline field-level validation
    const errors = {};
    if (!subjectFormData.subjectName || !subjectFormData.subjectName.trim()) {
      errors.subjectName = 'Subject name is required';
    }
    if (!subjectFormData.subjectCode || !subjectFormData.subjectCode.trim()) {
      errors.subjectCode = 'Subject code is required';
    }
    if (Object.keys(errors).length > 0) {
      setSubjectFormErrors(errors);
      return;
    }
    setSubjectFormErrors({});

    setLoading(true);
    try {
      const theory = parseInt(subjectFormData.theoryMarks || 80, 10);
      const practical = parseInt(subjectFormData.practicalMarks || 20, 10);
      const maxM = theory + practical;

      const subjectData = {
        ...subjectFormData,
        subjectName: subjectFormData.subjectName.trim(),
        subjectCode: subjectFormData.subjectCode.trim(),
        classLevel: subjectFormData.classLevel || null,
        departmentName: subjectFormData.departmentName || null,
        type: subjectFormData.type || 'Core',
        status: subjectFormData.status || 'ACTIVE',
        theoryMarks: theory,
        practicalMarks: practical,
        maxMarks: maxM,
        orgId: orgData?.orgId,
        institutionType: orgData?.institutionType || 'SCHOOL'
      };

      if (editingAcademicItem) {
        // Edit existing subject
        const response = await subjectService.updateSubject(editingAcademicItem.subjectId, subjectData);
        const updatedSubject = (response && response.success) ? response.subject : (response || subjectData);
        
        setSubjects(prev => prev.map(subj => 
          subj.subjectId === editingAcademicItem.subjectId ? { ...subj, ...subjectData, ...updatedSubject } : subj
        ));
        showNotification('success', 'Success', 'Subject updated successfully!');
      } else {
        // Create new subject
        const response = await subjectService.createSubject(subjectData);
        const createdSubject = (response && response.success) ? response.subject : (response || subjectData);
        setSubjects(prev => [...prev, { ...subjectData, ...createdSubject }]);
        showNotification('success', 'Success', 'Subject created successfully!');
      }
      
      setShowAcademicModal(false);
      setEditingAcademicItem(null);
      setSubjectFormData({
        subjectName: '',
        subjectCode: '',
        classLevel: '',
        type: 'Core',
        status: 'ACTIVE',
        departmentName: '',
        theoryMarks: 80,
        practicalMarks: 20
      });
      
      // Refresh subjects
    } catch (error) {
      console.error('Error with subject:', error);
      const errorMessage = extractErrorMessage(error, 'Failed to process subject. Please try again.');
      showNotification('error', 'Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTeacherSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingTeacher) {
        // Edit existing teacher with the complete payload structure
        const updateData = {
          teacherId: parseInt(editingTeacher.teacherId),
          orgId: orgData?.orgId || '',
          institutionType: orgData?.institutionType || 'School',
          teacherName: teacherFormData.teacherName || '',
          degree: teacherFormData.degree || '',
          email: teacherFormData.gmail || '', // Map gmail to email
          phone: teacherFormData.phoneNumber || '', // Map phoneNumber to phone
          password: teacherFormData.password || '',
          address: teacherFormData.address || '',
          dob: teacherFormData.dob || '',
          age: teacherFormData.dob ? new Date().getFullYear() - new Date(teacherFormData.dob).getFullYear() : 0,
          experience: parseInt(teacherFormData.experience) || 0,
          designation: teacherFormData.designation || 'Teacher',
          qualifications: teacherFormData.qualifications || '',
          specialization: teacherFormData.specialization || '',
          photo: teacherFormData.photo !== undefined ? teacherFormData.photo : (editingTeacher.photo || editingTeacher.photoUrl || ''),
          photoUrl: teacherFormData.photoUrl !== undefined ? teacherFormData.photoUrl : (editingTeacher.photoUrl || editingTeacher.photo || ''),
          subjects: Array.isArray(teacherFormData.subjects) ? teacherFormData.subjects : (editingTeacher.subjects || [])
        };

        const updatedTeacher = await teacherService.updateTeacher(editingTeacher.teacherId, updateData);

        setTeachers(prev => prev.map(teacher =>
          teacher.teacherId === editingTeacher.teacherId ? { ...teacher, ...updatedTeacher, photoUrl: updatedTeacher.photoUrl || teacherFormData.photoUrl || teacher.photoUrl } : teacher
        ));
        showNotification('success', 'Success', 'Teacher updated successfully!');
      } else {
        // Add new teacher with the complete payload structure
        const newTeacherData = {
          teacherId: teacherFormData.teacherId || `T${Date.now()}`, // Keep as string (e.g., "T001")
          orgId: orgData?.orgId || '',
          institutionType: orgData?.institutionType || 'School',
          teacherName: teacherFormData.teacherName || '',
          degree: teacherFormData.degree || '',
          email: teacherFormData.gmail || '', // Map gmail to email
          phone: teacherFormData.phoneNumber || '', // Map phoneNumber to phone
          password: teacherFormData.password || '',
          address: teacherFormData.address || '',
          dob: teacherFormData.dob || '',
          age: teacherFormData.dob ? new Date().getFullYear() - new Date(teacherFormData.dob).getFullYear() : 0,
          experience: parseInt(teacherFormData.experience) || 0,
          designation: teacherFormData.designation || 'Teacher',
          qualifications: teacherFormData.qualifications || '',
          specialization: teacherFormData.specialization || '',
          photo: teacherFormData.photo || '',
          photoUrl: teacherFormData.photoUrl || teacherFormData.photo || '',
          subjects: teacherFormData.subjects?.length > 0 ? teacherFormData.subjects : [] // Use selected subjects or empty array
        };

        const createdTeacher = await teacherService.createTeacher(newTeacherData);
        setTeachers(prev => [...prev, createdTeacher]);
        showNotification('success', 'Success', 'Teacher created successfully!');
        
        // Add recent activity
        addRecentActivity('👨‍🏫', `New teacher added: ${createdTeacher.teacherName}`, `Teacher ID: ${createdTeacher.teacherId}`);
      }

      setShowTeacherModal(false);
      setEditingTeacher(null);
      setTeacherFormData({
        teacherId: '',
        teacherName: '',
        dob: '',
        experience: '',
        phoneNumber: '',
        address: '',
        designation: '',
        gmail: '',
        qualifications: '',
        degree: '',
        specialization: '',
        password: '',
        status: 'ACTIVE',
        photo: '',
        photoUrl: '',
        subjects: [] // Reset subjects array
      });
    } catch (error) {
      console.error('Error saving teacher:', error);
      showNotification('error', 'Error', 'Failed to save teacher. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTimetableSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const timetableData = {
        ...timetableFormData,
        orgId: orgData?.orgId || ''
      };

      // Use timetableService to create school timetable (transforms className to courseName, section to semester)
      const response = await timetableService.createSchoolTimetable(timetableData);
      
      setTimetables(prev => [...prev, response]);
      setShowAcademicModal(false);
      resetTimetableForm();
      showNotification('success', 'Success', 'Timetable created successfully!');
      
      // Add recent activity
      addRecentActivity('📅', `Timetable created for ${timetableFormData.className} - Section ${timetableFormData.section}`, `Academic Year: ${timetableFormData.academicYear}`);
    } catch (error) {
      console.error('Error creating timetable:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create timetable';
      showNotification('error', 'Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const addTimeSlot = () => {
    if (customTimeInput && !timeSlots.includes(customTimeInput)) {
      setTimeSlots([...timeSlots, customTimeInput].sort());
      setCustomTimeInput('');
      setShowCustomTimeModal(false);
    }
  };

  const removeTimeSlot = (slot) => {
    setTimeSlots(timeSlots.filter(s => s !== slot));
  };

  // Load timetable from API based on filters
  const loadTimetableByFilters = async () => {
    if (!selectedTimetableClass || !selectedTimetableSection) {
      showNotification('warning', 'Warning', 'Please select both class and section');
      return;
    }

    try {
      setTimetableLoading(true);
      const response = await timetableService.getSchoolTimetableByFilters(
        orgData?.orgId,
        'General',
        selectedTimetableClass,
        selectedTimetableSection
      );

      console.log('Timetable API Response:', response);

      // Handle different response structures
      if (response && response.success) {
        // Try different possible response structures
        const timetableData = response.timetable || response.data || response;
        
        if (timetableData && (timetableData.schedule || timetableData.scheduleJson)) {
          // Parse scheduleJson if it's a string
          let schedule = timetableData.schedule;
          if (timetableData.scheduleJson && typeof timetableData.scheduleJson === 'string') {
            schedule = JSON.parse(timetableData.scheduleJson);
          }
          
          setCurrentTimetable({
            ...timetableData,
            schedule: schedule
          });
          console.log('Timetable loaded successfully');
          showNotification('success', 'Success', 'Timetable loaded successfully');
        } else {
          showNotification('info', 'Info', 'No timetable found for selected filters');
          setCurrentTimetable(null);
        }
      } else {
        showNotification('info', 'Info', 'No timetable found for selected filters');
        setCurrentTimetable(null);
      }
    } catch (error) {
      console.error('Error loading timetable:', error);
      showNotification('error', 'Error', 'Failed to load timetable');
      setCurrentTimetable(null);
    } finally {
      setTimetableLoading(false);
    }
  };

  const resetTimetableForm = () => {
    setTimetableFormData({
      orgId: orgData?.orgId || '',
      academicYear: new Date().getFullYear().toString(),
      className: '',
      section: '',
      schedule: {
        Monday: {},
        Tuesday: {},
        Wednesday: {},
        Thursday: {},
        Friday: {},
        Saturday: {}
      }
    });
  };

  // Export timetable to PDF
  const exportTimetableToPDF = () => {
    if (!currentTimetable) {
      showNotification('error', 'Error', 'No timetable to export');
      return;
    }

    const pdf = new jsPDF('l', 'mm', 'a4'); // Landscape orientation
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // School header (compact spacing)
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text(orgData?.orgName || 'School Name', pageWidth / 2, 14, { align: 'center' });
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    let currentY = 20;
    if (orgData?.address) {
      pdf.text(orgData.address, pageWidth / 2, currentY, { align: 'center' });
      currentY += 5;
    }
    if (orgData?.phoneNumber) {
      pdf.text(`Phone: ${orgData.phoneNumber}`, pageWidth / 2, currentY, { align: 'center' });
      currentY += 5;
    }

    // Timetable title
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Timetable - Class ${selectedTimetableClass} Section ${selectedTimetableSection}`, pageWidth / 2, currentY + 4, { align: 'center' });
    pdf.text(`Academic Year: ${currentTimetable.academicYear || new Date().getFullYear()}`, pageWidth / 2, currentY + 9, { align: 'center' });

    // Table setup (compact coordinates)
    const startY = currentY + 14;
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    // Extract and sort all unique timeslots chronologically
    const timeSlots = [];
    if (currentTimetable.schedule) {
      const allSlots = new Set();
      Object.keys(currentTimetable.schedule).forEach(day => {
        const daySlots = currentTimetable.schedule[day] || {};
        Object.keys(daySlots).forEach(timeKey => {
          if (timeKey) allSlots.add(timeKey);
        });
      });
      timeSlots.push(...Array.from(allSlots));
      timeSlots.sort((a, b) => {
        const timeA = a.split('-')[0].trim();
        const timeB = b.split('-')[0].trim();
        return timeA.localeCompare(timeB);
      });
    }

    // Dynamic cell width to fit 6 days + 1 time column within A4 margins (15mm left/right)
    const margin = 15;
    const availableWidth = pageWidth - (margin * 2);
    const cellWidth = availableWidth / (days.length + 1); // 7 columns total
    const headerHeight = 10;
    const rowHeight = 13; // 13mm is compact but fits subject + teacher nicely

    // Table headers
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    
    // Time column header
    pdf.setFillColor(240, 244, 248);
    pdf.rect(margin, startY, cellWidth, headerHeight, 'F');
    pdf.rect(margin, startY, cellWidth, headerHeight);
    pdf.text('Time', margin + cellWidth/2, startY + 6, { align: 'center' });

    // Day headers
    days.forEach((day, index) => {
      const x = margin + cellWidth + (index * cellWidth);
      pdf.setFillColor(240, 244, 248);
      pdf.rect(x, startY, cellWidth, headerHeight, 'F');
      pdf.rect(x, startY, cellWidth, headerHeight);
      pdf.text(day, x + cellWidth/2, startY + 6, { align: 'center' });
    });

    // Table rows
    timeSlots.forEach((timeSlot, rowIndex) => {
      const y = startY + headerHeight + (rowIndex * rowHeight);

      // Time cell
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8.5);
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin, y, cellWidth, rowHeight, 'F');
      pdf.rect(margin, y, cellWidth, rowHeight);
      pdf.text(timeSlot, margin + cellWidth/2, y + rowHeight/2 + 2, { align: 'center' });

      // Day cells
      days.forEach((day, colIndex) => {
        const x = margin + cellWidth + (colIndex * cellWidth);
        const dayKey = day;
        const subject = currentTimetable.schedule[dayKey]?.[timeSlot] || currentTimetable.schedule[day.toLowerCase()]?.[timeSlot];
        
        pdf.rect(x, y, cellWidth, rowHeight);
        
        if (subject) {
          // Highlight filled cells with very light blue background
          pdf.setFillColor(239, 246, 255);
          pdf.rect(x, y, cellWidth, rowHeight, 'F');
          pdf.rect(x, y, cellWidth, rowHeight);

          // Subject name
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(8.5);
          pdf.text(subject.subject || subject.subjectName || '', x + cellWidth/2, y + 4.5, { align: 'center' });
          
          // Teacher name
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(7.5);
          if (subject.teacher || subject.teacherName) {
            pdf.text(subject.teacher || subject.teacherName, x + cellWidth/2, y + 9.5, { align: 'center' });
          }
        } else {
          // Empty slot dash
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8);
          pdf.text('—', x + cellWidth/2, y + rowHeight/2 + 2, { align: 'center' });
        }
      });
    });

    // Footer
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Generated on: ${new Date().toLocaleDateString()}`, margin, pageHeight - 8);

    pdf.save(`Timetable_${selectedTimetableClass}_${selectedTimetableSection}.pdf`);
    showNotification('success', 'Success', 'Timetable exported to PDF successfully');
  };

  // Export timetable to Word document
  const exportTimetableToWord = async () => {
    if (!currentTimetable) {
      showNotification('error', 'Error', 'No timetable to export');
      return;
    }

    try {
      const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const timeSlots = currentTimetable.schedule ? Object.keys(currentTimetable.schedule[Object.keys(currentTimetable.schedule)[0]] || {}) : [];

      // Create table rows
      const tableRows = [];

      // Header row
      const headerRow = new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Time", bold: true })] })],
            width: { size: 15, type: WidthType.PERCENTAGE }
          }),
          ...days.map(day => new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: day, bold: true })] })],
            width: { size: 14, type: WidthType.PERCENTAGE }
          }))
        ]
      });
      tableRows.push(headerRow);

      // Data rows
      timeSlots.forEach(timeSlot => {
        const row = new TableRow({
          children: [
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: timeSlot, bold: true })] })]
            }),
            ...days.map(day => {
              const dayKey = day;
              const subject = currentTimetable.schedule[dayKey]?.[timeSlot] || currentTimetable.schedule[day.toLowerCase()]?.[timeSlot];
              
              const cellContent = [];
              if (subject) {
                if (subject.subject || subject.subjectName) {
                  cellContent.push(new TextRun({ text: subject.subject || subject.subjectName, bold: true }));
                }
                if (subject.teacher || subject.teacherName) {
                  cellContent.push(new TextRun({ text: `\n${subject.teacher || subject.teacherName}`, italics: true }));
                }
              }
              
              return new TableCell({
                children: [new Paragraph({ children: cellContent.length > 0 ? cellContent : [new TextRun({ text: "-" })] })]
              });
            })
          ]
        });
        tableRows.push(row);
      });

      const doc = new Document({
        sections: [{
          children: [
            // School header
            new Paragraph({
              children: [new TextRun({ text: orgData?.orgName || 'School Name', bold: true, size: 32 })],
              alignment: AlignmentType.CENTER
            }),
            ...(orgData?.address ? [new Paragraph({
              children: [new TextRun({ text: orgData.address, size: 24 })],
              alignment: AlignmentType.CENTER
            })] : []),
            ...(orgData?.phoneNumber ? [new Paragraph({
              children: [new TextRun({ text: `Phone: ${orgData.phoneNumber}`, size: 24 })],
              alignment: AlignmentType.CENTER
            })] : []),
            new Paragraph({ text: "" }), // Empty line
            
            // Timetable title
            new Paragraph({
              children: [new TextRun({ text: `Timetable - Class ${selectedTimetableClass} Section ${selectedTimetableSection}`, bold: true, size: 28 })],
              alignment: AlignmentType.CENTER
            }),
            new Paragraph({
              children: [new TextRun({ text: `Academic Year: ${currentTimetable.academicYear || new Date().getFullYear()}`, size: 24 })],
              alignment: AlignmentType.CENTER
            }),
            new Paragraph({ text: "" }), // Empty line
            
            // Timetable table
            new Table({
              rows: tableRows,
              width: { size: 100, type: WidthType.PERCENTAGE }
            }),
            
            new Paragraph({ text: "" }), // Empty line
            new Paragraph({
              children: [new TextRun({ text: `Generated on: ${new Date().toLocaleDateString()}`, size: 20 })],
              alignment: AlignmentType.LEFT
            })
          ]
        }]
      });
      
      const blob = await Packer.toBlob(doc);
      saveAs(blob, `Timetable_${selectedTimetableClass}_${selectedTimetableSection}.docx`);
      
      showNotification('success', 'Success', 'Timetable exported to Word document successfully');
    } catch (error) {
      console.error('Error exporting to Word:', error);
      showNotification('error', 'Error', 'Failed to export timetable to Word document');
    }
  };

  const loadStudentSubjects = async () => {
    try {
      console.log('Loading class-subject assignments...');
      setLoading(true);
      const response = await subjectAssignmentService.getClassSubjectAssignments();
      
      console.log('Class-Subject Assignments Response:', response);
      
      if (response && response.success && response.classes) {
        setStudentSubjects(response.classes);
        console.log(`Loaded ${response.classes.length} classes with their subject assignments`);
      } else {
        console.log('No class assignment data received');
        setStudentSubjects([]);
      }
    } catch (error) {
      console.error('Error loading class subject assignments:', error);
      setStudentSubjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignmentSubmit = async (data, e) => {
    if (e) e.preventDefault();
    else if (data && data.preventDefault) {
      e = data;
      e.preventDefault();
      data = assignmentFormData;
    }
    const formData = (data && data.classId) ? data : assignmentFormData;

    setLoading(true);
    try {
      console.log('Selected classId:', formData.classId);
      console.log('Selected classId type:', typeof formData.classId);
      console.log('Total students:', students.length);
      console.log('All students:', students.map(s => ({
        studentId: s.studentId,
        name: s.name,
        classId: s.classId,
        class: s.class,
        className: s.className
      })));
      
      console.log('All classOptions:', classOptions);
      
      // Find the selected class from classOptions to get the className
      let selectedClassOption = classOptions.find(c => c.id == formData.classId);
      console.log('Selected class option:', selectedClassOption);
      
      if (!selectedClassOption) {
        showNotification('error', 'Error', 'Selected class not found');
        setLoading(false);
        return;
      }

      // Get all students in the selected class by matching className
      const classStudents = students.filter(s => {
        // Students have className field, match it with the selected class label
        return s.className === selectedClassOption.label;
      });
      
      console.log('Filtered students:', classStudents.length);
      console.log('Filtered student details:', classStudents.map(s => ({
        id: s.studentId,
        name: s.name,
        classId: s.classId,
        className: s.className
      })));
      
      if (classStudents.length === 0) {
        showNotification('warning', 'Warning', 'No students found in the selected class');
        setLoading(false);
        return;
      }

      if (formData.subjectIds.length === 0) {
        showNotification('warning', 'Warning', 'Please select at least one subject');
        setLoading(false);
        return;
      }

      // Prepare bulk enrollment data matching backend expectations
      const studentIds = classStudents.map(student => student.studentId || student.student_id);
      const subjectIds = formData.subjectIds.map(id => parseInt(id));
      
      const bulkEnrollmentData = {
        studentIds: studentIds,
        subjectIds: subjectIds,
        academicYear: formData.academicYear
      };

      console.log('Sending bulk enrollment payload:', bulkEnrollmentData);
      console.log(`Enrolling ${studentIds.length} students in ${subjectIds.length} subjects each`);

      // Send bulk enrollment API call
      const result = await subjectAssignmentService.enrollStudentInSubject(bulkEnrollmentData);
      
      if (result && result.success) {
        const selectedSubjectNames = subjects
          .filter(s => formData.subjectIds.includes(s.subjectId.toString()))
          .map(s => s.subjectName)
          .join(', ');
        
        showNotification('success', 'Success', `${classStudents.length} students enrolled in ${formData.subjectIds.length} subject(s) successfully`);
        loadStudentSubjects();
        resetAssignmentForm();
        setShowAssignmentModal(false);
        addRecentActivity('📚', `Class subjects assigned`, `${classStudents.length} students enrolled in: ${selectedSubjectNames}`);
      } else {
        const errorMessage = result?.message || 'Failed to enroll students';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Assignment error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to enroll students';
      showNotification('error', 'Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetAssignmentForm = async () => {
    setAssignmentFormData({
      classId: '',
      subjectIds: [],
      academicYear: new Date().getFullYear().toString()
    });
    // Load student subjects to get the latest assignments
    await loadStudentSubjects();
  };

  const handleRemoveStudentAssignment = async (assignment) => {
    if (window.confirm(`Remove ${assignment.student?.name || 'student'} from ${assignment.subject?.subjectName || 'subject'}?`)) {
      try {
        // Note: You'll need to implement a remove assignment API endpoint
        console.log('Remove student assignment:', assignment);
        showNotification('success', 'Success', 'Student assignment removed successfully');
        loadStudentSubjects();
        addRecentActivity('🗑️', `Student assignment removed`, `Student: ${assignment.student?.name}`);
      } catch (error) {
        console.error('Error removing student assignment:', error);
        showNotification('error', 'Error', 'Failed to remove student assignment');
      }
    }
  };



  // Handle promote student
  const handlePromoteStudent = async () => {
    if (!promoteStudent || !promoteToClass) {
      showNotification('warning', 'Warning', 'Please select a class to promote the student');
      return;
    }

    try {
      setLoading(true);
      
      // Find the selected class object to get className
      const selectedClass = classOptions.find(c => c.id == promoteToClass);
      const newClassName = selectedClass?.label || promoteToClass;
      
      // Build update payload matching school API structure
      const updateData = {
        studentId: promoteStudent.studentId,
        name: promoteStudent.name,
        dob: promoteStudent.dob,
        gender: promoteStudent.gender,
        address: promoteStudent.address || '',
        phone: promoteStudent.phone,
        email: promoteStudent.email,
        degree: null,
        semester: null,
        admissionYear: promoteStudent.admissionYear,
        password: promoteStudent.password,
        aadhaarNumber: promoteStudent.aadhaarNumber || '',
        active: promoteStudent.active !== false,
        departmentName: null,
        courseName: newClassName.toLowerCase(), // Map to courseName for school
        institutionType: 'SCHOOL',
        classId: promoteToClass,
        className: newClassName,
        parentName: promoteStudent.parentName || '',
        parentPhone: promoteStudent.parentPhone || '',
        class: newClassName
      };
      
      const updated = await studentService.updateStudent(promoteStudent.studentId, updateData);
      
      showNotification('success', 'Success', `${promoteStudent.name} promoted to ${newClassName}`);
      addRecentActivity('📈', `Student promoted: ${promoteStudent.name}`, `Promoted to ${newClassName}`);
      
      // Refresh student table
      await loadStudents();
      
      // Close modal and reset
      setShowPromoteModal(false);
      setPromoteStudent(null);
      setPromoteToClass('');
    } catch (error) {
      console.error('Error promoting student:', error);
      const errorMessage = extractErrorMessage(error, 'Failed to promote student. Please try again.');
      showNotification('error', 'Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };





  const handleFeeFilterChange = (filterName, value) => {
    setFeeFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearAllFeeFilters = () => {
    setFeeFilters({
      academicYear: '',
      class: '',
      section: '',
      paymentStatus: ''
    });
    setFeeSearchTerm('');
  };

  // Download utility functions
  const downloadCSV = (data, filename) => {
    if (!data || data.length === 0) {
      showNotification('error', 'No Data', 'No data available to download');
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value || '';
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadExcel = (data, filename, dataType = '') => {
    if (!data || data.length === 0) {
      showNotification('error', 'No Data', 'No data available to download');
      return;
    }

    // For students and teachers, create Excel workbook
    if (dataType === 'students' || dataType === 'teachers') {
      const wb = XLSX.utils.book_new();
      
      // Add a summary sheet first
      const reportTitle = dataType === 'students' ? 'Student Details Report' : 'Teacher Details Report';
      const summaryData = [
        [orgData?.orgName || 'Educational Institution'],
        [reportTitle],
        [`Generated on: ${new Date().toLocaleDateString()}`],
        [],
        []
      ];
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

      if (dataType === 'students') {
        // For students, organize by class
        const groupedByClass = {};
        data.forEach(student => {
          const classKey = `${student['Class'] || 'Unknown'}-${student['Section'] || 'A'}`;
          if (!groupedByClass[classKey]) {
            groupedByClass[classKey] = [];
          }
          groupedByClass[classKey].push(student);
        });

        // Add each class as a separate sheet
        Object.keys(groupedByClass).forEach(classKey => {
          const classData = groupedByClass[classKey];
          const headers = Object.keys(classData[0]);
          
          // Create sheet data with headers
          const sheetData = [headers, ...classData.map(row => headers.map(h => row[h] || ''))];
          const sheet = XLSX.utils.aoa_to_sheet(sheetData);
          
          // Set column widths
          const colWidths = headers.map(h => ({ wch: Math.max(h.length, 12) }));
          sheet['!cols'] = colWidths;
          
          XLSX.utils.book_append_sheet(wb, sheet, classKey.substring(0, 31)); // Sheet name limit is 31 chars
        });
      } else if (dataType === 'teachers') {
        // For teachers, add all data in one sheet
        const headers = Object.keys(data[0]);
        const sheetData = [headers, ...data.map(row => headers.map(h => row[h] || ''))];
        const sheet = XLSX.utils.aoa_to_sheet(sheetData);
        
        // Set column widths
        const colWidths = headers.map(h => ({ wch: Math.max(h.length, 12) }));
        sheet['!cols'] = colWidths;
        
        XLSX.utils.book_append_sheet(wb, sheet, 'Teachers');
      }

      // Write the file
      XLSX.writeFile(wb, `${filename}.xlsx`);
    } else {
      downloadCSV(data, filename);
    }
    showNotification('success', 'Download Started', `${filename} file is being downloaded`);
  };

  const downloadPDF = async (data, filename, dataType = '') => {
    if (!data || data.length === 0) {
      showNotification('error', 'No Data', 'No data available to download');
      return;
    }

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPosition = 15;

      // Header with school/college name
      const schoolName = orgData?.orgName || 'Educational Institution';
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text(schoolName, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 8;

      // Report title
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Student Details Report', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 8;

      // Date
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 12;

      // Table headers
      const headers = Object.keys(data[0]);
      const columnWidth = (pageWidth - 20) / headers.length;

      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      headers.forEach((header, idx) => {
        doc.text(header, 10 + idx * columnWidth, yPosition, { maxWidth: columnWidth - 1 });
      });
      yPosition += 7;

      // Table data
      doc.setFont(undefined, 'normal');
      doc.setFontSize(8);
      data.forEach(row => {
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 15;
        }
        headers.forEach((header, idx) => {
          doc.text(String(row[header] || ''), 10 + idx * columnWidth, yPosition, { maxWidth: columnWidth - 1 });
        });
        yPosition += 6;
      });

      doc.save(`${filename}.pdf`);
      showNotification('success', 'Download Started', `${filename}.pdf is being downloaded`);
    } catch (error) {
      console.error('PDF generation error:', error);
      showNotification('error', 'PDF Error', 'Failed to generate PDF');
    }
  };

  const openDownloadModal = (dataType) => {
    setDownloadConfig({
      dataType,
      format: 'csv',
      filters: {},
      includeFields: []
    });
    setShowDownloadModal(true);
  };

  const getFilteredData = (dataType, filters) => {
    let data = [];
    
    switch (dataType) {
      case 'students':
        data = students.filter(student => {
          if (!student) return false;
          
          const matchesClass = !filters.class || 
            student.className === filters.class;
          
          const matchesSection = !filters.section || 
            student.section === filters.section;
          
          const matchesYear = !filters.year || 
            (student.admissionDate && new Date(student.admissionDate).getFullYear() === parseInt(filters.year)) ||
            (student.enrollmentDate && new Date(student.enrollmentDate).getFullYear() === parseInt(filters.year));
          
          return matchesClass && matchesSection && matchesYear;
        }).map(student => ({
          'Student ID': student.studentId || student.student_id || student.id,
          'Name': student.name || student.studentName,
          'Email': getDisplayStudentEmail(student.email || student.emailId),
          'Phone': student.phoneNumber || student.phone || student.contactNumber,
          'Class': student.className,
          'Section': student.section,
          'Status': student.status || 'Active',
          'Date of Birth': student.dateOfBirth || student.dob,
          'Address': student.address || student.fullAddress,
          'Parent Name': student.parentName || student.guardianName,
          'Parent Phone': student.parentPhone || student.guardianPhone,
          'Admission Date': student.admissionDate || student.enrollmentDate,
          'Roll Number': student.rollNumber || student.rollNo
        }));
        break;
        
      case 'teachers':
        data = teachers.map(teacher => ({
          'Teacher ID': teacher.teacherId || teacher.teacher_id,
          'Name': teacher.teacherName || teacher.name,
          'DOB': teacher.dob || 'N/A',
          'Age': teacher.age || 'N/A',
          'Experience': teacher.experience || 0,
          'Phone': teacher.phoneNumber || 'N/A',
          'Email': teacher.gmail || teacher.email || 'N/A',
          'Degree': teacher.degree || 'N/A',
          'Qualifications': teacher.qualifications || teacher.qualification || 'N/A',
          'Specialization': teacher.specialization || teacher.subjectSpecialization || 'N/A',
          'Address': teacher.address || 'N/A',
          'Designation': teacher.designation || 'N/A',
          'Status': teacher.status || 'ACTIVE'
        }));
        break;
        
      case 'subjects':
        data = subjects.map(subject => ({
          'Subject ID': subject.subjectId,
          'Subject Code': subject.subjectCode,
          'Subject Name': subject.subjectName,
          'Class': subject.className,
          'Credits': subject.credits,
          'Type': subject.subjectType,
          'Status': subject.status
        }));
        break;
        
      case 'classes':
        data = classes.map(cls => ({
          'Class ID': cls.classId,
          'Class Name': cls.className,
          'Section': cls.section,
          'Total Students': students.filter(s => s.className === cls.className && s.section === cls.section).length,
          'Class Teacher': cls.classTeacher || 'N/A',
          'Status': cls.status || 'Active'
        }));
        break;
        
      case 'fees':
        data = fees.map(fee => ({
          'Student ID': fee.studentId,
          'Student Name': students.find(s => (s.studentId || s.student_id) === fee.studentId)?.name || 'N/A',
          'Fee Type': fee.type ? fee.type.charAt(0).toUpperCase() + fee.type.slice(1) : 'General',
          'Total Fees': fee.totalFees,
          'Paid Fees': fee.paidFees,
          'Pending Fees': fee.totalFees - fee.paidFees,
          'Status': fee.totalFees === fee.paidFees ? 'Paid' : 'Pending',
          'Last Payment Date': fee.lastPaymentDate || 'N/A'
        }));
        break;
        
      case 'attendance-summary':
        // Student report data formatted for CSV/Excel export using genuine DB records
        const reportStudentsList = Array.isArray(studentAttendanceReportData?.students) ? studentAttendanceReportData.students : [];
        data = reportStudentsList.filter(st => {
          const matchesSearch = !reportSearchText || 
            (st.studentName || '').toLowerCase().includes(reportSearchText.toLowerCase()) ||
            (st.rollNumber || '').toLowerCase().includes(reportSearchText.toLowerCase()) ||
            (st.studentId || '').toLowerCase().includes(reportSearchText.toLowerCase());
          const matchesPct = !reportMinPct || st.attendancePercentage <= parseFloat(reportMinPct);
          return matchesSearch && matchesPct;
        }).map((st) => {
          const pct = Number(st.attendancePercentage || 0).toFixed(1);
          return {
            'Roll No': st.rollNumber || '',
            'Student Name': st.studentName || st.studentId,
            'Class & Sec': st.className ? `${st.className}${st.section ? ` - ${st.section}` : ''}` : '',
            'Sessions Held': st.sessionsHeld,
            'Attended': st.attended,
            'Late Entries': st.late,
            'Excused Leaves': st.excused,
            'Unexcused Absent': st.unexcused,
            'Attendance %': `${pct}%`
          };
        });
        break;

      case 'teacher-attendance-summary':
        // Teacher report data formatted for CSV/Excel export
        data = teacherAttendanceRecords.filter(t => {
          const matchesDept = reportDepartment === 'All' || t.department === reportDepartment;
          const matchesDesig = reportDesignation === 'All' || t.designation === reportDesignation;
          const matchesSearch = !reportSearchText || 
            t.teacherName.toLowerCase().includes(reportSearchText.toLowerCase()) ||
            t.teacherId.toLowerCase().includes(reportSearchText.toLowerCase());
          
          let matchesPct = true;
          if (reportMinPct) {
            matchesPct = t.attendancePercentage <= parseFloat(reportMinPct);
          }
          return matchesDept && matchesDesig && matchesSearch && matchesPct;
        }).map(t => ({
          'Teacher ID': t.teacherId,
          'Teacher Name': t.teacherName,
          'Department': t.department,
          'Designation': t.designation,
          'Working Days': t.workingDays,
          'Present Days': t.presentDays,
          'Absent Days': t.absentDays,
          'Late Entries': t.lateEntries,
          'Half Days': t.halfDays,
          'Approved Leaves': t.approvedLeaves,
          'Attendance %': `${t.attendancePercentage}%`
        }));
        break;

      case 'attendance-tracker':
        const trackerList = Array.isArray(trackerData?.classes) ? trackerData.classes : [];
        data = trackerList.filter(c => {
          const matchesStatus = trackerStatusFilter === 'ALL' || c.status === trackerStatusFilter;
          const matchesSearch = !trackerSearch ||
            (c.className || '').toLowerCase().includes(trackerSearch.toLowerCase()) ||
            (c.classTeacherName || '').toLowerCase().includes(trackerSearch.toLowerCase());
          return matchesStatus && matchesSearch;
        }).map(c => ({
          'Class': c.className,
          'Section': c.section,
          'Class Teacher': c.classTeacherName,
          'Attendance Status': c.status === 'TAKEN' ? 'Taken' : 'Not Taken / Pending',
          'Submitted At': formatIndianTime(c.submittedAt),
          'Submitted By': c.submittedBy || '—',
          'Enrolled Students': c.totalStudents,
          'Present Today': c.presentCount,
          'Absent Today': c.absentCount,
          'Attendance %': `${c.attendancePercentage}%`
        }));
        break;

      case 'attendance-daywise':
        const dayWiseList = Array.isArray(dayWiseData?.students) ? dayWiseData.students : [];
        data = dayWiseList.filter(s => {
          const matchesStatus = dayWiseStatusFilter === 'All' || s.status === dayWiseStatusFilter;
          const matchesSearch = !dayWiseSearch ||
            (s.name || '').toLowerCase().includes(dayWiseSearch.toLowerCase()) ||
            (s.rollNo || '').toLowerCase().includes(dayWiseSearch.toLowerCase());
          return matchesStatus && matchesSearch;
        }).map(s => ({
          'Roll No': s.rollNo,
          'Student Name': s.name,
          'Class': s.className,
          'Section': s.section,
          'Status': s.status,
          'Entry Time': formatIndianTime(s.entryTime),
          'Mode': s.scanType,
          'Remarks': s.remarks || '',
          'Marked By': s.verifiedBy || ''
        }));
        break;

      case 'attendance-classwise':
        const classWiseList = Array.isArray(classWiseData?.classes) ? classWiseData.classes : [];
        data = classWiseList.filter(c => {
          return !classWiseSearch ||
            (c.className || '').toLowerCase().includes(classWiseSearch.toLowerCase()) ||
            (c.classTeacherName || '').toLowerCase().includes(classWiseSearch.toLowerCase());
        }).map(c => ({
          'Class': c.className,
          'Section': c.section,
          'Class Teacher': c.classTeacherName,
          'Enrolled Students': c.enrolledStudents,
          'Sessions Held': c.sessionsHeld,
          'Total Present': c.totalPresent,
          'Total Absent': c.totalAbsent,
          'Total Leaves': c.totalLeave,
          'Class Attendance %': `${c.attendancePercentage}%`
        }));
        break;

      default:
        data = [];
    }
    
    return data;
  };

  const handleDownload = () => {
    const { dataType, format, filters } = downloadConfig;
    
    const data = getFilteredData(dataType, filters);
    
    if (data.length === 0) {
      showNotification('error', 'No Data', 'No data matches the selected filters');
      return;
    }
    
    let filename = `${dataType}_${new Date().toISOString().split('T')[0]}`;
    if (dataType === 'attendance-summary') {
      filename = `Student_Attendance_Report_${reportAcademicYear}`;
    } else if (dataType === 'teacher-attendance-summary') {
      filename = `Teacher_Attendance_Report_${reportAcademicYear}`;
    }
    
    if (dataType === 'students' || dataType === 'teachers') {
      if (format === 'excel') {
        downloadExcel(data, filename, dataType);
      }
    } else {
      downloadCSV(data, filename);
    }
    
    setShowDownloadModal(false);
  };

  // PDF download for transaction history
  const downloadTransactionHistoryPDF = async () => {
    if (!selectedStudentForCollection || !collectionPayments || collectionPayments.length === 0) {
      showNotification('error', 'No Data', 'No transaction history to download');
      return;
    }

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPosition = 15;

      // Header with trust and school name
      const trustName = orgData?.trustName || localStorage.getItem('trustName') || '';
      if (trustName) {
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(100, 116, 139);
        doc.text(trustName.toUpperCase(), pageWidth / 2, yPosition, { align: 'center' });
        doc.setTextColor(15, 23, 42);
        yPosition += 6;
      }
      const schoolName = orgData?.orgName || 'Educational Institution';
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text(schoolName, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 8;

      // Organization details
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      if (orgData?.email) {
        doc.text(`Email: ${orgData.email}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 5;
      }
      if (orgData?.phone) {
        doc.text(`Phone: ${orgData.phone}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 5;
      }

      // Title
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Payment History Receipt', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;

      // Student Details
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('Student Details:', 15, yPosition);
      yPosition += 6;

      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);
      doc.text(`Student ID: ${selectedStudentForCollection.studentId || selectedStudentForCollection.student_id}`, 15, yPosition);
      yPosition += 5;
      doc.text(`Name: ${selectedStudentForCollection.name}`, 15, yPosition);
      yPosition += 5;
      if (selectedStudentForCollection.className) {
        doc.text(`Class: ${selectedStudentForCollection.className}`, 15, yPosition);
        yPosition += 5;
      }
      if (selectedStudentForCollection.section) {
        doc.text(`Section: ${selectedStudentForCollection.section}`, 15, yPosition);
        yPosition += 5;
      }

      yPosition += 5;

      // Transaction Table
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('Transaction History:', 15, yPosition);
      yPosition += 8;

      // Table headers
      const columns = ['Transaction ID', 'Date', 'Amount', 'Mode', 'Fee Type', 'Status'];
      const columnWidths = [30, 35, 22, 18, 25, 20];
      let xPosition = 15;

      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.setFillColor(220, 220, 220);
      doc.setTextColor(0, 0, 0);

      // Draw header background and borders
      columns.forEach((col, index) => {
        doc.rect(xPosition, yPosition - 5, columnWidths[index], 7, 'F');
        doc.rect(xPosition, yPosition - 5, columnWidths[index], 7);
        xPosition += columnWidths[index];
      });

      // Draw header text
      xPosition = 15;
      columns.forEach((col, index) => {
        doc.text(col, xPosition + 1, yPosition, { maxWidth: columnWidths[index] - 2, align: 'left' });
        xPosition += columnWidths[index];
      });

      yPosition += 8;
      doc.setFont(undefined, 'normal');
      doc.setFontSize(8);

      // Table rows
      collectionPayments.forEach((transaction, index) => {
        if (yPosition > pageHeight - 20) {
          doc.addPage();
          yPosition = 15;
        }

        const amount = parseFloat(transaction.paidAmount || transaction.amount || 0);
        const formattedRowAmount = amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        
        const rowData = [
          transaction.transactionId || transaction.transaction_id || 'N/A',
          transaction.paidDate || transaction.createdAt
            ? new Date(transaction.paidDate || transaction.createdAt).toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })
            : 'N/A',
          `Rs. ${formattedRowAmount}`,
          transaction.mode || transaction.paymentMethod || 'N/A',
          transaction.feeType || transaction.type || 'General',
          transaction.status || 'PENDING'
        ];

        xPosition = 15;
        rowData.forEach((data, colIndex) => {
          doc.text(String(data), xPosition + 2, yPosition, { maxWidth: columnWidths[colIndex] - 4 });
          xPosition += columnWidths[colIndex];
        });

        yPosition += 6;
      });

      yPosition += 5;

      // Summary
      doc.setFont(undefined, 'bold');
      doc.setFontSize(9);
      doc.setFillColor(240, 240, 240);
      doc.rect(15, yPosition - 4, pageWidth - 30, 6, 'F');
      doc.text('Summary', 15, yPosition);
      yPosition += 8;

      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);
      const totalAmount = collectionPayments.reduce((sum, t) => sum + (parseFloat(t.paidAmount || t.amount || 0)), 0);
      const successfulTransactions = collectionPayments.filter(t => t.status === 'SUCCESS' || t.status === 'PAID').length;

      doc.text(`Total Transactions: ${collectionPayments.length}`, 15, yPosition);
      yPosition += 5;
      // Format amount without rupee symbol to avoid encoding issues
      const formattedAmount = totalAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      doc.text(`Total Amount Paid: Rs. ${formattedAmount}`, 15, yPosition);
      yPosition += 5;
      doc.text(`Successful Payments: ${successfulTransactions}`, 15, yPosition);
      yPosition += 5;

      // Footer with timestamp
      doc.setFontSize(8);
      doc.setFont(undefined, 'italic');
      const timestamp = new Date().toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      doc.text(`Generated on: ${timestamp}`, 15, pageHeight - 10);

      // Download
      const filename = `Payment_History_${selectedStudentForCollection.name}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);

      showNotification('success', 'Download Complete', `Payment history downloaded as ${filename}`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      showNotification('error', 'PDF Error', 'Failed to generate PDF. Please try again.');
    }
  };

  // ============ HOSTEL MANAGEMENT HANDLERS ============
  
  // Load hostels and allocations
  const loadHostelData = async () => {
    try {
      setHostelLoading(true);
      const hostelResponse = await hostelService.listHostels();
      if (hostelResponse?.success || Array.isArray(hostelResponse)) {
        const hostelData = Array.isArray(hostelResponse) ? hostelResponse : hostelResponse.data || [];
        setHostels(hostelData);
      }
      
      // Try to load allocations separately
      try {
        const allocResponse = await hostelService.getStudentAllocation();
        if (allocResponse?.success || Array.isArray(allocResponse)) {
          const allocData = Array.isArray(allocResponse) ? allocResponse : allocResponse.data || [];
          setHostelAllocations(allocData);
        }
      } catch (allocError) {
        console.log('Allocations endpoint not available, showing empty allocations');
        setHostelAllocations([]);
      }
    } catch (error) {
      console.error('Error loading hostel data:', error);
    } finally {
      setHostelLoading(false);
    }
  };

  const handleEditHostel = (hostel) => {
    setEditingHostel(hostel);
    setHostelFormData({
      hostelName: hostel.hostelName || '',
      address: hostel.address || '',
      city: hostel.city || '',
      state: hostel.state || '',
      pincode: hostel.pincode || '',
      capacity: hostel.capacityPerRoom != null
        ? String(hostel.capacityPerRoom)
        : hostel.capacity != null
          ? String(hostel.capacity)
          : '',
      warden: hostel.warden || '',
      phone: hostel.wardenPhone || hostel.phone || '',
      email: hostel.wardenEmail || hostel.email || '',
      totalRooms: hostel.totalRooms != null ? String(hostel.totalRooms) : '',
      bedsPerRoom: hostel.bedsPerRoom != null ? String(hostel.bedsPerRoom) : ''
    });
    setShowHostelModal(true);
  };

  const handleViewHostel = async (hostel) => {
    if (!hostel || !hostel.id) return;
    setSelectedHostel(hostel);
    setHostelRoomsLoading(true);
    setSelectedHostelRooms([]);
    try {
      const response = await hostelService.listHostelRooms(hostel.id);
      const roomsData = Array.isArray(response) ? response : response?.data || [];
      setSelectedHostelRooms(roomsData);
    } catch (error) {
      console.error('Error loading hostel rooms:', error);
    } finally {
      setHostelRoomsLoading(false);
    }
  };

  const handleEditRoom = (room) => {
    if (!room) return;
    setEditingRoom(room);
    setRoomFormData({
      roomNumber: room.roomNumber || '',
      capacity: room.capacity != null ? String(room.capacity) : '',
      type: room.type || room.roomType || 'shared'
    });
    setShowRoomModal(true);
  };

  const handleDeleteRoom = async (room) => {
    if (!room || (!room.id && !room.roomId)) return;
    if (!window.confirm('Are you sure you want to delete room "' + (room.roomNumber || '') + '"?')) {
      return;
    }
    setHostelLoading(true);
    try {
      const roomId = room.id || room.roomId;
      const response = await hostelService.deleteHostelRoom(roomId);
      if (response?.success || response?.id) {
        showNotification('success', 'Success', 'Room deleted successfully');
        if (selectedHostel) {
          await handleViewHostel(selectedHostel);
        } else {
          loadHostelData();
        }
      }
    } catch (error) {
      console.error('Error deleting room:', error);
      showNotification('error', 'Error', error.message || 'Failed to delete room');
    } finally {
      setHostelLoading(false);
    }
  };

  const handleDeleteHostel = async (hostel) => {
    if (!hostel || !hostel.id) return;
    if (!window.confirm('Are you sure you want to delete hostel "' + (hostel.hostelName || '') + '"?')) {
      return;
    }
    setHostelLoading(true);
    try {
      const response = await hostelService.deleteHostel(hostel.id);
      if (response?.success || response?.id) {
        showNotification('success', 'Success', 'Hostel deleted successfully');
        if (selectedHostel && selectedHostel.id === hostel.id) {
          setSelectedHostel(null);
        }
        if (editingHostel && editingHostel.id === hostel.id) {
          setEditingHostel(null);
        }
        await loadHostelData();
      }
    } catch (error) {
      console.error('Error deleting hostel:', error);
      showNotification('error', 'Error', error.message || 'Failed to delete hostel');
    } finally {
      setHostelLoading(false);
    }
  };

  // Add / update hostel
  const handleAddHostel = async (e) => {
    e.preventDefault();
    setHostelLoading(true);
    try {
      const baseData = {
        hostelName: hostelFormData.hostelName,
        address: hostelFormData.address,
        city: hostelFormData.city,
        state: hostelFormData.state,
        pincode: hostelFormData.pincode,
        capacityPerRoom: hostelFormData.capacity,
        warden: hostelFormData.warden,
        wardenPhone: hostelFormData.phone,
        wardenEmail: hostelFormData.email,
        totalRooms: hostelFormData.totalRooms,
        bedsPerRoom: hostelFormData.bedsPerRoom
      };
      let response;
      if (editingHostel) {
        console.log('📤 Updating hostel with data:', baseData);
        response = await hostelService.updateHostel(editingHostel.id, baseData);
      } else {
        const data = {
          ...baseData,
          orgId: orgData?.orgId
        };
        console.log('📤 Creating hostel with data:', data);
        response = await hostelService.createHostel(data);
      }
      console.log('📤 Total Rooms:', baseData.totalRooms, 'Type:', typeof baseData.totalRooms);
      console.log('📤 Beds Per Room:', baseData.bedsPerRoom, 'Type:', typeof baseData.bedsPerRoom);
      if (response?.success || response?.id) {
        showNotification('success', 'Success', editingHostel ? 'Hostel updated successfully' : 'Hostel added successfully');
        setShowHostelModal(false);
        setEditingHostel(null);
        setHostelFormData({
          hostelName: '',
          address: '',
          city: '',
          state: '',
          pincode: '',
          capacity: '',
          warden: '',
          phone: '',
          email: '',
          totalRooms: '',
          bedsPerRoom: ''
        });
        loadHostelData();
      }
    } catch (error) {
      console.error('Error adding hostel:', error);
      showNotification('error', 'Error', error.message || 'Failed to add hostel');
    } finally {
      setHostelLoading(false);
    }
  };

  // Add room
  const handleAddRoom = async (e) => {
    e.preventDefault();
    if (!selectedHostel) {
      showNotification('error', 'Error', 'Please select a hostel');
      return;
    }
    setHostelLoading(true);
    try {
      let response;
      if (editingRoom) {
        const roomData = {
          orgId: editingRoom.orgId || orgData?.orgId,
          hostelId: editingRoom.hostelId || selectedHostel.id,
          roomNumber: roomFormData.roomNumber,
          capacity: parseInt(roomFormData.capacity, 10) || 0,
          occupied: editingRoom.occupied != null ? editingRoom.occupied : 0,
          active: editingRoom.active != null ? editingRoom.active : true
        };
        const roomId = editingRoom.id || editingRoom.roomId;
        response = await hostelService.updateHostelRoom(roomId, roomData);
      } else {
        const roomData = {
          orgId: orgData?.orgId,
          hostelId: selectedHostel.id,
          roomNumber: roomFormData.roomNumber,
          capacity: parseInt(roomFormData.capacity, 10) || 0,
          occupied: 0,
          active: true
        };
        response = await hostelService.createHostelRoom(selectedHostel.id, roomData);
      }

      if (response?.success || response?.id) {
        showNotification('success', 'Success', editingRoom ? 'Room updated successfully' : 'Room added successfully');
        setShowRoomModal(false);
        setEditingRoom(null);
        setRoomFormData({ roomNumber: '', capacity: '', type: 'shared' });
        if (editingRoom && selectedHostel) {
          await handleViewHostel(selectedHostel);
        } else {
          loadHostelData();
        }
      }
    } catch (error) {
      console.error('Error adding room:', error);
      showNotification('error', 'Error', error.message || 'Failed to add room');
    } finally {
      setHostelLoading(false);
    }
  };

  // Allocate student
  const handleAllocateStudent = async (e) => {
    e.preventDefault();
    setHostelLoading(true);
    try {
      const response = await hostelService.allocateHostel(allocationFormData);
      if (response?.success || response?.id) {
        showNotification('success', 'Success', 'Student allocated successfully');
        setShowAllocationModal(false);
        setAllocationFormData({ studentId: '', roomId: '', allocationDate: '', checkoutDate: '' });
        loadHostelData();
      }
    } catch (error) {
      console.error('Error allocating student:', error);
      showNotification('error', 'Error', error.message || 'Failed to allocate student');
    } finally {
      setHostelLoading(false);
    }
  };

  // Add maintenance record
  const handleAddMaintenance = async (e) => {
    e.preventDefault();
    if (!selectedHostel) {
      showNotification('error', 'Error', 'Please select a hostel');
      return;
    }
    setHostelLoading(true);
    try {
      const response = await hostelService.updateHostel(selectedHostel.id, maintenanceFormData);
      if (response?.success || response?.id) {
        showNotification('success', 'Success', 'Maintenance record added successfully');
        setShowMaintenanceModal(false);
        setMaintenanceFormData({ description: '', type: 'repair', priority: 'normal', status: 'pending' });
      }
    } catch (error) {
      console.error('Error adding maintenance record:', error);
      showNotification('error', 'Error', error.message || 'Failed to add maintenance record');
    } finally {
      setHostelLoading(false);
    }
  };

  // Load hostels on component mount
  useEffect(() => {
    if (activeTab === 'hostel' && orgData?.orgId) {
      loadHostelData();
    }
  }, [activeTab, orgData?.orgId]);

  // ============ CERTIFICATES MANAGEMENT HANDLERS ============

  const loadIssuedCertificates = async () => {
    try {
      setCertLoading(true);
      const res = await brandingService.getIssuedCertificates();
      if (res && Array.isArray(res)) {
        setIssuedCertificatesList(res);
      }
    } catch (err) {
      console.error('Error loading issued certificates:', err);
    } finally {
      setCertLoading(false);
    }
  };

  const loadCertificateTemplate = async (type) => {
    try {
      const templates = await brandingService.getTemplates();
      if (templates && Array.isArray(templates)) {
        const found = templates.find(t => t.templateType === type);
        if (found) {
          setCertTemplateName(found.templateName || 'Classic Formal');
          setCertPrimaryColor(found.primaryColor || '#1e3a8a');
          setCertSecondaryColor(found.secondaryColor || '#f59e0b');
          setCertTemplateContent(found.templateContent || '');
        } else {
          setCertTemplateName('Classic Formal');
          setCertPrimaryColor('#1e3a8a');
          setCertSecondaryColor('#f59e0b');
          setCertTemplateContent('');
        }
      }
    } catch (err) {
      console.error('Error loading certificate template:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'certificates') {
      loadIssuedCertificates();
      loadCertificateTemplate(certType);
      loadBrandingData();
    }
  }, [activeTab, certType]);

  const handleSaveCertificateTemplate = async (e) => {
    e.preventDefault();
    try {
      setCertLoading(true);
      await brandingService.saveTemplate({
        templateType: certType,
        templateName: certTemplateName,
        primaryColor: certPrimaryColor,
        secondaryColor: certSecondaryColor,
        templateContent: certTemplateContent,
        isDefault: true
      });
      showNotification('success', 'Success', 'Certificate template saved successfully');
    } catch (err) {
      console.error('Error saving certificate template:', err);
      showNotification('error', 'Error', 'Failed to save certificate template');
    } finally {
      setCertLoading(false);
    }
  };

  const handleIssueCertificate = async (e) => {
    e.preventDefault();
    if (!certSelectedStudent) {
      showNotification('error', 'Error', 'Please select a student first');
      return;
    }

    try {
      setCertLoading(true);
      const res = await brandingService.issueCertificate({
        studentId: certSelectedStudent.id,
        certificateType: certType,
        templateContent: certTemplateContent,
        issuedBy: 'Admin'
      });

      if (res && res.success) {
        showNotification('success', 'Success', `Certificate issued: ${res.certificateNumber}`);
        setCertSelectedStudent(null);
        loadIssuedCertificates();
      }
    } catch (err) {
      console.error('Error issuing certificate:', err);
      showNotification('error', 'Error', err.message || 'Failed to issue certificate');
    } finally {
      setCertLoading(false);
    }
  };

  const handleRevokeCertificate = async (id) => {
    if (!window.confirm('Are you sure you want to revoke this certificate? This action cannot be undone.')) {
      return;
    }

    try {
      setCertLoading(true);
      await brandingService.revokeCertificate(id);
      showNotification('success', 'Success', 'Certificate revoked successfully');
      loadIssuedCertificates();
    } catch (err) {
      console.error('Error revoking certificate:', err);
      showNotification('error', 'Error', 'Failed to revoke certificate');
    } finally {
      setCertLoading(false);
    }
  };

  const handleDownloadCertificatePDF = async (cert) => {
    try {
      const container = document.createElement('div');
      container.style.cssText = 'position:fixed;top:-9999px;left:-9999px;z-index:-1;';
      document.body.appendChild(container);

      const { createRoot } = await import('react-dom/client');
      const el = document.createElement('div');
      container.appendChild(el);

      const studentObj = students.find(s => s.name === cert.studentName) || { name: cert.studentName, studentId: cert.studentId };

      const root = createRoot(el);
      await new Promise(resolve => {
        root.render(
          <CertificateRenderer 
            certificateNumber={cert.certificateNumber}
            issuedDate={new Date(cert.issuedDate).toLocaleDateString()}
            certificateType={cert.certificateType}
            templateName={certTemplateName}
            primaryColor={certPrimaryColor}
            secondaryColor={certSecondaryColor}
            logoUrl={brandingData?.logoUrl}
            signatureUrl={brandingData?.signatureUrl}
            sealUrl={brandingData?.sealUrl}
            institutionName={orgData?.orgName}
            institutionAddress={orgData?.address ? `${orgData.address}, ${orgData.city || ''}, ${orgData.state || ''} - ${orgData.pincode || ''}`.replace(/,\s*,/g, ',').trim() : ''}
            templateContent={cert.templateContent}
            studentData={studentObj}
            id="cert-download-capture"
          />
        );
        setTimeout(resolve, 300);
      });

      const captureEl = document.getElementById('cert-download-capture');
      const canvas = await html2canvas(captureEl, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('l', 'mm', 'a4');
      pdf.addImage(imgData, 'PNG', 0, 0, 297, 210);
      pdf.save(`${cert.studentName}_${cert.certificateType.toLowerCase()}.pdf`);

      root.unmount();
      container.removeChild(el);
      showNotification('success', 'Success', 'PDF downloaded successfully');
    } catch (err) {
      console.error('PDF generation failed:', err);
      showNotification('error', 'Error', 'Failed to download PDF: ' + err.message);
    }
  };


  // ============ VEHICLE MANAGEMENT HANDLERS ============

  const loadVehicles = async () => {
    try {
      setVehicleLoading(true);
      const response = await vehicleService.listVehicles();
      if (response?.success || Array.isArray(response)) {
        setVehicles(Array.isArray(response) ? response : response.data || []);
      }
    } catch (error) {
      console.error('Error loading vehicles:', error);
      showNotification('error', 'Error', 'Failed to load vehicles');
    } finally {
      setVehicleLoading(false);
    }
  };

  const loadRoutes = async () => {
    try {
      const response = await vehicleService.listVehicles();
      if (response?.success || Array.isArray(response)) {
        setVehicleRoutes(Array.isArray(response) ? response : response.data || []);
      }
    } catch (error) {
      console.error('Error loading routes:', error);
    }
  };

  const loadAssignments = async () => {
    try {
      const response = await vehicleService.listVehicles();
      if (response?.success || Array.isArray(response)) {
        setVehicleAssignments(Array.isArray(response) ? response : response.data || []);
      }
    } catch (error) {
      console.error('Error loading assignments:', error);
    }
  };

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    setVehicleLoading(true);
    try {
      const data = { ...vehicleFormData, orgId: orgData?.orgId };
      const response = await vehicleService.addVehicle(data);
      if (response?.success || response?.id) {
        showNotification('success', 'Success', 'Vehicle added successfully');
        setShowVehicleModal(false);
        setVehicleFormData({ vehicleNumber: '', vehicleType: 'bus', capacity: '', driverName: '', driverPhone: '', registrationNumber: '', insuranceExpiry: '', status: 'active' });
        loadVehicles();
      }
    } catch (error) {
      console.error('Error adding vehicle:', error);
      showNotification('error', 'Error', error.message || 'Failed to add vehicle');
    } finally {
      setVehicleLoading(false);
    }
  };

  const handleAddRoute = async (e) => {
    e.preventDefault();
    setVehicleLoading(true);
    try {
      const data = { ...routeFormData, orgId: orgData?.orgId };
      const response = await vehicleService.addRoute(data);
      if (response?.success || response?.id) {
        showNotification('success', 'Success', 'Route added successfully');
        setShowRouteModal(false);
        setRouteFormData({ routeName: '', startPoint: '', endPoint: '', distance: '', estimatedTime: '', vehicleId: '' });
        loadRoutes();
      }
    } catch (error) {
      console.error('Error adding route:', error);
      showNotification('error', 'Error', error.message || 'Failed to add route');
    } finally {
      setVehicleLoading(false);
    }
  };

  const handleAssignStudent = async (e) => {
    e.preventDefault();
    setVehicleLoading(true);
    try {
      const data = { ...vehicleAssignmentFormData, orgId: orgData?.orgId };
      const response = await vehicleService.assignStudent(data);
      if (response?.success || response?.id) {
        showNotification('success', 'Success', 'Student assigned successfully');
        setShowVehicleAssignmentModal(false);
        setVehicleAssignmentFormData({ studentId: '', vehicleId: '', routeId: '', pickupPoint: '', dropPoint: '', startDate: '' });
        loadAssignments();
      }
    } catch (error) {
      console.error('Error assigning student:', error);
      showNotification('error', 'Error', error.message || 'Failed to assign student');
    } finally {
      setVehicleLoading(false);
    }
  };

  const handleAddVehicleMaintenance = async (e) => {
    e.preventDefault();
    if (!selectedVehicle) {
      showNotification('error', 'Error', 'Please select a vehicle');
      return;
    }
    setVehicleLoading(true);
    try {
      const response = await vehicleService.addMaintenance(selectedVehicle.id, vehicleMaintenanceFormData);
      if (response?.success || response?.id) {
        showNotification('success', 'Success', 'Maintenance record added successfully');
        setShowVehicleMaintenanceModal(false);
        setVehicleMaintenanceFormData({ description: '', type: 'service', cost: '', date: '', nextDueDate: '' });
      }
    } catch (error) {
      console.error('Error adding maintenance:', error);
      showNotification('error', 'Error', error.message || 'Failed to add maintenance record');
    } finally {
      setVehicleLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'vehicle' && orgData?.orgId) {
      loadVehicles();
      loadRoutes();
      loadAssignments();
    }
  }, [activeTab, orgData?.orgId]);

  // Fee reminder handlers
  const handleSendReminder = async (e) => {
    e.preventDefault();
    
    setReminderLoading(true);
    try {
      const reminderData = {
        orgId: orgData?.orgId,
        academicYear: reminderFormData.class ? new Date().getFullYear().toString() : undefined,
        class: reminderFormData.class || undefined,
        section: reminderFormData.section || undefined
      };

      // Remove undefined fields
      Object.keys(reminderData).forEach(key => reminderData[key] === undefined && delete reminderData[key]);

      console.log('Sending reminder with data:', reminderData);
      const response = await feeReminderService.sendFeeReminders(reminderData);
      
      console.log('Response received:', response);
      
      // Handle different response formats
      const isSuccess = response?.success || response?.status === 'success' || response?.message?.includes('success');
      const count = response?.count || response?.data?.count || 0;
      const message = response?.message || response?.data?.message || 'Reminders sent successfully';

      if (isSuccess || response) {
        showNotification('success', 'Reminders Sent', 
          `Fee reminders sent to ${count > 0 ? count : 'all'} students with pending fees via mobile app notification`);
        
        setShowReminderModal(false);
        setReminderFormData({ class: '', section: '' });
        addRecentActivity('🔔', 'Fee reminders sent', `Notifications sent to students with pending fees`);
      } else {
        throw new Error(message || 'Failed to send reminders');
      }
    } catch (error) {
      console.error('Error sending fee reminders:', error);
      const errorMessage = extractErrorMessage(error, 'Failed to send fee reminders. Please try again.');
      showNotification('error', 'Error', errorMessage);
    } finally {
      setReminderLoading(false);
    }
  };

  const openReminderModal = () => {
    setReminderTimeRemaining(null);
    setReminderFormData({ class: '', section: '' });
    setShowReminderModal(true);
  };

  const openAcademicModal = () => {
    setEditingAcademicItem(null);
    // Reset form data based on active tab
    if (academicActiveTab === 'classes') {
      setClassFormData({
        className: '',
        section: '',
        maxCapacity: '',
        classTeacherName: '',
        status: 'ACTIVE'
      });
    } else if (academicActiveTab === 'subjects') {
      setSubjectFormData({
        subjectName: '',
        subjectCode: '',
        classLevel: '',
        type: 'Core',
        status: 'ACTIVE'
      });
    } else if (academicActiveTab === 'timetable') {
      resetTimetableForm();
    }
    setShowAcademicModal(true);
  };

  // Filter students based on search and filters
  const filteredStudents = students.filter(student => {
    const matchesSearch = !searchTerm ||
      student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.studentId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.phone?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesClass = !filters.class || student.className === filters.class;
    const matchesSection = !filters.section || student.section === filters.section;
    const matchesYear = !filters.academicYear || student.admissionYear === filters.academicYear;
    
    return matchesSearch && matchesClass && matchesSection && matchesYear;
  });

  // Filter teachers based on search
  const filteredTeachers = teachers.filter(teacher => {
    const searchLower = (teacherSearchTerm || '').toLowerCase();
    return !teacherSearchTerm ||
      String(teacher.teacherId || '').toLowerCase().includes(searchLower) ||
      String(teacher.teacherName || '').toLowerCase().includes(searchLower) ||
      String(teacher.gmail || '').toLowerCase().includes(searchLower) ||
      String(teacher.phoneNumber || '').toLowerCase().includes(searchLower) ||
      String(teacher.degree || '').toLowerCase().includes(searchLower) ||
      String(teacher.specialization || '').toLowerCase().includes(searchLower) ||
      String(teacher.designation || '').toLowerCase().includes(searchLower);
  });

  const classOptions = useMemo(() => {
    if (!Array.isArray(classes)) return [];
    return classes
      .filter(cls => cls?.classId || cls?.className)
      .map(cls => ({
        id: cls.classId ?? cls.id ?? cls.className,
        label: cls.className || cls.name || `Class ${cls.classId || cls.id}`,
        section: cls.section || cls.classSection || ''
      }));
  }, [classes]);

  const sectionOptions = useMemo(() => {
    const sections = new Set();
    classes?.forEach(cls => {
      if (cls?.section) sections.add(cls.section);
      if (cls?.classSection) sections.add(cls.classSection);
    });
    const sectionArray = Array.from(sections).filter(Boolean);
    if (sectionArray.length === 0) {
      return ['A', 'B', 'C', 'D'];
    }
    return sectionArray.sort();
  }, [classes]);

  const filteredStudentsForFees = useMemo(() => {
    return students.filter(student => {
      const searchLower = (feeSearchTerm || '').toLowerCase();
      const matchesSearch = !feeSearchTerm || (
        String(student.studentId || '').toLowerCase().includes(searchLower) ||
        String(student.name || '').toLowerCase().includes(searchLower) ||
        String(student.email || '').toLowerCase().includes(searchLower) ||
        String(student.phone || '').toLowerCase().includes(searchLower)
      );

      const matchesYear = !feeFilters.academicYear || 
        String(student.admissionYear || '') === feeFilters.academicYear;

      const matchesClass = !feeFilters.class || 
        String(student.className || '') === feeFilters.class;

      const matchesSection = !feeFilters.section || 
        String(student.section || '') === feeFilters.section;

      let matchesPaymentStatus = true;
      if (feeFilters.paymentStatus) {
        const studentFee = fees.find(fee => 
          fee.studentId === (student.studentId || student.student_id)
        );
        
        if (studentFee) {
          const balance = studentFee.totalFees - studentFee.paidFees;
          let status = 'Pending';
          if (balance === 0) status = 'Paid';
          else if (balance < 0) status = 'Overpaid';
          matchesPaymentStatus = status.toLowerCase() === feeFilters.paymentStatus.toLowerCase();
        } else {
          matchesPaymentStatus = feeFilters.paymentStatus.toLowerCase() === 'pending';
        }
      }

      return matchesSearch && matchesYear && matchesClass && matchesSection && matchesPaymentStatus;
    });
  }, [students, feeSearchTerm, feeFilters, fees]);



  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.error("Error attempting to enable fullscreen:", err);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  return (
    <div className="admin-container">
      {/* Notification */}
      {notification && (
        <div className={`notification ${notification.type}`} style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '14px 20px',
          borderRadius: '8px',
          backgroundColor: notification.type === 'success' ? '#d4edda' : '#f8d7da',
          color: notification.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${notification.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
          zIndex: 9999,
          minWidth: '280px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div>
            {notification.title && <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{notification.title}</div>}
            <div>{notification.message}</div>
          </div>
          <button 
            onClick={() => setNotification(null)}
            style={{
              background: 'none',
              border: 'none',
              color: 'inherit',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: 'pointer',
              marginLeft: '15px',
              padding: 0,
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>
      )}

            {/* Enterprise School ERP Sidebar Component */}
      <div className="admin-layout">
        <SchoolSidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          academicActiveTab={academicActiveTab}
          setAcademicActiveTab={setAcademicActiveTab}
          attendanceSubTab={attendanceSubTab}
          setAttendanceSubTab={setAttendanceSubTab}
          feesSubTab={feesSubTab}
          setFeesSubTab={setFeesSubTab}
          libraryActiveTab={libraryActiveTab}
          setLibraryActiveTab={setLibraryActiveTab}
          reportsSubTab={reportsSubTab}
          setReportsSubTab={setReportsSubTab}
          studentsMenuExpanded={studentsMenuExpanded}
          setStudentsMenuExpanded={setStudentsMenuExpanded}
          academicsMenuExpanded={academicsMenuExpanded}
          setAcademicsMenuExpanded={setAcademicsMenuExpanded}
          attendanceMenuExpanded={attendanceMenuExpanded}
          setAttendanceMenuExpanded={setAttendanceMenuExpanded}
          feesMenuExpanded={feesMenuExpanded}
          setFeesMenuExpanded={setFeesMenuExpanded}
          libraryMenuExpanded={libraryMenuExpanded}
          setLibraryMenuExpanded={setLibraryMenuExpanded}
          reportsMenuExpanded={reportsMenuExpanded}
          setReportsMenuExpanded={setReportsMenuExpanded}
          showModal={showModal}
          setShowModal={setShowModal}
          showBulkAddModal={showBulkAddModal}
          openBulkAddModal={openBulkAddModal}
          openAddModal={() => {
            setEditingItem(null);
            setModalStep(1);
            setStudentFormData({
              studentId: '', name: '', rollNumber: '', photo: '', dob: '', gender: '', bloodGroup: '', phone: '', email: '', class: '', section: '', admissionYear: new Date().getFullYear().toString(), aadhaarNumber: '', address: '', fatherName: '', motherName: '', parentName: '', parentPhone: '', emergencyPhone: '', hostelOpted: 'No', transportOpted: 'No', password: '', status: 'Active'
            });
            setShowModal(true);
          }}
          orgData={orgData}
          user={{
            name: localStorage.getItem('username') || localStorage.getItem('name') || 'Administrator',
            role: localStorage.getItem('userRole') || 'School Admin'
          }}
          userRole={userRole}
          permissions={userPermissions}
          onLogout={onLogout}
        />

              <div 
                className="admin-content"
                style={{
                  padding: (activeTab === 'dashboard' || activeTab === 'marks-cards' || activeTab === 'id-cards' || (activeTab === 'academics' && (academicActiveTab === 'subjects' || academicActiveTab === 'classes'))) ? '0' : undefined,
                  marginLeft: 0
                }}
              >
                <div style={{ width: '100%', boxSizing: 'border-box', display: 'block' }}>
        {activeTab === 'dashboard' && (
          <div style={{ padding: '16px 20px', backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter', sans-serif" }}>
            {/* Header Greeting Bar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', margin: '0 0 2px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {getGreetingMessage()}, Admin! 👋
                </h1>
                <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
                  Welcome back to {orgData?.orgName || 'School ERP'}
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#334155', fontWeight: '500', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                  <span>📅</span>
                  <span>{getTodayFormatted()}</span>
                  <span style={{ fontSize: '10px', color: '#94a3b8' }}>▼</span>
                </div>
                <div onClick={toggleFullScreen} title="Toggle Full Screen" style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', color: '#64748b', transition: 'all 0.2s' }} onMouseOver={(e) => { e.currentTarget.style.color = '#0f172a'; e.currentTarget.style.background = '#f8fafc'; }} onMouseOut={(e) => { e.currentTarget.style.color = '#64748b'; e.currentTarget.style.background = '#ffffff'; }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </div>
                <div ref={notificationRef} style={{ position: 'relative' }}>
                  <div 
                    onClick={() => setShowNotificationMenu(prev => !prev)}
                    title="Notifications"
                    style={{ 
                      position: 'relative', 
                      background: showNotificationMenu ? '#f1f5f9' : '#ffffff', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '50%', 
                      width: '40px', 
                      height: '40px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      cursor: 'pointer', 
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                      transition: 'all 0.2s'
                    }}
                  >
                    <span style={{ fontSize: '18px' }}>🔔</span>
                    {unreadNotifCount > 0 && (
                      <span style={{ 
                        position: 'absolute', 
                        top: '2px', 
                        right: '2px', 
                        background: '#ef4444', 
                        color: '#fff', 
                        fontSize: '10px', 
                        fontWeight: 'bold', 
                        borderRadius: '50%', 
                        width: '18px', 
                        height: '18px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        border: '2px solid #ffffff'
                      }}>
                        {unreadNotifCount > 99 ? '99+' : unreadNotifCount}
                      </span>
                    )}
                  </div>

                  {/* Notification Dropdown Menu */}
                  {showNotificationMenu && (
                    <div style={{
                      position: 'absolute',
                      top: '48px',
                      right: '0',
                      width: '380px',
                      maxHeight: '520px',
                      backgroundColor: '#ffffff',
                      borderRadius: '16px',
                      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                      border: '1px solid #e2e8f0',
                      zIndex: 9999,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden'
                    }}>
                      {/* Header */}
                      <div style={{
                        padding: '14px 18px',
                        borderBottom: '1px solid #f1f5f9',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>Notifications</h3>
                          {unreadNotifCount > 0 && (
                            <span style={{
                              background: '#eff6ff',
                              color: '#2563eb',
                              fontSize: '11px',
                              fontWeight: '700',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              border: '1px solid #bfdbfe'
                            }}>
                              {unreadNotifCount} new
                            </span>
                          )}
                        </div>
                        {unreadNotifCount > 0 && (
                          <button 
                            onClick={handleMarkAllNotificationsAsRead}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#2563eb',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              padding: '4px 8px',
                              borderRadius: '6px',
                              transition: 'background 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.background = '#eff6ff'}
                            onMouseOut={(e) => e.target.style.background = 'none'}
                          >
                            Mark all read
                          </button>
                        )}
                      </div>

                      {/* Filter Tabs */}
                      <div style={{
                        display: 'flex',
                        padding: '8px 14px',
                        gap: '6px',
                        borderBottom: '1px solid #f1f5f9',
                        backgroundColor: '#ffffff'
                      }}>
                        {[
                          { key: 'ALL', label: 'All' },
                          { key: 'ALERTS', label: 'Alerts' },
                          { key: 'ACTIVITIES', label: 'Activities' }
                        ].map(tab => (
                          <button
                            key={tab.key}
                            onClick={() => setNotificationFilterTab(tab.key)}
                            style={{
                              padding: '5px 12px',
                              borderRadius: '8px',
                              border: 'none',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              backgroundColor: notificationFilterTab === tab.key ? '#2563eb' : '#f1f5f9',
                              color: notificationFilterTab === tab.key ? '#ffffff' : '#64748b',
                              transition: 'all 0.2s'
                            }}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>

                      {/* Notification List */}
                      <div style={{
                        overflowY: 'auto',
                        maxHeight: '360px',
                        padding: '8px'
                      }}>
                        {loadingNotifications ? (
                          <div style={{ padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                            Loading notifications...
                          </div>
                        ) : filteredNotificationsList.length === 0 ? (
                          <div style={{ padding: '36px 20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '28px', marginBottom: '8px' }}>🔔</div>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#334155' }}>No notifications</div>
                            <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>You're all caught up!</div>
                          </div>
                        ) : (
                          filteredNotificationsList.map((item) => {
                            const isUnread = item.status !== 'READ';
                            return (
                              <div
                                key={item.id}
                                onClick={() => {
                                  handleMarkNotificationAsRead(item.id);
                                  if (item.type === 'PTM_BOOKING' || item.type === 'APPOINTMENT_BOOKED') {
                                    if (typeof setActiveTab === 'function') setActiveTab('academics');
                                    if (typeof setAcademicActiveTab === 'function') setAcademicActiveTab('ptm');
                                    if (typeof setShowNotificationMenu === 'function') setShowNotificationMenu(false);
                                  }
                                }}
                                style={{
                                  display: 'flex',
                                  gap: '12px',
                                  padding: '12px',
                                  borderRadius: '10px',
                                  backgroundColor: isUnread ? '#f8fafc' : '#ffffff',
                                  border: isUnread ? '1px solid #e2e8f0' : '1px solid #f1f5f9',
                                  marginBottom: '6px',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  position: 'relative'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = isUnread ? '#f8fafc' : '#ffffff'}
                              >
                                <div style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '10px',
                                  backgroundColor: item.type === 'FEE_ALERT' ? '#fef3c7' : (item.type === 'SMART_ALERT' ? '#fee2e2' : (item.type === 'PTM_BOOKING' || item.type === 'APPOINTMENT_BOOKED' ? '#ede9fe' : '#e0f2fe')),
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '18px',
                                  flexShrink: 0
                                }}>
                                  {item.icon || (item.type === 'FEE_ALERT' ? '💰' : (item.type === 'SMART_ALERT' ? '⚠️' : (item.type === 'PTM_BOOKING' || item.type === 'APPOINTMENT_BOOKED' ? '📅' : '🔔')))}
                                </div>
                                <div style={{ flex: 1, paddingRight: '12px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                                    <span style={{ fontSize: '13px', fontWeight: isUnread ? '700' : '600', color: '#0f172a' }}>
                                      {item.title}
                                    </span>
                                    <span style={{ fontSize: '10px', color: '#94a3b8', marginLeft: '6px' }}>
                                      {formatRelativeTime(item.createdAt)}
                                    </span>
                                  </div>
                                  <p style={{ margin: 0, fontSize: '12px', color: '#475569', lineHeight: '1.4' }}>
                                    {item.message}
                                  </p>
                                </div>
                                {isUnread && (
                                  <div style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: '#2563eb',
                                    position: 'absolute',
                                    top: '14px',
                                    right: '12px'
                                  }} />
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>

                      {/* Footer */}
                      <div style={{
                        padding: '10px 16px',
                        borderTop: '1px solid #f1f5f9',
                        backgroundColor: '#f8fafc',
                        textAlign: 'center'
                      }}>
                        <button
                          onClick={() => fetchAdminNotifications()}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#64748b',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          🔄 Refresh notifications
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#3b82f6', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '16px' }}>
                    {((orgData?.name || orgData?.username || localStorage.getItem('username') || 'A')[0] || 'A').toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>{orgData?.name || orgData?.username || localStorage.getItem('username') || 'Administrator'}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{(orgData?.userType === 'SUB_USER' || localStorage.getItem('userType') === 'SUB_USER') ? (orgData?.role || localStorage.getItem('userRole') || 'Staff User') : 'Super Admin'} ▾</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Top 4 KPI Stat Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '12px' }}>
              {/* Card 1: Students */}
              <div style={{ background: 'linear-gradient(135deg, #f0f5ff 0%, #e0e7ff 100%)', borderRadius: '12px', padding: '12px 14px', border: '1px solid #c7d2fe', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                <div>
                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#818cf8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', color: '#ffffff', marginBottom: '6px' }}>
                    👥
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#4f46e5', textTransform: 'uppercase' }}>Total Students</div>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: '#1e1b4b', margin: '2px 0' }}>{dashboardSummary?.students?.total ?? students.length}</div>
                  <div style={{ fontSize: '10px', color: '#16a34a', fontWeight: '600' }}>{dashboardSummary?.students?.newThisMonth != null ? `${dashboardSummary.students.newThisMonth} new this month` : 'Enrolled Students'}</div>
                </div>
                <div style={{ width: '40px', height: '32px', display: 'flex', alignItems: 'flex-end', gap: '3px' }}>
                  {[0.4, 0.6, 0.5, 0.8, 1.0].map((h, idx) => (
                    <div key={idx} style={{ width: '5px', height: `${h * 100}%`, background: '#6366f1', borderRadius: '2px' }}></div>
                  ))}
                </div>
              </div>

              {/* Card 2: Teachers / Faculty */}
              <div style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderRadius: '12px', padding: '12px 14px', border: '1px solid #bbf7d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                <div>
                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#4ade80', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', color: '#ffffff', marginBottom: '6px' }}>
                    🎓
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#16a34a', textTransform: 'uppercase' }}>{isCollege ? 'Total Faculty' : 'Total Teachers'}</div>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: '#064e3b', margin: '2px 0' }}>{dashboardSummary?.teachers?.total ?? teachers.length}</div>
                  <div style={{ fontSize: '10px', color: '#16a34a', fontWeight: '600' }}>{dashboardSummary?.teachers?.active != null ? `${dashboardSummary.teachers.active} active staff` : (isCollege ? 'Faculty Members' : 'Teachers')}</div>
                </div>
                <div style={{ width: '40px', height: '32px', display: 'flex', alignItems: 'flex-end', gap: '3px' }}>
                  {[0.5, 0.7, 0.6, 0.9, 1.0].map((h, idx) => (
                    <div key={idx} style={{ width: '5px', height: `${h * 100}%`, background: '#22c55e', borderRadius: '2px' }}></div>
                  ))}
                </div>
              </div>

              {/* Card 3: Revenue / Fees Collected */}
              <div style={{ background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', borderRadius: '12px', padding: '12px 14px', border: '1px solid #fde68a', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                <div>
                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', color: '#ffffff', marginBottom: '6px' }}>
                    ₹
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#d97706', textTransform: 'uppercase' }}>Fees Collected</div>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: '#78350f', margin: '2px 0' }}>
                    {dashboardSummary?.fees?.collected != null 
                      ? (dashboardSummary.fees.collected >= 100000 ? `₹${(dashboardSummary.fees.collected / 100000).toFixed(2)}L` : `₹${dashboardSummary.fees.collected.toLocaleString('en-IN')}`) 
                      : '₹0'}
                  </div>
                  <div style={{ fontSize: '10px', color: '#16a34a', fontWeight: '600' }}>{dashboardSummary?.fees?.collectionRate != null ? `${dashboardSummary.fees.collectionRate}% collected` : 'Total Collection'}</div>
                </div>
                <div style={{ width: '40px', height: '32px', display: 'flex', alignItems: 'flex-end', gap: '3px' }}>
                  {[0.3, 0.5, 0.7, 0.8, 1.0].map((h, idx) => (
                    <div key={idx} style={{ width: '5px', height: `${h * 100}%`, background: '#f59e0b', borderRadius: '2px' }}></div>
                  ))}
                </div>
              </div>

              {/* Card 4: Attendance */}
              <div style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', borderRadius: '12px', padding: '12px 14px', border: '1px solid #bae6fd', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                <div>
                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#38bdf8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', color: '#ffffff', marginBottom: '6px' }}>
                    📋
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#0284c7', textTransform: 'uppercase' }}>Attendance Rate</div>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: '#0c4a6e', margin: '2px 0' }}>{dashboardSummary?.attendance?.rate != null ? `${dashboardSummary.attendance.rate}%` : '0.0%'}</div>
                  <div style={{ fontSize: '10px', color: '#16a34a', fontWeight: '600' }}>{dashboardSummary?.attendance?.present != null ? `${dashboardSummary.attendance.present} present today` : 'Daily marked'}</div>
                </div>
                <div style={{ width: '40px', height: '32px', display: 'flex', alignItems: 'flex-end', gap: '3px' }}>
                  {[0.7, 0.8, 0.85, 0.9, 0.96].map((h, idx) => (
                    <div key={idx} style={{ width: '5px', height: `${h * 100}%`, background: '#0284c7', borderRadius: '2px' }}></div>
                  ))}
                </div>
              </div>
            </div>

            {/* Middle Analytics Section: Student Growth, Fee Collection, Today's Highlights */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.1fr 0.9fr', gap: '16px', marginBottom: '16px' }}>
              {/* Student Growth Chart Card */}
              <div style={{ background: '#ffffff', borderRadius: '12px', padding: '14px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Student Growth</h3>
                  <select style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '2px 8px', fontSize: '11px', color: '#64748b', outline: 'none' }}>
                    <option>This Year</option>
                    <option>Last Year</option>
                  </select>
                </div>
                {(() => {
                  const currentCount = dashboardSummary?.students?.total ?? students.length;
                  const growthData = dashboardSummary?.studentGrowth || [
                    { month: 'Mar', students: Math.round(currentCount * 0.85) },
                    { month: 'Apr', students: Math.round(currentCount * 0.90) },
                    { month: 'May', students: Math.round(currentCount * 0.93) },
                    { month: 'Jun', students: Math.round(currentCount * 0.96) },
                    { month: 'Jul', students: Math.round(currentCount * 0.98) },
                    { month: 'Aug', students: currentCount }
                  ];

                  const latestMonth = growthData[growthData.length - 1];

                  return (
                    <div style={{ position: 'relative', height: '140px', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '6px 0' }}>
                      <svg style={{ position: 'absolute', top: 5, left: 0, width: '100%', height: '110px' }} viewBox="0 0 460 110" preserveAspectRatio="none">
                        <defs>
                          <linearGradient id="growthGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#6366f1" stopOpacity="0.3" />
                            <stop offset="100%" stopColor="#6366f1" stopOpacity="0.0" />
                          </linearGradient>
                        </defs>
                        <path d="M 10 90 Q 70 75, 130 65 T 250 45 T 370 20 T 450 10 L 450 110 L 10 110 Z" fill="url(#growthGradient)" />
                        <path d="M 10 90 Q 70 75, 130 65 T 250 45 T 370 20 T 450 10" fill="none" stroke="#6366f1" strokeWidth="2.5" />
                        <circle cx="450" cy="10" r="4" fill="#4338ca" stroke="#ffffff" strokeWidth="2" />
                      </svg>
                      {/* Tooltip Overlay */}
                      <div style={{ position: 'absolute', top: '5px', right: '10px', background: '#ffffff', padding: '4px 8px', borderRadius: '6px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                        <div style={{ fontSize: '9px', color: '#64748b' }}>{latestMonth?.month || 'Current'}</div>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#1e1b4b' }}>{latestMonth?.students ?? currentCount} Students</div>
                      </div>
                      {/* Y Axis Labels */}
                      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100px', fontSize: '10px', color: '#94a3b8' }}>
                        <div>{Math.round(currentCount * 1.2)}</div>
                        <div>{currentCount}</div>
                        <div>{Math.round(currentCount * 0.8)}</div>
                        <div>{Math.round(currentCount * 0.5)}</div>
                        <div>0</div>
                      </div>
                      {/* X Axis Labels */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#94a3b8', borderTop: '1px border-dashed #f1f5f9', paddingTop: '4px' }}>
                        {growthData.map((g, idx) => (
                          <span key={idx}>{g.month}</span>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Fee Collection Bar Chart Card */}
              <div style={{ background: '#ffffff', borderRadius: '12px', padding: '14px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Fee Collection</h3>
                  <select style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '2px 8px', fontSize: '11px', color: '#64748b', outline: 'none' }}>
                    <option>This Month</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-end', height: '140px' }}>
                  <div style={{ flex: '0 0 85px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ fontSize: '10px', color: '#64748b' }}>Collected</div>
                    <div style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>
                      {dashboardSummary?.fees?.collected != null 
                        ? (dashboardSummary.fees.collected >= 100000 ? `₹${(dashboardSummary.fees.collected / 100000).toFixed(2)}L` : `₹${dashboardSummary.fees.collected.toLocaleString('en-IN')}`) 
                        : '₹0'}
                    </div>
                    <div style={{ fontSize: '10px', color: '#94a3b8', margin: '2px 0 6px 0' }}>
                      Dues: ₹{(dashboardSummary?.fees?.pendingAmount || 0).toLocaleString('en-IN')}
                    </div>
                    <div style={{ background: '#e2e8f0', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                      <div style={{ width: `${Math.min(100, dashboardSummary?.fees?.collectionRate || 0)}%`, height: '100%', background: '#6366f1' }}></div>
                    </div>
                    <div style={{ fontSize: '9px', fontWeight: '700', color: '#6366f1', textAlign: 'right', marginTop: '2px' }}>{dashboardSummary?.fees?.collectionRate || 0}%</div>
                  </div>
                  <div style={{ flex: 1, height: '110px', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', paddingBottom: '14px', borderBottom: '1px solid #f1f5f9' }}>
                    {(dashboardSummary?.weeklyFeeCollection || [
                      { week: 'W1', val: 0.4 },
                      { week: 'W2', val: 0.6 },
                      { week: 'W3', val: 0.7 },
                      { week: 'W4', val: 0.95 }
                    ]).map((item, idx) => (
                      <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                        <div style={{ width: '14px', height: `${(item.val || 0.3) * 80}px`, background: '#6366f1', borderRadius: '3px 3px 0 0' }} title={`₹${item.amount || 0}`}></div>
                        <span style={{ fontSize: '9px', color: '#94a3b8' }}>{item.week}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Today's Highlights Card */}
              <div style={{ background: '#ffffff', borderRadius: '12px', padding: '14px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', margin: '0 0 10px 0' }}>Today's Highlights</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '500', color: '#334155' }}>
                      <span style={{ background: '#dbeafe', padding: '4px', borderRadius: '6px', fontSize: '12px' }}>👤</span>
                      <span>Admissions Today</span>
                    </div>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>{dashboardSummary?.today?.admissions ?? 0}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '500', color: '#334155' }}>
                      <span style={{ background: '#fef3c7', padding: '4px', borderRadius: '6px', fontSize: '12px' }}>👛</span>
                      <span>Fees Pending</span>
                    </div>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>{dashboardSummary?.today?.feesPending ?? 0}</span>
                  </div>
                  <div style={{ padding: '6px 10px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '500', color: '#334155' }}>
                        <span style={{ background: '#fce7f3', padding: '4px', borderRadius: '6px', fontSize: '12px' }}>🎂</span>
                        <span>Today's Birthdays</span>
                      </div>
                      <span style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>{birthdayStudents.length}</span>
                    </div>
                    {birthdayStudents.length > 0 && (
                      <div style={{ marginTop: '6px', padding: '6px 8px', background: '#FFF1F2', borderRadius: '6px', fontSize: '11px', color: '#BE123C', fontWeight: '600' }}>
                        🎉 {birthdayStudents.map(s => `${s.name} (${s.className || s.classLevel || 'N/A'}${s.section ? ` - ${s.section}` : ''})`).join(', ')}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '500', color: '#334155' }}>
                      <span style={{ background: '#dcfce7', padding: '4px', borderRadius: '6px', fontSize: '12px' }}>📅</span>
                      <span>Holidays</span>
                    </div>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>{dashboardSummary?.today?.holidays ?? 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom 3 Section Grid: Quick Actions, AI Insights, Upcoming Events */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              {/* Quick Actions Card Grid */}
              <div style={{ background: '#ffffff', borderRadius: '12px', padding: '14px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', margin: '0 0 10px 0' }}>Quick Actions</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                  {[
                    { label: 'Add Student', icon: '👤', tab: 'students', bg: '#eff6ff', color: '#2563eb' },
                    { label: isCollege ? 'Add Faculty' : 'Add Teacher', icon: '🎓', tab: 'teachers', bg: '#f0fdf4', color: '#16a34a' },
                    { label: isCollege ? 'Collect Fees' : 'Collect Fees', icon: '₹', tab: 'fees', bg: '#fffbeb', color: '#d97706' },
                    { label: 'Attendance', icon: '📅', tab: 'academics', bg: '#f0f9ff', color: '#0284c7' },
                    { label: isCollege ? 'Exams' : 'Exams', icon: '📋', tab: 'exams', bg: '#fdf2f8', color: '#db2777' },
                    { label: 'Time Table', icon: '⏰', tab: 'academics', bg: '#eff6ff', color: '#3b82f6' },
                    { label: isCollege ? 'Transport' : 'Transport', icon: '🚌', tab: 'vehicle', bg: '#fff7ed', color: '#ea580c' },
                    { label: 'Reports', icon: '📊', tab: 'reports', bg: '#faf5ff', color: '#9333ea' }
                  ].map((act, i) => (
                    <div 
                      key={i} 
                      onClick={() => setActiveTab(act.tab)} 
                      style={{ background: '#f8fafc', borderRadius: '8px', padding: '8px 4px', textAlign: 'center', cursor: 'pointer', border: '1px solid #f1f5f9' }}
                    >
                      <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: act.bg, color: act.color, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 4px auto', fontSize: '13px' }}>
                        {act.icon}
                      </div>
                      <div style={{ fontSize: '10px', fontWeight: '600', color: '#334155' }}>{act.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Real-time Dynamic AI Insights */}
              {(() => {
                const lowAttendanceCount = students.filter(s => s.attendancePercentage != null && s.attendancePercentage < 75).length;
                const pendingFeeCount = dashboardSummary?.today?.feesPending ?? students.filter(s => (s.pendingFee && s.pendingFee > 0) || (s.dueFee && s.dueFee > 0)).length;
                const totalPendingAmt = dashboardSummary?.fees?.pendingAmount || 0;
                const activeClassCount = classes?.length || 0;
                const totalStudentCount = students?.length || 0;

                return (
                  <div style={{ background: '#ffffff', borderRadius: '16px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>AI Insights</h3>
                      <span style={{ background: '#2563eb', color: '#ffffff', fontSize: '10px', fontWeight: 'bold', padding: '2px 8px', borderRadius: '10px' }}>Real-Time</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      
                      {/* Insight 1: Attendance Risk */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: lowAttendanceCount > 0 ? '#fef2f2' : '#f0fdf4', borderRadius: '10px', border: lowAttendanceCount > 0 ? '1px solid #fee2e2' : '1px solid #dcfce7' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: lowAttendanceCount > 0 ? '#991b1b' : '#166534', fontWeight: '500' }}>
                          <span>{lowAttendanceCount > 0 ? '⚠️' : '✅'}</span>
                          <span>
                            {lowAttendanceCount > 0 
                              ? `${lowAttendanceCount} students are at risk of low attendance (<75%)` 
                              : `All ${totalStudentCount} enrolled students maintain healthy attendance above 75%`}
                          </span>
                        </div>
                        <span onClick={() => setActiveTab('academics')} style={{ fontSize: '12px', fontWeight: '600', color: '#2563eb', cursor: 'pointer' }}>View</span>
                      </div>

                      {/* Insight 2: Pending Fees */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: totalPendingAmt > 0 ? '#fffbeb' : '#f0fdf4', borderRadius: '10px', border: totalPendingAmt > 0 ? '1px solid #fef3c7' : '1px solid #dcfce7' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: totalPendingAmt > 0 ? '#92400e' : '#166534', fontWeight: '500' }}>
                          <span>₹</span>
                          <span>
                            {totalPendingAmt > 0 
                              ? `₹${totalPendingAmt.toLocaleString('en-IN')} pending fee balance across ${pendingFeeCount || 1} students` 
                              : '100% Fee collection compliance achieved for current session'}
                          </span>
                        </div>
                        <span onClick={() => setActiveTab('fees')} style={{ fontSize: '12px', fontWeight: '600', color: '#2563eb', cursor: 'pointer' }}>View</span>
                      </div>

                      {/* Insight 3: Active Class Strength */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#f0f9ff', borderRadius: '10px', border: '1px solid #e0f2fe' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: '#075985', fontWeight: '500' }}>
                          <span>🏫</span>
                          <span>
                            {activeClassCount > 0 
                              ? `${activeClassCount} active classes running with ${totalStudentCount} enrolled students` 
                              : 'School directory loaded with current academic session'}
                          </span>
                        </div>
                        <span onClick={() => setActiveTab('classes')} style={{ fontSize: '12px', fontWeight: '600', color: '#2563eb', cursor: 'pointer' }}>View</span>
                      </div>

                      {/* Insight 4: Directory Status */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#faf5ff', borderRadius: '10px', border: '1px solid #f3e8ff' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: '#6b21a8', fontWeight: '500' }}>
                          <span>📊</span>
                          <span>{teachers.length} faculty members & {totalStudentCount} active student records</span>
                        </div>
                        <span onClick={() => setActiveTab('students')} style={{ fontSize: '12px', fontWeight: '600', color: '#2563eb', cursor: 'pointer' }}>View</span>
                      </div>

                    </div>
                  </div>
                );
              })()}

              {/* Real-time Dynamic Upcoming Events Card */}
              {(() => {
                const eventsList = (dashboardSummary?.upcomingEvents && dashboardSummary.upcomingEvents.length > 0)
                  ? dashboardSummary.upcomingEvents
                  : [
                    { month: 'AUG', day: '25', title: 'Mid-Term Progress & Parent Meeting', time: '11:00 AM', color: '#10b981', bg: '#f0fdf4' },
                    { month: 'SEP', day: '05', title: 'Teachers Day Celebration', time: '09:30 AM', color: '#3b82f6', bg: '#eff6ff' }
                  ];

                return (
                  <div style={{ background: '#ffffff', borderRadius: '16px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Upcoming Events</h3>
                      <span onClick={() => setActiveTab('academics')} style={{ fontSize: '12px', color: '#2563eb', fontWeight: '600', cursor: 'pointer' }}>Academic Calendar</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {eventsList.map((evt, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '10px', borderRadius: '10px', border: '1px solid #f1f5f9' }}>
                          <div style={{ background: evt.bg || '#eff6ff', borderRadius: '8px', padding: '6px 12px', textAlign: 'center', minWidth: '45px' }}>
                            <div style={{ fontSize: '9px', fontWeight: 'bold', color: evt.color || '#3b82f6', textTransform: 'uppercase' }}>{evt.month}</div>
                            <div style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>{evt.day}</div>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a' }}>{evt.title}</div>
                            <div style={{ fontSize: '11px', color: '#64748b' }}>{evt.time}</div>
                          </div>
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: evt.color || '#3b82f6' }}></span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Bottom Row: Recent Activity Table & Fee Collection Donut Chart */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.2fr', gap: '20px' }}>
              {/* Recent Activity Table */}
              <div style={{ background: '#ffffff', borderRadius: '16px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: '0 0 16px 0' }}>Recent Activity</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #f1f5f9', color: '#64748b', fontSize: '11px', textTransform: 'uppercase' }}>
                      <th style={{ padding: '8px 12px' }}>Activity</th>
                      <th style={{ padding: '8px 12px' }}>Details</th>
                      <th style={{ padding: '8px 12px' }}>Date & Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {((dashboardSummary?.recentActivities && dashboardSummary.recentActivities.length > 0)
                      ? dashboardSummary.recentActivities
                      : recentActivities
                    ).length === 0 ? (
                      <tr>
                        <td colSpan="3" style={{ padding: '16px', textAlign: 'center', color: '#94a3b8' }}>No recent activity records found.</td>
                      </tr>
                    ) : (
                      ((dashboardSummary?.recentActivities && dashboardSummary.recentActivities.length > 0)
                        ? dashboardSummary.recentActivities
                        : recentActivities
                      ).slice(0, 6).map((act, idx) => (
                        <tr key={act.id || idx} style={{ borderBottom: '1px solid #f8fafc' }}>
                          <td style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '8px', color: '#334155', fontWeight: '500' }}>
                            <span style={{ background: '#f1f5f9', padding: '4px', borderRadius: '50%', fontSize: '14px' }}>
                              {act.type === 'FEE' ? '💰' : act.type === 'EVENT' ? '📸' : (act.icon || '📋')}
                            </span>
                            <span>{act.type || act.description || 'System Activity'}</span>
                          </td>
                          <td style={{ padding: '10px 12px', color: '#64748b' }}>{act.details || act.description || '-'}</td>
                          <td style={{ padding: '10px 12px', color: '#94a3b8', fontSize: '11px' }}>
                            {act.time || (act.timestamp ? new Date(act.timestamp).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Today')}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Fee Collection Overview Donut Chart */}
              <div style={{ background: '#ffffff', borderRadius: '16px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: '0 0 16px 0' }}>Fee Collection Overview</h3>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '160px' }}>
                  {/* Circular Donut Ring */}
                  <div style={{ position: 'relative', width: '130px', height: '130px', borderRadius: '50%', background: `conic-gradient(#6366f1 0% ${dashboardSummary?.fees?.collectionRate || 0}%, #fda4af ${dashboardSummary?.fees?.collectionRate || 0}% 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: '90px', height: '90px', borderRadius: '50%', background: '#ffffff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ fontSize: '13px', fontWeight: '800', color: '#0f172a' }}>
                        {dashboardSummary?.fees?.collected != null
                          ? (dashboardSummary.fees.collected >= 100000 ? `₹${(dashboardSummary.fees.collected / 100000).toFixed(2)}L` : `₹${dashboardSummary.fees.collected.toLocaleString('en-IN')}`)
                          : '₹0'}
                      </div>
                      <div style={{ fontSize: '10px', color: '#64748b' }}>Collected</div>
                    </div>
                  </div>
                  {/* Legend Labels */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#6366f1' }}></span>
                        <span>Collected</span>
                      </div>
                      <div style={{ fontSize: '11px', color: '#64748b', marginLeft: '16px' }}>
                        ₹{(dashboardSummary?.fees?.collected || 0).toLocaleString('en-IN')} ({dashboardSummary?.fees?.collectionRate || 0}%)
                      </div>
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#fda4af' }}></span>
                        <span>Outstanding Dues</span>
                      </div>
                      <div style={{ fontSize: '11px', color: '#64748b', marginLeft: '16px' }}>
                        ₹{(dashboardSummary?.fees?.pendingAmount || 0).toLocaleString('en-IN')} ({dashboardSummary?.fees?.collectionRate != null ? (100 - dashboardSummary.fees.collectionRate).toFixed(1) : 0}%)
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #f1f5f9', fontSize: '12px', color: '#64748b' }}>
                  <span>Dues Count: {dashboardSummary?.fees?.pendingCount || 0} students</span>
                  <span style={{ color: '#2563eb', fontWeight: '600', cursor: 'pointer' }} onClick={() => setActiveTab('fees')}>View Fees →</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'students' && (
          <div style={{ paddingBottom: '30px' }}>
            {/* 1. Page Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              flexWrap: 'wrap',
              gap: '15px'
            }}>
              <div>
                <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', margin: 0, letterSpacing: '-0.3px' }}>
                  Student Management
                </h1>
                <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 0' }}>
                  Manage student records, admissions and academic information
                </p>
              </div>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => openModal('add')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: '#2563eb',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '9px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(37,99,235,0.2)',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <span style={{ fontSize: '15px', fontWeight: '700' }}>+</span> Add Student
                </button>

                <button
                  onClick={() => {
                    setShowPromotionModal(true);
                    fetchPromotionPreview(promotionSourceClass);
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: '#059669',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '9px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(5,150,105,0.2)',
                    transition: 'all 0.15s ease'
                  }}
                >
                  🎓 Bulk Student Promotion
                </button>

                <button
                  onClick={() => openDownloadModal('students')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: '#ffffff',
                    color: '#334155',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    padding: '8px 14px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  📥 Export
                </button>

                <button
                  onClick={refreshStudentData}
                  disabled={loading}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: '#ffffff',
                    color: '#334155',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    padding: '8px 14px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    opacity: loading ? 0.7 : 1,
                    transition: 'all 0.15s ease'
                  }}
                >
                  🔄 {loading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
            </div>

            {/* 2. Student Summary KPIs */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: '700' }}>🎓</div>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>Total Students</div>
                  <div style={{ fontSize: '18px', color: '#0f172a', fontWeight: '700', marginTop: '2px' }}>{students.length}</div>
                </div>
              </div>

              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: '#f0fdf4', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: '700' }}>✅</div>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>Active Enrolled</div>
                  <div style={{ fontSize: '18px', color: '#0f172a', fontWeight: '700', marginTop: '2px' }}>
                    {students.filter(s => s.active === true || s.status === 'Active').length}
                  </div>
                </div>
              </div>

              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: '#f0f9ff', color: '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: '700' }}>👦</div>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>Boys</div>
                  <div style={{ fontSize: '18px', color: '#0f172a', fontWeight: '700', marginTop: '2px' }}>
                    {students.filter(s => String(s.gender || '').toLowerCase() === 'male' || String(s.gender || '').toLowerCase() === 'boy').length}
                  </div>
                </div>
              </div>

              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: '#fdf2f8', color: '#db2777', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: '700' }}>👧</div>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>Girls</div>
                  <div style={{ fontSize: '18px', color: '#0f172a', fontWeight: '700', marginTop: '2px' }}>
                    {students.filter(s => String(s.gender || '').toLowerCase() === 'female' || String(s.gender || '').toLowerCase() === 'girl').length}
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Search + Filter Toolbar */}
            <div style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              padding: '12px 16px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              {/* Left: Search input */}
              <div style={{ position: 'relative', flex: '1 1 260px', minWidth: '220px' }}>
                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '14px' }}>🔍</span>
                <input
                  type="text"
                  placeholder="Search by name, Student ID, phone or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px 8px 34px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#0f172a',
                    outline: 'none',
                    transition: 'border-color 0.15s ease'
                  }}
                />
              </div>

              {/* Right: Filters */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <select
                  value={filters.academicYear}
                  onChange={(e) => handleFilterChange('academicYear', e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#334155',
                    background: '#ffffff',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">Academic Year: All</option>
                  {getUniqueValues('admissionYear').map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>

                <select
                  value={filters.class}
                  onChange={(e) => handleFilterChange('class', e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#334155',
                    background: '#ffffff',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">Class: All</option>
                  {getUniqueValues('class').map(cls => (
                    <option key={cls} value={cls}>Class {cls}</option>
                  ))}
                </select>

                <select
                  value={filters.section}
                  onChange={(e) => handleFilterChange('section', e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#334155',
                    background: '#ffffff',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">Section: All</option>
                  {getUniqueValues('section').map(section => (
                    <option key={section} value={section}>Section {section}</option>
                  ))}
                </select>

                {(searchTerm || filters.academicYear || filters.class || filters.section) && (
                  <button
                    onClick={clearAllFilters}
                    style={{
                      padding: '8px 14px',
                      background: '#f1f5f9',
                      color: '#475569',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            {/* 4. Production Table Container */}
            <div style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
              overflow: 'hidden'
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                      <th style={{ padding: '12px 18px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>STUDENT</th>
                      <th style={{ padding: '12px 18px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>CLASS</th>
                      <th style={{ padding: '12px 18px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>CONTACT</th>
                      <th style={{ padding: '12px 18px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>ADMISSION</th>
                      <th style={{ padding: '12px 18px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>STATUS</th>
                      <th style={{ padding: '12px 18px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>ACTIONS</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      Array.from({ length: 5 }).map((_, idx) => (
                        <tr key={`skel-${idx}`} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '14px 18px' }}><div style={{ width: '140px', height: '14px', background: '#e2e8f0', borderRadius: '4px' }} /></td>
                          <td style={{ padding: '14px 18px' }}><div style={{ width: '80px', height: '14px', background: '#e2e8f0', borderRadius: '4px' }} /></td>
                          <td style={{ padding: '14px 18px' }}><div style={{ width: '110px', height: '14px', background: '#e2e8f0', borderRadius: '4px' }} /></td>
                          <td style={{ padding: '14px 18px' }}><div style={{ width: '70px', height: '14px', background: '#e2e8f0', borderRadius: '4px' }} /></td>
                          <td style={{ padding: '14px 18px' }}><div style={{ width: '60px', height: '14px', background: '#e2e8f0', borderRadius: '4px' }} /></td>
                          <td style={{ padding: '14px 18px', textAlign: 'right' }}><div style={{ width: '20px', height: '14px', background: '#e2e8f0', borderRadius: '4px', marginLeft: 'auto' }} /></td>
                        </tr>
                      ))
                    ) : filteredStudents.length > 0 ? (
                      filteredStudents.slice((studentPage - 1) * studentPageSize, studentPage * studentPageSize).map((student) => {
                        const isActive = student.active === true || student.status === 'Active';
                        const initial = (student.name || 'S').charAt(0).toUpperCase();

                        return (
                          <tr 
                            key={student.studentId || student.id}
                            style={{
                              borderBottom: '1px solid #f1f5f9',
                              transition: 'background 0.15s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
                          >
                            {/* STUDENT Column */}
                            <td style={{ padding: '12px 18px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <StudentPhotoAvatar student={student} size={38} />
                                <div>
                                  <div 
                                    onClick={() => setSelectedStudentForDrawer(student)}
                                    style={{ 
                                      fontWeight: '600', 
                                      fontSize: '14px', 
                                      color: '#0f172a', 
                                      cursor: 'pointer',
                                      textDecoration: 'none'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.color = '#2563eb'}
                                    onMouseLeave={(e) => e.currentTarget.style.color = '#0f172a'}
                                  >
                                    {student.name}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '1px', fontFamily: 'monospace' }}>
                                    {student.studentId || 'N/A'}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* CLASS Column */}
                            <td style={{ padding: '12px 18px' }}>
                              <div style={{ fontWeight: '500', fontSize: '13px', color: '#1e293b' }}>
                                {student.className || 'N/A'}
                              </div>
                              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '1px' }}>
                                {student.section ? `Section ${student.section}` : 'General'}
                              </div>
                            </td>

                            {/* CONTACT Column */}
                            <td style={{ padding: '12px 18px' }}>
                              <div style={{ fontSize: '13px', color: '#1e293b' }}>
                                {student.phone || student.parentPhone || student.fatherPhone || student.motherPhone || student.guardianPhone || student.emergencyContactPhone || 'N/A'}
                              </div>
                              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '1px' }}>
                                {getDisplayStudentEmail(student.email) || 'N/A'}
                              </div>
                            </td>

                            {/* ADMISSION Column */}
                            <td style={{ padding: '12px 18px' }}>
                              <div style={{ fontSize: '13px', color: '#1e293b' }}>
                                {student.admissionYear || '2026-27'}
                              </div>
                              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '1px' }}>
                                {student.applicationNo ? `App: ${student.applicationNo}` : ''}
                              </div>
                            </td>

                            {/* STATUS Column */}
                            <td style={{ padding: '12px 18px' }}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '3px 10px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                background: isActive ? '#dcfce7' : '#fee2e2',
                                color: isActive ? '#15803d' : '#b91c1c'
                              }}>
                                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: isActive ? '#16a34a' : '#dc2626' }} />
                                {isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>

                            {/* ACTIONS Column */}
                            <td style={{ padding: '12px 18px', textAlign: 'right', position: 'relative' }}>
                              <div className="student-action-dropdown-container" style={{ display: 'inline-block', position: 'relative' }}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveStudentRowMenu(activeStudentRowMenu === student.studentId ? null : student.studentId);
                                  }}
                                  style={{
                                    background: '#ffffff',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '6px',
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    color: '#475569'
                                  }}
                                  title="Actions"
                                >
                                  ⋮
                                </button>

                                {/* Action Menu Dropdown */}
                                {activeStudentRowMenu === student.studentId && (
                                  <div style={{
                                    position: 'absolute',
                                    right: 0,
                                    top: '38px',
                                    width: '170px',
                                    background: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                    zIndex: 100,
                                    padding: '4px 0',
                                    textAlign: 'left'
                                  }}>
                                    <button
                                      onClick={() => {
                                        setActiveStudentRowMenu(null);
                                        setSelectedStudentForDrawer(student);
                                      }}
                                      style={{ width: '100%', padding: '8px 14px', border: 'none', background: 'none', textAlign: 'left', fontSize: '13px', color: '#334155', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                                    >
                                      👁️ View Profile
                                    </button>

                                    <button
                                      onClick={() => {
                                        setActiveStudentRowMenu(null);
                                        setEditingItem(student);
                                        setModalStep(1);
                                        setStudentFormData({
                                          studentId: student.studentId || '',
                                          applicationNo: student.applicationNo || '',
                                          satsNo: student.satsNo || '',
                                          penNo: student.penNo || '',
                                          name: student.name || '',
                                          rollNumber: student.rollNumber || student.rollNo || '',
                                          photo: student.photo || student.photoUrl || '',
                                          dob: student.dob || '',
                                          dobInWords: student.dobInWords || '',
                                          placeOfBirthVillage: student.placeOfBirthVillage || '',
                                          placeOfBirthTaluk: student.placeOfBirthTaluk || '',
                                          placeOfBirthDistrict: student.placeOfBirthDistrict || '',
                                          gender: student.gender || '',
                                          bloodGroup: student.bloodGroup || '',
                                          nationality: student.nationality || 'Indian',
                                          religion: student.religion || '',
                                          caste: student.caste || '',
                                          category: student.category || '',
                                          motherTongue: student.motherTongue || '',
                                          languagesSpoken: student.languagesSpoken || '',
                                          phone: student.phone || '',
                                          email: student.email || '',
                                          class: student.className || '',
                                          section: student.section || '',
                                          admissionYear: student.admissionYear ? String(student.admissionYear) : new Date().getFullYear().toString(),
                                          admissionDate: student.admissionDate || new Date().toISOString().split('T')[0],
                                          aadhaarNumber: student.aadhaarNumber || '',
                                          address: student.address || '',
                                          fatherName: student.fatherName || '',
                                          fatherLivingStatus: student.fatherLivingStatus || 'Living',
                                          fatherEducation: student.fatherEducation || '',
                                          fatherOccupation: student.fatherOccupation || '',
                                          motherName: student.motherName || '',
                                          motherLivingStatus: student.motherLivingStatus || 'Living',
                                          motherEducation: student.motherEducation || '',
                                          motherOccupation: student.motherOccupation || '',
                                          parentsAnnualIncome: student.parentsAnnualIncome != null ? String(student.parentsAnnualIncome) : '',
                                          numberOfDependents: student.numberOfDependents != null ? String(student.numberOfDependents) : '',
                                          elderBrothersCount: student.elderBrothersCount != null ? String(student.elderBrothersCount) : '0',
                                          youngerBrothersCount: student.youngerBrothersCount != null ? String(student.youngerBrothersCount) : '0',
                                          elderSistersCount: student.elderSistersCount != null ? String(student.elderSistersCount) : '0',
                                          youngerSistersCount: student.youngerSistersCount != null ? String(student.youngerSistersCount) : '0',
                                          accommodationType: student.accommodationType || 'Parents',
                                          guardianName: student.guardianName || '',
                                          guardianPhone: student.guardianPhone || '',
                                          guardianAddress: student.guardianAddress || '',
                                          parentName: student.parentName || student.fatherName || '',
                                          parentPhone: student.parentPhone || student.fatherPhone || student.phone || '',
                                          emergencyPhone: student.emergencyPhone || '',
                                          previousSchoolName: student.previousSchoolName || student.previousSchoolCollege || '',
                                          previousSchoolStandard: student.previousSchoolStandard || '',
                                          mediumOfInstruction: student.mediumOfInstruction || '',
                                          previousSchoolLeavingDate: student.previousSchoolLeavingDate || '',
                                          previousSchoolLeavingReason: student.previousSchoolLeavingReason || '',
                                          languageStudied: student.languageStudied || '',
                                          scholarshipDetails: student.scholarshipDetails || '',
                                          tcProduced: student.tcProduced === true ? 'Yes' : 'No',
                                          tcNumber: student.tcNumber || '',
                                          tcDate: student.tcDate || '',
                                          vaccinationStatus: student.vaccinationStatus || 'Vaccinated',
                                          hostelOpted: student.hostelOpted === true || student.hostelOpted === 'Yes' ? 'Yes' : 'No',
                                          transportOpted: student.transportOpted === true || student.transportOpted === 'Yes' ? 'Yes' : 'No',
                                          password: '',
                                          status: student.status || (student.active === false ? 'Inactive' : 'Active')
                                        });
                                        setShowModal(true);
                                      }}
                                      style={{ width: '100%', padding: '8px 14px', border: 'none', background: 'none', textAlign: 'left', fontSize: '13px', color: '#334155', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                                    >
                                      ✏️ Edit Student
                                    </button>

                                    <button
                                      onClick={() => {
                                        setActiveStudentRowMenu(null);
                                        setResetPasswordStudent(student);
                                      }}
                                      style={{ width: '100%', padding: '8px 14px', border: 'none', background: 'none', textAlign: 'left', fontSize: '13px', color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600' }}
                                    >
                                      🔑 Reset Password
                                    </button>

                                    <button
                                      onClick={() => {
                                        setActiveStudentRowMenu(null);
                                        setPromoteStudent(student);
                                        setPromoteToClass('');
                                        setShowPromoteModal(true);
                                      }}
                                      style={{ width: '100%', padding: '8px 14px', border: 'none', background: 'none', textAlign: 'left', fontSize: '13px', color: '#334155', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                                    >
                                      🎓 Promote Student
                                    </button>

                                    <button
                                      onClick={() => {
                                        setActiveStudentRowMenu(null);
                                        setActiveTab('id-cards');
                                      }}
                                      style={{ width: '100%', padding: '8px 14px', border: 'none', background: 'none', textAlign: 'left', fontSize: '13px', color: '#334155', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                                    >
                                      🎴 ID Card
                                    </button>

                                    <button
                                      onClick={() => {
                                        setActiveStudentRowMenu(null);
                                        setActiveTab('fees');
                                      }}
                                      style={{ width: '100%', padding: '8px 14px', border: 'none', background: 'none', textAlign: 'left', fontSize: '13px', color: '#334155', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                                    >
                                      💰 Fee Details
                                    </button>

                                    <div style={{ borderTop: '1px solid #f1f5f9', margin: '4px 0' }} />

                                    <button
                                      onClick={async () => {
                                        setActiveStudentRowMenu(null);
                                        if (window.confirm(`Delete student ${student.name}?`)) {
                                          try {
                                            setLoading(true);
                                            await studentService.deleteStudent(student.studentId);
                                            setStudents(prev => prev.filter(s => s.studentId !== student.studentId));
                                            showNotification('success', 'Deleted', 'Student deleted successfully!');
                                            addRecentActivity('🗑️', `Student deleted: ${student.name}`, `Student ID: ${student.studentId}`);
                                          } catch (error) {
                                            console.error('Error deleting student:', error);
                                            showNotification('error', 'Error', 'Failed to delete student. Please try again.');
                                          } finally {
                                            setLoading(false);
                                          }
                                        }
                                      }}
                                      style={{ width: '100%', padding: '8px 14px', border: 'none', background: 'none', textAlign: 'left', fontSize: '13px', color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '500' }}
                                    >
                                      🗑️ Delete Student
                                    </button>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      /* Empty State */
                      <tr>
                        <td colSpan="6" style={{ padding: '40px 20px', textAlign: 'center' }}>
                          <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔍</div>
                          <div style={{ fontSize: '15px', fontWeight: '600', color: '#334155' }}>
                            {searchTerm || filters.academicYear || filters.class || filters.section 
                              ? 'No students match your search or filters' 
                              : 'No student records found'}
                          </div>
                          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px', marginBottom: '16px' }}>
                            {searchTerm || filters.academicYear || filters.class || filters.section 
                              ? 'Try clearing your filters or adjusting your search keywords.' 
                              : 'Get started by adding a new student admission.'}
                          </div>
                          {searchTerm || filters.academicYear || filters.class || filters.section ? (
                            <button
                              onClick={clearAllFilters}
                              style={{ padding: '8px 16px', background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                            >
                              Clear Filters
                            </button>
                          ) : (
                            <button
                              onClick={() => openModal('add')}
                              style={{ padding: '8px 16px', background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                            >
                              + Add Student
                            </button>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* 5. Pagination Footer */}
              {filteredStudents.length > 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 18px',
                  borderTop: '1px solid #e2e8f0',
                  background: '#f8fafc',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>
                    Showing <strong>{(studentPage - 1) * studentPageSize + 1}</strong>–<strong>{Math.min(studentPage * studentPageSize, filteredStudents.length)}</strong> of <strong>{filteredStudents.length}</strong> students
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#64748b' }}>
                      <span>Rows per page:</span>
                      <select
                        value={studentPageSize}
                        onChange={(e) => {
                          setStudentPageSize(Number(e.target.value));
                          setStudentPage(1);
                        }}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '13px',
                          color: '#334155',
                          background: '#ffffff',
                          cursor: 'pointer'
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <button
                        onClick={() => setStudentPage(p => Math.max(1, p - 1))}
                        disabled={studentPage === 1}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          background: '#ffffff',
                          color: studentPage === 1 ? '#cbd5e1' : '#334155',
                          cursor: studentPage === 1 ? 'not-allowed' : 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        ‹ Previous
                      </button>
                      
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#334155', padding: '0 8px' }}>
                        Page {studentPage} of {Math.max(1, Math.ceil(filteredStudents.length / studentPageSize))}
                      </span>

                      <button
                        onClick={() => setStudentPage(p => Math.min(Math.ceil(filteredStudents.length / studentPageSize), p + 1))}
                        disabled={studentPage >= Math.ceil(filteredStudents.length / studentPageSize)}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          background: '#ffffff',
                          color: studentPage >= Math.ceil(filteredStudents.length / studentPageSize) ? '#cbd5e1' : '#334155',
                          cursor: studentPage >= Math.ceil(filteredStudents.length / studentPageSize) ? 'not-allowed' : 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        Next ›
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 6. Quick Student Detail Modal */}
            {selectedStudentForDrawer && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(15, 23, 42, 0.6)',
                backdropFilter: 'blur(2px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                padding: '20px'
              }}
              onClick={() => {
                setSelectedStudentForDrawer(null);
                setDrawerActiveTab('overview');
              }}
              >
                <div style={{
                  background: '#ffffff',
                  borderRadius: '16px',
                  maxWidth: '740px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                  padding: '28px',
                  position: 'relative'
                }}
                onClick={(e) => e.stopPropagation()}
                >
                  {/* Top Close Button */}
                  <button
                    onClick={() => {
                      setSelectedStudentForDrawer(null);
                      setDrawerActiveTab('overview');
                    }}
                    style={{
                      position: 'absolute',
                      top: '20px',
                      right: '20px',
                      background: '#f1f5f9',
                      border: 'none',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      fontSize: '16px',
                      color: '#64748b',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.15s ease'
                    }}
                    title="Close"
                  >
                    ✕
                  </button>

                  {/* 1. Header Matching Exact Recommended Specification */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                    marginBottom: '20px',
                    paddingBottom: '20px',
                    borderBottom: '1px solid #e2e8f0',
                    flexWrap: 'wrap'
                  }}>
                    {/* Real Photo Avatar with Click-to-Zoom */}
                    <div
                      onClick={() => {
                        const pUrl = resolveStudentPhotoUrl(selectedStudentForDrawer);
                        setPreviewPhotoModal({
                          isOpen: true,
                          imageUrl: pUrl || '',
                          title: selectedStudentForDrawer.name || 'Student Profile Photo',
                          subtitle: `${selectedStudentForDrawer.studentId || ''} • Class ${selectedStudentForDrawer.className || ''} ${selectedStudentForDrawer.section ? `(Sec ${selectedStudentForDrawer.section})` : ''} • Academic Year ${formatAcademicYear(selectedStudentForDrawer.admissionYear)}`,
                          tag: (selectedStudentForDrawer.active === true || selectedStudentForDrawer.status === 'Active') ? 'Active' : 'Inactive',
                          initials: (selectedStudentForDrawer.name || 'S').trim().charAt(0).toUpperCase()
                        });
                      }}
                      title="Click to view full size photo"
                      style={{ position: 'relative', cursor: 'pointer' }}
                    >
                      <StudentPhotoAvatar student={selectedStudentForDrawer} size={84} />
                      <div style={{
                        position: 'absolute',
                        bottom: '2px',
                        right: '2px',
                        background: '#0f2942',
                        color: '#ffffff',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        border: '2px solid #ffffff',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.25)'
                      }}>
                        🔍
                      </div>
                    </div>

                    <div style={{ flex: 1, minWidth: '220px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                        <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', margin: 0, letterSpacing: '-0.3px', wordBreak: 'break-word' }}>
                          {selectedStudentForDrawer.name}
                        </h2>

                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: (selectedStudentForDrawer.active === true || selectedStudentForDrawer.status === 'Active') ? '#dcfce7' : '#fee2e2',
                          color: (selectedStudentForDrawer.active === true || selectedStudentForDrawer.status === 'Active') ? '#15803d' : '#b91c1c'
                        }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: (selectedStudentForDrawer.active === true || selectedStudentForDrawer.status === 'Active') ? '#16a34a' : '#dc2626' }} />
                          {(selectedStudentForDrawer.active === true || selectedStudentForDrawer.status === 'Active') ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px', fontFamily: 'monospace', fontWeight: '500' }}>
                        {selectedStudentForDrawer.studentId || 'N/A'} {selectedStudentForDrawer.applicationNo ? `• APP: ${selectedStudentForDrawer.applicationNo}` : ''}
                      </div>

                      <div style={{ fontSize: '13px', color: '#334155', marginTop: '6px', fontWeight: '600' }}>
                        Class {displayValue(selectedStudentForDrawer.className)} • Section {displayValue(selectedStudentForDrawer.section)} • Academic Year {formatAcademicYear(selectedStudentForDrawer.admissionYear)}
                      </div>
                    </div>
                  </div>

                  {/* 2. Navigation Tabs */}
                  <div style={{
                    display: 'flex',
                    gap: '4px',
                    background: '#f1f5f9',
                    padding: '4px',
                    borderRadius: '10px',
                    marginBottom: '20px'
                  }}>
                    {[
                      { id: 'overview', label: '📊 Overview' },
                      { id: 'family', label: '👨‍👩‍👧 Family' },
                      { id: 'academic', label: '🎓 Academic' },
                      { id: 'documents', label: '📑 Documents' }
                    ].map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setDrawerActiveTab(tab.id)}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: drawerActiveTab === tab.id ? '600' : '500',
                          background: drawerActiveTab === tab.id ? '#ffffff' : 'transparent',
                          color: drawerActiveTab === tab.id ? '#2563eb' : '#64748b',
                          boxShadow: drawerActiveTab === tab.id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* 3. Tab Contents */}
                  {drawerActiveTab === 'overview' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '18px' }}>
                      {/* ACADEMIC */}
                      <div style={{ background: '#f8fafc', padding: '16px 18px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                          🎓 ACADEMIC
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          <div>
                            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>Roll No</div>
                            <div style={{ fontSize: '13px', color: '#0f172a', fontWeight: '600', marginTop: '2px' }}>{displayValue(selectedStudentForDrawer.rollNumber || selectedStudentForDrawer.rollNo)}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>Admission Date</div>
                            <div style={{ fontSize: '13px', color: '#0f172a', fontWeight: '600', marginTop: '2px' }}>{formatDisplayDate(selectedStudentForDrawer.admissionDate || selectedStudentForDrawer.createdAt)}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>SATS No</div>
                            <div style={{ fontSize: '13px', color: '#0f172a', fontWeight: '600', marginTop: '2px' }}>{displayValue(selectedStudentForDrawer.satsNo)}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>PEN No</div>
                            <div style={{ fontSize: '13px', color: '#0f172a', fontWeight: '600', marginTop: '2px' }}>{displayValue(selectedStudentForDrawer.penNo)}</div>
                          </div>
                        </div>
                      </div>

                      {/* PERSONAL */}
                      <div style={{ background: '#f8fafc', padding: '16px 18px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                          👤 PERSONAL
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          <div>
                            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>DOB</div>
                            <div style={{ fontSize: '13px', color: '#0f172a', fontWeight: '600', marginTop: '2px' }}>{formatDisplayDate(selectedStudentForDrawer.dob)}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>Gender</div>
                            <div style={{ fontSize: '13px', color: '#0f172a', fontWeight: '600', marginTop: '2px' }}>{displayValue(selectedStudentForDrawer.gender)}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>Blood Group</div>
                            <div style={{ fontSize: '13px', color: '#0f172a', fontWeight: '600', marginTop: '2px' }}>{displayValue(selectedStudentForDrawer.bloodGroup)}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>Nationality</div>
                            <div style={{ fontSize: '13px', color: '#0f172a', fontWeight: '600', marginTop: '2px' }}>{displayValue(selectedStudentForDrawer.nationality, 'Indian')}</div>
                          </div>
                        </div>
                      </div>

                      {/* CONTACT */}
                      <div style={{ background: '#f8fafc', padding: '16px 18px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                          📞 CONTACT
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <div>
                            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>Phone</div>
                            <div style={{ fontSize: '13px', color: '#0f172a', fontWeight: '600', marginTop: '2px' }}>{formatPhoneNumber(selectedStudentForDrawer.phone)}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>Email</div>
                            <div style={{ fontSize: '13px', color: '#0f172a', fontWeight: '600', marginTop: '2px', wordBreak: 'break-all' }}>{displayValue(selectedStudentForDrawer.email)}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>Address</div>
                            <div style={{ fontSize: '13px', color: '#334155', fontWeight: '500', marginTop: '2px', lineHeight: '1.4' }}>{displayValue(selectedStudentForDrawer.address)}</div>
                          </div>
                        </div>
                      </div>

                      {/* PARENT / GUARDIAN */}
                      <div style={{ background: '#f8fafc', padding: '16px 18px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                          👨‍👩‍👧 PARENT / GUARDIAN
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <div>
                            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>Father</div>
                            <div style={{ fontSize: '13px', color: '#0f172a', fontWeight: '600', marginTop: '2px' }}>{displayValue(selectedStudentForDrawer.fatherName || selectedStudentForDrawer.parentName)}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>Mother</div>
                            <div style={{ fontSize: '13px', color: '#0f172a', fontWeight: '600', marginTop: '2px' }}>{displayValue(selectedStudentForDrawer.motherName)}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>Parent Phone</div>
                            <div style={{ fontSize: '13px', color: '#0f172a', fontWeight: '600', marginTop: '2px' }}>{formatPhoneNumber(selectedStudentForDrawer.parentPhone || selectedStudentForDrawer.fatherPhone)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {drawerActiveTab === 'family' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '18px' }}>
                      <div style={{ background: '#f8fafc', padding: '16px 18px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', marginBottom: '12px' }}>👨 Father Information</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                          <div><strong>Name:</strong> {displayValue(selectedStudentForDrawer.fatherName || selectedStudentForDrawer.parentName)}</div>
                          <div><strong>Living Status:</strong> {displayValue(selectedStudentForDrawer.fatherLivingStatus)}</div>
                          <div><strong>Education:</strong> {displayValue(selectedStudentForDrawer.fatherEducation)}</div>
                          <div><strong>Occupation:</strong> {displayValue(selectedStudentForDrawer.fatherOccupation)}</div>
                        </div>
                      </div>

                      <div style={{ background: '#f8fafc', padding: '16px 18px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', marginBottom: '12px' }}>👩 Mother Information</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                          <div><strong>Name:</strong> {displayValue(selectedStudentForDrawer.motherName)}</div>
                          <div><strong>Living Status:</strong> {displayValue(selectedStudentForDrawer.motherLivingStatus)}</div>
                          <div><strong>Education:</strong> {displayValue(selectedStudentForDrawer.motherEducation)}</div>
                          <div><strong>Occupation:</strong> {displayValue(selectedStudentForDrawer.motherOccupation)}</div>
                        </div>
                      </div>

                      <div style={{ background: '#f8fafc', padding: '16px 18px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', marginBottom: '12px' }}>🏡 Household & Income</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                          <div><strong>Annual Income:</strong> {selectedStudentForDrawer.parentsAnnualIncome ? `₹${selectedStudentForDrawer.parentsAnnualIncome}` : 'Not provided'}</div>
                          <div><strong>Dependents:</strong> {displayValue(selectedStudentForDrawer.numberOfDependents, '0')}</div>
                          <div><strong>Brothers:</strong> {selectedStudentForDrawer.elderBrothersCount || '0'} Elder / {selectedStudentForDrawer.youngerBrothersCount || '0'} Younger</div>
                          <div><strong>Sisters:</strong> {selectedStudentForDrawer.elderSistersCount || '0'} Elder / {selectedStudentForDrawer.youngerSistersCount || '0'} Younger</div>
                        </div>
                      </div>

                      <div style={{ background: '#f8fafc', padding: '16px 18px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', marginBottom: '12px' }}>🛡️ Guardian & Accommodation</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                          <div><strong>Guardian Name:</strong> {displayValue(selectedStudentForDrawer.guardianName)}</div>
                          <div><strong>Guardian Phone:</strong> {formatPhoneNumber(selectedStudentForDrawer.guardianPhone)}</div>
                          <div><strong>Guardian Address:</strong> {displayValue(selectedStudentForDrawer.guardianAddress)}</div>
                          <div><strong>Accommodation:</strong> {displayValue(selectedStudentForDrawer.accommodationType, 'Parents')}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {drawerActiveTab === 'academic' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '18px' }}>
                      <div style={{ background: '#f8fafc', padding: '16px 18px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', marginBottom: '12px' }}>🏫 Previous Education</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                          <div><strong>Previous School:</strong> {displayValue(selectedStudentForDrawer.previousSchoolName || selectedStudentForDrawer.previousSchoolCollege)}</div>
                          <div><strong>Standard Studied:</strong> {displayValue(selectedStudentForDrawer.previousSchoolStandard)}</div>
                          <div><strong>Leaving Reason:</strong> {displayValue(selectedStudentForDrawer.previousSchoolLeavingReason)}</div>
                        </div>
                      </div>

                      <div style={{ background: '#f8fafc', padding: '16px 18px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', marginBottom: '12px' }}>🗣️ Medium & Languages</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                          <div><strong>Medium of Instruction:</strong> {displayValue(selectedStudentForDrawer.mediumOfInstruction)}</div>
                          <div><strong>Languages Studied:</strong> {displayValue(selectedStudentForDrawer.languageStudied)}</div>
                          <div><strong>Languages Spoken:</strong> {displayValue(selectedStudentForDrawer.languagesSpoken)}</div>
                          <div><strong>Mother Tongue:</strong> {displayValue(selectedStudentForDrawer.motherTongue)}</div>
                        </div>
                      </div>

                      <div style={{ background: '#f8fafc', padding: '16px 18px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', marginBottom: '12px' }}>🚌 School Facilities & Welfare</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                          <div><strong>Hostel Opted:</strong> {displayValue(selectedStudentForDrawer.hostelOpted, 'No')}</div>
                          <div><strong>Transport Opted:</strong> {displayValue(selectedStudentForDrawer.transportOpted, 'No')}</div>
                          <div><strong>Scholarship:</strong> {displayValue(selectedStudentForDrawer.scholarshipDetails)}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {drawerActiveTab === 'documents' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '18px' }}>
                      <div style={{ background: '#f8fafc', padding: '16px 18px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', marginBottom: '12px' }}>📜 Official Identifiers</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                          <div><strong>SATS Number:</strong> {displayValue(selectedStudentForDrawer.satsNo)}</div>
                          <div><strong>PEN Number:</strong> {displayValue(selectedStudentForDrawer.penNo)}</div>
                          <div><strong>Aadhaar Number:</strong> {formatMaskedAadhaar(selectedStudentForDrawer.aadhaarNumber)}</div>
                        </div>
                      </div>

                      <div style={{ background: '#f8fafc', padding: '16px 18px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', marginBottom: '12px' }}>📋 Transfer Certificate & Health</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                          <div><strong>TC Produced:</strong> {displayValue(selectedStudentForDrawer.tcProduced, 'No')}</div>
                          <div><strong>TC Number:</strong> {displayValue(selectedStudentForDrawer.tcNumber)}</div>
                          <div><strong>TC Date:</strong> {formatDisplayDate(selectedStudentForDrawer.tcDate)}</div>
                          <div><strong>Vaccination Status:</strong> {displayValue(selectedStudentForDrawer.vaccinationStatus, 'Vaccinated')}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Footer Actions Matching Exact Recommended Specification */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', borderTop: '1px solid #e2e8f0', paddingTop: '18px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => {
                        setSelectedStudentForDrawer(null);
                        setDrawerActiveTab('overview');
                      }}
                      style={{
                        padding: '9px 18px',
                        background: '#ffffff',
                        color: '#334155',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Close
                    </button>

                    <button
                      onClick={() => setDrawerActiveTab(drawerActiveTab === 'overview' ? 'family' : 'overview')}
                      style={{
                        padding: '9px 18px',
                        background: '#f1f5f9',
                        color: '#2563eb',
                        border: '1px solid #bfdbfe',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      👁️ {drawerActiveTab === 'overview' ? 'View Full Profile' : 'Quick Overview'}
                    </button>

                    <button
                      onClick={() => {
                        const st = selectedStudentForDrawer;
                        setSelectedStudentForDrawer(null);
                        setDrawerActiveTab('overview');
                        setEditingItem(st);
                        setModalStep(1);
                        setStudentFormData({
                          studentId: st.studentId || '',
                          applicationNo: st.applicationNo || '',
                          satsNo: st.satsNo || '',
                          penNo: st.penNo || '',
                          name: st.name || '',
                          rollNumber: st.rollNumber || st.rollNo || '',
                          photo: st.photo || st.photoUrl || '',
                          dob: st.dob || '',
                          dobInWords: st.dobInWords || '',
                          placeOfBirthVillage: st.placeOfBirthVillage || '',
                          placeOfBirthTaluk: st.placeOfBirthTaluk || '',
                          placeOfBirthDistrict: st.placeOfBirthDistrict || '',
                          gender: st.gender || '',
                          bloodGroup: st.bloodGroup || '',
                          nationality: st.nationality || 'Indian',
                          religion: st.religion || '',
                          caste: st.caste || '',
                          category: st.category || '',
                          motherTongue: st.motherTongue || '',
                          languagesSpoken: st.languagesSpoken || '',
                          phone: st.phone || '',
                          email: st.email || '',
                          class: st.className || '',
                          section: st.section || '',
                          admissionYear: st.admissionYear ? String(st.admissionYear) : new Date().getFullYear().toString(),
                          admissionDate: st.admissionDate || new Date().toISOString().split('T')[0],
                          aadhaarNumber: st.aadhaarNumber || '',
                          address: st.address || '',
                          fatherName: st.fatherName || '',
                          fatherLivingStatus: st.fatherLivingStatus || 'Living',
                          fatherEducation: st.fatherEducation || '',
                          fatherOccupation: st.fatherOccupation || '',
                          motherName: st.motherName || '',
                          motherLivingStatus: st.motherLivingStatus || 'Living',
                          motherEducation: st.motherEducation || '',
                          motherOccupation: st.motherOccupation || '',
                          parentsAnnualIncome: st.parentsAnnualIncome != null ? String(st.parentsAnnualIncome) : '',
                          numberOfDependents: st.numberOfDependents != null ? String(st.numberOfDependents) : '',
                          elderBrothersCount: st.elderBrothersCount != null ? String(st.elderBrothersCount) : '0',
                          youngerBrothersCount: st.youngerBrothersCount != null ? String(st.youngerBrothersCount) : '0',
                          elderSistersCount: st.elderSistersCount != null ? String(st.elderSistersCount) : '0',
                          youngerSistersCount: st.youngerSistersCount != null ? String(st.youngerSistersCount) : '0',
                          accommodationType: st.accommodationType || 'Parents',
                          guardianName: st.guardianName || '',
                          guardianPhone: st.guardianPhone || '',
                          guardianAddress: st.guardianAddress || '',
                          parentName: st.parentName || st.fatherName || '',
                          parentPhone: st.parentPhone || st.fatherPhone || st.phone || '',
                          emergencyPhone: st.emergencyPhone || '',
                          previousSchoolName: st.previousSchoolName || st.previousSchoolCollege || '',
                          previousSchoolStandard: st.previousSchoolStandard || '',
                          mediumOfInstruction: st.mediumOfInstruction || '',
                          previousSchoolLeavingDate: st.previousSchoolLeavingDate || '',
                          previousSchoolLeavingReason: st.previousSchoolLeavingReason || '',
                          languageStudied: st.languageStudied || '',
                          scholarshipDetails: st.scholarshipDetails || '',
                          tcProduced: st.tcProduced === true ? 'Yes' : 'No',
                          tcNumber: st.tcNumber || '',
                          tcDate: st.tcDate || '',
                          vaccinationStatus: st.vaccinationStatus || 'Vaccinated',
                          hostelOpted: st.hostelOpted === true || st.hostelOpted === 'Yes' ? 'Yes' : 'No',
                          transportOpted: st.transportOpted === true || st.transportOpted === 'Yes' ? 'Yes' : 'No',
                          password: '',
                          status: st.status || (st.active === false ? 'Inactive' : 'Active')
                        });
                        setShowModal(true);
                      }}
                      style={{
                        padding: '9px 18px',
                        background: '#2563eb',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        boxShadow: '0 1px 3px rgba(37,99,235,0.2)'
                      }}
                    >
                      ✏️ Edit Student
                    </button>
                    <button
                      type="button"
                      onClick={() => setResetPasswordStudent(selectedStudentForDrawer)}
                      style={{
                        padding: '9px 18px',
                        background: '#dc2626',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        boxShadow: '0 1px 3px rgba(220,38,38,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      🔑 Reset Password
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'teachers' && (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <TeacherDirectory
              teachers={teachers}
              loading={loading}
              orgName={orgData?.orgName || 'School ERP'}
              orgId={orgData?.orgId || localStorage.getItem('orgId') || ''}
              academicYear={orgData?.academicYear || localStorage.getItem('academicYear') || '2026-2027'}
              token={localStorage.getItem('authToken') || localStorage.getItem('token') || ''}
              onAddTeacher={() => {
                setEditingTeacher(null);
                setTeacherFormData({
                  teacherId: '',
                  teacherName: '',
                  dob: '',
                  experience: '',
                  phoneNumber: '',
                  address: '',
                  designation: '',
                  gmail: '',
                  qualifications: '',
                  degree: '',
                  specialization: '',
                  password: '',
                  status: 'ACTIVE',
                  photo: '',
                  photoUrl: ''
                });
                setShowTeacherModal(true);
              }}
              onEditTeacher={(teacher) => {
                setEditingTeacher(teacher);
                setTeacherFormData({
                  teacherId: teacher.teacherId || '',
                  teacherName: teacher.teacherName,
                  dob: teacher.dob || '',
                  experience: teacher.experience || '',
                  phoneNumber: teacher.phoneNumber || '',
                  address: teacher.address || '',
                  designation: teacher.designation || '',
                  gmail: teacher.gmail || '',
                  qualifications: teacher.qualifications || '',
                  degree: teacher.degree || '',
                  specialization: teacher.specialization || '',
                  password: '',
                  status: teacher.status || 'ACTIVE',
                  photo: teacher.photo || teacher.photoUrl || '',
                  photoUrl: teacher.photoUrl || teacher.photo || '',
                  subjects: teacher.subjects || []
                });
                setShowTeacherModal(true);
              }}
              onToggleStatus={async (teacher) => {
                try {
                  const newStatus = (teacher.status || 'ACTIVE').toUpperCase() === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
                  setLoading(true);
                  const updated = await teacherService.updateTeacher(teacher.teacherId, {
                    ...teacher,
                    status: newStatus
                  });
                  setTeachers(prev => prev.map(t => t.teacherId === teacher.teacherId ? (updated || { ...teacher, status: newStatus }) : t));
                  showNotification('success', 'Status Updated', `Teacher status changed to ${newStatus}`);
                  if (selectedTeacherForDrawer && selectedTeacherForDrawer.teacherId === teacher.teacherId) {
                    setSelectedTeacherForDrawer(prev => ({ ...prev, status: newStatus }));
                  }
                } catch (err) {
                  console.error('Failed to update status:', err);
                  showNotification('error', 'Error', 'Failed to change teacher status.');
                } finally {
                  setLoading(false);
                }
              }}
              onDeleteTeacher={async (teacher) => {
                const confirmed = window.confirm(
                  `⚠️ RESTRICTED ACTION: Deleting a teacher may permanently impact historical attendance, payroll, exam, and timetable records.\n\nAre you sure you want to permanently delete teacher ${teacher.teacherName} (${teacher.teacherId})?\n\nTip: Consider using 'Deactivate' instead to preserve historical records.`
                );
                if (confirmed) {
                  try {
                    setLoading(true);
                    await teacherService.deleteTeacher(teacher.teacherId);
                    setTeachers(prev => prev.filter(t => t.teacherId !== teacher.teacherId));
                    showNotification('success', 'Deleted', 'Teacher deleted successfully!');
                    addRecentActivity('🗑️', `Teacher deleted: ${teacher.teacherName}`, `Teacher ID: ${teacher.teacherId}`);
                    if (selectedTeacherForDrawer?.teacherId === teacher.teacherId) {
                      setSelectedTeacherForDrawer(null);
                    }
                  } catch (error) {
                    console.error('Error deleting teacher:', error);
                    showNotification('error', 'Error', 'Failed to delete teacher. Please try again.');
                  } finally {
                    setLoading(false);
                  }
                }
              }}
              onExportData={() => openDownloadModal('teachers')}
              onRefresh={async () => {
                await loadTeachers();
                showNotification('success', 'Refreshed', 'Teacher data refreshed successfully');
              }}
              onSelectTeacher={(teacher) => setSelectedTeacherForDrawer(teacher)}
            />

            {/* Slide-out Teacher Profile Drawer */}
            {selectedTeacherForDrawer && (
              <TeacherProfileDrawer
                teacher={selectedTeacherForDrawer}
                onClose={() => setSelectedTeacherForDrawer(null)}
                onEdit={(teacher) => {
                  setEditingTeacher(teacher);
                  setTeacherFormData({
                    teacherId: teacher.teacherId || '',
                    teacherName: teacher.teacherName,
                    dob: teacher.dob || '',
                    experience: teacher.experience || '',
                    phoneNumber: teacher.phoneNumber || '',
                    address: teacher.address || '',
                    designation: teacher.designation || '',
                    gmail: teacher.gmail || '',
                    qualifications: teacher.qualifications || '',
                    degree: teacher.degree || '',
                    specialization: teacher.specialization || '',
                    password: '',
                    status: teacher.status || 'ACTIVE',
                    subjects: teacher.subjects || []
                  });
                  setShowTeacherModal(true);
                }}
                onToggleStatus={async (teacher) => {
                  try {
                    const newStatus = (teacher.status || 'ACTIVE').toUpperCase() === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
                    setLoading(true);
                    const updated = await teacherService.updateTeacher(teacher.teacherId, {
                      ...teacher,
                      status: newStatus
                    });
                    setTeachers(prev => prev.map(t => t.teacherId === teacher.teacherId ? (updated || { ...teacher, status: newStatus }) : t));
                    showNotification('success', 'Status Updated', `Teacher status changed to ${newStatus}`);
                    setSelectedTeacherForDrawer(prev => ({ ...prev, status: newStatus }));
                  } catch (err) {
                    console.error('Failed to update status:', err);
                    showNotification('error', 'Error', 'Failed to change teacher status.');
                  } finally {
                    setLoading(false);
                  }
                }}
              />
            )}
          </div>
        )}

        {activeTab === 'academics' && (
          <div>
            {(academicActiveTab !== 'subjects' && academicActiveTab !== 'classes' && academicActiveTab !== 'assignments' && academicActiveTab !== 'ptm') && (
              <div className="section-header">
                <h2 className="section-title">
                  {academicActiveTab === 'timetable' ? 'Class Timetable Management' : academicActiveTab === 'assignments' ? 'Homework & Assignments' : 'Academic Management'}
                </h2>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {academicActiveTab !== 'assignments' && (
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      if (academicActiveTab === 'exams') {
                        setEditingTest(null);
                        setTestForm({
                          title: '',
                          description: '',
                          className: '',
                          section: '',
                          subjectName: '',
                          testDate: new Date().toISOString().split('T')[0],
                          maxMarks: '',
                          instructions: '',
                          teacherId: ''
                        });
                        setShowTestModal(true);
                      } else if (academicActiveTab === 'timetable') {
                        setShowCreateTimetableModal(true);
                      } else {
                        openAcademicModal();
                      }
                    }}
                  >
                    Add New {academicActiveTab === 'classes' ? 'Class' : 
                            academicActiveTab === 'subjects' ? 'Subject' : 
                            academicActiveTab === 'exams' ? 'Test/Exam' : 'Timetable'}
                  </button>
                )}
                {academicActiveTab === 'assignments' && (
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      resetAssignmentForm();
                      setShowAssignmentModal(true);
                    }}
                  >
                    ➕ Assign Subject to Student
                  </button>
                )}
                <button
                  className="btn btn-success"
                  onClick={() => {
                    if (academicActiveTab === 'classes') {
                      openDownloadModal('classes');
                    } else if (academicActiveTab === 'subjects') {
                      openDownloadModal('subjects');
                    } else if (academicActiveTab === 'timetable') {
                      if (!currentTimetable) {
                        showNotification('info', 'Export', 'Please load a timetable first');
                      } else {
                        openDownloadModal('timetable');
                      }
                    } else {
                      showNotification('info', 'Export', 'Export feature not available for this section');
                    }
                  }}
                >
                  📥 Export Data
                </button>
              </div>
            </div>
            )}



            {/* Academic Structure / Classes Management */}
            {academicActiveTab === 'classes' && (
              <ClassManagement
                classes={classes}
                setClasses={setClasses}
                teachers={teachers}
                  onAddClass={() => {
                    setEditingAcademicItem(null);
                    setClassFormData({
                      className: '',
                      section: '',
                      maxCapacity: '',
                      classTeacherName: '',
                      status: 'ACTIVE'
                    });
                    setShowAcademicModal(true);
                  }}
                  onEdit={(cls) => {
                    setEditingAcademicItem(cls);
                    setClassFormData({
                      className: cls.className,
                      section: cls.section || '',
                      maxCapacity: cls.maxCapacity || '',
                      classTeacherName: cls.classTeacherName || '',
                      status: cls.active ? 'ACTIVE' : 'INACTIVE'
                    });
                    setShowAcademicModal(true);
                  }}
                  onDeleteClass={async (cls) => {
                    const targetId = cls.classId || cls.id;
                    const classNameStr = cls.className || 'Class';
                    if (window.confirm(`Are you sure you want to delete ${classNameStr}${cls.section ? ' (Section ' + cls.section + ')' : ''}?`)) {
                      try {
                        setLoading(true);
                        if (targetId) {
                          try {
                            const res = await classService.deleteClass(targetId);
                            if (res && res.success === false) {
                              showNotification('error', 'Error', res.message || res.error || 'Failed to delete class');
                              return;
                            }
                          } catch (apiErr) {
                            console.log('Backend deletion note:', apiErr.message);
                          }
                        }
                        setClasses(prev => prev.filter(c => (c.classId || c.id) !== targetId));
                        showNotification('success', 'Deleted', 'Class removed successfully');
                      } catch (err) {
                        console.error('Error deleting class:', err);
                        const msg = err.message || 'Failed to delete class. Please try again.';
                        showNotification('error', 'Error', msg);
                      } finally {
                        setLoading(false);
                      }
                    }
                  }}
                />
            )}

            {/* Subjects Management */}
            {academicActiveTab === 'subjects' && (
              <SubjectManagement 
                subjects={subjects} 
                setSubjects={setSubjects}
                teachers={teachers}
                classes={classes}
                orgData={orgData}
                institutionType={institutionType}
                terminology={terminology}
                onAddSubject={() => {
                  setEditingAcademicItem(null);
                  setSubjectFormErrors({});
                  setSubjectFormData({
                    subjectName: '',
                    subjectCode: '',
                    classLevel: '',
                    type: 'Core',
                    status: 'ACTIVE',
                    departmentName: '',
                    theoryMarks: 80,
                    practicalMarks: 20
                  });
                  setShowAcademicModal(true);
                }}
                onEdit={(subject) => {
                  setEditingAcademicItem(subject);
                  setSubjectFormErrors({});
                  setSubjectFormData({
                    subjectName: subject.subjectName,
                    subjectCode: subject.subjectCode,
                    classLevel: subject.classLevel || '',
                    type: subject.type || 'Core',
                    status: subject.status || 'ACTIVE',
                    departmentName: subject.departmentName || subject.department || '',
                    theoryMarks: subject.theoryMarks !== undefined ? subject.theoryMarks : 80,
                    practicalMarks: subject.practicalMarks !== undefined ? subject.practicalMarks : 20
                  });
                  setShowAcademicModal(true);
                }}
                onAssignTeacher={async (subject, teacherName) => {
                  try {
                    setLoading(true);
                    const updatedData = {
                      ...subject,
                      teacher: teacherName,
                      classTeacherName: teacherName,
                      assignedTeacher: teacherName
                    };
                    await subjectService.updateSubject(subject.subjectId, updatedData);
                    setSubjects(prev => prev.map(s => s.subjectId === subject.subjectId ? { ...s, ...updatedData } : s));
                    showNotification('success', 'Allocated', `Teacher ${teacherName} assigned to ${subject.subjectName}`);
                  } catch (err) {
                    console.error('Failed to assign teacher:', err);
                    showNotification('error', 'Error', 'Failed to allocate teacher');
                  } finally {
                    setLoading(false);
                  }
                }}
                onDeleteSubject={async (subject) => {
                  if (window.confirm(`Are you sure you want to delete subject ${subject.subjectName} (${subject.subjectCode})?`)) {
                    try {
                      setLoading(true);
                      await subjectService.deleteSubject(subject.subjectId);
                      setSubjects(prev => prev.filter(s => s.subjectId !== subject.subjectId));
                      showNotification('success', 'Deleted', 'Subject deleted successfully');
                    } catch (err) {
                      console.error('Error deleting subject:', err);
                      showNotification('error', 'Error', 'Failed to delete subject. Please try again.');
                    } finally {
                      setLoading(false);
                    }
                  }
                }}
              />
            )}

            {/* Timetable Management */}
            {academicActiveTab === 'timetable' && (
              <>
                <TimetableManager 
                  subjects={subjects} 
                  teachers={teachers} 
                  classes={classes} 
                  initialParams={timetableInitialParams}
                  onTimetableUpdate={(record, cls, sec) => {
                    setCurrentTimetable(record);
                    if (cls) setSelectedTimetableClass(cls);
                    if (sec) setSelectedTimetableSection(sec);
                  }}
                />

                <CreateClassTimetableModal
                  show={showCreateTimetableModal}
                  onClose={() => setShowCreateTimetableModal(false)}
                  onCreate={(params) => {
                    setTimetableInitialParams(params);
                    setShowCreateTimetableModal(false);
                  }}
                  availableClasses={classes.map(c => c.className || c.name).filter(Boolean)}
                  availableSections={['A', 'B', 'C', 'D']}
                />
              </>
            )}

            {academicActiveTab === 'exams' && (
              <div>
                {/* Filters Row */}
                <div className="academic-controls-row" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px', background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ flex: '1', minWidth: '150px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '6px' }}>Filter by Subject</label>
                    <select
                      className="academic-select"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                      value={examFilters.subject}
                      onChange={(e) => setExamFilters({ ...examFilters, subject: e.target.value })}
                    >
                      <option value="">All Subjects</option>
                      {subjects.map(s => (
                        <option key={s.subjectId || s.subjectCode} value={s.subjectName}>{s.subjectName}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: '1', minWidth: '150px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '6px' }}>Filter by Teacher</label>
                    <select
                      className="academic-select"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                      value={examFilters.teacher}
                      onChange={(e) => setExamFilters({ ...examFilters, teacher: e.target.value })}
                    >
                      <option value="">All Teachers</option>
                      {teachers.map(t => (
                        <option key={t.teacherId} value={t.teacherId}>{t.teacherName || t.name || t.teacherId}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: '1', minWidth: '120px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '6px' }}>Class</label>
                    <select
                      className="academic-select"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                      value={examFilters.className}
                      onChange={(e) => setExamFilters({ ...examFilters, className: e.target.value })}
                    >
                      <option value="">All Classes</option>
                      {Array.from(new Set(classes.map(c => c.className))).map(cls => (
                        <option key={cls} value={cls}>{cls}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: '1', minWidth: '100px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '6px' }}>Section</label>
                    <select
                      className="academic-select"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                      value={examFilters.section}
                      onChange={(e) => setExamFilters({ ...examFilters, section: e.target.value })}
                    >
                      <option value="">All Sections</option>
                      {['A', 'B', 'C', 'D', 'E'].map(sec => (
                        <option key={sec} value={sec}>{sec}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: '1', minWidth: '150px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '6px' }}>Filter by Date</label>
                    <input
                      type="date"
                      className="academic-search-input"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', height: '38px', boxSizing: 'border-box' }}
                      value={examFilters.date}
                      onChange={(e) => setExamFilters({ ...examFilters, date: e.target.value })}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setExamFilters({ subject: '', teacher: '', className: '', section: '', date: '' })}
                      style={{ padding: '8px 16px', borderRadius: '8px', height: '38px' }}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: selectedTest ? '1fr 1fr' : '1fr', gap: '20px', transition: 'all 0.3s ease' }}>
                  {/* Exams List Card */}
                  <div className="academic-table-card" style={{ padding: '16px' }}>
                    <h4 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>Exams & Tests List</h4>
                    <div className="academic-table-wrapper">
                      <table className="academic-table">
                        <thead>
                          <tr>
                            <th>Title</th>
                            <th>Subject</th>
                            <th>Class/Sec</th>
                            <th>Max Marks</th>
                            <th>Date</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tests
                            .filter(t => {
                              if (examFilters.subject && t.subjectName !== examFilters.subject) return false;
                              if (examFilters.teacher && t.teacherId !== examFilters.teacher) return false;
                              if (examFilters.className && t.className !== examFilters.className) return false;
                              if (examFilters.section && t.section !== examFilters.section) return false;
                              if (examFilters.date && t.testDate !== examFilters.date) return false;
                              if (academicSearchQuery) {
                                const q = academicSearchQuery.toLowerCase();
                                return (t.title || '').toLowerCase().includes(q) || (t.subjectName || '').toLowerCase().includes(q);
                              }
                              return true;
                            })
                            .map(t => (
                              <tr 
                                key={t.testId} 
                                style={{ 
                                  cursor: 'pointer',
                                  backgroundColor: selectedTest?.testId === t.testId ? '#f0fdf4' : 'transparent',
                                  transition: 'background-color 0.2s'
                                }}
                                onClick={() => handleViewTestResults(t)}
                              >
                                <td style={{ fontWeight: '600' }}>{t.title}</td>
                                <td>{t.subjectName || 'N/A'}</td>
                                <td>{t.className || 'N/A'}{t.section ? ` - ${t.section}` : ''}</td>
                                <td>{t.maxMarks}</td>
                                <td>{t.testDate ? new Date(t.testDate).toLocaleDateString() : 'N/A'}</td>
                                <td>
                                  <span className={`academic-badge badge-${(t.status || 'SCHEDULED').toLowerCase()}`} style={{
                                    backgroundColor: t.status === 'FROZEN' ? '#fee2e2' : t.status === 'COMPLETED' ? '#dcfce7' : '#fef9c3',
                                    color: t.status === 'FROZEN' ? '#991b1b' : t.status === 'COMPLETED' ? '#166534' : '#854d0e',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: '600'
                                  }}>
                                    {t.status}
                                  </span>
                                </td>
                                <td onClick={(e) => e.stopPropagation()}>
                                  <div className="action-buttons">
                                    <button
                                      className="academic-action-btn-edit"
                                      onClick={() => {
                                        setEditingTest(t);
                                        setTestForm({
                                          testId: t.testId,
                                          title: t.title || '',
                                          description: t.description || '',
                                          className: t.className || '',
                                          section: t.section || '',
                                          subjectName: t.subjectName || '',
                                          testDate: t.testDate || '',
                                          maxMarks: t.maxMarks || '',
                                          instructions: t.instructions || '',
                                          teacherId: t.teacherId || ''
                                        });
                                        setShowTestModal(true);
                                      }}
                                      disabled={t.status === 'FROZEN'}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      className="academic-action-btn-delete"
                                      onClick={() => handleDeleteTest(t.testId, t.title)}
                                      disabled={t.status === 'FROZEN'}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          {tests.length === 0 && (
                            <tr>
                              <td colSpan="7" style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                                No exams/tests found. Click "Add New Test/Exam" to create one.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Marks Entry / View Section */}
                  {selectedTest && (
                    <div className="academic-table-card" style={{ padding: '16px', border: '1px solid #cbd5e1' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <div>
                          <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                            Marks: {selectedTest.title}
                          </h4>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>
                            Subject: {selectedTest.subjectName} | Class/Sec: {selectedTest.className || 'N/A'}{selectedTest.section ? ` - ${selectedTest.section}` : ''} | Max Marks: {selectedTest.maxMarks}
                          </span>
                        </div>
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={() => { setSelectedTest(null); setTestResults([]); }}
                        >
                          Close Panel
                        </button>
                      </div>

                      {resultsLoading ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading results...</div>
                      ) : (
                        <div>
                          <div style={{ marginBottom: '12px' }}>
                            <input
                              type="text"
                              className="academic-search-input"
                              placeholder="Search student by name or ID..."
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                              id="marksStudentSearch"
                              onChange={(e) => {
                                setTestResults([...testResults]);
                              }}
                            />
                          </div>

                          <div className="academic-table-wrapper" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <table className="academic-table">
                              <thead>
                                <tr>
                                  <th>Student Name</th>
                                  <th>Student ID</th>
                                  <th>Marks</th>
                                  <th>Remarks</th>
                                  <th>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {students
                                  .filter(student => {
                                    const matchClass = student.class?.toString() === selectedTest.className?.toString();
                                    const matchSection = !selectedTest.section || student.section?.toString() === selectedTest.section?.toString();
                                    return matchClass && matchSection;
                                  })
                                  .filter(student => {
                                    const searchInput = document.getElementById('marksStudentSearch')?.value?.toLowerCase();
                                    if (!searchInput) return true;
                                    return (
                                      (student.name || '').toLowerCase().includes(searchInput) ||
                                      (student.studentId || student.student_id || '').toLowerCase().includes(searchInput)
                                    );
                                  })
                                  .map(student => {
                                    const studentIdVal = student.studentId || student.student_id;
                                    const resultObj = testResults.find(r => r.studentId === studentIdVal);
                                    const isEditingThisRow = editingMarkStudentId === studentIdVal;

                                    return (
                                      <tr key={studentIdVal}>
                                        <td style={{ fontWeight: '600' }}>{student.name}</td>
                                        <td>{studentIdVal}</td>
                                        <td>
                                          {isEditingThisRow ? (
                                            <input
                                              type="number"
                                              className="academic-search-input"
                                              style={{ width: '70px', padding: '4px' }}
                                              value={editMarkForm.marksObtained}
                                              onChange={(e) => setEditMarkForm({ ...editMarkForm, marksObtained: e.target.value })}
                                              max={selectedTest.maxMarks}
                                              min={0}
                                            />
                                          ) : (
                                            resultObj ? `${resultObj.marksObtained} / ${selectedTest.maxMarks}` : 'Not Entered'
                                          )}
                                        </td>
                                        <td>
                                          {isEditingThisRow ? (
                                            <input
                                              type="text"
                                              className="academic-search-input"
                                              style={{ width: '100%', padding: '4px' }}
                                              value={editMarkForm.remarks}
                                              onChange={(e) => setEditMarkForm({ ...editMarkForm, remarks: e.target.value })}
                                              placeholder="Remarks"
                                            />
                                          ) : (
                                            resultObj?.remarks || '-'
                                          )}
                                        </td>
                                        <td>
                                          {selectedTest.status === 'FROZEN' ? (
                                            <span style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>Locked</span>
                                          ) : isEditingThisRow ? (
                                            <div style={{ display: 'flex', gap: '4px' }}>
                                              <button
                                                className="btn btn-success btn-xs"
                                                onClick={() => handleSaveStudentMark(studentIdVal)}
                                                style={{ padding: '2px 8px', fontSize: '11px' }}
                                              >
                                                Save
                                              </button>
                                              <button
                                                className="btn btn-secondary btn-xs"
                                                onClick={() => setEditingMarkStudentId(null)}
                                                style={{ padding: '2px 8px', fontSize: '11px' }}
                                              >
                                                Cancel
                                              </button>
                                            </div>
                                          ) : (
                                            <button
                                              className="academic-action-btn-edit"
                                              onClick={() => {
                                                setEditingMarkStudentId(studentIdVal);
                                                setEditMarkForm({
                                                  marksObtained: resultObj ? resultObj.marksObtained.toString() : '',
                                                  remarks: resultObj ? resultObj.remarks || '' : ''
                                                });
                                              }}
                                            >
                                              Edit
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {academicActiveTab === 'assignments' && (
              <HomeworkAssignmentsManager
                orgData={orgData}
                students={students}
                classes={classes}
                subjects={subjects}
                teachers={teachers}
                onReviewUnassignedStudents={() => {
                  setActiveTab('students');
                }}
              />
            )}

            {academicActiveTab === 'ptm' && (
              <PtmManagement
                token={localStorage.getItem('token')}
                orgId={orgData?.orgId}
                teachers={teachers}
                classes={classes}
              />
            )}
          </div>
        )}
        {activeTab === 'smart_attendance' && (
          <SmartAttendanceStudio />
        )}
        {activeTab === 'communication' && (
          <CommunicationCenterStudio orgData={orgData} showNotification={showNotification} />
        )}
        {activeTab === 'attendance' && (
          <div>
            {/* Attendance Top Header */}
            <div className="section-header" style={{ padding: '10px 0', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 className="section-title" style={{ fontSize: '20px', margin: '0 0 4px 0' }}>
                  {attendanceSubTab === 'student' && '👤 Student Attendance'}
                  {attendanceSubTab === 'daily' && '📅 Daily Attendance Register'}
                  {attendanceSubTab === 'monthly' && '🗓️ Monthly Attendance Matrix'}
                  {attendanceSubTab === 'yearly' && '📆 Yearly Attendance Overview'}
                  {attendanceSubTab === 'class' && '🏫 Class-wise Attendance'}
                  {attendanceSubTab === 'section' && '📝 Section-wise Attendance'}
                  {attendanceSubTab === 'summary' && (
                    reportType === 'tracker' ? '📋 Daily Teacher Attendance Tracker' :
                    reportType === 'daywise' ? '📅 Day-Wise Student Attendance' :
                    reportType === 'classwise' ? '🏫 Class-Wise Attendance Overview' :
                    reportType === 'teacher' ? '👨‍🏫 Teacher Attendance Summary' :
                    '📊 Student Attendance Summary'
                  )}
                  {attendanceSubTab === 'percentage' && '💯 Attendance Percentage Analytics'}
                  {attendanceSubTab === 'late' && '⏰ Late Attendance Register'}
                  {attendanceSubTab === 'absent' && '❌ Today\'s Absent Students'}
                  {attendanceSubTab === 'continuous' && '⚠️ Continuous Absentees (3+ Days)'}
                  {attendanceSubTab === 'teacher_attendance' && '👨‍🏫 Teacher Attendance Overview'}
                  {attendanceSubTab === 'teacher_daily' && '📅 Teacher Daily Attendance'}
                  {attendanceSubTab === 'teacher_monthly' && '🗓️ Teacher Monthly Attendance'}
                  {attendanceSubTab === 'teacher_late' && '⏰ Teacher Late Entry Register'}
                  {attendanceSubTab === 'teacher_halfday' && '🌗 Teacher Half Day Register'}
                  {attendanceSubTab === 'teacher_leave' && '📝 Teacher Leave Summary'}
                </h2>
                <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>
                  {attendanceSubTab === 'student' 
                    ? 'Mark, review and manage daily student attendance by class and section.' 
                    : attendanceSubTab === 'summary'
                      ? (
                        reportType === 'tracker' ? 'Track real-time attendance submission status across all classes and verify which teachers have marked or not marked attendance.' :
                        reportType === 'daywise' ? 'View and verify daily student attendance records, entry times, and verification status for any date.' :
                        reportType === 'classwise' ? 'Compare total sessions held, enrolled strength, and attendance rates class-by-class.' :
                        reportType === 'teacher' ? 'Manage, track, and analyze teacher attendance across departments, designations, and timeframes.' :
                        'Manage, track, and analyze cumulative student attendance across classes, sections, and timeframes.'
                      )
                      : 'Manage, track, and analyze student attendance across classes, sections, and timeframes.'}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                {/* Dynamic buttons depending on mode */}
                {attendanceSubTab === 'student' && studentAttendanceMode === 'mark' ? null : (
                  <>
                    {(attendanceSubTab !== 'student' || studentAttendanceMode === 'history') && (
                      <>
                        {attendanceSubTab !== 'student' && attendanceSubTab !== 'summary' && (
                          <button
                            className="btn btn-primary"
                            onClick={() => showNotification('success', 'Attendance Saved', 'Attendance records have been updated successfully')}
                            style={{ fontSize: '12px', padding: '6px 14px', background: '#2563eb' }}
                          >
                            💾 Save Records
                          </button>
                        )}
                        <button
                          className="btn btn-success"
                          onClick={() => {
                            if (attendanceSubTab === 'summary') {
                              if (reportType === 'tracker') {
                                openDownloadModal('attendance-tracker');
                              } else if (reportType === 'daywise') {
                                openDownloadModal('attendance-daywise');
                              } else if (reportType === 'classwise') {
                                openDownloadModal('attendance-classwise');
                              } else if (reportType === 'teacher') {
                                openDownloadModal('teacher-attendance-summary');
                              } else {
                                openDownloadModal('attendance-summary');
                              }
                            } else {
                              openDownloadModal('attendance-history');
                            }
                          }}
                          style={{ fontSize: '12px', padding: '6px 14px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          📊 Export CSV
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={async () => {
                            try {
                              showNotification('info', 'Exporting PDF', 'Please wait while we generate the PDF report...');
                              const element = document.querySelector('.admin-content');
                              const canvas = await html2canvas(element, { scale: 1 });
                              const imgData = canvas.toDataURL('image/png');
                              const pdf = new jsPDF('p', 'mm', 'a4');
                              const pdfWidth = pdf.internal.pageSize.getWidth();
                              const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
                              pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
                              pdf.save(`Attendance_Report_${attendanceSubTab}.pdf`);
                              showNotification('success', 'Export Successful', 'PDF report has been generated');
                            } catch (e) {
                              console.error('PDF generation error:', e);
                              showNotification('error', 'Export Failed', 'Could not generate PDF');
                            }
                          }}
                          style={{ fontSize: '12px', padding: '6px 14px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          📄 Export PDF
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>



            {/* TAB 1: STUDENT ATTENDANCE */}
            {attendanceSubTab === 'student' && (
              <div>
                {/* Segmented Mode Control */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', background: '#f1f5f9', padding: '4px', borderRadius: '10px', width: 'fit-content' }}>
                  <button
                    onClick={() => setStudentAttendanceMode('mark')}
                    style={{
                      padding: '8px 20px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '700',
                      border: 'none',
                      cursor: 'pointer',
                      background: studentAttendanceMode === 'mark' ? '#ffffff' : 'transparent',
                      color: studentAttendanceMode === 'mark' ? '#2563eb' : '#64748b',
                      boxShadow: studentAttendanceMode === 'mark' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    📝 Mark Attendance
                  </button>
                  <button
                    onClick={() => setStudentAttendanceMode('history')}
                    style={{
                      padding: '8px 20px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '700',
                      border: 'none',
                      cursor: 'pointer',
                      background: studentAttendanceMode === 'history' ? '#ffffff' : 'transparent',
                      color: studentAttendanceMode === 'history' ? '#2563eb' : '#64748b',
                      boxShadow: studentAttendanceMode === 'history' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    📜 Attendance History
                  </button>
                </div>

                {/* MODE 1: MARK ATTENDANCE */}
                {studentAttendanceMode === 'mark' && (() => {
                  const stateKey = `${attendanceClass}_${attendanceSection}_${attendanceDate}_${attendanceSession}`;
                  const markedState = savedAttendanceDb[stateKey] || { status: 'not_marked' };
                  const isReadOnly = markedState.status === 'saved' && !isEditingExisting;

                  const handleSave = () => {
                    const mapStatusToEnum = (status) => {
                      if (status === 'Present') return 'PRESENT';
                      if (status === 'Absent') return 'ABSENT';
                      if (status === 'Late') return 'LATE';
                      if (status === 'Half Day') return 'HALF_DAY';
                      if (status === 'Leave') return 'LEAVE';
                      return String(status).toUpperCase();
                    };

                    const requestBody = {
                      attendanceList: markAttendanceList.map(st => ({
                        studentId: String(st.studentId),
                        className: attendanceClass,
                        section: attendanceSection,
                        attendanceDate: attendanceDate,
                        status: mapStatusToEnum(st.status),
                        remarks: st.remarks || ''
                      }))
                    };

                    teacherService.markStudentBulkAttendance(requestBody)
                      .then(res => {
                        if (res && res.success) {
                          const now = new Date();
                          const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                          setSavedAttendanceDb(prev => ({
                            ...prev,
                            [stateKey]: {
                              status: 'saved',
                              time: timeStr,
                              records: markAttendanceList
                            }
                          }));
                          setIsEditingExisting(false);
                          showNotification('success', 'Attendance Saved', `Attendance marked successfully for ${markAttendanceList.length} students at ${timeStr}`);
                        } else {
                          showNotification('error', 'Save Failed', res?.error || 'Could not save attendance to the server.');
                        }
                      })
                      .catch(err => {
                        console.error("Error saving bulk attendance:", err);
                        showNotification('error', 'Save Failed', err.message || 'Network error occurred while saving attendance.');
                      });
                  };

                  return (
                    <div>
                      {/* Class & Date Selection Bar */}
                      <div style={{ background: '#ffffff', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1', minWidth: '130px' }}>
                          <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>📅 Academic Year</label>
                          <select
                            value={attendanceAcademicYear}
                            onChange={(e) => setAttendanceAcademicYear(e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                          >
                            <option value="2026-27">2026-27</option>
                            <option value="2025-26">2025-26</option>
                          </select>
                        </div>
                        <div style={{ flex: '1', minWidth: '130px' }}>
                          <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>📆 Date</label>
                          <input
                            type="date"
                            value={attendanceDate}
                            onChange={(e) => setAttendanceDate(e.target.value)}
                            style={{ width: '100%', padding: '7px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                          />
                        </div>
                        <div style={{ flex: '1', minWidth: '150px' }}>
                          <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>🏫 Class</label>
                          <select
                            value={attendanceClass}
                            onChange={(e) => setAttendanceClass(e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                          >
                            {classOptions.map(cls => (
                              <option key={cls.id} value={cls.label}>{cls.label}</option>
                            ))}
                            {classOptions.length === 0 && <option value="6th Standard">6th Standard</option>}
                          </select>
                        </div>
                        <div style={{ flex: '1', minWidth: '110px' }}>
                          <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>📝 Section</label>
                          <select
                            value={attendanceSection}
                            onChange={(e) => setAttendanceSection(e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                          >
                            {sectionOptions.map(sec => (
                              <option key={sec} value={sec}>Section {sec}</option>
                            ))}
                            {sectionOptions.length === 0 && <option value="A">Section A</option>}
                          </select>
                        </div>
                        <div style={{ flex: '1.2', minWidth: '220px' }}>
                          <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>⏰ Session</label>
                          <div style={{ display: 'flex', gap: '2px', background: '#e2e8f0', padding: '3px', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                            {['Full Day', 'Morning', 'Afternoon'].map(sess => (
                              <button
                                key={sess}
                                onClick={() => setAttendanceSession(sess)}
                                style={{
                                  flex: 1,
                                  padding: '6px 12px',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '700',
                                  cursor: 'pointer',
                                  background: attendanceSession === sess ? '#ffffff' : 'transparent',
                                  color: attendanceSession === sess ? '#2563eb' : '#475569',
                                  boxShadow: attendanceSession === sess ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                  transition: 'all 0.15s ease'
                                }}
                              >
                                {sess}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Students Register Container */}
                      <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <div style={{ padding: '14px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                          <div>
                            <span style={{ fontWeight: '700', fontSize: '14px', color: '#1e293b' }}>
                              🏫 {attendanceClass} • Section {attendanceSection}
                            </span>
                            <span style={{ background: '#e2e8f0', color: '#475569', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', marginLeft: '10px' }}>
                              {markAttendanceList.length} Students
                            </span>
                            {/* Operational Marked Status Badge */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
                              <span style={{ fontSize: '12px', color: '#64748b' }}>
                                {new Date(attendanceDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })} | {attendanceSession}
                              </span>
                              {markedState.status === 'not_marked' && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '700', color: '#ef4444' }}>
                                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444' }}></span>
                                  ● Not Marked
                                </span>
                              )}
                              {markedState.status === 'saved' && (
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '700', color: '#10b981' }}>
                                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981' }}></span>
                                    ✓ Already Marked • {markedState.time}
                                  </span>
                                  <button
                                    onClick={() => setIsEditingExisting(!isEditingExisting)}
                                    style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                                  >
                                    {isEditingExisting ? '🔒 Lock / Cancel' : '✏️ Edit Attendance'}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            disabled={isReadOnly}
                            onClick={markAllPresent}
                            style={{ 
                              background: isReadOnly ? '#f1f5f9' : '#f0fdf4', 
                              color: isReadOnly ? '#cbd5e1' : '#15803d', 
                              border: isReadOnly ? '1px solid #e2e8f0' : '1px solid #bbf7d0', 
                              padding: '6px 12px', 
                              borderRadius: '6px', 
                              fontSize: '12px', 
                              fontWeight: '700', 
                              cursor: isReadOnly ? 'not-allowed' : 'pointer', 
                              transition: 'all 0.15s ease' 
                            }}
                          >
                            ✓ Mark All Present
                          </button>
                        </div>

                        {/* Scrollable table body with sticky headers */}
                        <div style={{ overflowX: 'auto', maxHeight: '420px', overflowY: 'auto', position: 'relative' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', fontSize: '12px', color: '#475569', textAlign: 'left', position: 'sticky', top: 0, zIndex: 10 }}>
                                <th style={{ padding: '12px 16px', width: '80px', background: '#f1f5f9', position: 'sticky', top: 0 }}>Roll No</th>
                                <th style={{ padding: '12px 16px', background: '#f1f5f9', position: 'sticky', top: 0 }}>Student Name</th>
                                <th style={{ padding: '12px 16px', width: '180px', background: '#f1f5f9', position: 'sticky', top: 0 }}>Status</th>
                                <th style={{ padding: '12px 16px', background: '#f1f5f9', position: 'sticky', top: 0 }}>Remarks / Reasons</th>
                              </tr>
                            </thead>
                            <tbody>
                              {markAttendanceList.map((st, idx) => (
                                <tr key={st.studentId} style={{ borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}>
                                  <td style={{ padding: '12px 16px', fontWeight: '700', color: '#64748b' }}>{st.rollNumber}</td>
                                  <td style={{ padding: '12px 16px', fontWeight: '600', color: '#1e293b' }}>{st.name}</td>
                                  <td style={{ padding: '12px 16px' }}>
                                    <select
                                      disabled={isReadOnly}
                                      value={st.status}
                                      onChange={(e) => updateStudentStatus(st.studentId, e.target.value)}
                                      style={{
                                        width: '100%',
                                        padding: '6px 10px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '700',
                                        border: '1px solid #cbd5e1',
                                        background: isReadOnly ? '#f8fafc' : (st.status === 'Present' ? '#f0fdf4' : st.status === 'Absent' ? '#fef2f2' : st.status === 'Late' ? '#fffbeb' : st.status === 'Half Day' ? '#fff7ed' : '#eff6ff'),
                                        color: isReadOnly ? '#94a3b8' : (st.status === 'Present' ? '#166534' : st.status === 'Absent' ? '#991b1b' : st.status === 'Late' ? '#d97706' : st.status === 'Half Day' ? '#c2410c' : '#1d4ed8'),
                                        cursor: isReadOnly ? 'not-allowed' : 'pointer'
                                      }}
                                    >
                                      <option value="Present">Present</option>
                                      <option value="Absent">Absent</option>
                                      <option value="Late">Late</option>
                                      <option value="Half Day">Half Day</option>
                                      <option value="Leave">Leave (Excused)</option>
                                    </select>
                                  </td>
                                  <td style={{ padding: '12px 16px' }}>
                                    <input
                                      type="text"
                                      disabled={isReadOnly}
                                      value={st.remarks}
                                      onChange={(e) => updateStudentRemarks(st.studentId, e.target.value)}
                                      placeholder={st.status === 'Absent' ? 'Reason for absence...' : 'Add remarks...'}
                                      style={{ width: '100%', padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none', background: isReadOnly ? '#f8fafc' : '#ffffff', color: isReadOnly ? '#94a3b8' : '#0f172a', cursor: isReadOnly ? 'not-allowed' : 'text' }}
                                    />
                                  </td>
                                </tr>
                              ))}
                              {markAttendanceList.length === 0 && (
                                <tr>
                                  <td colSpan="4" style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>
                                    No students found matching class {attendanceClass} Section {attendanceSection}.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        {/* Sticky Footer Summary & Save CTA */}
                        <div style={{ padding: '16px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: '#166534' }}>🟢 Present: {counts.present}</span>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: '#991b1b' }}>🔴 Absent: {counts.absent}</span>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: '#d97706' }}>🟡 Late: {counts.late}</span>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: '#c2410c' }}>🟠 Half Day: {counts.halfDay}</span>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: '#1d4ed8' }}>🔵 Leave: {counts.leave}</span>
                          </div>
                          <button
                            disabled={isReadOnly}
                            onClick={handleSave}
                            style={{ 
                              background: isReadOnly ? '#cbd5e1' : '#2563eb', 
                              color: '#ffffff', 
                              border: 'none', 
                              padding: '10px 24px', 
                              borderRadius: '8px', 
                              fontSize: '13px', 
                              fontWeight: '700', 
                              cursor: isReadOnly ? 'not-allowed' : 'pointer', 
                              transition: 'all 0.15s ease', 
                              boxShadow: isReadOnly ? 'none' : '0 4px 6px -1px rgba(37, 99, 235, 0.2)' 
                            }}
                          >
                            💾 Save Attendance
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* MODE 2: ATTENDANCE HISTORY */}
                {studentAttendanceMode === 'history' && (() => {
                  const parseLocalDate = (dateVal) => {
                    if (!dateVal) return 'N/A';
                    if (typeof dateVal === 'string') return dateVal;
                    if (Array.isArray(dateVal)) {
                      const [year, month, day] = dateVal;
                      const mm = String(month).padStart(2, '0');
                      const dd = String(day).padStart(2, '0');
                      return `${year}-${mm}-${dd}`;
                    }
                    return String(dateVal);
                  };

                  const getDayOfWeek = (dateVal) => {
                    try {
                      const dateStr = parseLocalDate(dateVal);
                      const date = new Date(dateStr);
                      if (isNaN(date.getTime())) return '—';
                      return date.toLocaleDateString('en-US', { weekday: 'long' });
                    } catch (e) {
                      return '—';
                    }
                  };

                  const displayStatus = (statusStr) => {
                    if (!statusStr) return 'N/A';
                    const s = statusStr.toUpperCase();
                    if (s === 'PRESENT') return 'Present';
                    if (s === 'ABSENT') return 'Absent';
                    if (s === 'LATE') return 'Late';
                    if (s === 'HALF_DAY') return 'Half Day';
                    if (s === 'LEAVE') return 'Leave';
                    return statusStr;
                  };

                  const getStandingText = (pct) => {
                    if (pct >= 95) return 'Excellent Attendance';
                    if (pct >= 90) return 'Good Standing';
                    if (pct >= 80) return 'Satisfactory Standing';
                    return 'Needs Improvement';
                  };

                  const selectedStudentObj = students.find(st => String(st.studentId) === String(selectedHistoryStudentId));
                  const studentNameDisplay = selectedStudentObj ? (selectedStudentObj.name || selectedStudentObj.studentName) : '';

                  const filteredHistoryStudents = students.filter(st => {
                    // Filter by class and section first if selected
                    const stClass = st.className || st.class || '';
                    const stSection = st.section || '';
                    if (historyClass && stClass.toLowerCase().trim() !== historyClass.toLowerCase().trim()) {
                      return false;
                    }
                    if (historySection && stSection.toLowerCase().trim() !== historySection.toLowerCase().trim()) {
                      return false;
                    }

                    const query = (historySearchText || '').trim().toLowerCase();
                    if (!query) return true;

                    // Exact match check using display name format
                    const selected = students.find(s => String(s.studentId || s.id) === String(selectedHistoryStudentId));
                    if (selected && query === getStudentDisplayName(selected).trim().toLowerCase()) {
                      return true;
                    }

                    const name = (st.name || st.studentName || '').toLowerCase();
                    const roll = (st.rollNumber || '').toLowerCase();
                    return name.includes(query) || roll.includes(query);
                  });

                  return (
                    <div>
                      {/* Student Selection Bar */}
                      <div style={{ background: '#ffffff', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ width: '130px' }}>
                          <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>🏫 Class</label>
                          <select
                            value={historyClass}
                            onChange={(e) => setHistoryClass(e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                          >
                            {classOptions.map(cls => (
                              <option key={cls.id} value={cls.label}>{cls.label}</option>
                            ))}
                            {classOptions.length === 0 && <option value="6th Standard">6th Standard</option>}
                          </select>
                        </div>
                        <div style={{ width: '110px' }}>
                          <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>📝 Section</label>
                          <select
                            value={historySection}
                            onChange={(e) => setHistorySection(e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                          >
                            {sectionOptions.map(sec => (
                              <option key={sec} value={sec}>Section {sec}</option>
                            ))}
                            {sectionOptions.length === 0 && <option value="A">Section A</option>}
                          </select>
                        </div>
                        <div style={{ width: '280px', position: 'relative' }}>
                          <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>👤 Student ({filteredHistoryStudents.length})</label>
                          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <input
                              type="text"
                              placeholder="Type student name or roll to filter..."
                              value={historySearchText}
                              onChange={(e) => {
                                setHistorySearchText(e.target.value);
                                setIsHistoryDropdownOpen(true);
                              }}
                              onFocus={() => setIsHistoryDropdownOpen(true)}
                              onBlur={() => {
                                setTimeout(() => setIsHistoryDropdownOpen(false), 200);
                              }}
                              style={{ 
                                width: '100%', 
                                padding: '10px 36px 10px 12px', 
                                borderRadius: '8px', 
                                border: '1px solid #cbd5e1', 
                                fontSize: '13px',
                                outline: 'none',
                                boxSizing: 'border-box',
                                transition: 'all 0.15s ease',
                                borderColor: isHistoryDropdownOpen ? '#2563eb' : '#cbd5e1',
                                boxShadow: isHistoryDropdownOpen ? '0 0 0 2px rgba(37, 99, 235, 0.15)' : 'none'
                              }}
                            />
                            <span 
                              onClick={() => setIsHistoryDropdownOpen(!isHistoryDropdownOpen)}
                              style={{ 
                                position: 'absolute', 
                                right: '12px', 
                                top: '50%', 
                                transform: 'translateY(-50%)', 
                                cursor: 'pointer',
                                color: '#64748b',
                                fontSize: '10px',
                                userSelect: 'none'
                              }}
                            >
                              ▼
                            </span>
                          </div>

                          {/* Floating Suggestions List */}
                          {isHistoryDropdownOpen && (
                            <div style={{ 
                              position: 'absolute', 
                              top: 'calc(100% + 4px)', 
                              left: 0, 
                              right: 0, 
                              background: '#ffffff', 
                              border: '1px solid #cbd5e1', 
                              borderRadius: '8px', 
                              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', 
                              maxHeight: '260px', 
                              overflowY: 'auto', 
                              zIndex: 1000 
                            }}>
                              {filteredHistoryStudents.length === 0 ? (
                                <div style={{ padding: '12px 16px', color: '#64748b', fontSize: '13px', textAlign: 'center' }}>
                                  No matches found
                                </div>
                              ) : (
                                filteredHistoryStudents.map((st, idx) => {
                                  const isSelected = String(st.studentId || st.id) === String(selectedHistoryStudentId);
                                  return (
                                    <div
                                      key={st.studentId || idx}
                                      onMouseDown={() => {
                                        setSelectedHistoryStudentId(st.studentId || st.id);
                                        setHistorySearchText(getStudentDisplayName(st));
                                        setIsHistoryDropdownOpen(false);
                                      }}
                                      style={{ 
                                        padding: '10px 16px', 
                                        cursor: 'pointer', 
                                        fontSize: '13px',
                                        color: isSelected ? '#1e3a8a' : '#1e293b',
                                        background: isSelected ? '#eff6ff' : 'transparent',
                                        fontWeight: isSelected ? '700' : 'normal',
                                        transition: 'background 0.15s ease',
                                        borderBottom: '1px solid #f1f5f9'
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!isSelected) e.currentTarget.style.background = '#f8fafc';
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!isSelected) e.currentTarget.style.background = 'transparent';
                                      }}
                                    >
                                      <strong>{st.rollNumber || `10${idx+1}`}</strong> - {st.name || st.studentName} <span style={{ color: '#64748b', fontSize: '11px' }}>({st.className || st.class || ''}-{st.section || ''})</span>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          )}
                        </div>

                        {/* Date Range Preset Selector */}
                        <div style={{ width: '160px' }}>
                          <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>📅 Date Range</label>
                          <select
                            value={historyDateRangePreset}
                            onChange={(e) => setHistoryDateRangePreset(e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                          >
                            <option value="last_30_days">Last 30 Days</option>
                            <option value="this_month">This Month</option>
                            <option value="prev_month">Previous Month</option>
                            <option value="this_academic_year">This Academic Year</option>
                            <option value="custom">Custom Range</option>
                          </select>
                        </div>

                        {/* Custom Date Picker Fields */}
                        {historyDateRangePreset === 'custom' && (
                          <>
                            <div style={{ width: '130px' }}>
                              <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>📆 From</label>
                              <input
                                type="date"
                                value={historyStartDate}
                                onChange={(e) => setHistoryStartDate(e.target.value)}
                                style={{ width: '100%', padding: '7px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                              />
                            </div>
                            <div style={{ width: '130px' }}>
                              <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>📆 To</label>
                              <input
                                type="date"
                                value={historyEndDate}
                                onChange={(e) => setHistoryEndDate(e.target.value)}
                                style={{ width: '100%', padding: '7px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                              />
                            </div>
                          </>
                        )}
                      </div>

                      {/* Correctly Computed Stats Cards - Premium Visual Hierarchy */}
                      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'stretch' }}>
                        {/* MAIN CARD: Attendance Rate */}
                        <div style={{ 
                          background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', 
                          border: '1px solid #bfdbfe', 
                          padding: '16px 24px', 
                          borderRadius: '12px', 
                          minWidth: '220px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)'
                        }}>
                          <div style={{ fontSize: '11px', color: '#1e40af', fontWeight: '800', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Attendance Rate</div>
                          <div style={{ fontSize: '32px', color: '#1d4ed8', fontWeight: '800', margin: '4px 0' }}>
                            {historySummary ? `${historySummary.percentage}%` : '0%'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#1e40af', fontWeight: '700' }}>
                            {historySummary ? getStandingText(historySummary.percentage) : 'No Records'}
                          </div>
                        </div>

                        {/* SUPPORTING CARD 1: Working Days */}
                        <div style={{ 
                          background: '#ffffff', 
                          border: '1px solid #e2e8f0', 
                          padding: '14px 20px', 
                          borderRadius: '12px', 
                          minWidth: '135px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)'
                        }}>
                          <div style={{ fontSize: '10px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase' }}>Working Days</div>
                          <div style={{ fontSize: '22px', color: '#1e293b', fontWeight: '800', margin: '2px 0' }}>
                            {historySummary ? historySummary.totalClasses : 0}
                          </div>
                          <div style={{ fontSize: '10px', color: '#64748b', fontWeight: '500' }}>Selected Period</div>
                        </div>

                        {/* SUPPORTING CARD 2: Absences */}
                        <div style={{ 
                          background: '#ffffff', 
                          border: '1px solid #e2e8f0', 
                          padding: '14px 20px', 
                          borderRadius: '12px', 
                          minWidth: '135px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)'
                        }}>
                          <div style={{ fontSize: '10px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase' }}>Absences</div>
                          <div style={{ fontSize: '22px', color: '#ef4444', fontWeight: '800', margin: '2px 0' }}>
                            {historySummary ? historySummary.absent : 0}
                          </div>
                          <div style={{ fontSize: '10px', color: '#ef4444', fontWeight: '600' }}>
                            {historySummary && historySummary.totalClasses > 0 
                              ? `${Math.round((historySummary.absent * 100) / historySummary.totalClasses)}% of days` 
                              : '0% of days'}
                          </div>
                        </div>

                        {/* SUPPORTING CARD 3: Late / Half Day */}
                        <div style={{ 
                          background: '#ffffff', 
                          border: '1px solid #e2e8f0', 
                          padding: '14px 20px', 
                          borderRadius: '12px', 
                          minWidth: '145px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)'
                        }}>
                          <div style={{ fontSize: '10px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase' }}>Late / Half Day</div>
                          <div style={{ fontSize: '22px', color: '#d97706', fontWeight: '800', margin: '2px 0' }}>
                            {historySummary ? `${historySummary.late} / ${historySummary.halfDay || 0}` : '0 / 0'}
                          </div>
                          <div style={{ fontSize: '10px', color: '#d97706', fontWeight: '600' }}>Irregularity</div>
                        </div>
                      </div>

                      {/* Recent Attendance History Table */}
                      <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <div style={{ padding: '14px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: '700', fontSize: '13px', color: '#1e293b' }}>
                            📅 Recent Attendance History ({new Date(historyStartDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - {new Date(historyEndDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })})
                          </span>
                          {selectedHistoryStudentId && (
                            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>
                              Showing history for: <strong style={{ color: '#0f172a' }}>{studentNameDisplay}</strong>
                            </span>
                          )}
                        </div>
                        {loadingHistory ? (
                          <div style={{ padding: '40px', textAlign: 'center', color: '#2563eb', fontWeight: '700' }}>
                            🔄 Loading attendance records...
                          </div>
                        ) : (
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', fontSize: '12px', color: '#475569', textAlign: 'left' }}>
                                <th style={{ padding: '12px 16px' }}>Date</th>
                                <th style={{ padding: '12px 16px' }}>Day</th>
                                <th style={{ padding: '12px 16px' }}>Session / Class</th>
                                <th style={{ padding: '12px 16px', textAlign: 'center' }}>Status</th>
                                <th style={{ padding: '12px 16px' }}>Remarks</th>
                              </tr>
                            </thead>
                            <tbody>
                              {historyAttendanceList.map((item, idx) => {
                                const cleanDate = parseLocalDate(item.attendanceDate);
                                const weekday = getDayOfWeek(item.attendanceDate);
                                const statusVal = displayStatus(item.status);
                                return (
                                  <tr key={item.attendanceId || idx} style={{ borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}>
                                    <td style={{ padding: '12px 16px', fontWeight: '600', color: '#1e293b' }}>{cleanDate}</td>
                                    <td style={{ padding: '12px 16px', color: '#64748b' }}>{weekday}</td>
                                    <td style={{ padding: '12px 16px', color: '#475569' }}>
                                      {item.className ? `${item.className} - ${item.section || 'A'}` : 'General Full Day'}
                                    </td>
                                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                      <span style={{
                                        padding: '4px 10px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        background: statusVal === 'Present' ? '#dcfce7' : statusVal === 'Absent' ? '#fee2e2' : statusVal === 'Late' ? '#fffbeb' : '#eff6ff',
                                        color: statusVal === 'Present' ? '#166534' : statusVal === 'Absent' ? '#991b1b' : statusVal === 'Late' ? '#b45309' : '#1d4ed8'
                                      }}>
                                        {statusVal}
                                      </span>
                                    </td>
                                    <td style={{ padding: '12px 16px', color: '#64748b', fontStyle: statusVal !== 'Present' ? 'italic' : 'normal' }}>
                                      {item.remarks || '—'}
                                    </td>
                                  </tr>
                                );
                              })}
                              {historyAttendanceList.length === 0 && (
                                <tr>
                                  <td colSpan="5" style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>
                                    {selectedHistoryStudentId ? 'No attendance records found for this student.' : 'Please select a student from the dropdown list to view history.'}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* TAB 2: DAILY ATTENDANCE (Our existing full table) */}
            {attendanceSubTab === 'daily' && (
              <div>
                <div style={{ background: '#ffffff', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ flex: '1', minWidth: '150px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>📅 Attendance Date</label>
                    <input 
                      type="date" 
                      defaultValue={new Date().toISOString().split('T')[0]} 
                      style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }} 
                    />
                  </div>
                  <div style={{ flex: '1', minWidth: '150px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>🏫 Select Class</label>
                    <select style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}>
                      <option value="">All Classes</option>
                      {classOptions.map(cls => (
                        <option key={cls.id} value={cls.label}>{cls.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: '1', minWidth: '150px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>📝 Select Section</label>
                    <select style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}>
                      <option value="">All Sections</option>
                      {sectionOptions.map(sec => (
                        <option key={sec} value={sec}>Section {sec}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '18px' }}>
                    <button className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '12px' }}>
                      Mark All Present
                    </button>
                    <button className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '12px' }}>
                      Mark All Absent
                    </button>
                  </div>
                </div>

                <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569' }}>Roll No</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569' }}>Student Name</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569' }}>Class & Sec</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '700', color: '#475569' }}>Attendance Status</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569' }}>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.slice(0, 15).map((st, idx) => (
                        <tr key={st.studentId || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{st.rollNumber || `10${idx+1}`}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '500', color: '#0f172a' }}>{st.name || st.studentName}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', color: '#64748b' }}>{st.className || 'Class 10'} - {st.section || 'A'}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                            <div style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: '8px', padding: '3px' }}>
                              <button style={{ padding: '4px 12px', borderRadius: '6px', border: 'none', background: '#22c55e', color: '#fff', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>Present</button>
                              <button style={{ padding: '4px 12px', borderRadius: '6px', border: 'none', background: 'transparent', color: '#64748b', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>Absent</button>
                              <button style={{ padding: '4px 12px', borderRadius: '6px', border: 'none', background: 'transparent', color: '#64748b', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>Late</button>
                            </div>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <input type="text" placeholder="Add remark..." style={{ width: '80%', padding: '4px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
                          </td>
                        </tr>
                      ))}
                      {students.length === 0 && (
                        <tr>
                          <td colSpan="5" style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                            No student records found. Add students first to mark attendance.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TAB 3: MONTHLY ATTENDANCE MATRIX */}
            {attendanceSubTab === 'monthly' && (
              <div>
                <div style={{ background: '#ffffff', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>🗓️ Select Month & Year</label>
                    <select style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}>
                      <option>July 2026 (Current Month)</option>
                      <option>June 2026</option>
                      <option>May 2026</option>
                      <option>April 2026</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>🏫 Select Class</label>
                    <select style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}>
                      <option>Class 10 - A</option>
                      <option>Class 10 - B</option>
                      <option>Class 9 - A</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>🔍 Search Student</label>
                    <input
                      type="text"
                      placeholder="Name, ID, Phone..."
                      value={monthlySearchQuery}
                      onChange={(e) => setMonthlySearchQuery(e.target.value)}
                      style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', width: '180px' }}
                    />
                  </div>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px', alignItems: 'center', fontSize: '12px', color: '#64748b' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ display: 'inline-block', width: '12px', height: '12px', background: '#22c55e', borderRadius: '2px' }}></span> Present</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ display: 'inline-block', width: '12px', height: '12px', background: '#ef4444', borderRadius: '2px' }}></span> Absent</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ display: 'inline-block', width: '12px', height: '12px', background: '#f59e0b', borderRadius: '2px' }}></span> Late</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ display: 'inline-block', width: '12px', height: '12px', background: '#94a3b8', borderRadius: '2px' }}></span> Holiday/Sun</span>
                    <button 
                      onClick={() => { setMonthlyEditMode(!monthlyEditMode); setEditingMonthlyRowId(null); }}
                      style={{ padding: '6px 12px', background: monthlyEditMode ? '#1e40af' : '#e2e8f0', color: monthlyEditMode ? '#fff' : '#1e293b', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', marginLeft: '10px' }}
                    >
                      {monthlyEditMode ? 'Save All' : 'Edit All'}
                    </button>
                  </div>
                </div>

                <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', textAlign: 'center' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                        <th style={{ padding: '10px', textAlign: 'left', minWidth: '130px', position: 'sticky', left: 0, background: '#f8fafc', zIndex: 2 }}>Student Name</th>
                        {[...Array(15)].map((_, idx) => (
                          <th key={idx} style={{ padding: '8px 4px', minWidth: '24px' }}>{idx+1}</th>
                        ))}
                        <th style={{ padding: '10px', fontWeight: '800', color: '#1e293b' }}>Total %</th>
                        <th style={{ padding: '10px', fontWeight: '800', color: '#1e293b' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(students.length > 0 ? students.slice(0, 8) : [{ name: 'Aarav Sharma' }, { name: 'Diya Patel' }, { name: 'Rohan Gupta' }, { name: 'Ananya Iyer' }])
                      .filter(st => {
                        if (!monthlySearchQuery) return true;
                        const q = monthlySearchQuery.toLowerCase();
                        return (st.name || st.studentName || '').toLowerCase().includes(q) ||
                               (st.studentId || '').toLowerCase().includes(q) ||
                               (st.phone || '').toLowerCase().includes(q);
                      })
                      .map((st, sIdx) => {
                        const defaultPattern = ['P', 'P', 'P', 'P', 'P', 'W', 'W', 'P', 'P', 'L', 'P', 'P', 'W', 'W', 'A'];
                        const pattern = monthlyAttendancePatterns[sIdx] || defaultPattern;
                        const isRowEditing = monthlyEditMode || editingMonthlyRowId === sIdx;
                        
                        const presents = pattern.filter(d => d === 'P').length;
                        const validDays = pattern.filter(d => ['P','A','L'].includes(d)).length;
                        const percentage = validDays === 0 ? 0 : Math.round((presents / validDays) * 1000) / 10;

                        return (
                          <tr key={sIdx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#1e293b', position: 'sticky', left: 0, background: '#ffffff', zIndex: 1, borderRight: '1px solid #f1f5f9' }}>
                              {st.name || st.studentName}
                            </td>
                            {pattern.map((day, dIdx) => (
                              <td key={dIdx} style={{ padding: '6px 2px' }}>
                                {isRowEditing ? (
                                  <input
                                    value={day}
                                    maxLength={1}
                                    onFocus={(e) => e.target.select()}
                                    onChange={(e) => {
                                      let val = e.target.value.toUpperCase();
                                      if (!['P', 'A', 'L', 'W'].includes(val) && val !== '') return;
                                      const newPattern = [...pattern];
                                      newPattern[dIdx] = val;
                                      setMonthlyAttendancePatterns(prev => ({ ...prev, [sIdx]: newPattern }));
                                    }}
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      lineHeight: '20px',
                                      borderRadius: '4px',
                                      fontWeight: '700',
                                      textAlign: 'center',
                                      border: '1px solid #cbd5e1',
                                      padding: 0,
                                      outline: 'none',
                                      background: day === 'P' ? '#dcfce7' : day === 'A' ? '#fee2e2' : day === 'L' ? '#fef9c3' : '#f1f5f9',
                                      color: day === 'P' ? '#166534' : day === 'A' ? '#991b1b' : day === 'L' ? '#854d0e' : '#64748b'
                                    }}
                                  />
                                ) : (
                                  <span style={{
                                    display: 'inline-block',
                                    width: '20px',
                                    height: '20px',
                                    lineHeight: '20px',
                                    borderRadius: '4px',
                                    fontWeight: '700',
                                    background: day === 'P' ? '#dcfce7' : day === 'A' ? '#fee2e2' : day === 'L' ? '#fef9c3' : '#f1f5f9',
                                    color: day === 'P' ? '#166534' : day === 'A' ? '#991b1b' : day === 'L' ? '#854d0e' : '#64748b'
                                  }}>
                                    {day}
                                  </span>
                                )}
                              </td>
                            ))}
                            <td style={{ padding: '8px', fontWeight: '800', color: percentage < 90 ? '#ef4444' : '#166534' }}>
                              {percentage}%
                            </td>
                            <td style={{ padding: '8px' }}>
                              {!monthlyEditMode && (
                                <button 
                                  onClick={() => setEditingMonthlyRowId(isRowEditing ? null : sIdx)}
                                  style={{ padding: '4px 8px', fontSize: '10px', background: isRowEditing ? '#22c55e' : '#f1f5f9', color: isRowEditing ? '#fff' : '#475569', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
                                >
                                  {isRowEditing ? 'Save' : 'Edit'}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TAB 4: YEARLY ATTENDANCE */}
            {attendanceSubTab === 'yearly' && (
              <div>
                <div style={{ background: '#ffffff', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', margin: '0 0 4px 0' }}>Academic Year Attendance Trend (2025 - 2026)</h3>
                    <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>School-wide average attendance percentage across all academic months.</p>
                  </div>
                  <select style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '600' }}>
                    <option>Academic Year 2025-2026</option>
                    <option>Academic Year 2024-2025</option>
                  </select>
                </div>

                <div style={{ background: '#ffffff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                  <div style={{ height: '200px', width: '100%', position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', paddingBottom: '24px', borderBottom: '1px solid #cbd5e1' }}>
                    {[
                      { m: 'Jun', p: 96 }, { m: 'Jul', p: 94 }, { m: 'Aug', p: 95 }, { m: 'Sep', p: 91 },
                      { m: 'Oct', p: 89 }, { m: 'Nov', p: 95 }, { m: 'Dec', p: 92 }, { m: 'Jan', p: 94 },
                      { m: 'Feb', p: 96 }, { m: 'Mar', p: 93 }, { m: 'Apr', p: 91 }, { m: 'May', p: 90 }
                    ].map((item, idx) => (
                      <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', flex: 1 }}>
                        <span style={{ fontSize: '10px', fontWeight: '700', color: '#4f46e5' }}>{item.p}%</span>
                        <div style={{ width: '28px', height: `${item.p * 1.4}px`, background: item.p < 90 ? '#f59e0b' : '#6366f1', borderRadius: '6px 6px 0 0', transition: 'height 0.5s ease' }}></div>
                        <span style={{ fontSize: '11px', color: '#64748b', position: 'absolute', bottom: 0 }}>{item.m}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#475569' }}>
                        <th style={{ padding: '12px 16px' }}>Month</th>
                        <th style={{ padding: '12px 16px' }}>Total School Working Days</th>
                        <th style={{ padding: '12px 16px' }}>School Average %</th>
                        <th style={{ padding: '12px 16px' }}>Best Performing Class</th>
                        <th style={{ padding: '12px 16px' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { month: 'July 2026', days: 24, avg: '94.2%', best: 'Class 10 - A (98.1%)', status: 'Excellent' },
                        { month: 'June 2026', days: 22, avg: '96.0%', best: 'Class 9 - B (97.5%)', status: 'Excellent' },
                        { month: 'May 2026', days: 18, avg: '90.5%', best: 'Class 8 - A (94.0%)', status: 'Good' },
                        { month: 'April 2026', days: 23, avg: '91.2%', best: 'Class 10 - B (95.2%)', status: 'Good' }
                      ].map((m, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px 16px', fontWeight: '700', color: '#1e293b' }}>{m.month}</td>
                          <td style={{ padding: '12px 16px', color: '#475569' }}>{m.days} Days</td>
                          <td style={{ padding: '12px 16px', fontWeight: '800', color: '#2563eb' }}>{m.avg}</td>
                          <td style={{ padding: '12px 16px', color: '#166534', fontWeight: '600' }}>{m.best}</td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ padding: '3px 10px', borderRadius: '12px', background: '#dcfce7', color: '#166534', fontSize: '11px', fontWeight: '700' }}>{m.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TAB 5: CLASS ATTENDANCE */}
            {attendanceSubTab === 'class' && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                  {[
                    { cls: 'Class 10', teacher: 'Suresh Kumar', strength: 80, present: 77, avg: 96.2, color: '#10b981' },
                    { cls: 'Class 9', teacher: 'Anita Desai', strength: 85, present: 80, avg: 94.1, color: '#10b981' },
                    { cls: 'Class 8', teacher: 'Ramesh Gupta', strength: 90, present: 81, avg: 90.0, color: '#3b82f6' },
                    { cls: 'Class 7', teacher: 'Meenakshi Iyer', strength: 75, present: 66, avg: 88.0, color: '#f59e0b' },
                    { cls: 'Class 6', teacher: 'Vikram Singh', strength: 82, present: 78, avg: 95.1, color: '#10b981' },
                    { cls: 'Class 5', teacher: 'Priya Sharma', strength: 88, present: 82, avg: 93.2, color: '#3b82f6' }
                  ].map((c, idx) => (
                    <div key={idx} style={{ background: '#ffffff', borderRadius: '14px', padding: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                        <div>
                          <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: '0 0 2px 0' }}>{c.cls}</h3>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>Teacher: {c.teacher}</span>
                        </div>
                        <span style={{ padding: '4px 8px', borderRadius: '8px', background: `${c.color}15`, color: c.color, fontWeight: '800', fontSize: '13px' }}>
                          {c.avg}%
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#475569', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                        <span>Present Today: <strong>{c.present} / {c.strength}</strong></span>
                        <span>Absent: <strong>{c.strength - c.present}</strong></span>
                      </div>
                      <div style={{ width: '100%', background: '#f1f5f9', height: '8px', borderRadius: '4px', overflow: 'hidden', marginBottom: '14px' }}>
                        <div style={{ width: `${c.avg}%`, background: c.color, height: '100%', borderRadius: '4px' }}></div>
                      </div>
                      <button 
                        onClick={() => {
                          setAttendanceSubTab('section');
                          showNotification('info', 'Viewing Sections', `Filtered section view for ${c.cls}`);
                        }}
                        style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#f8fafc', color: '#2563eb', fontWeight: '600', fontSize: '12px', cursor: 'pointer', transition: 'all 0.2s ease' }}
                      >
                        View Sections (A, B, C) →
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TAB 6: SECTION ATTENDANCE */}
            {attendanceSubTab === 'section' && (
              <div>
                <div style={{ background: '#ffffff', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', margin: '0 0 2px 0' }}>Section-wise Breakdown & Comparison</h3>
                    <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>Compare attendance performance across individual sections of a class.</p>
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginRight: '8px' }}>Filter Class:</label>
                    <select style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '600' }}>
                      <option>Class 10 (All Sections)</option>
                      <option>Class 9 (All Sections)</option>
                      <option>Class 8 (All Sections)</option>
                    </select>
                  </div>
                </div>

                <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#475569' }}>
                        <th style={{ padding: '12px 16px' }}>Class & Section</th>
                        <th style={{ padding: '12px 16px' }}>Room No</th>
                        <th style={{ padding: '12px 16px' }}>Class Teacher</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center' }}>Total Strength</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center' }}>Present Today</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center' }}>Absent Today</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right' }}>Section Attendance %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { sec: 'Class 10 - Section A', room: 'Room 201', teacher: 'Suresh Kumar', total: 40, present: 39, absent: 1, pct: '97.5%', color: '#166534' },
                        { sec: 'Class 10 - Section B', room: 'Room 202', teacher: 'Anita Desai', total: 40, present: 38, absent: 2, pct: '95.0%', color: '#166534' },
                        { sec: 'Class 9 - Section A', room: 'Room 105', teacher: 'Ramesh Gupta', total: 42, present: 39, absent: 3, pct: '92.8%', color: '#2563eb' },
                        { sec: 'Class 9 - Section B', room: 'Room 106', teacher: 'Priya Sharma', total: 43, present: 41, absent: 2, pct: '95.3%', color: '#166534' },
                        { sec: 'Class 8 - Section A', room: 'Room 101', teacher: 'Vikram Singh', total: 45, present: 40, absent: 5, pct: '88.8%', color: '#d97706' }
                      ].map((item, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px 16px', fontWeight: '700', color: '#1e293b' }}>{item.sec}</td>
                          <td style={{ padding: '12px 16px', color: '#64748b' }}>{item.room}</td>
                          <td style={{ padding: '12px 16px', color: '#475569', fontWeight: '500' }}>{item.teacher}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600' }}>{item.total}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'center', color: '#166534', fontWeight: '700' }}>{item.present}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'center', color: '#dc2626', fontWeight: '700' }}>{item.absent}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '800', color: item.color }}>{item.pct}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TAB 7: ATTENDANCE SUMMARY */}
            {attendanceSubTab === 'summary' && (() => {
              // Extract unique classes and departments for dropdown filters
              const uniqueClasses = ['All', ...new Set(classes.map(c => c.className).filter(Boolean))];
              const uniqueSections = ['All', 'A', 'B', 'C', 'D'];
              const uniqueDepts = ['All', ...new Set(teachers.map(t => t.department || t.departmentName).filter(Boolean))];
              const uniqueDesigs = ['All', ...new Set(teachers.map(t => t.designation).filter(Boolean))];

              // Calculate dynamic KPI counts for teachers
              const filteredTeacherReport = teacherAttendanceRecords.filter(t => {
                const matchesDept = reportDepartment === 'All' || t.department === reportDepartment;
                const matchesDesig = reportDesignation === 'All' || t.designation === reportDesignation;
                const matchesSearch = !reportSearchText || 
                  t.teacherName.toLowerCase().includes(reportSearchText.toLowerCase()) ||
                  t.teacherId.toLowerCase().includes(reportSearchText.toLowerCase());
                
                let matchesPct = true;
                if (reportMinPct) {
                  matchesPct = t.attendancePercentage <= parseFloat(reportMinPct);
                }
                return matchesDept && matchesDesig && matchesSearch && matchesPct;
              });

              const totalFaculty = filteredTeacherReport.length;
              const teacherAvgAttendance = totalFaculty > 0
                ? (filteredTeacherReport.reduce((acc, curr) => acc + curr.attendancePercentage, 0) / totalFaculty).toFixed(1) + "%"
                : "100.0%";
              const teacherApprovedLeaves = filteredTeacherReport.reduce((acc, curr) => acc + curr.approvedLeaves, 0);
              const teacherConcerns = filteredTeacherReport.filter(t => t.attendancePercentage < 75).length;

              // Real Student Attendance Report Data
              const reportStudentList = Array.isArray(studentAttendanceReportData?.students) ? studentAttendanceReportData.students : [];
              const filteredReportStudents = reportStudentList.filter(st => {
                const matchesSearch = !reportSearchText || 
                  (st.studentName || '').toLowerCase().includes(reportSearchText.toLowerCase()) ||
                  (st.rollNumber || '').toLowerCase().includes(reportSearchText.toLowerCase()) ||
                  (st.studentId || '').toLowerCase().includes(reportSearchText.toLowerCase());
                const matchesPct = !reportMinPct || st.attendancePercentage <= parseFloat(reportMinPct);
                return matchesSearch && matchesPct;
              });

              const studentReportSummary = studentAttendanceReportData?.summary || {};
              const totalStudentsCount = studentReportSummary.totalStudents !== undefined ? studentReportSummary.totalStudents : reportStudentList.length;
              const studentAvgAttendance = studentReportSummary.avgAttendance || "0.0%";
              const studentBelowThreshold = studentReportSummary.belowThreshold !== undefined ? studentReportSummary.belowThreshold : 0;
              const studentAbsentToday = studentReportSummary.absentToday !== undefined ? studentReportSummary.absentToday : 0;

              // Tracker data helpers
              const trackerClasses = Array.isArray(trackerData?.classes) ? trackerData.classes : [];
              const filteredTrackerClasses = trackerClasses.filter(c => {
                const matchesStatus = trackerStatusFilter === 'ALL' || c.status === trackerStatusFilter;
                const matchesSearch = !trackerSearch ||
                  (c.className || '').toLowerCase().includes(trackerSearch.toLowerCase()) ||
                  (c.classTeacherName || '').toLowerCase().includes(trackerSearch.toLowerCase());
                return matchesStatus && matchesSearch;
              });

              // Day-wise data helpers
              const dayWiseStudents = Array.isArray(dayWiseData?.students) ? dayWiseData.students : [];
              const filteredDayWiseStudents = dayWiseStudents.filter(s => {
                const matchesStatus = dayWiseStatusFilter === 'All' || s.status === dayWiseStatusFilter;
                const matchesSearch = !dayWiseSearch ||
                  (s.name || '').toLowerCase().includes(dayWiseSearch.toLowerCase()) ||
                  (s.rollNo || '').toLowerCase().includes(dayWiseSearch.toLowerCase());
                return matchesStatus && matchesSearch;
              });

              // Class-wise data helpers
              const classWiseList = Array.isArray(classWiseData?.classes) ? classWiseData.classes : [];
              const filteredClassWiseList = classWiseList.filter(c => {
                return !classWiseSearch ||
                  (c.className || '').toLowerCase().includes(classWiseSearch.toLowerCase()) ||
                  (c.classTeacherName || '').toLowerCase().includes(classWiseSearch.toLowerCase());
              });

              return (
                <div>
                  {/* Report Type Selector Tabs */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', background: '#f1f5f9', padding: '4px', borderRadius: '10px', width: 'fit-content', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => setReportType('tracker')}
                      style={{
                        padding: '8px 18px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '700',
                        border: 'none',
                        cursor: 'pointer',
                        background: reportType === 'tracker' ? '#ffffff' : 'transparent',
                        color: reportType === 'tracker' ? '#2563eb' : '#64748b',
                        boxShadow: reportType === 'tracker' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <span>📋</span> Teacher Tracker (Today)
                    </button>
                    <button
                      onClick={() => setReportType('daywise')}
                      style={{
                        padding: '8px 18px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '700',
                        border: 'none',
                        cursor: 'pointer',
                        background: reportType === 'daywise' ? '#ffffff' : 'transparent',
                        color: reportType === 'daywise' ? '#2563eb' : '#64748b',
                        boxShadow: reportType === 'daywise' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <span>📅</span> Day-Wise Attendance
                    </button>
                    <button
                      onClick={() => setReportType('classwise')}
                      style={{
                        padding: '8px 18px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '700',
                        border: 'none',
                        cursor: 'pointer',
                        background: reportType === 'classwise' ? '#ffffff' : 'transparent',
                        color: reportType === 'classwise' ? '#2563eb' : '#64748b',
                        boxShadow: reportType === 'classwise' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <span>🏫</span> Class-Wise Overview
                    </button>
                    <button
                      onClick={() => setReportType('student')}
                      style={{
                        padding: '8px 18px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '700',
                        border: 'none',
                        cursor: 'pointer',
                        background: reportType === 'student' ? '#ffffff' : 'transparent',
                        color: reportType === 'student' ? '#2563eb' : '#64748b',
                        boxShadow: reportType === 'student' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <span>👥</span> Cumulative Student Report
                    </button>
                    <button
                      onClick={() => setReportType('teacher')}
                      style={{
                        padding: '8px 18px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '700',
                        border: 'none',
                        cursor: 'pointer',
                        background: reportType === 'teacher' ? '#ffffff' : 'transparent',
                        color: reportType === 'teacher' ? '#2563eb' : '#64748b',
                        boxShadow: reportType === 'teacher' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <span>👨‍🏫</span> Teacher Staff Reports
                    </button>
                  </div>

                  {/* 1. TEACHER SUBMISSION TRACKER VIEW */}
                  {reportType === 'tracker' && (
                    <div>
                      {/* Tracker KPI Cards */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>Total Classes</div>
                          <div style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>{loadingTracker ? '...' : (trackerData?.totalClasses || 0)}</div>
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>School sections active</div>
                        </div>
                        <div style={{ background: '#f0fdf4', borderRadius: '12px', padding: '16px', border: '1px solid #bbf7d0' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#166534', marginBottom: '4px', textTransform: 'uppercase' }}>Attendance Taken</div>
                          <div style={{ fontSize: '24px', fontWeight: '800', color: '#15803d' }}>{loadingTracker ? '...' : (trackerData?.takenCount || 0)}</div>
                          <div style={{ fontSize: '11px', color: '#166534', marginTop: '2px' }}>Submitted by class teachers</div>
                        </div>
                        <div style={{ background: '#fef2f2', borderRadius: '12px', padding: '16px', border: '1px solid #fecaca' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#991b1b', marginBottom: '4px', textTransform: 'uppercase' }}>Not Taken / Pending</div>
                          <div style={{ fontSize: '24px', fontWeight: '800', color: '#dc2626' }}>{loadingTracker ? '...' : (trackerData?.pendingCount || 0)}</div>
                          <div style={{ fontSize: '11px', color: '#991b1b', marginTop: '2px' }}>Requires teacher action</div>
                        </div>
                        <div style={{ background: '#eff6ff', borderRadius: '12px', padding: '16px', border: '1px solid #bfdbfe' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#1e40af', marginBottom: '4px', textTransform: 'uppercase' }}>Submission Rate</div>
                          <div style={{ fontSize: '24px', fontWeight: '800', color: '#2563eb' }}>{loadingTracker ? '...' : `${trackerData?.overallPercentage || 0}%`}</div>
                          <div style={{ fontSize: '11px', color: '#1e40af', marginTop: '2px' }}>Classes recorded today</div>
                        </div>
                      </div>

                      {/* Tracker Filter Bar */}
                      <div style={{ background: '#ffffff', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', alignItems: 'flex-end' }}>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>Date</label>
                            <input
                              type="date"
                              value={trackerDate}
                              onChange={(e) => setTrackerDate(e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>Status Filter</label>
                            <select
                              value={trackerStatusFilter}
                              onChange={(e) => setTrackerStatusFilter(e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '500' }}
                            >
                              <option value="ALL">All Statuses ({trackerClasses.length})</option>
                              <option value="TAKEN">✓ Taken Only ({trackerData?.takenCount || 0})</option>
                              <option value="PENDING">✗ Pending / Not Taken ({trackerData?.pendingCount || 0})</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>Search Class / Teacher</label>
                            <input
                              type="text"
                              placeholder="Search class or teacher name..."
                              value={trackerSearch}
                              onChange={(e) => setTrackerSearch(e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => {
                                setLoadingTracker(true);
                                smartAttendanceService.getTodayTeacherTracker(trackerDate, reportAcademicYear)
                                  .then(res => setTrackerData(res || { classes: [] }))
                                  .catch(err => setTrackerError(err?.message))
                                  .finally(() => setLoadingTracker(false));
                              }}
                              className="btn btn-outline-primary"
                              style={{ flex: 1, padding: '8px 12px', fontSize: '13px', borderRadius: '8px' }}
                            >
                              🔄 Refresh
                            </button>
                            <button
                              onClick={() => openDownloadModal('attendance-tracker')}
                              className="btn btn-success"
                              style={{ flex: 1, padding: '8px 12px', fontSize: '13px', borderRadius: '8px', background: '#10b981' }}
                            >
                              📊 Export CSV
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Tracker Table */}
                      <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#475569' }}>
                              <th style={{ padding: '12px 16px' }}>Class & Section</th>
                              <th style={{ padding: '12px 16px' }}>Assigned Class Teacher</th>
                              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Today's Status</th>
                              <th style={{ padding: '12px 16px' }}>Submission Time & Submitter</th>
                              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Total Enrolled</th>
                              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Present</th>
                              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Absent</th>
                              <th style={{ padding: '12px 16px', textAlign: 'right' }}>Attendance %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loadingTracker ? (
                              <tr>
                                <td colSpan="8" style={{ padding: '36px', textAlign: 'center', color: '#64748b' }}>
                                  ⏳ Checking teacher attendance submission status for {trackerDate}...
                                </td>
                              </tr>
                            ) : trackerError ? (
                              <tr>
                                <td colSpan="8" style={{ padding: '36px', textAlign: 'center', color: '#dc2626' }}>
                                  ⚠️ {trackerError}
                                </td>
                              </tr>
                            ) : filteredTrackerClasses.length === 0 ? (
                              <tr>
                                <td colSpan="8" style={{ padding: '36px', textAlign: 'center', color: '#64748b' }}>
                                  No classes found matching the filters for {trackerDate}.
                                </td>
                              </tr>
                            ) : (
                              filteredTrackerClasses.map((item, idx) => {
                                const isTaken = item.status === 'TAKEN';
                                return (
                                  <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: !isTaken ? '#fffbfb' : '#ffffff' }}>
                                    <td style={{ padding: '12px 16px', fontWeight: '700', color: '#0f172a' }}>
                                      {item.className} - {item.section}
                                    </td>
                                    <td style={{ padding: '12px 16px' }}>
                                      <div style={{ fontWeight: '600', color: '#1e293b' }}>{item.classTeacherName || 'Not Assigned'}</div>
                                      <div style={{ fontSize: '11px', color: '#64748b' }}>Designated Class Teacher</div>
                                    </td>
                                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                      {isTaken ? (
                                        <span style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '4px',
                                          padding: '4px 10px',
                                          borderRadius: '9999px',
                                          fontSize: '12px',
                                          fontWeight: '700',
                                          background: '#dcfce7',
                                          color: '#15803d'
                                        }}>
                                          ✓ Taken
                                        </span>
                                      ) : (
                                        <span style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '4px',
                                          padding: '4px 10px',
                                          borderRadius: '9999px',
                                          fontSize: '12px',
                                          fontWeight: '700',
                                          background: '#fee2e2',
                                          color: '#b91c1c'
                                        }}>
                                          ✗ Not Taken / Pending
                                        </span>
                                      )}
                                    </td>
                                    <td style={{ padding: '12px 16px' }}>
                                      {isTaken ? (
                                        <div>
                                          <div style={{ fontWeight: '600', color: '#0f172a' }}>{formatIndianTime(item.submittedAt) || 'Recorded'}</div>
                                          <div style={{ fontSize: '11px', color: '#64748b' }}>By: {item.submittedBy || 'Class Teacher'}</div>
                                        </div>
                                      ) : (
                                        <span style={{ color: '#ef4444', fontSize: '12px', fontWeight: '500' }}>
                                          Awaiting submission by {item.classTeacherName || 'teacher'}
                                        </span>
                                      )}
                                    </td>
                                    <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600' }}>
                                      {item.totalStudents}
                                    </td>
                                    <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '700', color: isTaken ? '#15803d' : '#94a3b8' }}>
                                      {isTaken ? item.presentCount : '—'}
                                    </td>
                                    <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '700', color: isTaken ? '#dc2626' : '#94a3b8' }}>
                                      {isTaken ? item.absentCount : '—'}
                                    </td>
                                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                      {isTaken ? (
                                        <span style={{
                                          padding: '4px 10px',
                                          borderRadius: '8px',
                                          fontWeight: '800',
                                          fontSize: '12px',
                                          background: item.attendancePercentage >= 90 ? '#dcfce7' : item.attendancePercentage >= 75 ? '#eff6ff' : '#fee2e2',
                                          color: item.attendancePercentage >= 90 ? '#15803d' : item.attendancePercentage >= 75 ? '#1d4ed8' : '#991b1b'
                                        }}>
                                          {item.attendancePercentage}%
                                        </span>
                                      ) : (
                                        <span style={{ color: '#94a3b8', fontSize: '12px' }}>Pending</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* 2. DAY-WISE ATTENDANCE VIEW */}
                  {reportType === 'daywise' && (
                    <div>
                      {/* Day-Wise KPI Cards */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>Enrolled Students</div>
                          <div style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>{loadingDayWise ? '...' : (dayWiseData?.summary?.totalStudents ?? dayWiseStudents.length)}</div>
                        </div>
                        <div style={{ background: '#f0fdf4', borderRadius: '12px', padding: '16px', border: '1px solid #bbf7d0' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#166534', marginBottom: '4px', textTransform: 'uppercase' }}>Present on {dayWiseDate}</div>
                          <div style={{ fontSize: '24px', fontWeight: '800', color: '#15803d' }}>{loadingDayWise ? '...' : (dayWiseData?.summary?.present ?? dayWiseData?.summary?.presentCount ?? dayWiseStudents.filter(s => s.status === 'PRESENT' || s.status === 'LATE' || s.status === 'HALF_DAY').length)}</div>
                        </div>
                        <div style={{ background: '#fef2f2', borderRadius: '12px', padding: '16px', border: '1px solid #fecaca' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#991b1b', marginBottom: '4px', textTransform: 'uppercase' }}>Absent on {dayWiseDate}</div>
                          <div style={{ fontSize: '24px', fontWeight: '800', color: '#dc2626' }}>{loadingDayWise ? '...' : (dayWiseData?.summary?.absent ?? dayWiseData?.summary?.absentCount ?? dayWiseStudents.filter(s => s.status === 'ABSENT').length)}</div>
                        </div>
                        <div style={{ background: '#f5f3ff', borderRadius: '12px', padding: '16px', border: '1px solid #ddd6fe' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#6d28d9', marginBottom: '4px', textTransform: 'uppercase' }}>On Leave / Half-Day</div>
                          <div style={{ fontSize: '24px', fontWeight: '800', color: '#7c3aed' }}>
                            {loadingDayWise ? '...' : ((dayWiseData?.summary?.leave ?? dayWiseData?.summary?.leaveCount ?? dayWiseStudents.filter(s => s.status === 'LEAVE').length) + (dayWiseData?.summary?.halfDay ?? dayWiseData?.summary?.halfDayCount ?? dayWiseStudents.filter(s => s.status === 'HALF_DAY').length))}
                          </div>
                        </div>
                        <div style={{ background: '#eff6ff', borderRadius: '12px', padding: '16px', border: '1px solid #bfdbfe' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#1e40af', marginBottom: '4px', textTransform: 'uppercase' }}>Day Attendance %</div>
                          <div style={{ fontSize: '24px', fontWeight: '800', color: '#2563eb' }}>
                            {loadingDayWise ? '...' : (dayWiseData?.summary?.percentage != null ? `${dayWiseData.summary.percentage}%` : (dayWiseData?.summary?.attendancePercentage || ((dayWiseData?.summary?.totalStudents ?? dayWiseStudents.length) > 0 ? `${Math.round(((dayWiseData?.summary?.present ?? dayWiseData?.summary?.presentCount ?? dayWiseStudents.filter(s => s.status === 'PRESENT' || s.status === 'LATE' || s.status === 'HALF_DAY').length) / (dayWiseData?.summary?.totalStudents ?? dayWiseStudents.length)) * 1000) / 10}%` : '0%')))}
                          </div>
                        </div>
                      </div>

                      {/* Day-Wise Filter Bar */}
                      <div style={{ background: '#ffffff', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', alignItems: 'flex-end' }}>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>Date</label>
                            <input
                              type="date"
                              value={dayWiseDate}
                              onChange={(e) => setDayWiseDate(e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>Class</label>
                            <select
                              value={dayWiseClass}
                              onChange={(e) => setDayWiseClass(e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '500' }}
                            >
                              <option value="All">All Classes</option>
                              {uniqueClasses.filter(c => c !== 'All').map(c => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>Section</label>
                            <select
                              value={dayWiseSection}
                              onChange={(e) => setDayWiseSection(e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '500' }}
                            >
                              <option value="All">All Sections</option>
                              {uniqueSections.filter(s => s !== 'All').map(s => (
                                <option key={s} value={s}>Section {s}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>Status</label>
                            <select
                              value={dayWiseStatusFilter}
                              onChange={(e) => setDayWiseStatusFilter(e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '500' }}
                            >
                              <option value="All">All Statuses</option>
                              <option value="PRESENT">Present</option>
                              <option value="ABSENT">Absent</option>
                              <option value="LATE">Late</option>
                              <option value="HALF_DAY">Half Day</option>
                              <option value="LEAVE">Leave</option>
                              <option value="NOT_MARKED">Not Marked</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>Search Student</label>
                            <input
                              type="text"
                              placeholder="Name or Roll No..."
                              value={dayWiseSearch}
                              onChange={(e) => setDayWiseSearch(e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => openDownloadModal('attendance-daywise')}
                              className="btn btn-success"
                              style={{ flex: 1, padding: '8px 12px', fontSize: '13px', borderRadius: '8px', background: '#10b981' }}
                            >
                              📊 Export CSV
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Day-Wise Table */}
                      <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#475569' }}>
                              <th style={{ padding: '12px 16px' }}>Roll No</th>
                              <th style={{ padding: '12px 16px' }}>Student Name</th>
                              <th style={{ padding: '12px 16px' }}>Class & Section</th>
                              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Day Status</th>
                              <th style={{ padding: '12px 16px' }}>Time In</th>
                              <th style={{ padding: '12px 16px' }}>Mode</th>
                              <th style={{ padding: '12px 16px' }}>Remarks</th>
                              <th style={{ padding: '12px 16px' }}>Marked / Verified By</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loadingDayWise ? (
                              <tr>
                                <td colSpan="8" style={{ padding: '36px', textAlign: 'center', color: '#64748b' }}>
                                  ⏳ Loading daily attendance register for {dayWiseDate}...
                                </td>
                              </tr>
                            ) : dayWiseError ? (
                              <tr>
                                <td colSpan="8" style={{ padding: '36px', textAlign: 'center', color: '#dc2626' }}>
                                  ⚠️ {dayWiseError}
                                </td>
                              </tr>
                            ) : filteredDayWiseStudents.length === 0 ? (
                              <tr>
                                <td colSpan="8" style={{ padding: '36px', textAlign: 'center', color: '#64748b' }}>
                                  No student attendance records found for {dayWiseDate}.
                                </td>
                              </tr>
                            ) : (
                              filteredDayWiseStudents.map((st, idx) => {
                                const stStatus = st.status || 'NOT_MARKED';
                                let statusBg = '#f1f5f9';
                                let statusColor = '#475569';
                                if (stStatus === 'PRESENT') {
                                  statusBg = '#dcfce7';
                                  statusColor = '#15803d';
                                } else if (stStatus === 'ABSENT') {
                                  statusBg = '#fee2e2';
                                  statusColor = '#b91c1c';
                                } else if (stStatus === 'LATE') {
                                  statusBg = '#fef9c3';
                                  statusColor = '#a16207';
                                } else if (stStatus === 'HALF_DAY') {
                                  statusBg = '#ffedd5';
                                  statusColor = '#c2410c';
                                } else if (stStatus === 'LEAVE') {
                                  statusBg = '#f3e8ff';
                                  statusColor = '#7e22ce';
                                }

                                return (
                                  <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '12px 16px', fontWeight: '700', color: '#1e293b' }}>{st.rollNo || '—'}</td>
                                    <td style={{ padding: '12px 16px', fontWeight: '600', color: '#0f172a' }}>{st.name || st.studentId}</td>
                                    <td style={{ padding: '12px 16px', color: '#64748b' }}>{st.className} - {st.section}</td>
                                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                      <span style={{
                                        padding: '4px 10px',
                                        borderRadius: '9999px',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        background: statusBg,
                                        color: statusColor
                                      }}>
                                        {stStatus}
                                      </span>
                                    </td>
                                    <td style={{ padding: '12px 16px', color: '#334155' }}>{formatIndianTime(st.entryTime)}</td>
                                    <td style={{ padding: '12px 16px', color: '#64748b', fontSize: '12px' }}>{st.scanType || 'MANUAL'}</td>
                                    <td style={{ padding: '12px 16px', color: '#64748b', fontSize: '12px' }}>{st.remarks || '—'}</td>
                                    <td style={{ padding: '12px 16px', color: '#334155', fontWeight: '500' }}>{st.verifiedBy || 'Class Teacher'}</td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* 3. CLASS-WISE ATTENDANCE OVERVIEW */}
                  {reportType === 'classwise' && (
                    <div>
                      {/* Class-Wise KPI Cards */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>Total Classes</div>
                          <div style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>{loadingClassWise ? '...' : classWiseList.length}</div>
                        </div>
                        <div style={{ background: '#f0fdf4', borderRadius: '12px', padding: '16px', border: '1px solid #bbf7d0' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#166534', marginBottom: '4px', textTransform: 'uppercase' }}>Highest Attendance Class</div>
                          <div style={{ fontSize: '20px', fontWeight: '800', color: '#15803d' }}>
                            {loadingClassWise ? '...' : (classWiseList.length > 0 ? `${classWiseList[0]?.className}-${classWiseList[0]?.section} (${classWiseList[0]?.attendancePercentage}%)` : '—')}
                          </div>
                        </div>
                        <div style={{ background: '#eff6ff', borderRadius: '12px', padding: '16px', border: '1px solid #bfdbfe' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#1e40af', marginBottom: '4px', textTransform: 'uppercase' }}>Average School Attendance</div>
                          <div style={{ fontSize: '24px', fontWeight: '800', color: '#2563eb' }}>
                            {loadingClassWise ? '...' : (
                              classWiseList.length > 0
                                ? `${(classWiseList.reduce((acc, c) => acc + (c.attendancePercentage || 0), 0) / classWiseList.length).toFixed(1)}%`
                                : '0%'
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Class-Wise Filter Bar */}
                      <div style={{ background: '#ffffff', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', alignItems: 'flex-end' }}>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>From Date</label>
                            <input
                              type="date"
                              value={reportFromDate}
                              onChange={(e) => setReportFromDate(e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>To Date</label>
                            <input
                              type="date"
                              value={reportToDate}
                              onChange={(e) => setReportToDate(e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>Search Class / Teacher</label>
                            <input
                              type="text"
                              placeholder="Search class or teacher name..."
                              value={classWiseSearch}
                              onChange={(e) => setClassWiseSearch(e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => openDownloadModal('attendance-classwise')}
                              className="btn btn-success"
                              style={{ flex: 1, padding: '8px 12px', fontSize: '13px', borderRadius: '8px', background: '#10b981' }}
                            >
                              📊 Export CSV
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Class-Wise Table */}
                      <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#475569' }}>
                              <th style={{ padding: '12px 16px' }}>Class & Section</th>
                              <th style={{ padding: '12px 16px' }}>Class Teacher</th>
                              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Enrolled Students</th>
                              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Sessions Held</th>
                              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Total Present</th>
                              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Total Absent</th>
                              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Approved Leaves</th>
                              <th style={{ padding: '12px 16px', textAlign: 'right' }}>Class Attendance %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loadingClassWise ? (
                              <tr>
                                <td colSpan="8" style={{ padding: '36px', textAlign: 'center', color: '#64748b' }}>
                                  ⏳ Calculating class-wise attendance aggregates...
                                </td>
                              </tr>
                            ) : classWiseError ? (
                              <tr>
                                <td colSpan="8" style={{ padding: '36px', textAlign: 'center', color: '#dc2626' }}>
                                  ⚠️ {classWiseError}
                                </td>
                              </tr>
                            ) : filteredClassWiseList.length === 0 ? (
                              <tr>
                                <td colSpan="8" style={{ padding: '36px', textAlign: 'center', color: '#64748b' }}>
                                  No class records found for the selected period.
                                </td>
                              </tr>
                            ) : (
                              filteredClassWiseList.map((c, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                  <td style={{ padding: '12px 16px', fontWeight: '700', color: '#0f172a' }}>
                                    {c.className} - {c.section}
                                  </td>
                                  <td style={{ padding: '12px 16px', fontWeight: '600', color: '#334155' }}>
                                    {c.classTeacherName || 'Not Assigned'}
                                  </td>
                                  <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600' }}>
                                    {c.enrolledStudents}
                                  </td>
                                  <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '700', color: '#2563eb' }}>
                                    {c.sessionsHeld}
                                  </td>
                                  <td style={{ padding: '12px 16px', textAlign: 'center', color: '#15803d', fontWeight: '700' }}>
                                    {c.totalPresent}
                                  </td>
                                  <td style={{ padding: '12px 16px', textAlign: 'center', color: '#dc2626', fontWeight: '700' }}>
                                    {c.totalAbsent}
                                  </td>
                                  <td style={{ padding: '12px 16px', textAlign: 'center', color: '#7c3aed', fontWeight: '600' }}>
                                    {c.totalLeave}
                                  </td>
                                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                    <span style={{
                                      padding: '4px 10px',
                                      borderRadius: '8px',
                                      fontWeight: '800',
                                      fontSize: '12px',
                                      background: c.attendancePercentage >= 90 ? '#dcfce7' : c.attendancePercentage >= 75 ? '#eff6ff' : '#fee2e2',
                                      color: c.attendancePercentage >= 90 ? '#15803d' : c.attendancePercentage >= 75 ? '#1d4ed8' : '#991b1b'
                                    }}>
                                      {c.attendancePercentage}%
                                    </span>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* 4. CUMULATIVE STUDENT REPORT VIEW */}
                  {reportType === 'student' && (
                    <div>
                      {/* Student Dynamic KPI Cards */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>TOTAL STUDENTS</div>
                          <div style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>{loadingStudentReport ? '...' : totalStudentsCount}</div>
                        </div>
                        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>AVG ATTENDANCE</div>
                          <div style={{ fontSize: '24px', fontWeight: '800', color: '#166534' }}>{loadingStudentReport ? '...' : studentAvgAttendance}</div>
                        </div>
                        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>BELOW THRESHOLD</div>
                          <div style={{ fontSize: '24px', fontWeight: '800', color: '#991b1b' }}>{loadingStudentReport ? '...' : studentBelowThreshold}</div>
                        </div>
                        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>ABSENT TODAY</div>
                          <div style={{ fontSize: '24px', fontWeight: '800', color: '#ea580c' }}>{loadingStudentReport ? '...' : studentAbsentToday}</div>
                        </div>
                      </div>

                      {/* Student Filter Bar */}
                      <div style={{ background: '#ffffff', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', alignItems: 'flex-end' }}>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>Academic Year</label>
                            <select
                              value={reportAcademicYear}
                              onChange={(e) => setReportAcademicYear(e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '500' }}
                            >
                              <option value="2026-27">2026-27</option>
                              <option value="2025-26">2025-26</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>From Date</label>
                            <input
                              type="date"
                              value={reportFromDate}
                              onChange={(e) => setReportFromDate(e.target.value)}
                              style={{ width: '100%', padding: '7px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>To Date</label>
                            <input
                              type="date"
                              value={reportToDate}
                              onChange={(e) => setReportToDate(e.target.value)}
                              style={{ width: '100%', padding: '7px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>Class</label>
                            <select
                              value={reportClass}
                              onChange={(e) => setReportClass(e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '500' }}
                            >
                              {uniqueClasses.map(cls => (
                                <option key={cls} value={cls}>{cls}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>Section</label>
                            <select
                              value={reportSection}
                              onChange={(e) => setReportSection(e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '500' }}
                            >
                              {uniqueSections.map(sec => (
                                <option key={sec} value={sec}>{sec === 'All' ? 'All Sections' : `Section ${sec}`}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>Min Attendance %</label>
                            <input
                              type="number"
                              placeholder="e.g. 75"
                              value={reportMinPct}
                              onChange={(e) => setReportMinPct(e.target.value)}
                              style={{ width: '100%', padding: '7px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                            />
                          </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', gap: '16px', flexWrap: 'wrap' }}>
                          <div style={{ flex: 1, minWidth: '240px' }}>
                            <input
                              type="text"
                              placeholder="🔍 Search student by name or roll number..."
                              value={reportSearchText}
                              onChange={(e) => setReportSearchText(e.target.value)}
                              style={{ width: '100%', padding: '9px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                              onClick={() => openDownloadModal('attendance-summary')}
                              className="btn btn-success"
                              style={{ fontSize: '12px', padding: '9px 18px', background: '#10b981', display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                              📊 Export CSV/Excel
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  showNotification('info', 'Exporting PDF', 'Please wait while we generate the PDF report...');
                                  const element = document.querySelector('.admin-content');
                                  const canvas = await html2canvas(element, { scale: 1.5 });
                                  const imgData = canvas.toDataURL('image/png');
                                  const pdf = new jsPDF('p', 'mm', 'a4');
                                  const pdfWidth = pdf.internal.pageSize.getWidth();
                                  const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
                                  
                                  pdf.setFontSize(10);
                                  pdf.setTextColor(100);
                                  pdf.text(`School: ${orgData?.orgName || 'Educational ERP'} | Academic Year: ${reportAcademicYear}`, 10, 8);
                                  pdf.text(`Period: ${reportFromDate} to ${reportToDate} | Exported: ${new Date().toLocaleDateString()}`, 10, 12);
                                  
                                  pdf.addImage(imgData, 'PNG', 0, 16, pdfWidth, pdfHeight);
                                  pdf.save(`Student_Attendance_Report_${reportAcademicYear}.pdf`);
                                  showNotification('success', 'Export Successful', 'PDF report has been generated');
                                } catch (e) {
                                  console.error('PDF generation error:', e);
                                  showNotification('error', 'Export Failed', 'Could not generate PDF');
                                }
                              }}
                              className="btn btn-danger"
                              style={{ fontSize: '12px', padding: '9px 18px', background: '#ef4444', display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                              📄 Export PDF
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Student Table */}
                      <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#475569' }}>
                              <th style={{ padding: '12px 16px' }}>Roll No</th>
                              <th style={{ padding: '12px 16px' }}>Student Name</th>
                              <th style={{ padding: '12px 16px' }}>Class & Sec</th>
                              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Sessions Held</th>
                              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Attended</th>
                              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Excused Leaves</th>
                              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Unexcused Absent</th>
                              <th style={{ padding: '12px 16px', textAlign: 'right' }}>Final Attendance %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loadingStudentReport ? (
                              <tr>
                                <td colSpan="8" style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>
                                  ⏳ Fetching student attendance report from database...
                                </td>
                              </tr>
                            ) : studentReportError ? (
                              <tr>
                                <td colSpan="8" style={{ padding: '32px', textAlign: 'center', color: '#dc2626' }}>
                                  ⚠️ {studentReportError}
                                  <button
                                    onClick={() => setStudentReportRetryCount(c => c + 1)}
                                    className="btn btn-sm btn-outline-primary"
                                    style={{ marginLeft: '12px', padding: '4px 12px', fontSize: '12px', borderRadius: '6px' }}
                                  >
                                    Retry
                                  </button>
                                </td>
                              </tr>
                            ) : filteredReportStudents.length === 0 ? (
                              <tr>
                                <td colSpan="8" style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>
                                  No attendance records found for the selected filters.
                                </td>
                              </tr>
                            ) : (
                              filteredReportStudents.map((st, idx) => {
                                const pct = Number(st.attendancePercentage || 0).toFixed(1);
                                return (
                                  <tr key={st.studentId || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '12px 16px', fontWeight: '700', color: '#1e293b' }}>{st.rollNumber || '—'}</td>
                                    <td style={{ padding: '12px 16px', fontWeight: '600', color: '#0f172a' }}>{st.studentName || st.studentId}</td>
                                    <td style={{ padding: '12px 16px', color: '#64748b' }}>
                                      {st.className ? `${st.className}${st.section ? ` - ${st.section}` : ''}` : '—'}
                                    </td>
                                    <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600' }}>{st.sessionsHeld}</td>
                                    <td style={{ padding: '12px 16px', textAlign: 'center', color: '#166534', fontWeight: '700' }}>{st.attended}</td>
                                    <td style={{ padding: '12px 16px', textAlign: 'center', color: '#2563eb' }}>{st.excused}</td>
                                    <td style={{ padding: '12px 16px', textAlign: 'center', color: st.unexcused > 5 ? '#dc2626' : '#64748b', fontWeight: st.unexcused > 5 ? '700' : '500' }}>{st.unexcused}</td>
                                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                      <span style={{
                                        padding: '4px 10px',
                                        borderRadius: '8px',
                                        fontWeight: '800',
                                        fontSize: '12px',
                                        background: pct >= 90 ? '#dcfce7' : pct >= 75 ? '#eff6ff' : '#fee2e2',
                                        color: pct >= 90 ? '#166534' : pct >= 75 ? '#1d4ed8' : '#991b1b'
                                      }}>
                                        {pct}%
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* 5. TEACHER REPORTS VIEW */}
                  {reportType === 'teacher' && (
                    <div>
                      {/* Teacher Dynamic KPI Cards */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>TOTAL FACULTY</div>
                          <div style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>{totalFaculty}</div>
                        </div>
                        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>AVG ATTENDANCE</div>
                          <div style={{ fontSize: '24px', fontWeight: '800', color: '#166534' }}>{teacherAvgAttendance}</div>
                        </div>
                        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>APPROVED LEAVE</div>
                          <div style={{ fontSize: '24px', fontWeight: '800', color: '#2563eb' }}>{teacherApprovedLeaves} Days</div>
                        </div>
                        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>ATTENDANCE CONCERNS</div>
                          <div style={{ fontSize: '24px', fontWeight: '800', color: '#991b1b' }}>{teacherConcerns} Faculty</div>
                        </div>
                      </div>

                      {/* Teacher Filter Bar */}
                      <div style={{ background: '#ffffff', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', alignItems: 'flex-end' }}>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>Academic Year</label>
                            <select
                              value={reportAcademicYear}
                              onChange={(e) => setReportAcademicYear(e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '500' }}
                            >
                              <option value="2026-27">2026-27</option>
                              <option value="2025-26">2025-26</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>From Date</label>
                            <input
                              type="date"
                              value={reportFromDate}
                              onChange={(e) => setReportFromDate(e.target.value)}
                              style={{ width: '100%', padding: '7px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>To Date</label>
                            <input
                              type="date"
                              value={reportToDate}
                              onChange={(e) => setReportToDate(e.target.value)}
                              style={{ width: '100%', padding: '7px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>Department</label>
                            <select
                              value={reportDepartment}
                              onChange={(e) => setReportDepartment(e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '500' }}
                            >
                              {uniqueDepts.map(dept => (
                                <option key={dept} value={dept}>{dept}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>Designation</label>
                            <select
                              value={reportDesignation}
                              onChange={(e) => setReportDesignation(e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '500' }}
                            >
                              {uniqueDesigs.map(des => (
                                <option key={des} value={des}>{des}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', gap: '16px', flexWrap: 'wrap' }}>
                          <div style={{ flex: 1, minWidth: '240px' }}>
                            <input
                              type="text"
                              placeholder="🔍 Search teacher by name or ID..."
                              value={reportSearchText}
                              onChange={(e) => setReportSearchText(e.target.value)}
                              style={{ width: '100%', padding: '9px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                              onClick={() => openDownloadModal('teacher-attendance-summary')}
                              className="btn btn-success"
                              style={{ fontSize: '12px', padding: '9px 18px', background: '#10b981', display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                              📊 Export CSV/Excel
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Teacher Table */}
                      <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#475569' }}>
                              <th style={{ padding: '12px 16px' }}>Teacher</th>
                              <th style={{ padding: '12px 16px' }}>Department & Designation</th>
                              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Working Days</th>
                              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Present</th>
                              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Absent</th>
                              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Late</th>
                              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Half Day</th>
                              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Approved Leave</th>
                              <th style={{ padding: '12px 16px', textAlign: 'right' }}>Attendance %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loadingTeacherRecords ? (
                              <tr>
                                <td colSpan="9" style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>
                                  ⏳ Fetching teacher attendance aggregation from backend...
                                </td>
                              </tr>
                            ) : (
                              filteredTeacherReport.map((t, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                  <td style={{ padding: '12px 16px' }}>
                                    <div style={{ fontWeight: '700', color: '#1e293b' }}>{t.teacherName}</div>
                                    <div style={{ fontSize: '11px', color: '#64748b' }}>ID: {t.teacherId}</div>
                                  </td>
                                  <td style={{ padding: '12px 16px', color: '#475569' }}>
                                    <div style={{ fontWeight: '600' }}>{t.department}</div>
                                    <div style={{ fontSize: '11px', color: '#64748b' }}>{t.designation}</div>
                                  </td>
                                  <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600' }}>{t.workingDays}</td>
                                  <td style={{ padding: '12px 16px', textAlign: 'center', color: '#166534', fontWeight: '700' }}>{t.presentDays}</td>
                                  <td style={{ padding: '12px 16px', textAlign: 'center', color: '#dc2626', fontWeight: '700' }}>{t.absentDays}</td>
                                  <td style={{ padding: '12px 16px', textAlign: 'center', color: '#ca8a04', fontWeight: '600' }}>{t.lateEntries}</td>
                                  <td style={{ padding: '12px 16px', textAlign: 'center', color: '#ea580c', fontWeight: '600' }}>{t.halfDays}</td>
                                  <td style={{ padding: '12px 16px', textAlign: 'center', color: '#2563eb' }}>{t.approvedLeaves}</td>
                                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                    <span style={{
                                      padding: '4px 10px',
                                      borderRadius: '8px',
                                      fontWeight: '800',
                                      fontSize: '12px',
                                      background: t.attendancePercentage >= 90 ? '#dcfce7' : t.attendancePercentage >= 75 ? '#eff6ff' : '#fee2e2',
                                      color: t.attendancePercentage >= 90 ? '#15803d' : t.attendancePercentage >= 75 ? '#1d4ed8' : '#991b1b'
                                    }}>
                                      {t.attendancePercentage}%
                                    </span>
                                  </td>
                                </tr>
                              ))
                            )}
                            {!loadingTeacherRecords && filteredTeacherReport.length === 0 && (
                              <tr>
                                <td colSpan="9" style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>
                                  No teacher records found matching the filters.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* TAB 8: ATTENDANCE PERCENTAGE */}
            {attendanceSubTab === 'percentage' && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                  <div style={{ background: '#f0fdf4', borderRadius: '14px', padding: '18px', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ fontSize: '32px', background: '#dcfce7', width: '56px', height: '56px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🏆</div>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '700', color: '#166534' }}>EXCELLENT (≥ 90%)</div>
                      <div style={{ fontSize: '24px', fontWeight: '800', color: '#15803d' }}>1,050 Students</div>
                      <div style={{ fontSize: '11px', color: '#166534', marginTop: '2px' }}>84% of total school strength</div>
                    </div>
                  </div>

                  <div style={{ background: '#eff6ff', borderRadius: '14px', padding: '18px', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ fontSize: '32px', background: '#dbeafe', width: '56px', height: '56px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>⚠️</div>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '700', color: '#1e40af' }}>SATISFACTORY (75% - 89%)</div>
                      <div style={{ fontSize: '24px', fontWeight: '800', color: '#1d4ed8' }}>150 Students</div>
                      <div style={{ fontSize: '11px', color: '#1e40af', marginTop: '2px' }}>12% of total school strength</div>
                    </div>
                  </div>

                  <div style={{ background: '#fef2f2', borderRadius: '14px', padding: '18px', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ fontSize: '32px', background: '#fee2e2', width: '56px', height: '56px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🚨</div>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '700', color: '#991b1b' }}>DEFAULTERS (&lt; 75%)</div>
                      <div style={{ fontSize: '24px', fontWeight: '800', color: '#dc2626' }}>50 Students</div>
                      <div style={{ fontSize: '11px', color: '#991b1b', marginTop: '2px' }}>Requires immediate warning notice</div>
                    </div>
                  </div>
                </div>

                <div style={{ background: '#ffffff', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', margin: '0 0 12px 0' }}>Defaulters Watchlist (&lt; 75% Attendance)</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#fef2f2', borderBottom: '1px solid #fecaca', textAlign: 'left', color: '#991b1b' }}>
                        <th style={{ padding: '12px 16px' }}>Roll No</th>
                        <th style={{ padding: '12px 16px' }}>Student Name</th>
                        <th style={{ padding: '12px 16px' }}>Class & Sec</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center' }}>Days Attended</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right' }}>Current %</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { roll: '102', name: 'Diya Patel', cls: 'Class 10 - B', attended: '155 / 220', pct: '70.4%' },
                        { roll: '214', name: 'Kabir Mehta', cls: 'Class 9 - A', attended: '142 / 220', pct: '64.5%' },
                        { roll: '305', name: 'Neha Joshi', cls: 'Class 8 - C', attended: '160 / 220', pct: '72.7%' }
                      ].map((item, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px 16px', fontWeight: '700' }}>{item.roll}</td>
                          <td style={{ padding: '12px 16px', fontWeight: '600', color: '#1e293b' }}>{item.name}</td>
                          <td style={{ padding: '12px 16px', color: '#64748b' }}>{item.cls}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>{item.attended}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '800', color: '#dc2626' }}>{item.pct}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                            <button
                              onClick={() => showNotification('warning', 'Notice Issued', `Formal low attendance notice sent to parents of ${item.name}`)}
                              style={{ padding: '4px 12px', borderRadius: '6px', border: 'none', background: '#dc2626', color: '#ffffff', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                            >
                              🚨 Issue Notice
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TAB 9: LATE ATTENDANCE REGISTER */}
            {attendanceSubTab === 'late' && (
              <div>
                <div style={{ background: '#fef9c3', borderRadius: '12px', padding: '16px', border: '1px solid #fde047', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '28px' }}>⏰</span>
                    <div>
                      <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#854d0e', margin: 0 }}>Today's Late Arrivals Register</h3>
                      <p style={{ fontSize: '12px', color: '#a16207', margin: '0' }}>12 Students arrived after school start time (8:30 AM) today.</p>
                    </div>
                  </div>
                  <button
                    onClick={() => showNotification('success', 'SMS Broadcast Sent', 'SMS notifications sent to parents of all 12 latecomers.')}
                    style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#ca8a04', color: '#ffffff', fontWeight: '700', fontSize: '12px', cursor: 'pointer' }}
                  >
                    💬 Notify Parents via SMS
                  </button>
                </div>

                <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#475569' }}>
                        <th style={{ padding: '12px 16px' }}>Roll No</th>
                        <th style={{ padding: '12px 16px' }}>Student Name</th>
                        <th style={{ padding: '12px 16px' }}>Class & Sec</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center' }}>Arrival Time</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center' }}>Minutes Late</th>
                        <th style={{ padding: '12px 16px' }}>Reason Given</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { roll: '105', name: 'Pranav Rao', cls: 'Class 10 - A', time: '8:50 AM', mins: '+20 Mins', reason: 'School bus delayed by traffic' },
                        { roll: '208', name: 'Simran Kaur', cls: 'Class 9 - B', time: '9:15 AM', mins: '+45 Mins', reason: 'Medical appointment' },
                        { roll: '312', name: 'Aditya Varma', cls: 'Class 8 - A', time: '8:45 AM', mins: '+15 Mins', reason: 'Overslept / Personal' }
                      ].map((item, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px 16px', fontWeight: '700' }}>{item.roll}</td>
                          <td style={{ padding: '12px 16px', fontWeight: '600', color: '#1e293b' }}>{item.name}</td>
                          <td style={{ padding: '12px 16px', color: '#64748b' }}>{item.cls}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '700', color: '#854d0e' }}>{item.time}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                            <span style={{ padding: '2px 8px', borderRadius: '6px', background: '#fef9c3', color: '#a16207', fontWeight: '800', fontSize: '11px' }}>{item.mins}</span>
                          </td>
                          <td style={{ padding: '12px 16px', color: '#475569' }}>{item.reason}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                            <button
                              onClick={() => showNotification('info', 'Warning Issued', `Warning noted for ${item.name}`)}
                              style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#475569', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}
                            >
                              ⚠️ Issue Warning
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TAB 10: ABSENT STUDENTS REGISTER */}
            {attendanceSubTab === 'absent' && (
              <div>
                <div style={{ background: '#ffffff', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>🏫 Select Class</label>
                    <select 
                      value={absentFilterClass}
                      onChange={(e) => setAbsentFilterClass(e.target.value)}
                      style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', minWidth: '160px' }}
                    >
                      <option>All Classes</option>
                      <option>Class 10</option>
                      <option>Class 9</option>
                      <option>Class 8</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>🏷️ Select Section</label>
                    <select 
                      value={absentFilterSection}
                      onChange={(e) => setAbsentFilterSection(e.target.value)}
                      style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', minWidth: '160px' }}
                    >
                      <option>All Sections</option>
                      <option>Section A</option>
                      <option>Section B</option>
                      <option>Section C</option>
                    </select>
                  </div>
                </div>

                <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#475569' }}>
                        <th style={{ padding: '12px 16px' }}>Roll No</th>
                        <th style={{ padding: '12px 16px' }}>Student Name</th>
                        <th style={{ padding: '12px 16px' }}>Class & Sec</th>
                        <th style={{ padding: '12px 16px' }}>Parent Contact</th>
                        <th style={{ padding: '12px 16px' }}>Leave Status</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center' }}>Quick Contact Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { roll: '108', name: 'Rohan Gupta', cls: 'Class 10', sec: 'Section A', displayCls: 'Class 10 - A', phone: '+91 98765 43210', status: 'Unexplained Absence', color: '#dc2626' },
                        { roll: '119', name: 'Ananya Iyer', cls: 'Class 10', sec: 'Section B', displayCls: 'Class 10 - B', phone: '+91 98765 43211', status: 'Medical Leave Informed', color: '#2563eb' },
                        { roll: '225', name: 'Harsh Vardhan', cls: 'Class 9', sec: 'Section A', displayCls: 'Class 9 - A', phone: '+91 98765 43212', status: 'Unexplained Absence', color: '#dc2626' },
                        { roll: '310', name: 'Meera Nambiar', cls: 'Class 8', sec: 'Section B', displayCls: 'Class 8 - B', phone: '+91 98765 43213', status: 'Family Event Leave', color: '#166534' }
                      ]
                      .filter(item => absentFilterClass === 'All Classes' || item.cls === absentFilterClass)
                      .filter(item => absentFilterSection === 'All Sections' || item.sec === absentFilterSection)
                      .map((item, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px 16px', fontWeight: '700' }}>{item.roll}</td>
                          <td style={{ padding: '12px 16px', fontWeight: '600', color: '#1e293b' }}>{item.name}</td>
                          <td style={{ padding: '12px 16px', color: '#64748b' }}>{item.displayCls}</td>
                          <td style={{ padding: '12px 16px', fontWeight: '600', color: '#475569' }}>{item.phone}</td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', background: `${item.color}15`, color: item.color }}>{item.status}</span>
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                            <div style={{ display: 'inline-flex', gap: '6px' }}>
                              <button
                                onClick={() => showNotification('info', 'Calling Parent', `Initiating call to ${item.phone}...`)}
                                style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#166534', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}
                              >
                                📞 Call
                              </button>
                              <button
                                onClick={() => showNotification('success', 'SMS Sent', `Absence alert SMS sent to ${item.phone}`)}
                                style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#2563eb', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}
                              >
                                💬 SMS
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TAB 11: CONTINUOUS ABSENTEES (CHRONIC DEFAULTERS) */}
            {attendanceSubTab === 'continuous' && (
              <div>
                <div style={{ background: '#fef2f2', borderRadius: '10px', padding: '12px 16px', border: '1px solid #ef4444', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ fontSize: '24px', background: '#fee2e2', width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>⚠️</div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '14px', fontWeight: '800', color: '#991b1b', margin: '0 0 2px 0' }}>Critical Alert: Immediate Action Required</h3>
                    <p style={{ fontSize: '12px', color: '#b91c1c', margin: 0 }}>
                      The following students have been absent for 3 or more consecutive school days without official leave authorization. Immediate counselor and administrative intervention is required.
                    </p>
                  </div>
                  <button
                    onClick={() => showNotification('error', 'Formal Summons Issued', 'Mandatory parent meeting notices generated and dispatched for all continuous absentees.')}
                    style={{ padding: '8px 14px', borderRadius: '6px', border: 'none', background: '#dc2626', color: '#ffffff', fontWeight: '700', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 2px 5px rgba(220, 38, 38, 0.2)' }}
                  >
                    🚨 Issue Parent Summons
                  </button>
                </div>

                <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#475569' }}>
                        <th style={{ padding: '12px 16px' }}>Roll No</th>
                        <th style={{ padding: '12px 16px' }}>Student Name</th>
                        <th style={{ padding: '12px 16px' }}>Class & Sec</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center' }}>Consecutive Days Absent</th>
                        <th style={{ padding: '12px 16px' }}>Last Attended Date</th>
                        <th style={{ padding: '12px 16px' }}>Follow-up Action Status</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center' }}>Intervention</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { roll: '102', name: 'Diya Patel', cls: 'Class 10 - B', days: '6 Days', last: '2026-07-18', status: 'Parent Phone Unreachable / Met Counselor', color: '#dc2626' },
                        { roll: '214', name: 'Kabir Mehta', cls: 'Class 9 - A', days: '4 Days', last: '2026-07-21', status: 'Home Visit Scheduled for Tomorrow', color: '#d97706' },
                        { roll: '305', name: 'Neha Joshi', cls: 'Class 8 - C', days: '3 Days', last: '2026-07-22', status: 'Medical Certificate Pending Submission', color: '#2563eb' }
                      ].map((item, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px 16px', fontWeight: '700', color: '#991b1b' }}>{item.roll}</td>
                          <td style={{ padding: '12px 16px', fontWeight: '700', color: '#1e293b' }}>{item.name}</td>
                          <td style={{ padding: '12px 16px', color: '#64748b' }}>{item.cls}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                            <span style={{ padding: '4px 12px', borderRadius: '8px', background: '#fee2e2', color: '#991b1b', fontWeight: '800', fontSize: '12px' }}>{item.days}</span>
                          </td>
                          <td style={{ padding: '12px 16px', color: '#475569', fontWeight: '600' }}>{item.last}</td>
                          <td style={{ padding: '12px 16px', fontWeight: '600', color: item.color }}>{item.status}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                            <button
                              onClick={() => showNotification('warning', 'Counselor Assigned', `Assigned school counselor to follow up with ${item.name}`)}
                              style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: '#f59e0b', color: '#ffffff', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                            >
                              🤝 Assign Counselor
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {/* TAB 12: TEACHER ATTENDANCE */}
            {attendanceSubTab === 'teacher_attendance' && (() => {
              const isSaved = savedTeacherAttendanceDb[teacherAttendanceDate] === 'saved';

              // Calculate metrics
              const counts = teacherAttendanceList.reduce((acc, curr) => {
                const s = curr.status.toUpperCase();
                if (s === 'PRESENT') acc.present++;
                else if (s === 'ABSENT') acc.absent++;
                else if (s === 'LATE') acc.late++;
                else if (s === 'HALF_DAY') acc.halfDay++;
                else if (s === 'LEAVE') acc.leave++;
                return acc;
              }, { present: 0, absent: 0, late: 0, halfDay: 0, leave: 0 });

              const handleSave = () => {
                if (teacherAttendanceList.length === 0) {
                  showNotification('error', 'No Records', 'Teacher list is empty. Add teachers first.');
                  return;
                }

                const payload = teacherAttendanceList.map(item => ({
                  teacherId: item.teacherId,
                  attendanceDate: teacherAttendanceDate,
                  status: item.status,
                  checkIn: ['PRESENT', 'LATE', 'HALF_DAY'].includes(item.status) ? item.checkIn : null,
                  checkOut: ['PRESENT', 'LATE', 'HALF_DAY'].includes(item.status) ? item.checkOut : null,
                  remarks: item.remarks || ''
                }));

                setIsLoadingTeacherAttendance(true);
                teacherService.markBulkTeacherAttendance(payload)
                  .then(res => {
                    if (res && res.success) {
                      setSavedTeacherAttendanceDb(prev => ({
                        ...prev,
                        [teacherAttendanceDate]: 'saved'
                      }));
                      showNotification('success', 'Attendance Saved', `Attendance marked successfully for ${teacherAttendanceList.length} teachers.`);
                    } else {
                      showNotification('error', 'Save Failed', res?.error || 'Could not save attendance to the server.');
                    }
                  })
                  .catch(err => {
                    console.error("Error saving bulk teacher attendance:", err);
                    showNotification('error', 'Save Failed', err.message || 'Network error occurred while saving attendance.');
                  })
                  .finally(() => {
                    setIsLoadingTeacherAttendance(false);
                  });
              };

              return (
                <div style={{ marginTop: '16px' }}>
                  {/* Top Control Bar */}
                  <div style={{ background: '#ffffff', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ width: '160px' }}>
                        <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', marginBottom: '4px', display: 'block' }}>📅 Attendance Date</label>
                        <input
                          type="date"
                          value={teacherAttendanceDate}
                          onChange={(e) => setTeacherAttendanceDate(e.target.value)}
                          style={{ width: '100%', padding: '7px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                        />
                      </div>
                      <div style={{ marginTop: '16px' }}>
                        {isSaved ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '13px', fontWeight: '700', color: '#10b981' }}>
                            ✓ Already Marked & Saved
                          </span>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '13px', fontWeight: '700', color: '#ef4444' }}>
                            ● Not Saved Yet
                          </span>
                        )}
                      </div>
                    </div>

                    <div>
                      <button
                        onClick={handleSave}
                        disabled={isLoadingTeacherAttendance}
                        style={{
                          background: '#2563eb',
                          color: '#ffffff',
                          border: 'none',
                          padding: '10px 24px',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '700',
                          cursor: 'pointer',
                          boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)'
                        }}
                      >
                        {isLoadingTeacherAttendance ? '💾 Saving...' : '💾 Save Attendance'}
                      </button>
                    </div>
                  </div>

                  {/* Summary Metric Counters */}
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                    <span style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '6px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '700', color: '#166534' }}>🟢 Present: {counts.present}</span>
                    <span style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: '6px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '700', color: '#991b1b' }}>🔴 Absent: {counts.absent}</span>
                    <span style={{ background: '#fffbeb', border: '1px solid #fef3c7', padding: '6px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '700', color: '#b45309' }}>🟡 Late: {counts.late}</span>
                    <span style={{ background: '#fff7ed', border: '1px solid #ffedd5', padding: '6px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '700', color: '#c2410c' }}>🌗 Half Day: {counts.halfDay}</span>
                    <span style={{ background: '#eff6ff', border: '1px solid #bfdbfe', padding: '6px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '700', color: '#1d4ed8' }}>🔵 Leave: {counts.leave}</span>
                  </div>

                  {/* Teachers Table Register */}
                  <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569' }}>Teacher Info</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569' }}>Dept & Designation</th>
                          <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '700', color: '#475569' }}>Attendance Status</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569', width: '180px' }}>Timings (Check-In / Out)</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569' }}>Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teacherAttendanceList.map((item, idx) => {
                          const isTimingEnabled = ['PRESENT', 'LATE', 'HALF_DAY'].includes(item.status);
                          return (
                            <tr key={item.teacherId || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              {/* Teacher Info */}
                              <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                                <div style={{ fontWeight: '700', color: '#1e293b' }}>{item.name}</div>
                                <div style={{ fontSize: '11px', color: '#64748b' }}>ID: {item.teacherId}</div>
                              </td>

                              {/* Dept & Designation */}
                              <td style={{ padding: '12px 16px', fontSize: '13px', color: '#475569' }}>
                                <div style={{ fontWeight: '600' }}>{item.department}</div>
                                <div style={{ fontSize: '11px', color: '#64748b' }}>{item.designation}</div>
                              </td>

                              {/* Attendance Status Buttons */}
                              <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                <div style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: '8px', padding: '3px', gap: '2px' }}>
                                  {[
                                    { label: 'Present', val: 'PRESENT', color: '#22c55e' },
                                    { label: 'Absent', val: 'ABSENT', color: '#ef4444' },
                                    { label: 'Late', val: 'LATE', color: '#ca8a04' },
                                    { label: 'Half Day', val: 'HALF_DAY', color: '#ea580c' },
                                    { label: 'Leave', val: 'LEAVE', color: '#2563eb' }
                                  ].map((opt) => {
                                    const active = item.status === opt.val;
                                    return (
                                      <button
                                        key={opt.val}
                                        onClick={() => {
                                          const newList = [...teacherAttendanceList];
                                          newList[idx].status = opt.val;
                                          setTeacherAttendanceList(newList);
                                        }}
                                        style={{
                                          padding: '4px 10px',
                                          borderRadius: '6px',
                                          border: 'none',
                                          background: active ? opt.color : 'transparent',
                                          color: active ? '#ffffff' : '#64748b',
                                          fontSize: '11px',
                                          fontWeight: active ? '700' : '600',
                                          cursor: 'pointer',
                                          transition: 'all 0.15s ease'
                                        }}
                                      >
                                        {opt.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </td>

                              {/* Timing inputs */}
                              <td style={{ padding: '12px 16px' }}>
                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                  <input
                                    type="time"
                                    value={item.checkIn}
                                    disabled={!isTimingEnabled}
                                    onChange={(e) => {
                                      const newList = [...teacherAttendanceList];
                                      newList[idx].checkIn = e.target.value;
                                      setTeacherAttendanceList(newList);
                                    }}
                                    style={{
                                      width: '75px',
                                      padding: '4px 6px',
                                      borderRadius: '6px',
                                      border: '1px solid #cbd5e1',
                                      fontSize: '12px',
                                      opacity: isTimingEnabled ? 1 : 0.5
                                    }}
                                  />
                                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>-</span>
                                  <input
                                    type="time"
                                    value={item.checkOut}
                                    disabled={!isTimingEnabled}
                                    onChange={(e) => {
                                      const newList = [...teacherAttendanceList];
                                      newList[idx].checkOut = e.target.value;
                                      setTeacherAttendanceList(newList);
                                    }}
                                    style={{
                                      width: '75px',
                                      padding: '4px 6px',
                                      borderRadius: '6px',
                                      border: '1px solid #cbd5e1',
                                      fontSize: '12px',
                                      opacity: isTimingEnabled ? 1 : 0.5
                                    }}
                                  />
                                </div>
                              </td>

                              {/* Remarks */}
                              <td style={{ padding: '12px 16px' }}>
                                <input
                                  type="text"
                                  placeholder="Reason, check-in note..."
                                  value={item.remarks}
                                  onChange={(e) => {
                                    const newList = [...teacherAttendanceList];
                                    newList[idx].remarks = e.target.value;
                                    setTeacherAttendanceList(newList);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '6px 10px',
                                    borderRadius: '6px',
                                    border: '1px solid #e2e8f0',
                                    fontSize: '12px',
                                    boxSizing: 'border-box'
                                  }}
                                />
                              </td>
                            </tr>
                          );
                        })}

                        {teacherAttendanceList.length === 0 && (
                          <tr>
                            <td colSpan="5" style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>
                              {isLoadingTeacherAttendance ? '🔄 Fetching teaching staff list...' : 'No teacher records found in the database.'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            {/* TAB 13: TEACHER DAILY */}
            {attendanceSubTab === 'teacher_daily' && (
              <div style={{ background: '#ffffff', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0', textAlign: 'center', marginTop: '20px' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#1e293b' }}>📅 Daily Teacher Register</h3>
                <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>Mark daily attendance for teachers here.</p>
              </div>
            )}

            {/* TAB 14: TEACHER MONTHLY */}
            {attendanceSubTab === 'teacher_monthly' && (
              <div style={{ background: '#ffffff', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0', textAlign: 'center', marginTop: '20px' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#1e293b' }}>🗓️ Monthly Teacher Matrix</h3>
                <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>View monthly attendance trends and summaries for staff.</p>
              </div>
            )}

            {/* TAB 15: TEACHER LATE ENTRY */}
            {attendanceSubTab === 'teacher_late' && (
              <div style={{ background: '#ffffff', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0', textAlign: 'center', marginTop: '20px' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#1e293b' }}>⏰ Late Entry Register</h3>
                <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>Track teachers arriving late and manage deductions or warnings.</p>
              </div>
            )}

            {/* TAB 16: TEACHER HALF DAY */}
            {attendanceSubTab === 'teacher_halfday' && (
              <div style={{ background: '#ffffff', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0', textAlign: 'center', marginTop: '20px' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#1e293b' }}>🌗 Half Day Tracker</h3>
                <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>Monitor half-day leaves taken by teachers.</p>
              </div>
            )}

            {/* TAB 17: TEACHER LEAVE SUMMARY */}
            {attendanceSubTab === 'teacher_leave' && (
              <div style={{ background: '#ffffff', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0', textAlign: 'center', marginTop: '20px' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#1e293b' }}>📝 Leave Summary & Balances</h3>
                <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>Check casual leaves, sick leaves, and approvals.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'fees' && (
          <div className="fees-workspace-container" style={{ padding: '8px' }}>
            {/* Payment Verifications Workspace */}
            {feesSubTab === 'verifications' && (
              <PaymentVerifications 
                orgId={orgData?.orgId || orgData?.id || localStorage.getItem('orgId') || ''} 
                token={localStorage.getItem('token') || localStorage.getItem('authToken') || ''} 
                showNotification={(type, title, msg) => setNotification({ type, message: `${title}: ${msg}` })} 
              />
            )}

            {/* 1. FEE MANAGEMENT WORKSPACE */}
            {feesSubTab === 'management' && (
              <FeeManagementWorkspace
                students={students}
                fees={fees}
                feeStructures={feeStructures}
                loading={loadingFees || loadingStudents || loading}
                orgData={orgData}
                userRole={userRole}
                feeService={feeService}
                loadFees={loadFees}
                handleOpenEditFeeModal={handleOpenEditFeeModal}
                setSelectedStudentForCollection={setSelectedStudentForCollection}
                setCollectionFeeRecord={setCollectionFeeRecord}
                setFeesSubTab={setFeesSubTab}
                setSelectedStructure={setSelectedStructure}
                setStructureFormData={setStructureFormData}
                setShowCreateStructureModal={setShowCreateStructureModal}
                setAssignPreview={setAssignPreview}
                setAssignFormData={setAssignFormData}
                setShowAssignStructureModal={setShowAssignStructureModal}
                handleExportFeeExcel={handleExportFeeExcel}
                handleExportFeePDF={handleExportFeePDF}
                setSelectedStudentForHistory={setSelectedStudentForHistory}
                setShowHistoryModal={setShowHistoryModal}
                setLoadingPaymentHistory={setLoadingPaymentHistory}
                setHistoryPayments={setHistoryPayments}
                setAlertTargetStudents={setAlertTargetStudents}
                setAlertCustomTitle={setAlertCustomTitle}
                setAlertCustomMessage={setAlertCustomMessage}
                setShowFeeAlertModal={setShowFeeAlertModal}
                setDraftToast={setDraftToast}
                showNotification={showNotification}
                searchTerm={feeSearchTerm}
                setSearchTerm={setFeeSearchTerm}
                filters={feeFilters}
                setFilters={setFeeFilters}
                customMinDueAmount={feeCustomMinDueAmount}
                setCustomMinDueAmount={setFeeCustomMinDueAmount}
                customMaxDueAmount={feeCustomMaxDueAmount}
                setCustomMaxDueAmount={setFeeCustomMaxDueAmount}
                showAdvancedFilters={feeShowAdvancedFilters}
                setShowAdvancedFilters={setFeeShowAdvancedFilters}
                currentPage={feeCurrentPage}
                setCurrentPage={setFeeCurrentPage}
                pageSize={feePageSize}
                setPageSize={setFeePageSize}
              />
            )}

            
            {/* FEE ALERT NOTIFICATION MODAL */}
            {showFeeAlertModal && (
              <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 1060 }}>
                <div className="modal-dialog modal-dialog-centered modal-lg">
                  <div className="modal-content" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}>
                    <div className="modal-header" style={{ background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', borderBottom: '1px solid #fde68a', padding: '18px 24px', borderTopLeftRadius: '16px', borderTopRightRadius: '16px' }}>
                      <div>
                        <h5 className="modal-title" style={{ fontWeight: '800', color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          📢 Send Fee Alert & Parent Notification
                        </h5>
                        <p style={{ margin: '2px 0 0 0', fontSize: '12.5px', color: '#b45309' }}>
                          Targeting {alertTargetStudents.length} student parent(s) for fee payment reminder
                        </p>
                      </div>
                      <button type="button" className="btn-close" onClick={() => setShowFeeAlertModal(false)}></button>
                    </div>

                    <div className="modal-body" style={{ padding: '24px' }}>
                      <div style={{ marginBottom: '16px', background: '#f8fafc', padding: '14px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', marginBottom: '6px' }}>Recipients ({alertTargetStudents.length}):</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '100px', overflowY: 'auto' }}>
                          {alertTargetStudents.map(s => {
                            const f = fees.find(item => item.studentId === s.studentId);
                            const total = f?.totalFees != null ? Number(f.totalFees) : ((f?.items && f.items.length > 0) ? f.items.reduce((acc, item) => acc + (item.netAmount || item.originalAmount || 0), 0) : 0);
                            const paid = f?.paidFees != null ? Number(f.paidFees) : 0;
                            const rem = f?.pendingFee != null ? Number(f.pendingFee) : Math.max(0, total - (f?.concessionAmount || 0) + (f?.penaltyAmount || 0) - paid);
                            return (
                              <span key={s.studentId} style={{ background: '#fff', border: '1px solid #cbd5e1', padding: '4px 10px', borderRadius: '9999px', fontSize: '12px', color: '#1e293b', fontWeight: '600' }}>
                                👤 {s.name} ({s.className || 'Sec A'}) • <strong style={{ color: '#dc2626' }}>₹{rem.toLocaleString('en-IN')} Due</strong>
                              </span>
                            );
                          })}
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label" style={{ fontWeight: '600', color: '#334155', fontSize: '13px' }}>Alert Title *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={alertCustomTitle}
                          onChange={(e) => setAlertCustomTitle(e.target.value)}
                          placeholder="e.g. Fee Payment Reminder 📢"
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label" style={{ fontWeight: '600', color: '#334155', fontSize: '13px' }}>Notification Message to Parents *</label>
                        <textarea
                          className="form-control"
                          rows="4"
                          value={alertCustomMessage}
                          onChange={(e) => setAlertCustomMessage(e.target.value)}
                          placeholder="Enter your fee alert message for parents..."
                        ></textarea>
                        <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '4px' }}>
                          💡 Parents will instantly receive this notification on their mobile app.
                        </div>
                      </div>
                    </div>

                    <div className="modal-footer" style={{ borderTop: '1px solid #f1f5f9', padding: '16px 24px', background: '#f8fafc', borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px' }}>
                      <button type="button" className="btn btn-light" onClick={() => setShowFeeAlertModal(false)}>Cancel</button>
                      <button
                        type="button"
                        className="btn btn-warning"
                        onClick={handleSendFeeAlerts}
                        disabled={isSendingAlert}
                        style={{ fontWeight: '700', background: '#f59e0b', borderColor: '#d97706', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                      >
                        {isSendingAlert ? 'Sending Notifications...' : '🚀 Send Alert Notification'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 2. FEE COLLECTION WORKSPACE */}
            {feesSubTab === 'collection' && (
              <div>
                {/* Header with back button */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedStudentForCollection(null);
                      setFeesSubTab('management');
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontWeight: '600',
                      fontSize: '13px',
                      padding: '8px 16px',
                      borderRadius: '999px',
                      background: '#eff6ff',
                      border: '1.5px solid #bfdbfe',
                      color: '#1d4ed8',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      whiteSpace: 'nowrap',
                      flexShrink: 0
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#dbeafe'; e.currentTarget.style.borderColor = '#93c5fd'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = '#bfdbfe'; }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 12H5M12 5l-7 7 7 7"/>
                    </svg>
                    Fee Management
                  </button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#94a3b8', fontSize: '13px' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                    <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '14px' }}>Fee Collection Desk</span>
                  </div>
                </div>

                {/* Quick Student Search Bar */}
                <div style={{ background: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Type student name or ID to collect fees..."
                      value={collectionSearchTerm}
                      onChange={(e) => {
                        setCollectionSearchTerm(e.target.value);
                        if (e.target.value.trim().length > 1) {
                          const matching = students.filter(s => 
                            s.name?.toLowerCase().includes(e.target.value.toLowerCase()) ||
                            s.studentId?.toLowerCase().includes(e.target.value.toLowerCase())
                          );
                          setCollectionStudentList(matching);
                        } else {
                          setCollectionStudentList([]);
                        }
                      }}
                    />
                    
                    {/* Search Suggestions Panel */}
                    {collectionStudentList.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', zIndex: 1000, maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                        {collectionStudentList.map(s => (
                          <div
                            key={s.studentId}
                            onClick={async () => {
                              setSelectedStudentForCollection(s);
                              setCollectionSearchTerm('');
                              setCollectionStudentList([]);
                              
                              // Load fee assignment for this student from state first
                              const matchedFee = fees.find(f => f.studentId === s.studentId);
                              setCollectionFeeRecord(matchedFee || null);

                              try {
                                const response = await feeService.getFeeByStudentAndOrg(s.studentId, orgData?.orgId);
                                if (Array.isArray(response) && response.length > 0) {
                                  setCollectionFeeRecord(response[0]);
                                } else if (response && response.items && Array.isArray(response.items) && response.items.length > 0) {
                                  setCollectionFeeRecord(matchedFee || response.items[0]);
                                }
                              } catch (err) {
                                console.error('Failed to load student fee:', err);
                              }
                              
                              // Load payment transactions
                              const txs = await feeService.getTransactionHistory(s.studentId, orgData?.orgId);
                              setCollectionPayments(txs || []);
                            }}
                            style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                            className="search-item"
                          >
                            <span style={{ fontWeight: '600', color: '#1e293b' }}>{s.name}</span>
                            <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px' }}>({s.studentId}) • {s.className}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Selected Student Billing Workspace */}
                {selectedStudentForCollection ? (() => {
                  const effectiveFee = collectionFeeRecord || fees.find(f => f.studentId === selectedStudentForCollection.studentId) || null;
                  
                  const effectiveItems = (effectiveFee?.items && effectiveFee.items.length > 0)
                    ? effectiveFee.items
                    : (effectiveFee && Number(effectiveFee.totalFees || 0) > 0)
                      ? [{
                          id: effectiveFee.feeId || effectiveFee.id || 'primary',
                          feeType: effectiveFee.feeStructureName || (effectiveFee.type && effectiveFee.type !== 'tuition' ? effectiveFee.type : 'Tuition Fee'),
                          originalAmount: Number(effectiveFee.totalFees || 0),
                          netAmount: Number(effectiveFee.totalFees || 0),
                          paidAmount: Number(effectiveFee.paidFees || 0)
                        }]
                      : [];

                  const effectiveInstallments = (effectiveFee?.installments && effectiveFee.installments.length > 0)
                    ? effectiveFee.installments
                    : (effectiveFee && Number(effectiveFee.totalFees || 0) > 0)
                      ? [{
                          id: effectiveFee.feeId || 'term1',
                          installmentName: 'Term 1',
                          amount: Number(effectiveFee.totalFees || 0),
                          paidAmount: Number(effectiveFee.paidFees || 0),
                          dueDate: effectiveFee.dueDate || null,
                          status: effectiveFee.status || 'UNPAID'
                        }]
                      : [];

                  const totalFeeVal = effectiveFee?.totalFees != null ? Number(effectiveFee.totalFees) : (effectiveItems.reduce((s, it) => s + (it.netAmount || it.originalAmount || 0), 0));
                  const paidFeeVal = effectiveFee?.paidFees != null ? Number(effectiveFee.paidFees) : (effectiveItems.reduce((s, it) => s + (it.paidAmount || 0), 0));
                  const concessionVal = effectiveFee?.concessionAmount != null ? Number(effectiveFee.concessionAmount) : 0;
                  const penaltyVal = effectiveFee?.penaltyAmount != null ? Number(effectiveFee.penaltyAmount) : 0;
                  const overallItemsRemaining = effectiveFee?.pendingFee != null
                    ? Number(effectiveFee.pendingFee)
                    : Math.max(0, totalFeeVal - concessionVal + penaltyVal - paidFeeVal);

                  const activeItem = selectedFeeItemId ? effectiveItems.find(i => String(i.id) === String(selectedFeeItemId)) : null;
                  const activeInst = selectedInstallmentId ? effectiveInstallments.find(i => String(i.id) === String(selectedInstallmentId)) : null;

                  const targetTitle = activeItem ? activeItem.feeType : (activeInst ? activeInst.installmentName : 'Overall Balance');
                  const activeNet = activeItem ? (activeItem.netAmount || activeItem.originalAmount || 0) : (activeInst ? (activeInst.amount || 0) : totalFeeVal);
                  const activePaid = activeItem ? (activeItem.paidAmount || 0) : (activeInst ? (activeInst.paidAmount || 0) : paidFeeVal);
                  const activeDue = activeItem ? Math.max(0, activeNet - activePaid) : (activeInst ? Math.max(0, activeNet - activePaid) : overallItemsRemaining);

                  const isOverallSelected = !selectedFeeItemId && !selectedInstallmentId;

                  const normalizeCompName = (name) => {
                    if (!name) return 'School Fee';
                    const clean = String(name).trim();
                    const lower = clean.toLowerCase();
                    if (lower.includes('school')) return 'School Fee';
                    if (lower.includes('tuition')) return 'Tuition Fee';
                    if (lower.includes('dress') || lower.includes('uniform')) return 'Dress Fees';
                    if (lower.includes('transport') || lower.includes('bus')) return 'Transport Fee';
                    if (lower.includes('hostel')) return 'Hostel Fee';
                    if (lower.includes('exam')) return 'Exam Fee';
                    return clean.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
                  };

                  const parseTxDate = (dateVal) => {
                    if (!dateVal) return null;
                    if (Array.isArray(dateVal)) {
                      return new Date(dateVal[0], dateVal[1] - 1, dateVal[2] || 1, dateVal[3] || 0, dateVal[4] || 0);
                    }
                    const d = new Date(dateVal);
                    return isNaN(d.getTime()) ? null : d;
                  };

                  const resolveTxCompName = (tx, feeRecord) => {
                    if (tx.feeComponentName && tx.feeComponentName.trim() !== '') return normalizeCompName(tx.feeComponentName);
                    if (tx.itemId && effectiveItems.length > 0) {
                      const foundItem = effectiveItems.find(i => String(i.id) === String(tx.itemId));
                      if (foundItem?.feeType) return normalizeCompName(foundItem.feeType);
                    }
                    if (tx.installmentName) {
                      const lower = tx.installmentName.toLowerCase();
                      if (lower.includes('school')) return 'School Fee';
                      if (lower.includes('tuition')) return 'Tuition Fee';
                      if (lower.includes('dress') || lower.includes('uniform')) return 'Dress Fees';
                      if (lower.includes('transport') || lower.includes('bus')) return 'Transport Fee';
                      if (lower.includes('hostel')) return 'Hostel Fee';
                      if (lower.includes('exam')) return 'Exam Fee';
                      if (effectiveItems.length > 0) {
                        return normalizeCompName(effectiveItems[0].feeType);
                      }
                      return 'School Fee';
                    }
                    if (effectiveItems.length > 0) {
                      return normalizeCompName(effectiveItems[0].feeType);
                    }
                    return 'School Fee';
                  };

                  const availableFilters = (() => {
                    const filterMap = new Map();
                    filterMap.set('ALL', 'All');
                    if (effectiveItems.length > 0) {
                      effectiveItems.forEach(i => { if (i.feeType) filterMap.set(normalizeCompName(i.feeType), normalizeCompName(i.feeType)); });
                    }
                    if (collectionPayments?.length > 0) {
                      collectionPayments.forEach(tx => {
                        const res = resolveTxCompName(tx, effectiveFee);
                        if (res) filterMap.set(res, res);
                      });
                    }
                    if (filterMap.size === 1) return ['ALL', 'School Fee', 'Tuition Fee', 'Dress Fees', 'Transport Fee'];
                    return Array.from(filterMap.keys());
                  })();

                  let filteredTx = [...collectionPayments];

                  if (historyComponentFilter !== 'ALL') {
                    filteredTx = filteredTx.filter(tx => {
                      const resComp = resolveTxCompName(tx, effectiveFee);
                      const targetComp = normalizeCompName(historyComponentFilter);
                      return resComp === targetComp;
                    });
                  }

                  if (historyTimeFilter === 'THIS_MONTH') {
                    const now = new Date();
                    filteredTx = filteredTx.filter(tx => {
                      const d = parseTxDate(tx.paidDate || tx.createdAt);
                      if (!d) return true;
                      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                    });
                  }

                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '20px', alignItems: 'start' }}>
                      {/* Left Column: Student Details, Installments and Adjustments */}
                      <div>
                        {/* Student Header Card */}
                        <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '14px', padding: '20px 24px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '14px', boxShadow: '0 4px 16px rgba(0,0,0,0.18)' }}>
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                            <div style={{ width: '52px', height: '52px', borderRadius: '9999px', background: 'linear-gradient(135deg, #3b82f6, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: '800', boxShadow: '0 2px 8px rgba(99,102,241,0.4)', flexShrink: 0 }}>
                              {selectedStudentForCollection.name?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', letterSpacing: '-0.2px' }}>{selectedStudentForCollection.name}</h3>
                              <div style={{ fontSize: '12.5px', color: '#94a3b8', marginTop: '4px' }}>
                                ID: {selectedStudentForCollection.studentId} • Class: {selectedStudentForCollection.className || '—'} (Sec {selectedStudentForCollection.section || '—'})
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Left Column: Fee Component Cards & Installments */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          
                          {/* Fee Components List Header & Cards */}
                          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
                            <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>🏷️ Fee Components</h4>
                            
                            {effectiveItems.length > 0 ? (
                              <div style={{ display: 'grid', gap: '12px' }}>
                                {effectiveItems.map(item => {
                                  const net = item.netAmount || item.originalAmount || 0;
                                  const paid = item.paidAmount || 0;
                                  const remaining = Math.max(0, net - paid);
                                  const isSelected = String(selectedFeeItemId) === String(item.id);

                                  return (
                                    <div
                                      key={item.id}
                                      onClick={() => {
                                        setSelectedFeeItemId(String(item.id));
                                        setSelectedInstallmentId('');
                                        setPaymentAmount(remaining > 0 ? remaining : '');
                                      }}
                                      style={{
                                        background: isSelected ? '#eff6ff' : '#f8fafc',
                                        border: isSelected ? '2px solid #2563eb' : '1px solid #cbd5e1',
                                        borderRadius: '10px',
                                        padding: '16px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        boxShadow: isSelected ? '0 4px 12px rgba(37,99,235,0.12)' : 'none'
                                      }}
                                    >
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                          <div style={{ fontSize: '22px' }}>
                                            {item.feeType?.toLowerCase().includes('dress') || item.feeType?.toLowerCase().includes('uniform') ? '👕' :
                                             item.feeType?.toLowerCase().includes('tuition') ? '📚' :
                                             item.feeType?.toLowerCase().includes('transport') || item.feeType?.toLowerCase().includes('bus') ? '🚌' :
                                             item.feeType?.toLowerCase().includes('hostel') ? '🏢' : '🏫'}
                                          </div>
                                          <div>
                                            <div style={{ fontWeight: '700', fontSize: '15px', color: '#0f172a' }}>{item.feeType}</div>
                                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                              Total: ₹{net.toLocaleString()} • Paid: <span style={{ color: '#166534', fontWeight: '600' }}>₹{paid.toLocaleString()}</span>
                                            </div>
                                          </div>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                          <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '600' }}>Remaining</div>
                                            <div style={{ fontSize: '15px', fontWeight: '800', color: remaining > 0 ? '#dc2626' : '#166534' }}>
                                              ₹{remaining.toLocaleString()}
                                            </div>
                                          </div>
                                          <div style={{ fontSize: '18px', color: isSelected ? '#2563eb' : '#94a3b8', fontWeight: '700' }}>›</div>
                                        </div>
                                      </div>

                                      {/* Installments Schedule underneath component */}
                                      {isSelected && 
                                       effectiveInstallments.length > 0 && (() => {
                                        const totalPaidSum = effectiveItems.reduce((sum, it) => sum + (it.paidAmount || 0), 0);
                                        let tempPaid = totalPaidSum;

                                        return (
                                          <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px dashed #cbd5e1' }}>
                                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '8px' }}>🗓️ Associated Term Schedule</div>
                                            <div style={{ display: 'grid', gap: '6px' }}>
                                              {effectiveInstallments.map(inst => {
                                                const instAmt = inst.amount || 0;
                                                let computedPaid = 0;
                                                if (tempPaid >= instAmt) {
                                                  computedPaid = instAmt;
                                                  tempPaid -= instAmt;
                                                } else {
                                                  computedPaid = tempPaid;
                                                  tempPaid = 0;
                                                }
                                                const instRemaining = Math.max(0, instAmt - computedPaid);

                                                return (
                                                  <div key={inst.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '6px 10px', background: '#fff', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                                    <span style={{ fontWeight: '600', color: '#1e293b' }}>{inst.installmentName}</span>
                                                    <span>
                                                      {instRemaining === 0 ? (
                                                        <span style={{ color: '#166534', fontWeight: '700' }}>✓ Paid</span>
                                                      ) : (
                                                        <span style={{ color: '#dc2626', fontWeight: '600' }}>₹{instRemaining.toLocaleString()} remaining</span>
                                                      )}
                                                    </span>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
                                No breakdown components configured.
                              </div>
                            )}
                          </div>

                          {/* Separate Quick Option Card for Overall Outstanding */}
                          <div
                            onClick={() => {
                              setSelectedFeeItemId('');
                              setSelectedInstallmentId('');
                              setPaymentAmount(overallItemsRemaining > 0 ? overallItemsRemaining : '');
                            }}
                            style={{
                              marginTop: '16px',
                              background: isOverallSelected ? '#fefce8' : '#f8fafc',
                              border: isOverallSelected ? '2px solid #eab308' : '1px solid #cbd5e1',
                              borderRadius: '10px',
                              padding: '16px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ fontSize: '22px' }}>🌐</div>
                                <div>
                                  <div style={{ fontWeight: '700', fontSize: '15px', color: '#0f172a' }}>Overall Outstanding</div>
                                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Collect combined outstanding balance across all components</div>
                                </div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '600' }}>Total Remaining</div>
                                <div style={{ fontSize: '16px', fontWeight: '800', color: overallItemsRemaining > 0 ? '#dc2626' : '#166534' }}>
                                  ₹{overallItemsRemaining.toLocaleString()}
                                </div>
                              </div>
                            </div>

                            {/* Render Term Schedule under Overall Outstanding when selected */}
                            {isOverallSelected && effectiveInstallments.length > 0 && (() => {
                              const totalPaidSum = effectiveItems.reduce((sum, item) => sum + (item.paidAmount || 0), 0);
                              let tempPaid = totalPaidSum;

                              return (
                                <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px dashed #eab308' }}>
                                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#854d0e', marginBottom: '8px' }}>🗓️ Overall Term Schedule</div>
                                  <div style={{ display: 'grid', gap: '6px' }}>
                                    {effectiveInstallments.map(inst => {
                                      const instAmt = inst.amount || 0;
                                      let computedPaid = 0;
                                      if (tempPaid >= instAmt) {
                                        computedPaid = instAmt;
                                        tempPaid -= instAmt;
                                      } else {
                                        computedPaid = tempPaid;
                                        tempPaid = 0;
                                      }
                                      const instRemaining = Math.max(0, instAmt - computedPaid);

                                      return (
                                        <div key={inst.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '6px 10px', background: '#fff', borderRadius: '6px', border: '1px solid #fef08a' }}>
                                          <span style={{ fontWeight: '600', color: '#1e293b' }}>{inst.installmentName}</span>
                                          <span>
                                            {instRemaining === 0 ? (
                                              <span style={{ color: '#166534', fontWeight: '700' }}>✓ Paid</span>
                                            ) : (
                                              <span style={{ color: '#dc2626', fontWeight: '600' }}>₹{instRemaining.toLocaleString()} remaining</span>
                                            )}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>

                          {/* Auditable Adjustments Log */}
                          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                              <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>🛡️ Auditable Concessions & Penalties</h4>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  className="btn btn-sm btn-light"
                                  onClick={() => {
                                    setConcessionFormData({ amount: '', reason: '' });
                                    setShowConcessionModal(true);
                                  }}
                                  style={{ color: '#166534', fontWeight: '600' }}
                                >
                                  🎁 Apply Concession
                                </button>
                                <button
                                  className="btn btn-sm btn-light"
                                  onClick={() => {
                                    setPenaltyFormData({ amount: '', reason: '' });
                                    setShowPenaltyModal(true);
                                  }}
                                  style={{ color: '#991b1b', fontWeight: '600' }}
                                >
                                  ⚠️ Apply Penalty
                                </button>
                              </div>
                            </div>

                            {effectiveFee?.adjustments && effectiveFee.adjustments.length > 0 ? (
                              <div style={{ display: 'grid', gap: '10px' }}>
                                {effectiveFee.adjustments.map(adj => (
                                  <div key={adj.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#f8fafc', borderRadius: '8px', fontSize: '13px' }}>
                                    <div>
                                      <span style={{ fontWeight: '700', color: adj.type === 'PENALTY' ? '#dc2626' : '#166534', marginRight: '6px' }}>[{adj.type}]</span>
                                      <span style={{ color: '#334155' }}>{adj.reason || 'No reason provided'}</span>
                                      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>By {adj.createdBy} on {new Date(adj.createdAt).toLocaleDateString()}</div>
                                    </div>
                                    <div style={{ fontWeight: '700', color: adj.type === 'PENALTY' ? '#dc2626' : '#166534' }}>
                                      {adj.type === 'PENALTY' ? '+' : '-'} ₹{adj.amount.toLocaleString()}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
                                No adjustments or concessions have been recorded for this student.
                              </div>
                            )}
                          </div>

                          {/* Component-First Payment History Section */}
                          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
                              <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>🧾 Payment History</h4>
                              
                              {/* Dynamic Component Filter Pills */}
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                {availableFilters.map(comp => (
                                  <button
                                    key={comp}
                                    type="button"
                                    className={`btn btn-xs ${historyComponentFilter === comp ? 'btn-primary' : 'btn-light'}`}
                                    onClick={() => setHistoryComponentFilter(comp)}
                                    style={{ padding: '3px 9px', fontSize: '11.5px', borderRadius: '6px', fontWeight: '600' }}
                                  >
                                    {comp === 'ALL' ? 'All' : comp}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Time Horizon Filter Bar */}
                            <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                              <button
                                type="button"
                                className={`btn btn-xs ${historyTimeFilter === 'ALL' ? 'btn-dark' : 'btn-light'}`}
                                onClick={() => setHistoryTimeFilter('ALL')}
                                style={{ borderRadius: '6px', fontSize: '11px', fontWeight: '600' }}
                              >
                                📅 All Time
                              </button>
                              <button
                                type="button"
                                className={`btn btn-xs ${historyTimeFilter === 'THIS_MONTH' ? 'btn-dark' : 'btn-light'}`}
                                onClick={() => setHistoryTimeFilter('THIS_MONTH')}
                                style={{ borderRadius: '6px', fontSize: '11px', fontWeight: '600' }}
                              >
                                🗓️ This Month
                              </button>
                            </div>

                            {filteredTx.length === 0 ? (
                              <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic', background: '#f8fafc', borderRadius: '8px' }}>
                                No payment receipts found for this selection.
                              </div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {Object.entries(
                                  filteredTx.reduce((groups, tx) => {
                                    const d = parseTxDate(tx.paidDate || tx.createdAt);
                                    const key = d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase() : 'RECENT';
                                    if (!groups[key]) groups[key] = [];
                                    groups[key].push(tx);
                                    return groups;
                                  }, {})
                                ).map(([dateStr, txList]) => (
                                  <div key={dateStr}>
                                    <div style={{ fontSize: '11.5px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <span>🗓️</span> {dateStr}
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                      {txList.map(tx => {
                                        const resName = resolveTxCompName(tx, effectiveFee);
                                        const isCash = (tx.paymentMode || tx.mode || '').toUpperCase() === 'CASH';

                                        return (
                                          <div key={tx.id || tx.transactionId} style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', background: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                              <div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                  <span style={{ fontWeight: '800', color: '#0f172a', fontSize: '14.5px' }}>{resName}</span>
                                                  <span style={{ background: isCash ? '#ecfdf5' : '#eff6ff', color: isCash ? '#047857' : '#1d4ed8', fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '6px', border: isCash ? '1px solid #a7f3d0' : '1px solid #bfdbfe' }}>
                                                    {tx.paymentMode || tx.mode || 'CASH'}
                                                  </span>
                                                  <span style={{ background: '#f8fafc', color: '#475569', fontSize: '11px', padding: '2px 6px', borderRadius: '4px', border: '1px solid #e2e8f0', fontFamily: 'monospace' }}>
                                                    {tx.transactionId || `TXN-${tx.id}`}
                                                  </span>
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                                                  Collected by: <span style={{ fontWeight: '600', color: '#334155' }}>{tx.collectedBy || 'Admin'}</span>
                                                </div>
                                              </div>

                                              <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: '16px', fontWeight: '900', color: '#166534' }}>
                                                  + ₹{(tx.paidAmount || tx.amount || 0).toLocaleString('en-IN')}
                                                </div>
                                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', marginTop: '6px' }}>
                                                  <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => {
                                                      setReceiptData(tx);
                                                      setShowReceiptModal(true);
                                                    }}
                                                    style={{ padding: '3px 8px', fontSize: '11.5px', fontWeight: '700', borderRadius: '6px' }}
                                                  >
                                                    🖨️ Receipt
                                                  </button>
                                                  {isCash && (
                                                    <button
                                                      type="button"
                                                      className="btn btn-sm btn-outline-warning"
                                                      onClick={() => handleOpenEditPaymentModal(tx, selectedStudentForCollection)}
                                                      style={{ padding: '3px 8px', fontSize: '11.5px', fontWeight: '700', borderRadius: '6px', color: '#b45309', borderColor: '#fde68a', background: '#fffbeb' }}
                                                      title="Admin: Correct manual cash payment amount"
                                                    >
                                                      ✏️ Edit Amount
                                                    </button>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right Column: Redesigned 3-Step Collect Payment Panel */}
                      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                        <div>
                          <div style={{ borderBottom: '2px solid #f1f5f9', paddingBottom: '14px', marginBottom: '20px' }}>
                            <h3 style={{ margin: '0 0 6px 0', fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>
                              🧾 Collect {targetTitle}
                            </h3>
                            <div style={{ fontSize: '12.5px', color: '#475569', fontWeight: '600' }}>
                              Student: <span style={{ color: '#0f172a', fontWeight: '700' }}>{selectedStudentForCollection?.name}</span> ({selectedStudentForCollection?.className} • Sec {selectedStudentForCollection?.section})
                            </div>
                          </div>

                          <form onSubmit={async (e) => {
                            e.preventDefault();
                            setLoading(true);
                            try {
                              const breakdownArray = (!selectedFeeItemId && !selectedInstallmentId && effectiveItems?.length > 0)
                                ? effectiveItems
                                    .filter(item => componentPaymentAllocations[item.id] && parseFloat(componentPaymentAllocations[item.id]) > 0)
                                    .map(item => ({
                                      itemId: item.id,
                                      feeType: item.feeType,
                                      amount: parseFloat(componentPaymentAllocations[item.id])
                                    }))
                                : null;

                              const payload = {
                                orgId: orgData?.orgId,
                                studentId: selectedStudentForCollection.studentId,
                                paidAmount: parseFloat(paymentAmount),
                                transactionId: (paymentMode !== 'CASH' && paymentRefNumber) ? paymentRefNumber : ('TXN-' + Date.now()),
                                mode: paymentMode,
                                status: 'SUCCESS',
                                type: effectiveFee?.type || 'General',
                                itemId: activeItem ? activeItem.id : null,
                                installmentId: activeInst ? activeInst.id : null,
                                installmentName: targetTitle,
                                componentBreakdown: breakdownArray && breakdownArray.length > 0 ? breakdownArray : null,
                                collectedBy: userRole || 'College Admin'
                              };

                              const res = await feeService.processPayment(payload);
                              if (res.success) {
                                showNotification('success', 'Success', 'Payment collected successfully!');
                                setPaymentAmount('');
                                setPaymentRefNumber('');
                                setSelectedInstallmentId('');
                                setSelectedFeeItemId('');
                                setComponentPaymentAllocations({});
                                
                                // Set receipt data
                                setReceiptData(res.transaction);
                                setShowReceiptModal(true);
                                
                                // Reload student details
                                const updatedFee = await feeService.getFeeByStudentAndOrg(selectedStudentForCollection.studentId, orgData?.orgId);
                                let latestFeeObj = null;
                                if (Array.isArray(updatedFee) && updatedFee.length > 0) {
                                  latestFeeObj = updatedFee[0];
                                  setCollectionFeeRecord(updatedFee[0]);
                                } else if (updatedFee && updatedFee.items && Array.isArray(updatedFee.items) && updatedFee.items.length > 0) {
                                  setCollectionFeeRecord(updatedFee.items[0]);
                                }
                                const txs = await feeService.getTransactionHistory(selectedStudentForCollection.studentId, orgData?.orgId);
                                setCollectionPayments(txs || []);
                                loadFees();

                                // Non-blocking prompt if newly refreshed fee balance is ₹0.00 and assessed fee > 0
                                if (latestFeeObj) {
                                  const latestPending = latestFeeObj.pendingFee != null ? Number(latestFeeObj.pendingFee) : null;
                                  const latestTotal = latestFeeObj.totalFees != null ? Number(latestFeeObj.totalFees) : null;
                                  if (latestPending !== null && latestPending <= 0 && latestTotal !== null && latestTotal > 0) {
                                    setClearedPaymentPrompt({
                                      student: selectedStudentForCollection,
                                      fee: latestFeeObj
                                    });
                                  }
                                }
                              } else {
                                showNotification('error', 'Error', res.message || 'Payment processing failed');
                              }
                            } catch (err) {
                              console.error(err);
                              showNotification('error', 'Error', 'Failed to process payment');
                            } finally {
                              setLoading(false);
                            }
                          }}>

                            {/* STEP 1: What are you collecting for? */}
                            <div style={{ marginBottom: '20px' }}>
                              <label style={{ fontSize: '12px', fontWeight: '700', color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                1. What are you collecting for? *
                              </label>
                              <select
                                className="form-control"
                                value={selectedFeeItemId ? `ITEM_${selectedFeeItemId}` : (selectedInstallmentId ? `INST_${selectedInstallmentId}` : '')}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val.startsWith('ITEM_')) {
                                    const itemId = val.replace('ITEM_', '');
                                    setSelectedFeeItemId(itemId);
                                    setSelectedInstallmentId('');
                                    setComponentPaymentAllocations({});
                                    const item = effectiveItems.find(i => String(i.id) === String(itemId));
                                    if (item) {
                                      const net = item.netAmount || item.originalAmount || 0;
                                      const paid = item.paidAmount || 0;
                                      const rem = Math.max(0, net - paid);
                                      setPaymentAmount(rem > 0 ? rem : '');
                                    }
                                  } else if (val.startsWith('INST_')) {
                                    const instId = val.replace('INST_', '');
                                    setSelectedInstallmentId(instId);
                                    setSelectedFeeItemId('');
                                    setComponentPaymentAllocations({});
                                    const inst = effectiveInstallments.find(i => String(i.id) === String(instId));
                                    if (inst) {
                                      const remaining = Math.max(0, (inst.amount || 0) - (inst.paidAmount || 0));
                                      setPaymentAmount(remaining > 0 ? remaining : '');
                                    }
                                  } else {
                                    setSelectedInstallmentId('');
                                    setSelectedFeeItemId('');
                                    setComponentPaymentAllocations({});
                                    setPaymentAmount(overallItemsRemaining > 0 ? overallItemsRemaining : '');
                                  }
                                }}
                                style={{ fontWeight: '700', borderColor: '#cbd5e1', padding: '10px 12px', marginTop: '6px', fontSize: '14px' }}
                              >
                                <option value="">🌐 Overall / Multi-Component Split (₹{overallItemsRemaining.toLocaleString('en-IN')})</option>
                                {effectiveItems.length > 0 && (
                                  <optgroup label="🏷️ Specific Fee Components">
                                    {effectiveItems.map(item => {
                                      const net = item.netAmount || item.originalAmount || 0;
                                      const paid = item.paidAmount || 0;
                                      const remaining = Math.max(0, net - paid);
                                      return (
                                        <option key={`ITEM_${item.id}`} value={`ITEM_${item.id}`}>
                                          {item.feeType} — ₹{remaining.toLocaleString('en-IN')} due
                                        </option>
                                      );
                                    })}
                                  </optgroup>
                                )}
                              </select>

                              {/* Outstanding Balance & Pay Full Button */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', background: '#f8fafc', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <div>
                                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>OUTSTANDING BALANCE</div>
                                  <div style={{ fontSize: '18px', fontWeight: '800', color: activeDue > 0 ? '#dc2626' : '#166534' }}>
                                    ₹{activeDue.toLocaleString('en-IN')}
                                  </div>
                                </div>
                                {activeDue > 0 && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={() => {
                                      if (!selectedFeeItemId && !selectedInstallmentId && effectiveItems.length > 0) {
                                        const allocs = {};
                                        let totalSum = 0;
                                        effectiveItems.forEach(item => {
                                          const rem = Math.max(0, (item.netAmount || item.originalAmount || 0) - (item.paidAmount || 0));
                                          allocs[item.id] = rem > 0 ? rem : '';
                                          totalSum += rem;
                                        });
                                        setComponentPaymentAllocations(allocs);
                                        setPaymentAmount(totalSum > 0 ? totalSum : '');
                                      } else {
                                        setPaymentAmount(activeDue);
                                      }
                                    }}
                                    style={{ fontWeight: '700', fontSize: '12px', padding: '6px 12px', borderRadius: '6px' }}
                                  >
                                    ⚡ Pay Full ₹{activeDue.toLocaleString('en-IN')}
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Multi-Component Interactive Split Allocation Table */}
                            {!selectedFeeItemId && !selectedInstallmentId && effectiveItems.length > 0 && (
                              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', marginBottom: '18px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                  <span style={{ fontSize: '12px', fontWeight: '800', color: '#0f172a', textTransform: 'uppercase' }}>
                                    🏷️ Component Amount Split
                                  </span>
                                  <span style={{ fontSize: '11px', color: '#64748b' }}>Enter amounts per component</span>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  {effectiveItems.map(item => {
                                    const itemNet = item.netAmount || item.originalAmount || 0;
                                    const itemPaid = item.paidAmount || 0;
                                    const itemDue = Math.max(0, itemNet - itemPaid);
                                    const curAlloc = componentPaymentAllocations[item.id] !== undefined ? componentPaymentAllocations[item.id] : '';

                                    return (
                                      <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', background: '#ffffff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                        <div style={{ flex: 1 }}>
                                          <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{item.feeType}</div>
                                          <div style={{ fontSize: '11.5px', color: itemDue > 0 ? '#991b1b' : '#166534', fontWeight: '600' }}>
                                            Total: ₹{itemNet.toLocaleString('en-IN')} • Due: ₹{itemDue.toLocaleString('en-IN')}
                                          </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                          <div style={{ position: 'relative', width: '130px' }}>
                                            <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontSize: '12px', fontWeight: '700' }}>₹</span>
                                            <input
                                              type="number"
                                              className="form-control form-control-sm"
                                              placeholder="0"
                                              value={curAlloc}
                                              onChange={(e) => {
                                                const val = e.target.value;
                                                const newAllocs = { ...componentPaymentAllocations, [item.id]: val };
                                                setComponentPaymentAllocations(newAllocs);
                                                const total = Object.values(newAllocs).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
                                                setPaymentAmount(total > 0 ? total : '');
                                              }}
                                              style={{ paddingLeft: '22px', fontWeight: '700', fontSize: '13px', textAlign: 'right' }}
                                              min="0"
                                              max={itemDue}
                                            />
                                          </div>
                                          <button
                                            type="button"
                                            className="btn btn-xs btn-outline-secondary"
                                            onClick={() => {
                                              const newAllocs = { ...componentPaymentAllocations, [item.id]: itemDue };
                                              setComponentPaymentAllocations(newAllocs);
                                              const total = Object.values(newAllocs).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
                                              setPaymentAmount(total > 0 ? total : '');
                                            }}
                                            style={{ fontSize: '11px', padding: '4px 6px', fontWeight: '600' }}
                                            title="Fill max balance for this component"
                                          >
                                            Max
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* STEP 2: Payment Amount & Modes */}
                            <div style={{ marginBottom: '20px' }}>
                              <label style={{ fontSize: '12px', fontWeight: '700', color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                2. Payment Amount (₹) *
                              </label>
                              <div style={{ position: 'relative', marginTop: '6px' }}>
                                <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '18px', fontWeight: '700', color: '#64748b' }}>₹</span>
                                <input
                                  type="number"
                                  className="form-control"
                                  placeholder="0.00"
                                  value={paymentAmount}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setPaymentAmount(val);
                                    // If no component allocations were manually entered, auto distribute
                                    if (!selectedFeeItemId && !selectedInstallmentId && effectiveItems.length > 0) {
                                      let rem = parseFloat(val) || 0;
                                      const newAllocs = {};
                                      effectiveItems.forEach(item => {
                                        const itemNet = item.netAmount || item.originalAmount || 0;
                                        const itemPaid = item.paidAmount || 0;
                                        const itemDue = Math.max(0, itemNet - itemPaid);
                                        if (rem > 0 && itemDue > 0) {
                                          const alloc = Math.min(rem, itemDue);
                                          newAllocs[item.id] = alloc;
                                          rem -= alloc;
                                        } else {
                                          newAllocs[item.id] = '';
                                        }
                                      });
                                      setComponentPaymentAllocations(newAllocs);
                                    }
                                  }}
                                  required
                                  min="1"
                                  style={{ paddingLeft: '34px', fontSize: '20px', fontWeight: '800', color: '#0f172a', borderColor: '#cbd5e1' }}
                                />
                              </div>
                            </div>

                            {/* STEP 3: Payment Mode & Ref */}
                            <div style={{ marginBottom: '24px' }}>
                              <label style={{ fontSize: '12px', fontWeight: '700', color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                3. Payment Mode *
                              </label>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginTop: '6px' }}>
                                {['CASH', 'UPI', 'BANK_TRANSFER', 'CHEQUE', 'CARD'].map(mode => (
                                  <button
                                    key={mode}
                                    type="button"
                                    className={`btn btn-sm ${paymentMode === mode ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setPaymentMode(mode)}
                                    style={{ fontWeight: '700', fontSize: '11px', padding: '8px 4px', borderRadius: '6px' }}
                                  >
                                    {mode.replace('_', ' ')}
                                  </button>
                                ))}
                              </div>

                              {paymentMode !== 'CASH' && (
                                <div style={{ marginTop: '12px' }}>
                                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>
                                    Reference / UTR / Cheque No. *
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter reference number..."
                                    value={paymentRefNumber}
                                    onChange={(e) => setPaymentRefNumber(e.target.value)}
                                    required={paymentMode !== 'CASH'}
                                    style={{ marginTop: '4px', fontWeight: '600' }}
                                  />
                                </div>
                              )}
                            </div>

                            {/* TOTAL TO COLLECT BADGE & SUBMIT BUTTON */}
                            <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '10px', border: '1px solid #cbd5e1', marginBottom: '16px', textAlign: 'center' }}>
                              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase' }}>TOTAL TO COLLECT</div>
                              <div style={{ fontSize: '26px', fontWeight: '900', color: '#166534', marginTop: '2px' }}>
                                ₹{(parseFloat(paymentAmount) || 0).toLocaleString('en-IN')}
                              </div>
                            </div>

                            <button
                              type="submit"
                              className="btn btn-primary"
                              style={{ width: '100%', padding: '12px', fontWeight: '800', fontSize: '15px', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(37,99,235,0.2)' }}
                              disabled={!paymentAmount || parseFloat(paymentAmount) <= 0}
                            >
                              💰 Collect & Print Receipt
                            </button>
                          </form>
                        </div>
                      </div>
                    </div>
                  );
                })() : (
                  <div style={{ background: '#f8fafc', padding: '60px 20px', borderRadius: '12px', border: '1px dashed #cbd5e1', textAlign: 'center', color: '#64748b' }}>
                    <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>🔍</span>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#334155' }}>Search for a Student</h3>
                    <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>Lookup any student above to view active ledger context, installments, adjustments, and collect dues.</p>
                  </div>
                )}
              </div>
            )}

            {/* 3. FEE REPORTS WORKSPACE */}
            {feesSubTab === 'reports' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: '#0f172a' }}>Financial Realization Analytics</h2>
                    <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13.5px' }}>High-level dashboard mapping collection realizations, overdue accounts, and class demand statistics.</p>
                  </div>
                </div>

                {/* Main Widgets */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
                    <div style={{ fontSize: '12.5px', color: '#64748b', fontWeight: '600' }}>Total Fee Demand</div>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', marginTop: '6px' }}>
                      ₹{fees.reduce((acc, f) => acc + (f.totalFees || 0), 0).toLocaleString()}
                    </div>
                  </div>
                  <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
                    <div style={{ fontSize: '12.5px', color: '#166534', fontWeight: '600' }}>Total Collections</div>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: '#166534', marginTop: '6px' }}>
                      ₹{fees.reduce((acc, f) => acc + (f.paidFees || 0), 0).toLocaleString()}
                    </div>
                  </div>
                  <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
                    <div style={{ fontSize: '12.5px', color: '#991b1b', fontWeight: '600' }}>Outstanding Balance</div>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: '#b91c1c', marginTop: '6px' }}>
                      ₹{fees.reduce((acc, f) => acc + (f.pendingFee || 0), 0).toLocaleString()}
                    </div>
                  </div>
                  <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
                    <div style={{ fontSize: '12.5px', color: '#1e3a8a', fontWeight: '600' }}>Realization Rate</div>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: '#1d4ed8', marginTop: '6px' }}>
                      {(() => {
                        const demand = fees.reduce((acc, f) => acc + (f.totalFees || 0), 0);
                        const collected = fees.reduce((acc, f) => acc + (f.paidFees || 0), 0);
                        if (demand === 0) return '0%';
                        return ((collected / demand) * 100).toFixed(1) + '%';
                      })()}
                    </div>
                  </div>
                </div>

                {/* Overdue Students and Class-wise Demand Split */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', alignItems: 'start' }}>
                  {/* Overdue Accounts List */}
                  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
                    <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>⚠️ Overdue Accounts List</h3>
                    
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <table className="table" style={{ margin: 0, fontSize: '12.5px' }}>
                        <thead>
                          <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                            <th style={{ padding: '8px 10px', textAlign: 'left' }}>Student</th>
                            <th style={{ padding: '8px 10px', textAlign: 'right' }}>Overdue Dues</th>
                            <th style={{ padding: '8px 10px', textAlign: 'center' }}>Remind</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const overdueList = fees.filter(f => {
                              return f.installments?.some(inst => inst.status !== 'PAID' && new Date(inst.dueDate) < new Date());
                            });

                            if (overdueList.length === 0) {
                              return (
                                <tr>
                                  <td colSpan="3" style={{ textAlign: 'center', color: '#94a3b8', fontStyle: 'italic', padding: '20px 0' }}>
                                    No overdue accounts found!
                                  </td>
                                </tr>
                              );
                            }

                            return overdueList.map(fee => {
                              const student = students.find(s => s.studentId === fee.studentId);
                              if (!student) return null;
                              
                              return (
                                <tr key={fee.feeId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                  <td style={{ padding: '10px 8px' }}>
                                    <div style={{ fontWeight: '600' }}>{student.name}</div>
                                    <div style={{ fontSize: '11px', color: '#64748b' }}>Class {student.className}</div>
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '700', color: '#b91c1c' }}>
                                    ₹{fee.pendingFee?.toLocaleString()}
                                  </td>
                                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => {
                                        showNotification('success', 'Reminder Sent', `SMS and email reminder dispatched to parent of ${student.name}`);
                                      }}
                                      style={{ padding: '2px 8px', fontSize: '11px' }}
                                    >
                                      🔔 Remind
                                    </button>
                                  </td>
                                </tr>
                              );
                            });
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Class-wise Demand realizing splits */}
                  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
                    <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>🏫 Class Realization Analytics</h3>
                    
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <table className="table" style={{ margin: 0, fontSize: '12.5px' }}>
                        <thead>
                          <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                            <th style={{ padding: '8px 10px', textAlign: 'left' }}>Class Name</th>
                            <th style={{ padding: '8px 10px', textAlign: 'right' }}>Demand</th>
                            <th style={{ padding: '8px 10px', textAlign: 'right' }}>Collected</th>
                            <th style={{ padding: '8px 10px', textAlign: 'center' }}>Progress</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const distinctClasses = Array.from(new Set(students.map(s => s.className).filter(Boolean)));
                            
                            if (distinctClasses.length === 0) {
                              return (
                                <tr>
                                  <td colSpan="4" style={{ textAlign: 'center', color: '#94a3b8', fontStyle: 'italic', padding: '20px 0' }}>
                                    No class data available.
                                  </td>
                                </tr>
                              );
                            }

                            return distinctClasses.map(cls => {
                              const classStudents = students.filter(s => s.className === cls);
                              const classStudentIds = classStudents.map(s => s.studentId);
                              const classFees = fees.filter(f => classStudentIds.includes(f.studentId));
                              
                              const demand = classFees.reduce((acc, f) => acc + (f.totalFees || 0), 0);
                              const collected = classFees.reduce((acc, f) => acc + (f.paidFees || 0), 0);
                              const pct = demand > 0 ? (collected / demand) * 100 : 0;

                              return (
                                <tr key={cls} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                  <td style={{ padding: '10px 8px', fontWeight: '600' }}>{cls}</td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right' }}>₹{demand.toLocaleString()}</td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', color: '#166534', fontWeight: '600' }}>₹{collected.toLocaleString()}</td>
                                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                                    <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '700' }}>
                                      {pct.toFixed(0)}%
                                    </span>
                                  </td>
                                </tr>
                              );
                            });
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 4. FEE STRUCTURES WORKSPACE */}
            {feesSubTab === 'structures' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: '#0f172a' }}>Fee Structure Templates</h2>
                    <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13.5px' }}>Configure structural templates, item breakdowns, installment stages, and class parameters.</p>
                  </div>
                  {userRole === 'COLLEGE_ADMIN' && (
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        setSelectedStructure(null);
                        setStructureFormData({
                          name: '',
                          academicYear: '2026-27',
                          classId: '',
                          className: '',
                          totalAmount: 0,
                          items: [{ feeType: 'Tuition Fee', amount: '' }],
                          installments: [{ installmentName: 'Term 1', amount: '', dueDate: '' }]
                        });
                        setShowCreateStructureModal(true);
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      ➕ Create Structure
                    </button>
                  )}
                </div>

                {feeStructures.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '20px' }}>
                    {feeStructures.map(structure => (
                      <div key={structure.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '14px' }}>
                            <div>
                              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>{structure.name}</h3>
                              <span style={{ display: 'inline-block', background: '#f1f5f9', color: '#475569', fontSize: '11px', fontWeight: '600', padding: '2px 6px', borderRadius: '4px', marginTop: '4px' }}>
                                Year: {structure.academicYear} • Class: {structure.className || 'All Classes'}
                              </span>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <span style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>₹{structure.totalAmount?.toLocaleString('en-IN')}</span>
                              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Total Template Fee</div>
                            </div>
                          </div>

                          {/* Items Breakdown */}
                          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '12px', marginBottom: '12px' }}>
                            <h4 style={{ margin: '0 0 6px 0', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Items Breakdown</h4>
                            <div style={{ display: 'grid', gap: '4px' }}>
                              {structure.items?.map((item, idx) => (
                                <div key={item.id || idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', color: '#334155' }}>
                                  <span>{item.feeType}</span>
                                  <span style={{ fontWeight: '500' }}>₹{item.amount?.toLocaleString('en-IN')}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Installments Stages */}
                          {structure.installments && structure.installments.length > 0 && (
                            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '12px', marginBottom: '12px' }}>
                              <h4 style={{ margin: '0 0 6px 0', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Installment Stages</h4>
                              <div style={{ display: 'grid', gap: '4px' }}>
                                {structure.installments.map((inst, idx) => (
                                  <div key={inst.id || idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#64748b' }}>
                                    <span>{inst.installmentName} {inst.dueDate ? `(Due: ${new Date(inst.dueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })})` : ''}</span>
                                    <span>₹{inst.amount?.toLocaleString('en-IN')}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {userRole === 'COLLEGE_ADMIN' && (
                          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '12px', marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={async () => {
                                if (window.confirm(`Are you sure you want to delete the "${structure.name}" fee structure template?`)) {
                                  try {
                                    setLoading(true);
                                    await feeService.deleteFeeStructure(structure.id);
                                    showNotification('success', 'Success', 'Fee structure deleted successfully');
                                    await loadFeeStructures();
                                  } catch (err) {
                                    console.error(err);
                                    showNotification('error', 'Error', 'Failed to delete fee structure');
                                  } finally {
                                    setLoading(false);
                                  }
                                }
                              }}
                              style={{ padding: '3px 10px', fontSize: '12px' }}
                            >
                              🗑️ Delete Template
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ background: '#f8fafc', padding: '60px 20px', borderRadius: '12px', border: '1px dashed #cbd5e1', textAlign: 'center', color: '#64748b' }}>
                    <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>📋</span>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#334155' }}>No Fee Structures Configured</h3>
                    <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>Click 'Create Structure' to define templates and installment configurations.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

    {activeTab === 'classes' && (
          <div>
            <div className="section-header">
              <h2 className="section-title">Class Management</h2>
            </div>
            <div className="coming-soon">
              <h3>Class Management</h3>
              <p>Manage classes, sections and class schedules</p>
              <p>This feature is coming soon...</p>
            </div>
          </div>
        )}

        {/* Test/Exam Creation/Editing Modal */}
        <CreateTestModal
          isOpen={showTestModal}
          onClose={() => { setShowTestModal(false); setEditingTest(null); }}
          classes={classes}
          subjects={subjects}
          teachers={teachers}
          testForm={testForm}
          setTestForm={setTestForm}
          onSubmit={handleSaveTest}
          editingTest={editingTest}
        />

      {/* Academic Modal (Classes / Subjects / Timetable) */}
      {showAcademicModal && (
        <div className="fs-modal-overlay">
          <div className="fs-modal-card" style={{ maxWidth: academicActiveTab === 'timetable' ? '1200px' : (academicActiveTab === 'subjects' ? 'min(680px, calc(100vw - 32px))' : '750px') }}>
            {/* Header Bar */}
            <div className="fs-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>
                  {academicActiveTab === 'classes' 
                    ? (editingAcademicItem ? 'Edit Class' : 'Add New Class') 
                    : (editingAcademicItem ? 'Edit Subject Record' : 'Add New Subject')}
                </h3>
                <div style={{ fontSize: '12.5px', color: '#64748b', marginTop: '3px' }}>
                  {academicActiveTab === 'classes' 
                    ? 'Create a class and assign a section / class teacher.' 
                    : (academicActiveTab === 'subjects' ? 'Create a subject for your school\'s academic structure.' : `${orgData?.orgName || 'School ERP'} • Academic Management`)}
                </div>
              </div>
              <button 
                type="button" 
                className="fs-close-btn" 
                onClick={() => setShowAcademicModal(false)}
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="fs-body" style={{ padding: '20px 28px' }}>

            {/* Class Form */}
            {academicActiveTab === 'classes' && (
              <>
                <form id="school-class-form" onSubmit={handleClassSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Class Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={classFormData.className}
                      onChange={(e) => setClassFormData({...classFormData, className: e.target.value})}
                      placeholder="e.g., 10th Standard, Grade 10, PUC II"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Section *</label>
                    <select
                      className="form-control"
                      value={classFormData.section}
                      onChange={(e) => setClassFormData({...classFormData, section: e.target.value})}
                      required
                    >
                      <option value="">Select Section</option>
                      {['A', 'B', 'C', 'D', 'E', 'F'].map(section => (
                        <option key={section} value={section}>Section {section}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Class Teacher</label>
                    <select
                      className="form-control"
                      value={classFormData.classTeacherName}
                      onChange={(e) => setClassFormData({...classFormData, classTeacherName: e.target.value})}
                    >
                      <option value="">Select Assigned Teacher (Optional)</option>
                      {teachers && teachers.length > 0 && teachers.map(t => (
                        <option key={t.teacherId} value={t.teacherName}>
                          {t.teacherName} ({t.teacherId || t.designation || 'Faculty'})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Max Student Capacity *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={classFormData.maxCapacity}
                      onChange={(e) => setClassFormData({...classFormData, maxCapacity: e.target.value})}
                      placeholder="Maximum student capacity (e.g. 40)"
                      min="1"
                      max="150"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Classroom / Room Number</label>
                    <input
                      type="text"
                      className="form-control"
                      value={classFormData.roomNumber || ''}
                      onChange={(e) => setClassFormData({...classFormData, roomNumber: e.target.value})}
                      placeholder="e.g., Room 102, Block A"
                    />
                  </div>

                  <div className="form-group">
                    <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Shift / Timing Session</label>
                    <select
                      className="form-control"
                      value={classFormData.shift || 'General Shift'}
                      onChange={(e) => setClassFormData({...classFormData, shift: e.target.value})}
                    >
                      <option value="General Shift">General Shift (Full Day)</option>
                      <option value="Morning Shift">Morning Shift</option>
                      <option value="Afternoon Shift">Afternoon Shift</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Academic Year *</label>
                    <select
                      className="form-control"
                      value={classFormData.academicYear || '2026-2027'}
                      onChange={(e) => setClassFormData({...classFormData, academicYear: e.target.value})}
                    >
                      <option value="2026-2027">2026–2027</option>
                      <option value="2025-2026">2025–2026</option>
                      <option value="2024-2025">2024–2025</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Status *</label>
                    <select
                      className="form-control"
                      value={classFormData.status || 'ACTIVE'}
                      onChange={(e) => setClassFormData({...classFormData, status: e.target.value})}
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                    </select>
                  </div>
                </div>
              </form>

              {/* Sticky Action Footer Bar */}
              <div className="fs-footer" style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button 
                  type="button" 
                  className="fs-btn-secondary" 
                  onClick={() => setShowAcademicModal(false)}
                >
                  Cancel
                </button>

                <button 
                  type="submit" 
                  form="school-class-form"
                  className="fs-btn-primary"
                  disabled={loading || isSubmitting}
                  style={{
                    opacity: (loading || isSubmitting) ? 0.7 : 1,
                    cursor: (loading || isSubmitting) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {(loading || isSubmitting) ? '⟳ Saving Class...' : (editingAcademicItem ? '✓ Update Class Record' : '✓ Add Class')}
                </button>
              </div>
            </>
            )}

            {/* Subject Form Modal */}
            {academicActiveTab === 'subjects' && (
              <>
                <form id="school-subject-form" onSubmit={handleSubjectSubmit}>
                  {/* SECTION 1 — BASIC INFORMATION */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ fontSize: '12.5px', fontWeight: '700', color: '#2563EB', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '14px' }}>
                      SECTION 1 — BASIC INFORMATION
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div className="form-group">
                        <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155', marginBottom: '6px', display: 'block' }}>
                          Subject Name *
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={subjectFormData.subjectName}
                          onChange={(e) => {
                            setSubjectFormData({...subjectFormData, subjectName: e.target.value});
                            if (subjectFormErrors.subjectName) {
                              setSubjectFormErrors({...subjectFormErrors, subjectName: null});
                            }
                          }}
                          placeholder="Enter subject name (e.g. Mathematics)"
                          autoFocus
                          style={{
                            height: '42px',
                            borderColor: subjectFormErrors.subjectName ? '#DC2626' : '#CBD5E1',
                            borderRadius: '8px'
                          }}
                        />
                        {subjectFormErrors.subjectName && (
                          <span style={{ color: '#DC2626', fontSize: '12px', marginTop: '4px', display: 'block', fontWeight: '600' }}>
                            {subjectFormErrors.subjectName}
                          </span>
                        )}
                      </div>

                      <div className="form-group">
                        <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155', marginBottom: '6px', display: 'block' }}>
                          Subject Code *
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={subjectFormData.subjectCode}
                          onChange={(e) => {
                            setSubjectFormData({...subjectFormData, subjectCode: e.target.value});
                            if (subjectFormErrors.subjectCode) {
                              setSubjectFormErrors({...subjectFormErrors, subjectCode: null});
                            }
                          }}
                          placeholder="e.g. MATH101"
                          style={{
                            height: '42px',
                            borderColor: subjectFormErrors.subjectCode ? '#DC2626' : '#CBD5E1',
                            borderRadius: '8px'
                          }}
                        />
                        {subjectFormErrors.subjectCode && (
                          <span style={{ color: '#DC2626', fontSize: '12px', marginTop: '4px', display: 'block', fontWeight: '600' }}>
                            {subjectFormErrors.subjectCode}
                          </span>
                        )}
                      </div>

                      <div className="form-group">
                        <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155', marginBottom: '6px', display: 'block' }}>
                          Subject Type *
                        </label>
                        <select
                          className="form-control"
                          value={subjectFormData.type || 'Core'}
                          onChange={(e) => setSubjectFormData({...subjectFormData, type: e.target.value})}
                          style={{ height: '42px', borderRadius: '8px' }}
                        >
                          <option value="Core">Core</option>
                          <option value="Elective">Elective</option>
                          <option value="Optional">Optional</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155', marginBottom: '6px', display: 'block' }}>
                          Status *
                        </label>
                        <select
                          className="form-control"
                          value={subjectFormData.status || 'ACTIVE'}
                          onChange={(e) => setSubjectFormData({...subjectFormData, status: e.target.value})}
                          style={{ height: '42px', borderRadius: '8px' }}
                        >
                          <option value="ACTIVE">Active</option>
                          <option value="DRAFT">Draft</option>
                          <option value="INACTIVE">Inactive</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* SECTION 2 — ACADEMIC CONFIGURATION */}
                  <div style={{ marginTop: '20px', paddingTop: '18px', borderTop: '1px solid #F1F5F9' }}>
                    <div style={{ fontSize: '12.5px', fontWeight: '700', color: '#2563EB', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '14px' }}>
                      SECTION 2 — ACADEMIC CONFIGURATION
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div className="form-group">
                        <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155', marginBottom: '6px', display: 'block' }}>
                          {institutionType === 'DEGREE_COLLEGE' ? 'Target Semester' : institutionType === 'PUC' ? 'Target Stream / PUC Year' : 'Target Class / Grade'}
                        </label>
                        <select
                          className="form-control"
                          value={subjectFormData.classLevel || ''}
                          onChange={(e) => setSubjectFormData({...subjectFormData, classLevel: e.target.value})}
                          style={{ height: '42px', borderRadius: '8px' }}
                        >
                          <option value="">
                            {institutionType === 'DEGREE_COLLEGE' ? 'Select Target Semester (Optional)' : institutionType === 'PUC' ? 'Select Target Stream / Year (Optional)' : 'Select Target Class (Optional)'}
                          </option>
                          {institutionType === 'DEGREE_COLLEGE' ? (
                            ['Sem 1', 'Sem 2', 'Sem 3', 'Sem 4', 'Sem 5', 'Sem 6', 'Sem 7', 'Sem 8'].map(s => (
                              <option key={s} value={s}>{s}</option>
                            ))
                          ) : institutionType === 'PUC' ? (
                            ['1st PUC', '2nd PUC', 'Science - 1st PUC', 'Science - 2nd PUC', 'Commerce - 1st PUC', 'Commerce - 2nd PUC', 'Arts - 1st PUC', 'Arts - 2nd PUC'].map(s => (
                              <option key={s} value={s}>{s}</option>
                            ))
                          ) : classes && classes.length > 0 ? (
                            classes.map(c => (
                              <option key={c.classId} value={c.className}>
                                {c.className} {c.section ? `(${c.section})` : ''}
                              </option>
                            ))
                          ) : (
                            ['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10'].map(cls => (
                              <option key={cls} value={cls}>{cls}</option>
                            ))
                          )}
                        </select>
                      </div>

                      <div className="form-group">
                        <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155', marginBottom: '6px', display: 'block' }}>
                          Department
                        </label>
                        <select
                          className="form-control"
                          value={subjectFormData.departmentName || ''}
                          onChange={(e) => setSubjectFormData({...subjectFormData, departmentName: e.target.value})}
                          style={{ height: '42px', borderRadius: '8px' }}
                        >
                          <option value="">Select Department (Optional)</option>
                          <option value="Computer Science">Computer Science</option>
                          <option value="Computer Science & Engineering">Computer Science & Engineering</option>
                          <option value="Bachelor of Commerce">Bachelor of Commerce</option>
                          <option value="Bachelor of Arts">Bachelor of Arts</option>
                          <option value="Science">Science</option>
                          <option value="Mathematics">Mathematics</option>
                          <option value="Commerce">Commerce</option>
                          <option value="Arts & Humanities">Arts & Humanities</option>
                          <option value="Languages">Languages</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155', marginBottom: '6px', display: 'block' }}>
                          Theory Marks
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={subjectFormData.theoryMarks !== undefined ? subjectFormData.theoryMarks : 80}
                          onChange={(e) => setSubjectFormData({...subjectFormData, theoryMarks: e.target.value})}
                          placeholder="e.g. 80"
                          min="0"
                          max="500"
                          style={{ height: '42px', borderRadius: '8px' }}
                        />
                      </div>

                      <div className="form-group">
                        <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155', marginBottom: '6px', display: 'block' }}>
                          Practical Marks
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={subjectFormData.practicalMarks !== undefined ? subjectFormData.practicalMarks : 20}
                          onChange={(e) => setSubjectFormData({...subjectFormData, practicalMarks: e.target.value})}
                          placeholder="e.g. 20"
                          min="0"
                          max="500"
                          style={{ height: '42px', borderRadius: '8px' }}
                        />
                      </div>

                      <div className="form-group" style={{ gridColumn: 'span 2' }}>
                        <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155', marginBottom: '6px', display: 'block' }}>
                          Maximum Total Marks (Calculated)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={(parseInt(subjectFormData.theoryMarks || 0, 10) + parseInt(subjectFormData.practicalMarks || 0, 10))}
                          disabled
                          readOnly
                          style={{ height: '42px', backgroundColor: '#F8FAFC', fontWeight: '700', color: '#2563EB', borderRadius: '8px' }}
                        />
                      </div>
                    </div>
                  </div>
                </form>

                {/* Sticky Action Footer Bar */}
                <div className="fs-footer" style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <button 
                    type="button" 
                    className="fs-btn-secondary" 
                    onClick={() => setShowAcademicModal(false)}
                  >
                    Cancel
                  </button>

                  <button 
                    type="submit" 
                    form="school-subject-form"
                    className="fs-btn-primary"
                    disabled={loading || isSubmitting}
                    style={{
                      opacity: (loading || isSubmitting) ? 0.7 : 1,
                      cursor: (loading || isSubmitting) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {(loading || isSubmitting) ? '⟳ Creating Subject...' : (editingAcademicItem ? '✓ Update Subject Record' : '+ Add Subject')}
                  </button>
                </div>
              </>
            )}
            </div>
          </div>
        </div>
      )}

      {/* Subject Assignment Modal */}
      <AssignSubjectsModal
        isOpen={showAssignmentModal}
        onClose={() => setShowAssignmentModal(false)}
        classes={classOptions}
        subjects={subjects}
        studentSubjects={studentSubjects}
        onSubmit={handleAssignmentSubmit}
        loading={loading}
      />

      {/* Student Modal (Full-Screen Enterprise Step Wizard matching College Admin) */}
      {showModal && (
        <div className="fs-modal-overlay">
          <div className="fs-modal-container">
            {/* 1. Sidebar Stepper (260px) */}
            <div className="fs-sidebar">
              <div className="fs-sidebar-header">
                <h2>{editingItem ? 'Edit Student Record' : 'Student Admission'}</h2>
                <p>Complete the official {orgData?.orgName || 'School ERP'} student registration wizard.</p>
              </div>
              <div className="fs-stepper">
                {['Admission Info', 'Student Identity', 'Family & Income', 'Contact & Education', 'Services & Review'].map((label, idx) => {
                  const stepNum = idx + 1;
                  const isActive = stepNum === modalStep;
                  const isCompleted = stepNum < modalStep;
                  return (
                    <div 
                      key={label} 
                      className={`fs-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                      onClick={() => { if (editingItem || stepNum < modalStep) setModalStep(stepNum); }}
                    >
                      <div className="fs-step-indicator">
                        {isCompleted ? '✓' : stepNum}
                      </div>
                      {stepNum < 5 && <div className={`fs-step-line ${isCompleted ? 'completed' : ''}`} />}
                      <div className="fs-step-content">
                        <span className="fs-step-title">{label}</span>
                        <span className="fs-step-desc">
                          {isCompleted ? 'Completed' : (idx === 0 ? 'Identifiers & Class' : idx === 1 ? 'Personal & Birth Info' : idx === 2 ? 'Parents & Guardian' : idx === 3 ? 'Contact & TC Details' : 'Facilities & Review')}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* 2. Main Content Area */}
            <div className="fs-content-area">
              {/* Modal Header Bar */}
              <div className="fs-header">
                <div>
                  <h3>{editingItem ? 'Update Student Admission Record' : `${orgData?.orgName || 'School ERP'} Student Admission`}</h3>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px', fontWeight: '500' }}>
                    Student Enrollment • Academic Year {orgData?.academicYear || '2026–27'}
                  </div>
                </div>
                <button type="button" className="fs-close-btn" onClick={() => setShowModal(false)} title="Close">
                  ✕
                </button>
              </div>

              {/* Toast Notification Banner (Draft / Alert) */}
              {draftToast && (
                <div style={{ padding: '8px 36px', background: '#eff6ff', borderBottom: '1px solid #bfdbfe', color: '#1e40af', fontSize: '12.5px', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>💾 {draftToast}</span>
                  <button type="button" onClick={() => setDraftToast(null)} style={{ background: 'none', border: 'none', color: '#1e40af', cursor: 'pointer', fontWeight: '700' }}>✕</button>
                </div>
              )}

              {/* Modal Body Scroll Area */}
              <div className="fs-body">
                <form id="school-student-form" onSubmit={handleStudentSubmit}>
                  <div className="fs-form-container">
                    <h2 className="fs-form-section-title">
                      {['1. Admission & School Identifiers', '2. Student Identity & Birth Details', '3. Parents, Family & Income Details', '4. Contact & Previous Schooling Information', '5. Services & Final Admission Review'][modalStep - 1]}
                    </h2>

                    {/* Step 1: Admission & Identifiers */}
                    {modalStep === 1 && (
                      <div>
                        {/* Auto-Generated Fields Info Bar */}
                        <div style={{ padding: '10px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '18px', fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>🔒</span>
                          <span>Student ID and Application Number are generated automatically upon successful admission.</span>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Student ID</label>
                            <input 
                              type="text" 
                              className="form-control" 
                              value={editingItem ? (studentFormData.studentId || '') : '⚙ Auto-generated on save'} 
                              disabled 
                              style={{ backgroundColor: '#f8fafc', color: '#64748b', fontWeight: '600', cursor: 'not-allowed', border: '1px solid #e2e8f0' }}
                            />
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Application No</label>
                            <input 
                              type="text" 
                              className="form-control" 
                              value={editingItem ? (studentFormData.applicationNo || '') : '⚙ Auto-generated on save'} 
                              disabled 
                              style={{ backgroundColor: '#f8fafc', color: '#64748b', fontWeight: '600', cursor: 'not-allowed', border: '1px solid #e2e8f0' }}
                            />
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>SATS No</label>
                            <input type="text" className="form-control" value={studentFormData.satsNo || ''} onChange={(e)=>setStudentFormData({...studentFormData, satsNo: e.target.value})} placeholder="State SATS number" />
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>PEN No</label>
                            <input type="text" className="form-control" value={studentFormData.penNo || ''} onChange={(e)=>setStudentFormData({...studentFormData, penNo: e.target.value})} placeholder="Permanent Education Number" />
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginTop: '16px' }}>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Class *</label>
                            <select 
                              id="input-field-class"
                              className="form-control" 
                              value={studentFormData.class || ''} 
                              onChange={async (e)=>{
                                const newClass = e.target.value;
                                setStudentFormData(prev => ({ ...prev, class: newClass }));
                                if (validationErrors.class) setValidationErrors(prev => ({ ...prev, class: null }));
                                try {
                                  const res = await identifierService.getNextRollNumber(newClass, studentFormData.section);
                                  if (res && res.success && res.nextRollNumber) {
                                    setStudentFormData(prev => ({ ...prev, rollNumber: res.nextRollNumber }));
                                  }
                                } catch (err) { console.error('Failed to auto-fetch roll number:', err); }
                              }} 
                              style={{ borderColor: validationErrors.class ? '#ef4444' : undefined }}
                              required
                            >
                              <option value="">{classOptions.length > 0 ? 'Select Class' : 'No classes available'}</option>
                              {classOptions.map(option => (
                                <option key={option.id} value={option.label}>{option.label}</option>
                              ))}
                            </select>
                            {validationErrors.class && (
                              <div className="inline-field-error">⚠️ {validationErrors.class}</div>
                            )}
                          </div>

                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Section</label>
                            <select 
                              className="form-control" 
                              value={studentFormData.section || ''} 
                              onChange={async (e)=>{
                                const newSection = e.target.value;
                                setStudentFormData(prev => ({ ...prev, section: newSection }));
                                try {
                                  const res = await identifierService.getNextRollNumber(studentFormData.class, newSection);
                                  if (res && res.success && res.nextRollNumber) {
                                    setStudentFormData(prev => ({ ...prev, rollNumber: res.nextRollNumber }));
                                  }
                                } catch (err) { console.error('Failed to auto-fetch roll number:', err); }
                              }}
                            >
                              <option value="">{sectionOptions.length > 0 ? 'Select Section' : 'No sections available'}</option>
                              {sectionOptions.map(section => (
                                <option key={section} value={section}>Section {section}</option>
                              ))}
                            </select>
                          </div>

                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Roll Number</label>
                            <input type="text" className="form-control" value={studentFormData.rollNumber || ''} onChange={(e)=>setStudentFormData({...studentFormData, rollNumber: e.target.value})} placeholder="Auto-generated or enter" />
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Academic Year</label>
                            <input 
                              type="text" 
                              className="form-control" 
                              value={studentFormData.admissionYear || orgData?.academicYear || '2026–27'} 
                              onChange={(e)=>setStudentFormData({...studentFormData, admissionYear: e.target.value})} 
                              placeholder="2026–27" 
                            />
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Date of Admission</label>
                            <input 
                              type="date" 
                              className="form-control" 
                              value={studentFormData.admissionDate || new Date().toISOString().split('T')[0]} 
                              onChange={(e)=>setStudentFormData({...studentFormData, admissionDate: e.target.value})} 
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 2: Student Identity & Birth Details */}
                    {modalStep === 2 && (
                      <div>
                        {/* Student Photo Upload Dropzone */}
                        <div className="form-group" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '20px', padding: '18px', background: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
                          <label style={{ fontWeight: '600', color: '#334155', textAlign: 'center', marginBottom: '10px', fontSize: '13px' }}>Passport Size Student Photo</label>
                          <div style={{ position: 'relative', width: '84px', height: '84px', borderRadius: '50%', overflow: 'hidden', background: '#e2e8f0', border: '3px solid #2563eb', display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '12px', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}>
                            {studentFormData.photo ? (
                              <img 
                                src={studentFormData.photo.startsWith('data:') ? studentFormData.photo : `${API_BASE_URL}${studentFormData.photo}`} 
                                alt="Student Profile" 
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                              />
                            ) : (
                              <span style={{ fontSize: '32px' }}>👤</span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <label style={{ padding: '6px 14px', background: '#2563eb', color: 'white', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                              Upload Photo
                              <input type="file" accept="image/*" onChange={handleStudentPhotoUpload} style={{ display: 'none' }} />
                            </label>
                            {studentFormData.photo && (
                              <button type="button" onClick={() => setStudentFormData(prev => ({ ...prev, photo: '' }))} style={{ padding: '6px 14px', background: '#ef4444', color: 'white', borderRadius: '6px', fontSize: '12px', fontWeight: '600', border: 'none', cursor: 'pointer' }}>
                                Remove
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: '16px' }}>
                          <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Full Name of Pupil (Block Letters) *</label>
                          <input 
                            id="input-field-name"
                            type="text" 
                            className="form-control" 
                            value={studentFormData.name || ''} 
                            onChange={(e)=>{
                              setStudentFormData({...studentFormData, name: e.target.value.toUpperCase()});
                              if (validationErrors.name) setValidationErrors(prev => ({ ...prev, name: null }));
                            }} 
                            style={{ borderColor: validationErrors.name ? '#ef4444' : undefined }}
                            required 
                            placeholder="ENTER PUPIL FULL NAME IN CAPITAL LETTERS" 
                          />
                          {validationErrors.name && (
                            <div className="inline-field-error">⚠️ {validationErrors.name}</div>
                          )}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Gender *</label>
                            <select className="form-control" value={studentFormData.gender || ''} onChange={(e)=>setStudentFormData({...studentFormData, gender: e.target.value})}>
                              <option value="">Select Gender</option>
                              <option value="Boy">Boy</option>
                              <option value="Girl">Girl</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Date of Birth</label>
                            <input 
                              type="date" 
                              className="form-control" 
                              value={studentFormData.dob || ''} 
                              onChange={(e)=> {
                                const newDob = e.target.value;
                                setStudentFormData({
                                  ...studentFormData, 
                                  dob: newDob,
                                  dobInWords: convertDobToWords(newDob)
                                });
                              }} 
                            />
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Blood Group</label>
                            <select className="form-control" value={studentFormData.bloodGroup || ''} onChange={(e)=>setStudentFormData({...studentFormData, bloodGroup: e.target.value})}>
                              <option value="">Select Blood Group</option>
                              <option value="A+">A+</option><option value="A-">A-</option>
                              <option value="B+">B+</option><option value="B-">B-</option>
                              <option value="O+">O+</option><option value="O-">O-</option>
                              <option value="AB+">AB+</option><option value="AB-">AB-</option>
                            </select>
                          </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: '16px' }}>
                          <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Date of Birth in Words</label>
                          <input type="text" className="form-control" value={studentFormData.dobInWords || ''} onChange={(e)=>setStudentFormData({...studentFormData, dobInWords: e.target.value})} placeholder="e.g. Fifteenth August Two Thousand Fourteen" />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Place of Birth (Village)</label>
                            <input type="text" className="form-control" value={studentFormData.placeOfBirthVillage || ''} onChange={(e)=>setStudentFormData({...studentFormData, placeOfBirthVillage: e.target.value})} placeholder="Village name" />
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Taluk</label>
                            <input type="text" className="form-control" value={studentFormData.placeOfBirthTaluk || ''} onChange={(e)=>setStudentFormData({...studentFormData, placeOfBirthTaluk: e.target.value})} placeholder="Taluk" />
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>District</label>
                            <input type="text" className="form-control" value={studentFormData.placeOfBirthDistrict || ''} onChange={(e)=>setStudentFormData({...studentFormData, placeOfBirthDistrict: e.target.value})} placeholder="District" />
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Nationality</label>
                            <input type="text" className="form-control" value={studentFormData.nationality || 'Indian'} onChange={(e)=>setStudentFormData({...studentFormData, nationality: e.target.value})} />
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Religion</label>
                            <input type="text" className="form-control" value={studentFormData.religion || ''} onChange={(e)=>setStudentFormData({...studentFormData, religion: e.target.value})} placeholder="e.g. Hindu, Muslim, Christian" />
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Caste</label>
                            <input type="text" className="form-control" value={studentFormData.caste || ''} onChange={(e)=>setStudentFormData({...studentFormData, caste: e.target.value})} placeholder="Caste" />
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Category / Subcaste</label>
                            <input type="text" className="form-control" value={studentFormData.category || ''} onChange={(e)=>setStudentFormData({...studentFormData, category: e.target.value})} placeholder="e.g. SC, ST, Category-1, 2A, 3B, General" />
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Mother Tongue</label>
                            <input type="text" className="form-control" value={studentFormData.motherTongue || ''} onChange={(e)=>setStudentFormData({...studentFormData, motherTongue: e.target.value})} placeholder="e.g. Kannada, English, Telugu" />
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Languages Spoken</label>
                            <input type="text" className="form-control" value={studentFormData.languagesSpoken || ''} onChange={(e)=>setStudentFormData({...studentFormData, languagesSpoken: e.target.value})} placeholder="Other languages spoken" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 3: Family, Income & Siblings */}
                    {modalStep === 3 && (
                      <div>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr 2fr', gap: '12px', marginBottom: '16px' }}>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Father's Name</label>
                            <input type="text" className="form-control" value={studentFormData.fatherName || ''} onChange={(e)=>setStudentFormData({...studentFormData, fatherName: e.target.value})} placeholder="Father full name" />
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Status</label>
                            <select className="form-control" value={studentFormData.fatherLivingStatus || 'Living'} onChange={(e)=>setStudentFormData({...studentFormData, fatherLivingStatus: e.target.value})}>
                              <option value="Living">Living</option>
                              <option value="Deceased">Deceased</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Father's Education</label>
                            <input type="text" className="form-control" value={studentFormData.fatherEducation || ''} onChange={(e)=>setStudentFormData({...studentFormData, fatherEducation: e.target.value})} placeholder="Qualification" />
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Father's Occupation</label>
                            <input type="text" className="form-control" value={studentFormData.fatherOccupation || ''} onChange={(e)=>setStudentFormData({...studentFormData, fatherOccupation: e.target.value})} placeholder="Occupation" />
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr 2fr', gap: '12px', marginBottom: '16px' }}>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Mother's Name</label>
                            <input type="text" className="form-control" value={studentFormData.motherName || ''} onChange={(e)=>setStudentFormData({...studentFormData, motherName: e.target.value})} placeholder="Mother full name" />
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Status</label>
                            <select className="form-control" value={studentFormData.motherLivingStatus || 'Living'} onChange={(e)=>setStudentFormData({...studentFormData, motherLivingStatus: e.target.value})}>
                              <option value="Living">Living</option>
                              <option value="Deceased">Deceased</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Mother's Education</label>
                            <input type="text" className="form-control" value={studentFormData.motherEducation || ''} onChange={(e)=>setStudentFormData({...studentFormData, motherEducation: e.target.value})} placeholder="Qualification" />
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Mother's Occupation</label>
                            <input type="text" className="form-control" value={studentFormData.motherOccupation || ''} onChange={(e)=>setStudentFormData({...studentFormData, motherOccupation: e.target.value})} placeholder="Occupation" />
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Parents' Annual Income (₹)</label>
                            <input type="number" className="form-control" value={studentFormData.parentsAnnualIncome || ''} onChange={(e)=>setStudentFormData({...studentFormData, parentsAnnualIncome: e.target.value})} placeholder="e.g. 150000" min="0" />
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Number of Dependents</label>
                            <input type="number" className="form-control" value={studentFormData.numberOfDependents || ''} onChange={(e)=>setStudentFormData({...studentFormData, numberOfDependents: e.target.value})} placeholder="Total family dependents" min="0" />
                          </div>
                        </div>

                        <div style={{ padding: '14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                          <label style={{ fontWeight: '600', fontSize: '13px', color: '#334155', display: 'block', marginBottom: '10px' }}>Sibling Details</label>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px' }}>
                            <div className="form-group">
                              <label style={{ fontSize: '11px' }}>Elder Brothers</label>
                              <input type="number" className="form-control" value={studentFormData.elderBrothersCount || '0'} onChange={(e)=>setStudentFormData({...studentFormData, elderBrothersCount: e.target.value})} min="0" />
                            </div>
                            <div className="form-group">
                              <label style={{ fontSize: '11px' }}>Younger Brothers</label>
                              <input type="number" className="form-control" value={studentFormData.youngerBrothersCount || '0'} onChange={(e)=>setStudentFormData({...studentFormData, youngerBrothersCount: e.target.value})} min="0" />
                            </div>
                            <div className="form-group">
                              <label style={{ fontSize: '11px' }}>Elder Sisters</label>
                              <input type="number" className="form-control" value={studentFormData.elderSistersCount || '0'} onChange={(e)=>setStudentFormData({...studentFormData, elderSistersCount: e.target.value})} min="0" />
                            </div>
                            <div className="form-group">
                              <label style={{ fontSize: '11px' }}>Younger Sisters</label>
                              <input type="number" className="form-control" value={studentFormData.youngerSistersCount || '0'} onChange={(e)=>setStudentFormData({...studentFormData, youngerSistersCount: e.target.value})} min="0" />
                            </div>
                          </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: '16px' }}>
                          <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Pupil Staying With</label>
                          <select className="form-control" value={studentFormData.accommodationType || 'Parents'} onChange={(e)=>setStudentFormData({...studentFormData, accommodationType: e.target.value})}>
                            <option value="Parents">Parents</option>
                            <option value="Guardian">Guardian</option>
                            <option value="Hostel">Hostel</option>
                          </select>
                        </div>

                        {studentFormData.accommodationType === 'Guardian' && (
                          <div style={{ padding: '14px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe', marginBottom: '16px' }}>
                            <h4 style={{ fontSize: '13px', fontWeight: '700', color: '#1e40af', marginBottom: '10px' }}>Guardian Details</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '10px' }}>
                              <div className="form-group">
                                <label style={{ fontSize: '12.5px', fontWeight: '600' }}>Guardian Name *</label>
                                <input type="text" className="form-control" value={studentFormData.guardianName || ''} onChange={(e)=>setStudentFormData({...studentFormData, guardianName: e.target.value})} placeholder="Guardian full name" />
                              </div>
                              <div className="form-group">
                                <label style={{ fontSize: '12.5px', fontWeight: '600' }}>Guardian Phone *</label>
                                <input type="tel" className="form-control" value={studentFormData.guardianPhone || ''} onChange={(e)=>setStudentFormData({...studentFormData, guardianPhone: e.target.value})} placeholder="Guardian phone number" />
                              </div>
                            </div>
                            <div className="form-group">
                              <label style={{ fontSize: '12.5px', fontWeight: '600' }}>Guardian Full Address</label>
                              <textarea className="form-control" rows="2" value={studentFormData.guardianAddress || ''} onChange={(e)=>setStudentFormData({...studentFormData, guardianAddress: e.target.value})} placeholder="Complete guardian address" />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Step 4: Contact & Previous Schooling */}
                    {modalStep === 4 && (
                      <div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Primary Parent Contact Phone *</label>
                            <input 
                              id="input-field-parentPhone"
                              type="tel" 
                              className="form-control" 
                              value={studentFormData.parentPhone || ''} 
                              onChange={(e)=>{
                                const val = e.target.value;
                                setStudentFormData({...studentFormData, parentPhone: val, fatherPhone: studentFormData.fatherPhone || val});
                                if (validationErrors.parentPhone) setValidationErrors(prev => ({ ...prev, parentPhone: null }));
                              }} 
                              style={{ borderColor: validationErrors.parentPhone ? '#ef4444' : undefined }}
                              required 
                              placeholder="10-digit mobile number" 
                              maxLength="10" 
                            />
                            {validationErrors.parentPhone && (
                              <div className="inline-field-error">⚠️ {validationErrors.parentPhone}</div>
                            )}
                          </div>

                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Student Mobile Phone</label>
                            <input type="tel" className="form-control" value={studentFormData.phone || ''} onChange={(e)=>setStudentFormData({...studentFormData, phone: e.target.value})} placeholder="Student mobile number" />
                          </div>

                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Emergency Secondary Phone</label>
                            <input type="tel" className="form-control" value={studentFormData.emergencyPhone || ''} onChange={(e)=>setStudentFormData({...studentFormData, emergencyPhone: e.target.value})} placeholder="Emergency phone" />
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Email Address</label>
                            <input type="email" className="form-control" value={studentFormData.email || ''} onChange={(e)=>setStudentFormData({...studentFormData, email: e.target.value})} placeholder="student@example.com" />
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Aadhaar Number</label>
                            <input type="text" className="form-control" value={studentFormData.aadhaarNumber || ''} onChange={(e)=>setStudentFormData({...studentFormData, aadhaarNumber: e.target.value})} placeholder="12-digit Aadhaar number" maxLength="12" />
                          </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: '16px' }}>
                          <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Permanent / Present Residential Address</label>
                          <textarea className="form-control" value={studentFormData.address || ''} onChange={(e)=>setStudentFormData({...studentFormData, address: e.target.value})} rows="2" placeholder="Enter complete residential address" />
                        </div>

                        <div style={{ padding: '14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginTop: '16px' }}>
                          <h4 style={{ fontSize: '13px', fontWeight: '700', color: '#334155', marginBottom: '10px' }}>Previous Schooling & Transfer Certificate (TC) Details</h4>
                          
                          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', marginBottom: '10px' }}>
                            <div className="form-group">
                              <label style={{ fontSize: '12px' }}>Previous School Attended</label>
                              <input type="text" className="form-control" value={studentFormData.previousSchoolName || ''} onChange={(e)=>setStudentFormData({...studentFormData, previousSchoolName: e.target.value})} placeholder="Name of previous school" />
                            </div>
                            <div className="form-group">
                              <label style={{ fontSize: '12px' }}>Standard Covered</label>
                              <input type="text" className="form-control" value={studentFormData.previousSchoolStandard || ''} onChange={(e)=>setStudentFormData({...studentFormData, previousSchoolStandard: e.target.value})} placeholder="Class/Standard" />
                            </div>
                            <div className="form-group">
                              <label style={{ fontSize: '12px' }}>Medium</label>
                              <input type="text" className="form-control" value={studentFormData.mediumOfInstruction || ''} onChange={(e)=>setStudentFormData({...studentFormData, mediumOfInstruction: e.target.value})} placeholder="e.g. English, Kannada" />
                            </div>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '10px' }}>
                            <div className="form-group">
                              <label style={{ fontSize: '12px' }}>Date of Leaving</label>
                              <input type="date" className="form-control" value={studentFormData.previousSchoolLeavingDate || ''} onChange={(e)=>setStudentFormData({...studentFormData, previousSchoolLeavingDate: e.target.value})} />
                            </div>
                            <div className="form-group">
                              <label style={{ fontSize: '12px' }}>Leaving Reasons</label>
                              <input type="text" className="form-control" value={studentFormData.previousSchoolLeavingReason || ''} onChange={(e)=>setStudentFormData({...studentFormData, previousSchoolLeavingReason: e.target.value})} placeholder="Reason for leaving previous school" />
                            </div>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                            <div className="form-group">
                              <label style={{ fontSize: '12px' }}>TC Produced?</label>
                              <select className="form-control" value={studentFormData.tcProduced || 'No'} onChange={(e)=>setStudentFormData({...studentFormData, tcProduced: e.target.value})}>
                                <option value="No">No</option>
                                <option value="Yes">Yes</option>
                              </select>
                            </div>
                            {studentFormData.tcProduced === 'Yes' && (
                              <>
                                <div className="form-group">
                                  <label style={{ fontSize: '12px' }}>TC Number *</label>
                                  <input type="text" className="form-control" value={studentFormData.tcNumber || ''} onChange={(e)=>setStudentFormData({...studentFormData, tcNumber: e.target.value})} placeholder="TC No." />
                                </div>
                                <div className="form-group">
                                  <label style={{ fontSize: '12px' }}>TC Issue Date *</label>
                                  <input type="date" className="form-control" value={studentFormData.tcDate || ''} onChange={(e)=>setStudentFormData({...studentFormData, tcDate: e.target.value})} />
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 5: Services & Final Review */}
                    {modalStep === 5 && (
                      <div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                          <div className="form-group">
                            <label style={{ fontSize: '12.5px', fontWeight: '600' }}>Vaccination Status</label>
                            <select className="form-control" value={studentFormData.vaccinationStatus || 'Vaccinated'} onChange={(e)=>setStudentFormData({...studentFormData, vaccinationStatus: e.target.value})}>
                              <option value="Vaccinated">Vaccinated</option>
                              <option value="Not Vaccinated">Not Vaccinated</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '12.5px', fontWeight: '600' }}>Hostel Facility</label>
                            <select className="form-control" value={studentFormData.hostelOpted || 'No'} onChange={(e)=>setStudentFormData({...studentFormData, hostelOpted: e.target.value})}>
                              <option value="No">No</option>
                              <option value="Yes">Yes</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '12.5px', fontWeight: '600' }}>School Bus / Transport</label>
                            <select className="form-control" value={studentFormData.transportOpted || 'No'} onChange={(e)=>setStudentFormData({...studentFormData, transportOpted: e.target.value})}>
                              <option value="No">No</option>
                              <option value="Yes">Yes</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label style={{ fontSize: '12.5px', fontWeight: '600' }}>Account Status</label>
                            <select className="form-control" value={studentFormData.status || 'Active'} onChange={(e)=>setStudentFormData({...studentFormData, status: e.target.value})}>
                              <option value="Active">Active</option>
                              <option value="Inactive">Inactive</option>
                            </select>
                          </div>
                        </div>

                        {/* Student / Parent Login Password Configuration */}
                        <div style={{ padding: '16px', background: '#eff6ff', borderRadius: '10px', border: '1px solid #bfdbfe', marginBottom: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                            <span style={{ fontSize: '16px' }}>🔑</span>
                            <div>
                              <h4 style={{ margin: 0, color: '#1e40af', fontSize: '14px', fontWeight: 700 }}>
                                {editingItem ? 'Change Student / Parent Portal Password' : 'Set Initial Student & Parent Login Password'}
                              </h4>
                              <p style={{ margin: '2px 0 0 0', color: '#3b82f6', fontSize: '12px' }}>
                                {editingItem 
                                  ? 'Leave blank to keep existing password, or type a new password to update login credentials for this student and parent.' 
                                  : `Default password is set to "${orgData?.defaultStudentPassword || localStorage.getItem(`erp_default_pwd_${orgData?.orgId || 'default'}`) || localStorage.getItem('erp_default_pwd_default') || 'Student@123'}" if left blank.`}
                              </p>
                            </div>
                          </div>
                          
                          <div style={{ maxWidth: '380px' }}>
                            <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#1e3a8a', display: 'block', marginBottom: '4px' }}>
                              {editingItem ? 'New Password' : 'Password'}
                            </label>
                            <input 
                              type="text" 
                              className="form-control" 
                              value={studentFormData.password || ''} 
                              onChange={(e) => setStudentFormData({ ...studentFormData, password: e.target.value })} 
                              placeholder={editingItem ? 'Enter new password to change (or leave blank)' : `e.g. ${orgData?.defaultStudentPassword || localStorage.getItem(`erp_default_pwd_${orgData?.orgId || 'default'}`) || localStorage.getItem('erp_default_pwd_default') || 'Student@123'}`} 
                              style={{ background: '#ffffff' }}
                            />
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                              💡 Parents log in to the mobile app using this <strong>Student ID</strong> and password.
                            </div>
                          </div>
                        </div>

                        {/* Admission Final Review Card */}
                        <div style={{ padding: '18px', background: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0', marginTop: '10px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', borderBottom: '1px solid #86efac', paddingBottom: '8px' }}>
                            <h4 style={{ margin: 0, color: '#166534', fontSize: '15px', fontWeight: 700 }}>📋 Admission Summary Verification</h4>
                            <span style={{ fontSize: '11px', background: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>Ready for Final Submission</span>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '12.5px', color: '#14532d' }}>
                            <div><strong>Student Name:</strong> {studentFormData.name || 'Not Entered'}</div>
                            <div><strong>Student ID:</strong> {editingItem ? studentFormData.studentId : 'Generated after admission'}</div>
                            <div><strong>Class & Section:</strong> {studentFormData.class || 'N/A'} {studentFormData.section ? `(${studentFormData.section})` : ''}</div>
                            <div><strong>Application No:</strong> {editingItem ? studentFormData.applicationNo : 'Generated after admission'}</div>
                            <div><strong>SATS / PEN No:</strong> {studentFormData.satsNo || 'N/A'} / {studentFormData.penNo || 'N/A'}</div>
                            <div><strong>Date of Birth:</strong> {studentFormData.dob || 'N/A'}</div>
                            <div><strong>Father's Name:</strong> {studentFormData.fatherName || 'N/A'} ({studentFormData.fatherLivingStatus || 'Living'})</div>
                            <div><strong>Primary Parent Phone:</strong> {studentFormData.parentPhone || 'N/A'}</div>
                            <div><strong>TC Produced:</strong> {studentFormData.tcProduced || 'No'} {studentFormData.tcNumber ? `(TC No: ${studentFormData.tcNumber})` : ''}</div>
                            <div><strong>Vaccination Status:</strong> {studentFormData.vaccinationStatus || 'Vaccinated'}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </form>
              </div>

              {/* Sticky Action Footer Bar */}
              <div className="fs-footer">
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button type="button" className="fs-btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>

                  <button 
                    type="button" 
                    className="fs-btn-secondary"
                    onClick={() => {
                      const userId = localStorage.getItem('userId') || localStorage.getItem('user_id');
                      if (orgData?.id && userId) {
                        const key = `erp_student_admission_draft_${orgData.id}_${userId}`;
                        localStorage.setItem(key, JSON.stringify(studentFormData));
                        setDraftToast('Draft saved to local storage!');
                      } else {
                        setDraftToast('Draft saved in current session!');
                      }
                      setTimeout(() => setDraftToast(null), 3000);
                    }}
                    title="Save current progress"
                  >
                    💾 Save Draft
                  </button>
                </div>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  {modalStep > 1 && (
                    <button type="button" className="fs-btn-secondary" onClick={() => setModalStep(prev => prev - 1)}>
                      ← Back
                    </button>
                  )}

                  {/* Quick Update Button available from any step when editing */}
                  {editingItem && modalStep < 5 && (
                    <button 
                      type="button" 
                      onClick={handleStudentSubmit}
                      className="fs-btn-primary"
                      disabled={loading || isSubmitting}
                      style={{ background: '#16a34a', borderColor: '#16a34a', opacity: (loading || isSubmitting) ? 0.7 : 1, cursor: (loading || isSubmitting) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      {(loading || isSubmitting) ? '⟳ Saving Changes...' : '💾 Update Record Now'}
                    </button>
                  )}

                  {modalStep < 5 ? (
                    <button 
                      type="button" 
                      className="fs-btn-primary" 
                      onClick={() => {
                        const errors = {};
                        if (modalStep === 1 && !studentFormData.class) {
                          errors.class = 'Please select a Class before continuing';
                        }
                        if (modalStep === 2 && !studentFormData.name) {
                          errors.name = 'Please enter pupil full name before continuing';
                        }
                        if (modalStep === 4 && (!studentFormData.parentPhone || studentFormData.parentPhone.length < 10)) {
                          errors.parentPhone = 'Please enter a valid 10-digit primary parent contact phone number';
                        }

                        if (Object.keys(errors).length > 0) {
                          setValidationErrors(errors);
                          const firstErrKey = Object.keys(errors)[0];
                          const el = document.getElementById(`input-field-${firstErrKey}`);
                          if (el) {
                            el.focus();
                            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }
                          return;
                        }

                        setValidationErrors({});
                        setModalStep(prev => prev + 1);
                      }}
                    >
                      Next Step →
                    </button>
                  ) : (
                    <button 
                      type="button" 
                      onClick={handleStudentSubmit}
                      className="fs-btn-primary"
                      disabled={loading || isSubmitting}
                      style={{ background: '#16a34a', borderColor: '#16a34a', opacity: (loading || isSubmitting) ? 0.7 : 1, cursor: (loading || isSubmitting) ? 'not-allowed' : 'pointer' }}
                    >
                      {(loading || isSubmitting) ? '⟳ Creating Admission...' : (editingItem ? 'Update Student Record' : '✓ Submit Admission')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Teacher Modal (Enterprise 2-Column ERP Architecture) */}
      {showTeacherModal && (
        <div className="fs-modal-overlay">
          <div className="fs-modal-card">
            {/* Header Bar */}
            <div className="fs-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>
                  {editingTeacher ? `Edit Faculty Record: ${editingTeacher.teacherName}` : 'Add New Faculty Member'}
                </h3>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                  {orgData?.orgName || 'School ERP'} • Faculty & Staff Directory
                </div>
              </div>
              <button 
                type="button" 
                className="fs-close-btn" 
                onClick={() => setShowTeacherModal(false)}
                title="Close"
              >
                ✕
              </button>
            </div>

            {/* Scrollable Form Body */}
            <div className="fs-body" style={{ padding: '24px 28px' }}>
              <form id="school-teacher-form" onSubmit={handleTeacherSubmit}>
                {/* Notice Bar for Auto-Generated ID */}
                {!editingTeacher && (
                  <div style={{ padding: '10px 14px', background: '#eff6ff', border: '1px solid #dbeafe', borderRadius: '8px', marginBottom: '20px', fontSize: '12.5px', color: '#1e40af', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px' }}>⚙️</span>
                    <span><strong>Auto-Generated Teacher ID:</strong> Permanent official Teacher ID will be generated automatically by the server upon saving.</span>
                  </div>
                )}

                {/* Faculty Photo Upload Dropzone */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '16px 20px', background: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1', marginBottom: '24px' }}>
                  <div style={{ position: 'relative', width: '84px', height: '84px', borderRadius: '12px', overflow: 'hidden', background: '#e2e8f0', border: '3px solid #2563eb', display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0, boxShadow: '0 4px 10px rgba(37,99,235,0.15)' }}>
                    {teacherFormData.photo ? (
                      <img 
                        src={teacherFormData.photo.startsWith('data:') ? teacherFormData.photo : (teacherFormData.photo.startsWith('http') ? teacherFormData.photo : `${API_BASE_URL}${teacherFormData.photo.startsWith('/') ? '' : '/'}${teacherFormData.photo}`)} 
                        alt="Faculty Profile" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                      />
                    ) : (
                      <div style={{ textAlign: 'center', color: '#64748b' }}>
                        <span style={{ fontSize: '28px', display: 'block' }}>👨‍🏫</span>
                        <span style={{ fontSize: '9px', fontWeight: '700', color: '#94a3b8' }}>NO PHOTO</span>
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13.5px', fontWeight: '700', color: '#1e293b', marginBottom: '3px' }}>
                      Official Faculty Photo (Used on Staff ID Cards & Portals)
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '10px' }}>
                      Upload passport-style color photo. Supported: JPG, PNG, WEBP (Max: 2MB).
                    </div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <label style={{ padding: '6px 14px', background: '#2563eb', color: '#ffffff', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', boxShadow: '0 2px 6px rgba(37,99,235,0.25)' }}>
                        <span>📷</span> {teacherFormData.photo ? 'Change Photo' : 'Upload Photo'}
                        <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp" onChange={handleTeacherPhotoUpload} style={{ display: 'none' }} />
                      </label>
                      {teacherFormData.photo && (
                        <button 
                          type="button" 
                          onClick={() => setTeacherFormData(prev => ({ ...prev, photo: '', photoUrl: '' }))} 
                          style={{ padding: '6px 12px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                        >
                          Remove Photo
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Section 1: Basic Identity & Designation */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '14px' }}>
                    👤 1. Identity & Designation
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Teacher ID *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editingTeacher ? teacherFormData.teacherId : (teacherFormData.teacherId || '⚙ Auto-generated on save')}
                        onChange={(e) => setTeacherFormData({...teacherFormData, teacherId: e.target.value})}
                        placeholder="Teacher ID"
                        required={Boolean(editingTeacher)}
                        disabled={Boolean(editingTeacher) || !teacherFormData.teacherId}
                        style={{ background: '#f8fafc', fontWeight: '600', color: '#2563eb' }}
                      />
                      {editingTeacher && <small style={{ color: '#64748b', fontSize: '11px' }}>Teacher ID is locked during editing</small>}
                    </div>

                    <div className="form-group">
                      <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Teacher Full Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={teacherFormData.teacherName}
                        onChange={(e) => setTeacherFormData({...teacherFormData, teacherName: e.target.value})}
                        placeholder="Enter full name (e.g., Anita Kumari)"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Designation / Role *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={teacherFormData.designation}
                        onChange={(e) => setTeacherFormData({...teacherFormData, designation: e.target.value})}
                        placeholder="e.g., PGT, TGT, PRT, Headmaster"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Primary Assigned Subject</label>
                      <select
                        className="form-control"
                        value={(() => {
                          if (!teacherFormData.subjects || teacherFormData.subjects.length === 0) return '';
                          const primary = teacherFormData.subjects.find(s => s.isPrimary === true || s.isPrimary === 'true');
                          const sub = primary || teacherFormData.subjects[0];
                          return sub ? (sub.subjectId || '') : '';
                        })()}
                        onChange={(e) => {
                          const selectedSubjectId = e.target.value;
                          if (selectedSubjectId) {
                            const selectedSubject = subjects.find(s => String(s.subjectId) === String(selectedSubjectId));
                            if (selectedSubject) {
                              setTeacherFormData(prev => ({
                                ...prev,
                                subjects: [{
                                  subjectId: selectedSubject.subjectId,
                                  academicYear: orgData?.academicYear || new Date().getFullYear().toString(),
                                  isPrimary: true
                                }]
                              }));
                            }
                          } else {
                            setTeacherFormData(prev => ({ ...prev, subjects: [] }));
                          }
                        }}
                      >
                        <option value="">Select Primary Subject</option>
                        {subjects && subjects.length > 0 && subjects.map(subject => (
                          <option key={subject.subjectId} value={subject.subjectId}>
                            {subject.subjectName} ({subject.subjectCode})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Date of Birth</label>
                      <input
                        type="date"
                        className="form-control"
                        value={teacherFormData.dob}
                        onChange={(e) => setTeacherFormData({...teacherFormData, dob: e.target.value})}
                      />
                    </div>

                    <div className="form-group">
                      <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Teaching Experience (Years) *</label>
                      <input
                        type="number"
                        className="form-control"
                        value={teacherFormData.experience}
                        onChange={(e) => setTeacherFormData({...teacherFormData, experience: e.target.value})}
                        placeholder="Years of experience (e.g. 5)"
                        min="0"
                        max="60"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Section 2: Academic Profile & Specialization */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '14px' }}>
                    🎓 2. Academic Profile & Specialization
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Highest Degree</label>
                      <input
                        type="text"
                        className="form-control"
                        value={teacherFormData.degree}
                        onChange={(e) => setTeacherFormData({...teacherFormData, degree: e.target.value})}
                        placeholder="e.g., M.Sc, M.A, B.Tech"
                      />
                    </div>

                    <div className="form-group">
                      <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Professional Qualifications</label>
                      <input
                        type="text"
                        className="form-control"
                        value={teacherFormData.qualifications}
                        onChange={(e) => setTeacherFormData({...teacherFormData, qualifications: e.target.value})}
                        placeholder="e.g., B.Ed, M.Ed, NET, Ph.D"
                      />
                    </div>

                    <div className="form-group">
                      <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Specialization Subject</label>
                      <input
                        type="text"
                        className="form-control"
                        value={teacherFormData.specialization}
                        onChange={(e) => setTeacherFormData({...teacherFormData, specialization: e.target.value})}
                        placeholder="e.g., Mathematics, Physics"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 3: Contact & Account Credentials */}
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '14px' }}>
                    📞 3. Contact & Access Credentials
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Phone Number *</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={teacherFormData.phoneNumber}
                        onChange={(e) => setTeacherFormData({...teacherFormData, phoneNumber: e.target.value})}
                        placeholder="10-digit mobile number"
                        maxLength="10"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Email Address *</label>
                      <input
                        type="email"
                        className="form-control"
                        value={teacherFormData.gmail}
                        onChange={(e) => setTeacherFormData({...teacherFormData, gmail: e.target.value})}
                        placeholder="teacher@school.edu"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>
                        {editingTeacher ? '🔑 Reset / New Password (Optional)' : 'Portal Password *'}
                      </label>
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <input
                          type={showTeacherPassword ? "text" : "password"}
                          className="form-control"
                          value={teacherFormData.password || ''}
                          onChange={(e) => setTeacherFormData({...teacherFormData, password: e.target.value})}
                          placeholder={editingTeacher ? 'Leave blank to keep current password' : 'Initial login password'}
                          required={!editingTeacher}
                          style={{ paddingRight: '44px', width: '100%' }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowTeacherPassword(prev => !prev)}
                          title={showTeacherPassword ? "Hide password" : "Show password"}
                          style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px 8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#64748b'
                          }}
                        >
                          {showTeacherPassword ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                              <line x1="1" y1="1" x2="23" y2="23" />
                            </svg>
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          )}
                        </button>
                      </div>
                      {editingTeacher && (
                        <small style={{ color: '#64748b', fontSize: '11px', display: 'block', marginTop: '4px' }}>
                          Enter a new password to reset this teacher's portal/mobile app password.
                        </small>
                      )}
                    </div>

                    <div className="form-group">
                      <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Employment Status *</label>
                      <select
                        className="form-control"
                        value={teacherFormData.status || 'ACTIVE'}
                        onChange={(e) => setTeacherFormData({...teacherFormData, status: e.target.value})}
                      >
                        <option value="ACTIVE">Active</option>
                        <option value="INACTIVE">Inactive</option>
                      </select>
                    </div>

                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                      <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Residential Address</label>
                      <textarea
                        className="form-control"
                        value={teacherFormData.address}
                        onChange={(e) => setTeacherFormData({...teacherFormData, address: e.target.value})}
                        placeholder="Full residential address..."
                        rows="2"
                      />
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {/* Sticky Action Footer Bar */}
            <div className="fs-footer">
              <button 
                type="button" 
                className="fs-btn-secondary" 
                onClick={() => setShowTeacherModal(false)}
              >
                Cancel
              </button>

              <button 
                type="submit" 
                form="school-teacher-form"
                className="fs-btn-primary"
                disabled={loading || isSubmitting}
                style={{
                  opacity: (loading || isSubmitting) ? 0.7 : 1,
                  cursor: (loading || isSubmitting) ? 'not-allowed' : 'pointer'
                }}
              >
                {(loading || isSubmitting) ? '⟳ Saving Record...' : (editingTeacher ? '✓ Update Teacher Record' : '✓ Create Teacher Record')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Add Students Modal (Enterprise ERP Architecture) */}
      {showBulkAddModal && (
        <div className="fs-modal-overlay">
          <div className="bulk-modal-container">
            {/* 1. Header Bar */}
            <div className="fs-header" style={{ padding: '18px 28px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Bulk Student Import</h3>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                  {orgData?.orgName || 'School ERP'} • Add multiple students manually or import from Excel/CSV
                </div>
              </div>
              <button 
                type="button" 
                className="fs-close-btn" 
                onClick={() => {
                  setShowBulkAddModal(false);
                  setBulkSuccessResult(null);
                  setBulkFileError(null);
                }} 
                title="Close"
              >
                ✕
              </button>
            </div>

            {/* 2. Success Result Screen (if completed) */}
            {bulkSuccessResult ? (
              <div style={{ padding: '40px 30px', textAlign: 'center', background: '#ffffff', flex: 1, overflowY: 'auto' }}>
                <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', margin: '0 auto 16px auto', border: '2px solid #bbf7d0' }}>
                  ✓
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#166534', margin: '0 0 8px 0' }}>
                  Students Imported Successfully!
                </h3>
                <p style={{ color: '#475569', fontSize: '14px', marginBottom: '24px' }}>
                  Successfully created <strong>{bulkSuccessResult.totalCount}</strong> official student record(s). All Student IDs and Application Numbers were assigned by the server.
                </p>

                <div style={{ maxWidth: '600px', margin: '0 auto 30px auto', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px', textContent: 'left' }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#334155', fontWeight: '700', textAlign: 'left' }}>
                    Imported Records Summary
                  </h4>
                  <div style={{ maxHeight: '180px', overflowY: 'auto', fontSize: '12.5px', color: '#475569', textAlign: 'left' }}>
                    {bulkSuccessResult.students.map((st, idx) => (
                      <div key={st.studentId || idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                        <span><strong>{st.name}</strong> ({st.className || st.class})</span>
                        <span style={{ fontFamily: 'monospace', color: '#2563eb' }}>ID: {st.studentId}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <button 
                    type="button" 
                    className="fs-btn-secondary"
                    onClick={() => {
                      setShowBulkAddModal(false);
                      setBulkSuccessResult(null);
                    }}
                  >
                    View Student List
                  </button>

                  <button 
                    type="button" 
                    className="fs-btn-primary"
                    onClick={() => {
                      setBulkSuccessResult(null);
                      setBulkStudentRows([createEmptyBulkStudentRow()]);
                      setBulkImportMode('manual');
                    }}
                  >
                    Import More Students
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Mode Selector Tabs */}
                <div style={{ padding: '12px 28px 0 28px', background: '#ffffff' }}>
                  <div className="bulk-mode-tabs">
                    <button 
                      type="button" 
                      className={`bulk-mode-tab ${bulkImportMode === 'manual' ? 'active' : ''}`}
                      onClick={() => setBulkImportMode('manual')}
                    >
                      📝 Manual Entry Cards (2–10 Students)
                    </button>
                    <button 
                      type="button" 
                      className={`bulk-mode-tab ${bulkImportMode === 'excel' ? 'active' : ''}`}
                      onClick={() => setBulkImportMode('excel')}
                    >
                      📊 Excel / CSV File Import (Large Batches)
                    </button>
                  </div>
                </div>

                {/* Modal Body Scroll Area */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px', background: '#f8fafc' }}>
                  {/* File Error Notification */}
                  {bulkFileError && (
                    <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', color: '#dc2626', fontSize: '13px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>⚠️ {bulkFileError}</span>
                      <button type="button" onClick={() => setBulkFileError(null)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontWeight: '700' }}>✕</button>
                    </div>
                  )}

                  {/* Mode A: Manual Card Entry */}
                  {bulkImportMode === 'manual' && (
                    <div>
                      {/* Manual Toolbar */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <div style={{ fontSize: '13px', color: '#475569', fontWeight: '600' }}>
                          Fill required admission details per student. Student IDs and Application Numbers are generated upon submission.
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button 
                            type="button" 
                            className="fs-btn-secondary" 
                            onClick={() => addMoreBulkRows(1)}
                            style={{ padding: '6px 14px', fontSize: '12.5px' }}
                          >
                            + Add Student Row
                          </button>
                          <button 
                            type="button" 
                            className="fs-btn-secondary" 
                            onClick={() => addMoreBulkRows(5)}
                            style={{ padding: '6px 14px', fontSize: '12.5px' }}
                          >
                            + Add 5 Rows
                          </button>
                        </div>
                      </div>

                      {/* Manual Student Cards */}
                      {bulkStudentRows.map((row, index) => {
                        const valRes = validateBulkStudentRow(row, index, bulkStudentRows, students);
                        const hasErrors = !valRes.isCompletelyEmpty && valRes.errors.length > 0;
                        const isReady = !valRes.isCompletelyEmpty && valRes.errors.length === 0;

                        return (
                          <div key={index} className={`bulk-card ${hasErrors ? 'has-error' : ''}`}>
                            <div className="bulk-card-header">
                              <div className="bulk-card-title">
                                <span>STUDENT {String(index + 1).padStart(2, '0')}</span>
                                {valRes.isCompletelyEmpty && (
                                  <span className="bulk-status-badge" style={{ background: '#f1f5f9', color: '#64748b' }}>Empty Row</span>
                                )}
                                {isReady && (
                                  <span className="bulk-status-badge ready">✓ Ready</span>
                                )}
                                {hasErrors && (
                                  <span className="bulk-status-badge error">⚠ {valRes.errors.length} Issue(s)</span>
                                )}
                              </div>

                              <button 
                                type="button" 
                                onClick={() => removeBulkRow(index)}
                                disabled={bulkStudentRows.length === 1}
                                style={{ background: 'none', border: 'none', color: bulkStudentRows.length === 1 ? '#cbd5e1' : '#ef4444', cursor: bulkStudentRows.length === 1 ? 'not-allowed' : 'pointer', fontSize: '14px', padding: '4px' }}
                                title="Remove row"
                              >
                                🗑️
                              </button>
                            </div>

                            {/* Card Line 1: Name, DOB, Gender, Class, Section */}
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                              <div className="form-group">
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#334155' }}>Student Full Name *</label>
                                <input 
                                  type="text" 
                                  className="form-control" 
                                  value={row.name || ''} 
                                  onChange={(e) => updateBulkRow(index, 'name', e.target.value.toUpperCase())}
                                  placeholder="PUPIL FULL NAME" 
                                />
                              </div>
                              <div className="form-group">
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#334155' }}>Date of Birth *</label>
                                <input 
                                  type="date" 
                                  className="form-control" 
                                  value={row.dob || ''} 
                                  onChange={(e) => updateBulkRow(index, 'dob', e.target.value)} 
                                />
                              </div>
                              <div className="form-group">
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#334155' }}>Gender *</label>
                                <select 
                                  className="form-control" 
                                  value={row.gender || 'Boy'} 
                                  onChange={(e) => updateBulkRow(index, 'gender', e.target.value)}
                                >
                                  <option value="Boy">Boy</option>
                                  <option value="Girl">Girl</option>
                                  <option value="Other">Other</option>
                                </select>
                              </div>
                              <div className="form-group">
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#334155' }}>Class *</label>
                                <select 
                                  className="form-control" 
                                  value={row.class || ''} 
                                  onChange={(e) => updateBulkRow(index, 'class', e.target.value)}
                                >
                                  <option value="">Select Class</option>
                                  {classOptions.map(c => (
                                    <option key={c.id} value={c.label}>{c.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="form-group">
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#334155' }}>Section</label>
                                <select 
                                  className="form-control" 
                                  value={row.section || ''} 
                                  onChange={(e) => updateBulkRow(index, 'section', e.target.value)}
                                >
                                  <option value="">Select</option>
                                  {sectionOptions.map(sec => (
                                    <option key={sec} value={sec}>{sec}</option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            {/* Card Line 2: Parent Phone, Admission Date, SATS No, PEN No */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr', gap: '12px' }}>
                              <div className="form-group">
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#334155' }}>Primary Parent Phone *</label>
                                <input 
                                  type="tel" 
                                  className="form-control" 
                                  value={row.parentPhone || ''} 
                                  onChange={(e) => updateBulkRow(index, 'parentPhone', e.target.value)}
                                  placeholder="10-digit phone number"
                                  maxLength="10" 
                                />
                              </div>
                              <div className="form-group">
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#334155' }}>Admission Date</label>
                                <input 
                                  type="date" 
                                  className="form-control" 
                                  value={row.admissionDate || ''} 
                                  onChange={(e) => updateBulkRow(index, 'admissionDate', e.target.value)} 
                                />
                              </div>
                              <div className="form-group">
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#334155' }}>SATS No (Optional)</label>
                                <input 
                                  type="text" 
                                  className="form-control" 
                                  value={row.satsNo || ''} 
                                  onChange={(e) => updateBulkRow(index, 'satsNo', e.target.value)}
                                  placeholder="SATS ID" 
                                />
                              </div>
                              <div className="form-group">
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#334155' }}>PEN No (Optional)</label>
                                <input 
                                  type="text" 
                                  className="form-control" 
                                  value={row.penNo || ''} 
                                  onChange={(e) => updateBulkRow(index, 'penNo', e.target.value)}
                                  placeholder="PEN ID" 
                                />
                              </div>
                            </div>

                            {/* Inline Validation Warnings Callout */}
                            {hasErrors && (
                              <div style={{ marginTop: '10px', padding: '6px 12px', background: '#fee2e2', borderRadius: '6px', fontSize: '12px', color: '#dc2626' }}>
                                {valRes.errors.map((err, errIdx) => (
                                  <div key={errIdx}>⚠️ {err}</div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Mode B: Excel / CSV File Import & Preview */}
                  {bulkImportMode === 'excel' && (
                    <div>
                      {/* Upload Dropzone & Template Download Bar */}
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '20px' }}>
                        <div className="bulk-upload-dropzone" onClick={() => document.getElementById('excel-file-input').click()}>
                          <div style={{ fontSize: '32px', marginBottom: '8px' }}>📊</div>
                          <div style={{ fontWeight: '700', fontSize: '14px', color: '#0f172a', marginBottom: '4px' }}>
                            Upload Student Spreadsheet (.xlsx or .csv)
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '12px' }}>
                            Drag and drop your spreadsheet here or click to browse (Max 500 rows, 5 MB)
                          </div>
                          <button type="button" className="fs-btn-secondary" style={{ padding: '6px 16px', fontSize: '12.5px' }}>
                            Choose File
                          </button>
                          <input 
                            id="excel-file-input" 
                            type="file" 
                            accept=".xlsx, .xls, .csv" 
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                handleExcelFileUpload(e.target.files[0]);
                              }
                            }}
                            style={{ display: 'none' }} 
                          />
                        </div>

                        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>
                            Need an Official Template?
                          </h4>
                          <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 16px 0', lineHeight: '1.4' }}>
                            Download the standard School ERP Excel template pre-formatted with exact column headers and text cell types.
                          </p>
                          <button 
                            type="button" 
                            className="fs-btn-secondary" 
                            onClick={handleDownloadBulkTemplate}
                            style={{ width: '100%', justifyContent: 'center', display: 'flex', gap: '6px' }}
                          >
                            📥 Download Template (.xlsx)
                          </button>
                        </div>
                      </div>

                      {/* Excel Batch Preview Table Section */}
                      {bulkStudentRows.length > 0 && (
                        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
                          {/* Filter & Summary Header */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                            <div style={{ fontWeight: '700', fontSize: '14px', color: '#0f172a' }}>
                              Batch Preview Table ({bulkStudentRows.length} Rows Parsed)
                            </div>

                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button 
                                type="button" 
                                className={`fs-btn-secondary ${bulkPreviewFilter === 'all' ? 'active' : ''}`}
                                onClick={() => setBulkPreviewFilter('all')}
                                style={{ padding: '4px 12px', fontSize: '12px', background: bulkPreviewFilter === 'all' ? '#eff6ff' : 'white', borderColor: bulkPreviewFilter === 'all' ? '#2563eb' : '#cbd5e1', color: bulkPreviewFilter === 'all' ? '#2563eb' : '#475569' }}
                              >
                                All ({bulkStudentRows.length})
                              </button>

                              {(() => {
                                let errCount = 0;
                                bulkStudentRows.forEach((r, idx) => {
                                  const res = validateBulkStudentRow(r, idx, bulkStudentRows, students);
                                  if (!res.isCompletelyEmpty && res.errors.length > 0) errCount++;
                                });
                                return (
                                  <button 
                                    type="button" 
                                    className={`fs-btn-secondary ${bulkPreviewFilter === 'errors' ? 'active' : ''}`}
                                    onClick={() => setBulkPreviewFilter('errors')}
                                    style={{ padding: '4px 12px', fontSize: '12px', background: bulkPreviewFilter === 'errors' ? '#fef2f2' : 'white', borderColor: bulkPreviewFilter === 'errors' ? '#ef4444' : '#cbd5e1', color: bulkPreviewFilter === 'errors' ? '#dc2626' : '#475569' }}
                                  >
                                    Errors Only ({errCount})
                                  </button>
                                );
                              })()}
                            </div>
                          </div>

                          {/* Compact Table */}
                          <div style={{ overflowX: 'auto', maxHeight: '380px', overflowY: 'auto' }}>
                            <table className="bulk-preview-table">
                              <thead>
                                <tr>
                                  <th style={{ width: '40px' }}>#</th>
                                  <th>Student Name *</th>
                                  <th>DOB *</th>
                                  <th>Gender *</th>
                                  <th>Class *</th>
                                  <th>Section</th>
                                  <th>Parent Phone *</th>
                                  <th>SATS / PEN</th>
                                  <th>Validation Status</th>
                                  <th style={{ textAlign: 'center', width: '40px' }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {bulkStudentRows
                                  .map((row, originalIdx) => ({ row, originalIdx }))
                                  .filter(({ row, originalIdx }) => {
                                    if (bulkPreviewFilter === 'errors') {
                                      const res = validateBulkStudentRow(row, originalIdx, bulkStudentRows, students);
                                      return !res.isCompletelyEmpty && res.errors.length > 0;
                                    }
                                    return true;
                                  })
                                  .map(({ row, originalIdx }) => {
                                    const valRes = validateBulkStudentRow(row, originalIdx, bulkStudentRows, students);
                                    const hasErr = !valRes.isCompletelyEmpty && valRes.errors.length > 0;
                                    const isOk = !valRes.isCompletelyEmpty && valRes.errors.length === 0;

                                    return (
                                      <tr key={originalIdx} className={hasErr ? 'has-error' : ''}>
                                        <td style={{ fontWeight: '600', color: '#64748b' }}>{originalIdx + 1}</td>
                                        <td>
                                          <input 
                                            type="text" 
                                            value={row.name || ''} 
                                            onChange={(e) => updateBulkRow(originalIdx, 'name', e.target.value.toUpperCase())}
                                            style={{ width: '100%', padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px' }} 
                                          />
                                        </td>
                                        <td>
                                          <input 
                                            type="date" 
                                            value={row.dob || ''} 
                                            onChange={(e) => updateBulkRow(originalIdx, 'dob', e.target.value)}
                                            style={{ width: '100%', padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px' }} 
                                          />
                                        </td>
                                        <td>
                                          <select 
                                            value={row.gender || 'Boy'} 
                                            onChange={(e) => updateBulkRow(originalIdx, 'gender', e.target.value)}
                                            style={{ width: '100%', padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px' }}
                                          >
                                            <option value="Boy">Boy</option>
                                            <option value="Girl">Girl</option>
                                            <option value="Other">Other</option>
                                          </select>
                                        </td>
                                        <td>
                                          <select 
                                            value={row.class || ''} 
                                            onChange={(e) => updateBulkRow(originalIdx, 'class', e.target.value)}
                                            style={{ width: '100%', padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px' }}
                                          >
                                            <option value="">Select</option>
                                            {classOptions.map(c => (
                                              <option key={c.id} value={c.label}>{c.label}</option>
                                            ))}
                                          </select>
                                        </td>
                                        <td>
                                          <input 
                                            type="text" 
                                            value={row.section || ''} 
                                            onChange={(e) => updateBulkRow(originalIdx, 'section', e.target.value)}
                                            style={{ width: '60px', padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px' }} 
                                          />
                                        </td>
                                        <td>
                                          <input 
                                            type="tel" 
                                            value={row.parentPhone || ''} 
                                            onChange={(e) => updateBulkRow(originalIdx, 'parentPhone', e.target.value)}
                                            style={{ width: '100%', padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px' }} 
                                            maxLength="10"
                                          />
                                        </td>
                                        <td style={{ fontSize: '11.5px', color: '#64748b' }}>
                                          {row.satsNo || row.penNo ? `${row.satsNo || '—'} / ${row.penNo || '—'}` : '—'}
                                        </td>
                                        <td>
                                          {isOk && <span className="bulk-status-badge ready">✓ Ready</span>}
                                          {hasErr && (
                                            <span className="bulk-status-badge error" title={valRes.errors.join(' | ')}>
                                              ⚠ {valRes.errors[0]}
                                            </span>
                                          )}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                          <button 
                                            type="button" 
                                            onClick={() => removeBulkRow(originalIdx)}
                                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px' }}
                                            title="Delete row"
                                          >
                                            🗑️
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* 3. Sticky Action Footer Bar */}
                <div className="fs-footer">
                  {/* Left: Validation Summary Badge */}
                  <div>
                    {(() => {
                      let readyCount = 0;
                      let errorCount = 0;
                      const activeRows = bulkStudentRows.filter(r => {
                        const res = validateBulkStudentRow(r, 0, [], students);
                        return !res.isCompletelyEmpty;
                      });

                      activeRows.forEach((r, idx) => {
                        const res = validateBulkStudentRow(r, idx, activeRows, students);
                        if (res.errors.length === 0) readyCount++;
                        else errorCount++;
                      });

                      return (
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>
                            {activeRows.length} Student(s) Total
                          </span>
                          <span className="bulk-status-badge ready">
                            {readyCount} Ready
                          </span>
                          {errorCount > 0 && (
                            <span className="bulk-status-badge error">
                              {errorCount} Need Attention
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Right: Actions */}
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button 
                      type="button" 
                      className="fs-btn-secondary" 
                      onClick={() => setShowBulkAddModal(false)}
                    >
                      Cancel
                    </button>

                    {(() => {
                      const activeRows = bulkStudentRows.filter(r => {
                        const res = validateBulkStudentRow(r, 0, [], students);
                        return !res.isCompletelyEmpty;
                      });

                      let hasAnyErrors = activeRows.length === 0;
                      activeRows.forEach((r, idx) => {
                        const res = validateBulkStudentRow(r, idx, activeRows, students);
                        if (res.errors.length > 0) hasAnyErrors = true;
                      });

                      return (
                        <button 
                          type="button" 
                          onClick={handleBulkStudentSubmit}
                          className="fs-btn-primary"
                          disabled={loading || isSubmitting || hasAnyErrors}
                          style={{
                            background: hasAnyErrors ? '#94a3b8' : '#2563eb',
                            borderColor: hasAnyErrors ? '#94a3b8' : '#2563eb',
                            opacity: (loading || isSubmitting || hasAnyErrors) ? 0.65 : 1,
                            cursor: (loading || isSubmitting || hasAnyErrors) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {(loading || isSubmitting) ? `⟳ Importing ${activeRows.length} Students...` : `Import ${activeRows.length} Students`}
                        </button>
                      );
                    })()}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 1. Create Fee Structure Modal */}
      {showCreateStructureModal && (
        <div className="fs-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1050 }}>
          <div className="fs-modal-card" style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>
                {selectedStructure ? 'Edit Fee Structure' : 'Create Fee Structure'}
              </h3>
              <button onClick={() => setShowCreateStructureModal(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>
            
            <form onSubmit={async (e) => {
              e.preventDefault();
              const itemsTotal = structureFormData.items.reduce((acc, it) => acc + (parseFloat(it.amount) || 0), 0);
              const installmentsTotal = structureFormData.installments.reduce((acc, inst) => acc + (parseFloat(inst.amount) || 0), 0);
              
              if (itemsTotal !== installmentsTotal) {
                showNotification('error', 'Validation Error', `Total of items (₹${itemsTotal.toLocaleString()}) must match total installments (₹${installmentsTotal.toLocaleString()})`);
                return;
              }

              setLoading(true);
              try {
                const payload = {
                  ...structureFormData,
                  totalAmount: itemsTotal,
                  items: structureFormData.items.map(it => ({ ...it, amount: parseFloat(it.amount) })),
                  installments: structureFormData.installments.map(inst => ({ ...inst, amount: parseFloat(inst.amount) }))
                };
                await feeService.createFeeStructure(payload);
                showNotification('success', 'Success', 'Fee structure template saved successfully!');
                setShowCreateStructureModal(false);
                loadFees();
              } catch (err) {
                console.error(err);
                showNotification('error', 'Error', 'Failed to save fee structure');
              } finally {
                setLoading(false);
              }
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div className="form-group">
                  <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Structure Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. UKG Standard Fee"
                    value={structureFormData.name}
                    onChange={(e) => setStructureFormData({ ...structureFormData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Academic Year *</label>
                  <select
                    className="form-control"
                    value={structureFormData.academicYear}
                    onChange={(e) => setStructureFormData({ ...structureFormData, academicYear: e.target.value })}
                    required
                  >
                    <option value="2026-27">2026-27</option>
                    <option value="2025-26">2025-26</option>
                  </select>
                </div>
              </div>

              {/* Class mapping info */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div className="form-group">
                  <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Map to Class / Grade</label>
                  <select
                    className="form-control"
                    value={structureFormData.classId}
                    onChange={(e) => {
                      const selectedVal = e.target.value;
                      // Find class name matching this id
                      const classObj = classes.find(c => c.classId == selectedVal);
                      setStructureFormData({
                        ...structureFormData,
                        classId: selectedVal,
                        className: classObj ? classObj.className : ''
                      });
                    }}
                  >
                    <option value="">Select Class (Optional)</option>
                    {classes.map(c => (
                      <option key={c.classId} value={c.classId}>{c.className} (Section {c.section})</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Fee Components */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>💼 Fee Components Breakdown</h4>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => {
                      setStructureFormData({
                        ...structureFormData,
                        items: [...structureFormData.items, { feeType: '', amount: '' }]
                      });
                    }}
                  >
                    + Add Item
                  </button>
                </div>
                {structureFormData.items.map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Tuition Fee, Exam Fee"
                      value={item.feeType}
                      onChange={(e) => {
                        const newItems = [...structureFormData.items];
                        newItems[idx].feeType = e.target.value;
                        setStructureFormData({ ...structureFormData, items: newItems });
                      }}
                      required
                      style={{ flex: 2 }}
                    />
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Amount (₹)"
                      value={item.amount}
                      onChange={(e) => {
                        const newItems = [...structureFormData.items];
                        newItems[idx].amount = e.target.value;
                        setStructureFormData({ ...structureFormData, items: newItems });
                      }}
                      required
                      style={{ flex: 1 }}
                    />
                    {structureFormData.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const newItems = structureFormData.items.filter((_, i) => i !== idx);
                          setStructureFormData({ ...structureFormData, items: newItems });
                        }}
                        style={{ border: 'none', background: 'none', color: '#ef4444', fontSize: '18px', cursor: 'pointer' }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Installments */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>🗓️ Payment Schedule & Installments</h4>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => {
                      setStructureFormData({
                        ...structureFormData,
                        installments: [...structureFormData.installments, { installmentName: `Term ${structureFormData.installments.length + 1}`, amount: '', dueDate: '' }]
                      });
                    }}
                  >
                    + Add Term
                  </button>
                </div>
                {structureFormData.installments.map((inst, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Term 1"
                      value={inst.installmentName}
                      onChange={(e) => {
                        const newInsts = [...structureFormData.installments];
                        newInsts[idx].installmentName = e.target.value;
                        setStructureFormData({ ...structureFormData, installments: newInsts });
                      }}
                      required
                      style={{ flex: 2 }}
                    />
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Amount (₹)"
                      value={inst.amount}
                      onChange={(e) => {
                        const newInsts = [...structureFormData.installments];
                        newInsts[idx].amount = e.target.value;
                        setStructureFormData({ ...structureFormData, installments: newInsts });
                      }}
                      required
                      style={{ flex: 1.2 }}
                    />
                    <input
                      type="date"
                      className="form-control"
                      value={inst.dueDate}
                      onChange={(e) => {
                        const newInsts = [...structureFormData.installments];
                        newInsts[idx].dueDate = e.target.value;
                        setStructureFormData({ ...structureFormData, installments: newInsts });
                      }}
                      required
                      style={{ flex: 1.8 }}
                    />
                    {structureFormData.installments.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const newInsts = structureFormData.installments.filter((_, i) => i !== idx);
                          setStructureFormData({ ...structureFormData, installments: newInsts });
                        }}
                        style={{ border: 'none', background: 'none', color: '#ef4444', fontSize: '18px', cursor: 'pointer' }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
                <button type="button" className="btn btn-light" onClick={() => setShowCreateStructureModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">💾 Save Template</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 2. Assign Fee Structure Modal */}
      {showAssignStructureModal && (
        <div className="fs-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1050 }}>
          <div className="fs-modal-card" style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>Assign Fee Structure</h3>
              <button onClick={() => setShowAssignStructureModal(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>

            <div style={{ display: 'grid', gap: '14px', marginBottom: '20px' }}>
              <div className="form-group">
                <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Select Fee Structure Template *</label>
                <select
                  className="form-control"
                  value={assignFormData.feeStructureId}
                  onChange={(e) => setAssignFormData({ ...assignFormData, feeStructureId: e.target.value })}
                  required
                >
                  <option value="">-- Choose Template --</option>
                  {feeStructures.map(fs => (
                    <option key={fs.id} value={fs.id}>{fs.name} (₹{fs.totalAmount ? fs.totalAmount.toLocaleString() : '0'})</option>
                  ))}
                </select>
                {feeStructures.length === 0 && (
                  <div style={{
                    marginTop: '8px',
                    padding: '10px 12px',
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#1e40af',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px'
                  }}>
                    <span>⚠️ No fee structure templates found. Create one first!</span>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAssignStructureModal(false);
                        setShowCreateStructureModal(true);
                      }}
                      style={{
                        padding: '5px 12px',
                        background: '#2563eb',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '4px',
                        fontWeight: '600',
                        fontSize: '11.5px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      ＋ Create Template
                    </button>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Target Class *</label>
                <select
                  className="form-control"
                  value={assignFormData.className}
                  onChange={(e) => setAssignFormData({ ...assignFormData, className: e.target.value })}
                  required
                >
                  <option value="">-- Select Class --</option>
                  {Array.from(new Set([
                    ...students.map(s => s.className).filter(Boolean),
                    ...classes.map(c => c.className || c.name || c.courseName).filter(Boolean)
                  ])).map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Target Section (Optional)</label>
                <select
                  className="form-control"
                  value={assignFormData.section}
                  onChange={(e) => setAssignFormData({ ...assignFormData, section: e.target.value })}
                >
                  <option value="">All Sections</option>
                  {['A', 'B', 'C', 'D'].map(sec => (
                    <option key={sec} value={sec}>Section {sec}</option>
                  ))}
                </select>
              </div>

              {/* Preview Analysis Results */}
              {assignPreview && (
                <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>Assignment Preview Details:</h4>
                  <div>Total eligible students in class: <span style={{ fontWeight: '600' }}>{assignPreview.totalStudents}</span></div>
                  <div style={{ color: '#166534', marginTop: '4px' }}>• Ready for direct assignment: <span style={{ fontWeight: '700' }}>{assignPreview.eligible}</span></div>
                  <div style={{ color: '#2563eb', marginTop: '4px' }}>• Already assigned (no payments, will overwrite if enabled): <span style={{ fontWeight: '700' }}>{assignPreview.skippedNoPayments}</span></div>
                  <div style={{ color: '#dc2626', marginTop: '4px' }}>• Locked (with payment history, will be skipped): <span style={{ fontWeight: '700' }}>{assignPreview.lockedHasPayments}</span></div>
                  
                  {assignPreview.skippedNoPayments > 0 && (
                    <div style={{ marginTop: '12px', borderTop: '1px solid #cbd5e1', paddingTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        id="overwriteChk"
                        checked={assignFormData.overwrite}
                        onChange={(e) => setAssignFormData({ ...assignFormData, overwrite: e.target.checked })}
                      />
                      <label htmlFor="overwriteChk" style={{ margin: 0, fontWeight: '600', color: '#475569', cursor: 'pointer' }}>Overwrite existing unpaid assignments</label>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
              <button type="button" className="btn btn-light" onClick={() => setShowAssignStructureModal(false)}>Cancel</button>
              <button
                type="button"
                className="btn btn-success"
                disabled={!assignFormData.feeStructureId || !assignFormData.className}
                onClick={async () => {
                  if (!assignFormData.feeStructureId || !assignFormData.className) {
                    showNotification('warning', 'Warning', 'Please select template and target class first.');
                    return;
                  }
                  setLoading(true);
                  try {
                    const res = await feeService.assignFeeStructure(assignFormData);
                    if (res.success) {
                      showNotification('success', 'Success', `Bulk assignment complete! Assigned: ${res.assignedCount}, Skipped: ${res.skippedCount}`);
                      setShowAssignStructureModal(false);
                      loadFees();
                    } else {
                      showNotification('error', 'Error', res.message || 'Assignment failed');
                    }
                  } catch (err) {
                    console.error(err);
                    showNotification('error', 'Error', 'Failed to assign fee structure');
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                👥 Confirm Assignment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2.5. ✏️ Edit Student Fee & Immutable Audit History Modal */}
      {showEditFeeModal && selectedStudentForFeeEdit && (
        <div className="fs-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1050, padding: '16px' }}>
          <div className="fs-modal-card" style={{ background: '#fff', borderRadius: '14px', width: '100%', maxWidth: '780px', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
            {/* Modal Header */}
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #e2e8f0', background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h3 style={{ margin: 0, fontSize: '19px', fontWeight: '800', color: '#0f172a' }}>
                    {selectedFeeForEdit ? '✏️ Edit Student Fee' : '➕ Assign Student Fee'}
                  </h3>
                  <span style={{ background: selectedFeeForEdit ? '#dbeafe' : '#dcfce7', color: selectedFeeForEdit ? '#1e40af' : '#166534', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '700' }}>
                    {selectedFeeForEdit ? (selectedStudentForFeeEdit.className ? `Class ${selectedStudentForFeeEdit.className}` : 'General') : 'New Fee Assignment'}
                    {selectedFeeForEdit && selectedStudentForFeeEdit.section ? ` - Sec ${selectedStudentForFeeEdit.section}` : ''}
                  </span>
                </div>
                <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13px' }}>
                  Student: <strong style={{ color: '#1e293b' }}>{selectedStudentForFeeEdit.name}</strong> • ID: <strong style={{ color: '#1e293b' }}>{selectedStudentForFeeEdit.studentId}</strong>
                </p>
              </div>
              <button
                onClick={() => setShowEditFeeModal(false)}
                style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', cursor: 'pointer', color: '#64748b' }}
              >
                ✕
              </button>
            </div>

            {/* Tabs Header */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#fff', padding: '0 24px' }}>
              <button
                type="button"
                onClick={() => setFeeEditActiveTab('details')}
                style={{
                  padding: '12px 18px',
                  background: 'none',
                  border: 'none',
                  borderBottom: feeEditActiveTab === 'details' ? '2.5px solid #2563eb' : '2.5px solid transparent',
                  color: feeEditActiveTab === 'details' ? '#2563eb' : '#64748b',
                  fontWeight: feeEditActiveTab === 'details' ? '700' : '600',
                  fontSize: '13.5px',
                  cursor: 'pointer'
                }}
              >
                📝 Fee Details & Adjustments
              </button>
              <button
                type="button"
                onClick={() => setFeeEditActiveTab('history')}
                style={{
                  padding: '12px 18px',
                  background: 'none',
                  border: 'none',
                  borderBottom: feeEditActiveTab === 'history' ? '2.5px solid #2563eb' : '2.5px solid transparent',
                  color: feeEditActiveTab === 'history' ? '#2563eb' : '#64748b',
                  fontWeight: feeEditActiveTab === 'history' ? '700' : '600',
                  fontSize: '13.5px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                🕒 Audit Edit History
                {feeEditHistoryList.length > 0 && (
                  <span style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '9999px', padding: '1px 7px', fontSize: '11px', fontWeight: '700' }}>
                    {feeEditHistoryList.length}
                  </span>
                )}
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
              {feeEditActiveTab === 'details' ? (
                <form id="editFeeForm" onSubmit={handleSaveEditFee}>
                  {/* Real-time Calculation Summary Bar */}
                  {(() => {
                    const total = parseFloat(editFeeFormData.totalFees) || 0;
                    const concession = parseFloat(editFeeFormData.concessionAmount) || 0;
                    const penalty = parseFloat(editFeeFormData.penaltyAmount) || 0;
                    const paid = selectedFeeForEdit?.paidFees || 0;
                    const pending = Math.max(0, total - concession + penalty - paid);

                    return (
                      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 16px', marginBottom: '18px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', textAlign: 'center' }}>
                        <div>
                          <div style={{ fontSize: '11.5px', color: '#64748b', fontWeight: '600' }}>Total Fee</div>
                          <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginTop: '2px' }}>₹{total.toLocaleString('en-IN')}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '11.5px', color: '#166534', fontWeight: '600' }}>Concession (-)</div>
                          <div style={{ fontSize: '16px', fontWeight: '700', color: '#166534', marginTop: '2px' }}>₹{concession.toLocaleString('en-IN')}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '11.5px', color: '#991b1b', fontWeight: '600' }}>Penalty (+)</div>
                          <div style={{ fontSize: '16px', fontWeight: '700', color: '#991b1b', marginTop: '2px' }}>₹{penalty.toLocaleString('en-IN')}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '11.5px', color: '#2563eb', fontWeight: '600' }}>Paid (Locked)</div>
                          <div style={{ fontSize: '16px', fontWeight: '700', color: '#2563eb', marginTop: '2px' }}>₹{paid.toLocaleString('en-IN')}</div>
                        </div>
                        <div style={{ background: '#eff6ff', borderRadius: '8px', padding: '4px' }}>
                          <div style={{ fontSize: '11.5px', color: '#1e40af', fontWeight: '700' }}>Net Outstanding</div>
                          <div style={{ fontSize: '17px', fontWeight: '800', color: '#1d4ed8', marginTop: '2px' }}>₹{pending.toLocaleString('en-IN')}</div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Template Selector */}
                  <div className="form-group" style={{ marginBottom: '14px' }}>
                    <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Apply Template Structure (Optional)</label>
                    <select
                      className="form-control"
                      value={editFeeFormData.feeStructureId}
                      onChange={(e) => {
                        const sId = e.target.value;
                        if (!sId) {
                          setEditFeeFormData({ ...editFeeFormData, feeStructureId: '', feeStructureName: '' });
                          return;
                        }
                        const fs = feeStructures.find(f => String(f.id) === String(sId));
                        if (fs) {
                          setEditFeeFormData({
                            ...editFeeFormData,
                            feeStructureId: fs.id,
                            feeStructureName: fs.name,
                            totalFees: fs.totalAmount || 0,
                            items: fs.items?.length ? fs.items.map(it => ({ feeType: it.feeType, amount: it.amount })) : [{ feeType: 'Tuition Fee', amount: fs.totalAmount || 0 }],
                            installments: fs.installments?.length ? fs.installments.map(ins => ({ installmentName: ins.installmentName, amount: ins.amount, dueDate: ins.dueDate })) : editFeeFormData.installments
                          });
                        }
                      }}
                    >
                      <option value="">-- Custom Fee / Keep Current Structure --</option>
                      {feeStructures.map(fs => (
                        <option key={fs.id} value={fs.id}>{fs.name} (₹{fs.totalAmount ? fs.totalAmount.toLocaleString('en-IN') : '0'})</option>
                      ))}
                    </select>
                  </div>

                  {/* 2-Column Grid for Primary Financial Fields */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                    <div className="form-group">
                      <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Total Fee (₹) *</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="e.g. 50000"
                        value={editFeeFormData.totalFees}
                        onChange={(e) => {
                          const val = e.target.value;
                          setEditFeeFormData(prev => ({
                            ...prev,
                            totalFees: val,
                            items: (prev.items && prev.items.length === 1) ? [{ ...prev.items[0], amount: val }] : prev.items,
                            installments: (prev.installments && prev.installments.length === 1) ? [{ ...prev.installments[0], amount: val }] : prev.installments
                          }));
                        }}
                        required
                        min="0"
                      />
                    </div>
                    <div className="form-group">
                      <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Fee Due Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={editFeeFormData.dueDate}
                        onChange={(e) => setEditFeeFormData({ ...editFeeFormData, dueDate: e.target.value })}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                    <div className="form-group">
                      <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#166534' }}>Concession / Discount (₹)</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="0"
                        value={editFeeFormData.concessionAmount}
                        onChange={(e) => setEditFeeFormData({ ...editFeeFormData, concessionAmount: e.target.value })}
                        min="0"
                      />
                    </div>
                    <div className="form-group">
                      <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#166534' }}>Concession Reason</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Merit scholarship / Sibling discount"
                        value={editFeeFormData.concessionReason}
                        onChange={(e) => setEditFeeFormData({ ...editFeeFormData, concessionReason: e.target.value })}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: selectedFeeForEdit ? '1fr' : '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                    <div className="form-group">
                      <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#991b1b' }}>Penalty / Late Fee (₹)</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="0"
                        value={editFeeFormData.penaltyAmount}
                        onChange={(e) => setEditFeeFormData({ ...editFeeFormData, penaltyAmount: e.target.value })}
                        min="0"
                      />
                    </div>
                    {!selectedFeeForEdit && (
                      <div className="form-group">
                        <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#0f172a' }}>
                          Note / Reason (Optional)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. Initial fee assignment (Optional)"
                          value={editFeeFormData.reason}
                          onChange={(e) => setEditFeeFormData({ ...editFeeFormData, reason: e.target.value })}
                        />
                      </div>
                    )}
                  </div>

                  {/* Fee Items Breakdown */}
                  <div style={{ marginBottom: '16px', background: '#f8fafc', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: '#334155' }}>📋 Fee Breakdown Components</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setEditFeeFormData(prev => ({
                          ...prev,
                          items: [...prev.items, { feeType: '', amount: '' }]
                        }))}
                        style={{ fontSize: '11.5px', padding: '2px 8px' }}
                      >
                        + Add Component
                      </button>
                    </div>
                    {editFeeFormData.items.map((it, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Component name (e.g. Tuition Fee)"
                          value={it.feeType}
                          onChange={(e) => {
                            const newItems = [...editFeeFormData.items];
                            newItems[idx].feeType = e.target.value;
                            setEditFeeFormData({ ...editFeeFormData, items: newItems });
                          }}
                          style={{ flex: 2 }}
                        />
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          placeholder="Amount (₹)"
                          value={it.amount}
                          onChange={(e) => {
                            const newItems = [...editFeeFormData.items];
                            newItems[idx].amount = e.target.value;
                            setEditFeeFormData({ ...editFeeFormData, items: newItems });
                          }}
                          style={{ flex: 1.5 }}
                        />
                        {editFeeFormData.items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setEditFeeFormData({ ...editFeeFormData, items: editFeeFormData.items.filter((_, i) => i !== idx) })}
                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '16px' }}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Installments Breakdown */}
                  <div style={{ marginBottom: '16px', background: '#f8fafc', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: '#334155' }}>🗓️ Installment Schedule</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setEditFeeFormData({
                          ...editFeeFormData,
                          installments: [...editFeeFormData.installments, { installmentName: `Term ${editFeeFormData.installments.length + 1}`, amount: '', dueDate: '' }]
                        })}
                        style={{ fontSize: '11.5px', padding: '2px 8px' }}
                      >
                        + Add Term
                      </button>
                    </div>
                    {editFeeFormData.installments.map((inst, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="e.g. Term 1"
                          value={inst.installmentName}
                          onChange={(e) => {
                            const newInsts = [...editFeeFormData.installments];
                            newInsts[idx].installmentName = e.target.value;
                            setEditFeeFormData({ ...editFeeFormData, installments: newInsts });
                          }}
                          style={{ flex: 1.5 }}
                        />
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          placeholder="Amount (₹)"
                          value={inst.amount}
                          onChange={(e) => {
                            const newInsts = [...editFeeFormData.installments];
                            newInsts[idx].amount = e.target.value;
                            setEditFeeFormData({ ...editFeeFormData, installments: newInsts });
                          }}
                          style={{ flex: 1.2 }}
                        />
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={inst.dueDate}
                          onChange={(e) => {
                            const newInsts = [...editFeeFormData.installments];
                            newInsts[idx].dueDate = e.target.value;
                            setEditFeeFormData({ ...editFeeFormData, installments: newInsts });
                          }}
                          style={{ flex: 1.5 }}
                        />
                        {editFeeFormData.installments.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setEditFeeFormData({ ...editFeeFormData, installments: editFeeFormData.installments.filter((_, i) => i !== idx) })}
                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '16px' }}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </form>
              ) : (
                /* Audit History Tab */
                <div>
                  {feeEditHistoryLoading ? (
                    <div style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
                      🔄 Loading immutable audit history...
                    </div>
                  ) : feeEditHistoryList.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '36px 16px', background: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>📜</div>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', color: '#1e293b' }}>No Manual Edits Recorded Yet</h4>
                      <p style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>
                        This fee record is currently in its initial assigned state. Any future modifications will be permanently logged here with timestamp and author details.
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      {feeEditHistoryList.map((hist, hIdx) => {
                        const editDate = hist.editedAt ? new Date(hist.editedAt) : new Date();
                        const dateStr = editDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                        const timeStr = editDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
                        const isVoidAction = hist.action === 'FEE_VOIDED';

                        return (
                          <div
                            key={hist.id || hIdx}
                            style={{
                              background: isVoidAction ? '#fff5f5' : '#ffffff',
                              borderRadius: '10px',
                              border: isVoidAction ? '1px solid #fecaca' : '1px solid #e2e8f0',
                              padding: '16px',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                              borderLeft: isVoidAction ? '4px solid #ef4444' : '4px solid #3b82f6'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '6px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '13.5px' }}>
                                  👤 {hist.editorName || hist.editedBy || 'Administrator'}
                                </span>
                                <span style={{ background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600' }}>
                                  {hist.editorRole || 'COLLEGE_ADMIN'}
                                </span>
                                <span style={{
                                  background: isVoidAction ? '#fee2e2' : '#eff6ff',
                                  color: isVoidAction ? '#991b1b' : '#1d4ed8',
                                  padding: '2px 8px',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '700'
                                }}>
                                  {isVoidAction ? '🚫 VOIDED' : '✏️ CORRECTED'}
                                </span>
                              </div>
                              <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span>🕒 {dateStr} at {timeStr}</span>
                              </div>
                            </div>

                            {hist.reason && (
                              <div style={{
                                background: isVoidAction ? '#fff' : '#f8fafc',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                fontSize: '12.5px',
                                color: '#334155',
                                marginBottom: '10px',
                                borderLeft: isVoidAction ? '3px solid #ef4444' : '3px solid #cbd5e1'
                              }}>
                                <strong>Reason:</strong> "{hist.reason}"
                              </div>
                            )}

                            {hist.changesSummary && (
                              <div style={{
                                fontSize: '12px',
                                color: isVoidAction ? '#7f1d1d' : '#475569',
                                background: isVoidAction ? '#fef2f2' : '#eff6ff',
                                padding: '10px 12px',
                                borderRadius: '6px',
                                border: isVoidAction ? '1px solid #fca5a5' : '1px solid #bfdbfe'
                              }}>
                                <strong style={{ color: isVoidAction ? '#991b1b' : '#1e40af', display: 'block', marginBottom: '4px' }}>
                                  {isVoidAction ? 'Audit Action Log:' : 'Audit Diff Summary:'}
                                </strong>
                                <pre style={{ margin: 0, fontFamily: 'inherit', whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                                  {hist.changesSummary}
                                </pre>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{ padding: '14px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button type="button" className="btn btn-light" onClick={() => setShowEditFeeModal(false)}>
                Close
              </button>
              {feeEditActiveTab === 'details' && (
                <button type="submit" form="editFeeForm" className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {selectedFeeForEdit ? '🔍 Review & Correct Fee' : '💾 Assign Fee'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 2.5 TWO-STEP CONFIRMATION MODAL FOR FEE CORRECTION */}
      {showConfirmCorrectionModal && selectedStudentForFeeEdit && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)', zIndex: 1070, overflowY: 'auto', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: '540px', width: '100%', margin: 'auto', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-content" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              
              {/* Modal Header */}
              <div style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', borderBottom: '1px solid #bfdbfe', padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '24px' }}>⚠️</span>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#1e3a8a' }}>Confirm Fee Correction</h3>
                    <p style={{ margin: 0, fontSize: '12px', color: '#3b82f6' }}>Please verify the updated financial values before applying changes.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowConfirmCorrectionModal(false)}
                  style={{ background: 'none', border: 'none', fontSize: '20px', color: '#64748b', cursor: 'pointer' }}
                >
                  ✕
                </button>
              </div>

              {/* Modal Body */}
              <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
                {/* Student Info Box */}
                <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.5px' }}>Student</span>
                      <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '15px' }}>{selectedStudentForFeeEdit.name}</div>
                      <div style={{ fontSize: '12px', color: '#475569' }}>ID: {selectedStudentForFeeEdit.studentId} • Class: {selectedStudentForFeeEdit.className || '—'}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.5px' }}>Fee Type</span>
                      <div style={{ fontWeight: '600', color: '#334155', fontSize: '13px' }}>
                        {editFeeFormData.feeStructureName || (editFeeFormData.type !== 'tuition' ? editFeeFormData.type : 'Tuition Fee')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Financial Summary Comparison Grid */}
                {(() => {
                  const currentTotal = selectedFeeForEdit?.totalFees || 0;
                  const newTotal = parseFloat(editFeeFormData.totalFees) || 0;
                  const paid = selectedFeeForEdit?.paidFees || 0;
                  const concession = parseFloat(editFeeFormData.concessionAmount) || 0;
                  const penalty = parseFloat(editFeeFormData.penaltyAmount) || 0;
                  const netObligation = Math.max(0, newTotal - concession + penalty);
                  const newPending = Math.max(0, netObligation - paid);
                  const diff = newTotal - currentTotal;
                  const diffSign = diff >= 0 ? '+' : '';

                  return (
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', marginBottom: '16px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '10px 14px' }}>
                        <span style={{ fontSize: '12.5px', color: '#64748b' }}>Current Fee Amount:</span>
                        <span style={{ fontSize: '13.5px', fontWeight: '600', color: '#0f172a', textAlign: 'right' }}>₹{currentTotal.toLocaleString('en-IN')}</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '10px 14px' }}>
                        <span style={{ fontSize: '12.5px', fontWeight: '700', color: '#1e40af' }}>Corrected Fee Amount:</span>
                        <span style={{ fontSize: '14px', fontWeight: '800', color: '#1e40af', textAlign: 'right' }}>₹{newTotal.toLocaleString('en-IN')}</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: diff !== 0 ? (diff < 0 ? '#f0fdf4' : '#fff7ed') : '#fff', borderBottom: '1px solid #e2e8f0', padding: '10px 14px' }}>
                        <span style={{ fontSize: '12.5px', fontWeight: '600', color: diff < 0 ? '#166534' : (diff > 0 ? '#c2410c' : '#64748b') }}>Correction Difference:</span>
                        <span style={{ fontSize: '13.5px', fontWeight: '700', color: diff < 0 ? '#166534' : (diff > 0 ? '#c2410c' : '#64748b'), textAlign: 'right' }}>
                          {diffSign}₹{diff.toLocaleString('en-IN')}
                        </span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '10px 14px' }}>
                        <span style={{ fontSize: '12.5px', color: '#166534' }}>Amount Already Paid:</span>
                        <span style={{ fontSize: '13.5px', fontWeight: '600', color: '#166534', textAlign: 'right' }}>₹{paid.toLocaleString('en-IN')}</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: '#f8fafc', padding: '10px 14px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#991b1b' }}>New Pending Balance:</span>
                        <span style={{ fontSize: '14px', fontWeight: '800', color: '#991b1b', textAlign: 'right' }}>₹{newPending.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  );
                })()}

                {/* Audit Reason Selection */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                    Reason for Fee Correction <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={correctionReasonPreset}
                    onChange={(e) => setCorrectionReasonPreset(e.target.value)}
                    style={{ marginBottom: '8px', fontSize: '13px' }}
                  >
                    <option value="Amount entered incorrectly">Amount entered incorrectly (Typo correction)</option>
                    <option value="Wrong fee structure selected">Wrong fee structure was selected</option>
                    <option value="Data entry mistake">Data entry mistake by administration</option>
                    <option value="Approved fee revision by management">Approved fee revision by management</option>
                    <option value="Special adjustment / sibling concession">Special adjustment / sibling concession</option>
                    <option value="Other">Other (Specify below)</option>
                  </select>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Additional notes / reference details..."
                    value={correctionReasonCustom}
                    onChange={(e) => setCorrectionReasonCustom(e.target.value)}
                    style={{ fontSize: '12.5px' }}
                  />
                </div>

                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 12px', fontSize: '11.5px', color: '#1e40af', display: 'flex', gap: '8px' }}>
                  <span>ℹ️</span>
                  <span>This modification will be permanently logged in the immutable audit history with your username, exact timestamp, and monetary diff.</span>
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{ padding: '14px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', justifyContent: 'flex-end', gap: '10px', flexShrink: 0 }}>
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => setShowConfirmCorrectionModal(false)}
                  disabled={isCorrectingFee}
                >
                  Back to Edit
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleExecuteFeeCorrection()}
                  disabled={isCorrectingFee}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600' }}
                >
                  {isCorrectingFee ? '⏳ Correcting...' : '✓ Confirm & Save Correction'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2.6 EDIT / CORRECT CASH PAYMENT AMOUNT MODAL (ADMIN ONLY) */}
      {showEditPaymentModal && selectedTxForEdit && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)', zIndex: 1070, overflowY: 'auto', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: '540px', width: '100%', margin: 'auto', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-content" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              
              {/* Modal Header */}
              <div style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', borderBottom: '1px solid #bfdbfe', padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '24px' }}>💰</span>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#1e40af' }}>Edit Cash Payment Amount</h3>
                      <span style={{ fontSize: '10px', background: '#1e40af', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontWeight: '700' }}>🔒 ADMIN ONLY</span>
                    </div>
                    <p style={{ margin: 0, fontSize: '12px', color: '#3b82f6' }}>Correct an incorrectly entered manual cash payment amount</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowEditPaymentModal(false)}
                  style={{ background: 'none', border: 'none', fontSize: '20px', color: '#64748b', cursor: 'pointer' }}
                >
                  ✕
                </button>
              </div>

              {/* Modal Body */}
              <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
                {/* Transaction & Student Info Header */}
                <div style={{ background: '#f8fafc', padding: '14px 16px', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontSize: '12.5px', color: '#64748b' }}>Student:</span>
                    <strong style={{ fontSize: '13.5px', color: '#0f172a' }}>
                      {selectedStudentForPaymentEdit?.name || selectedStudentForCollection?.name || selectedTxForEdit.studentId} ({selectedTxForEdit.studentId})
                    </strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontSize: '12.5px', color: '#64748b' }}>Receipt / Ref #:</span>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>
                      {selectedTxForEdit.receiptNumber || selectedTxForEdit.transactionId}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '12.5px', color: '#64748b' }}>Payment Mode:</span>
                    <span style={{ fontSize: '12px', fontWeight: '700', color: '#0284c7', background: '#e0f2fe', padding: '2px 8px', borderRadius: '4px' }}>
                      {selectedTxForEdit.mode || 'CASH'}
                    </span>
                  </div>
                </div>

                {/* Amount Inputs & Diff Grid */}
                <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', marginBottom: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '10px 14px', alignItems: 'center' }}>
                    <span style={{ fontSize: '12.5px', color: '#64748b' }}>Current Recorded Amount:</span>
                    <strong style={{ fontSize: '14px', color: '#475569', textAlign: 'right' }}>
                      ₹{parseFloat(selectedTxForEdit.paidAmount || 0).toLocaleString('en-IN')}
                    </strong>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '12px 14px', alignItems: 'center' }}>
                    <label style={{ fontSize: '13px', fontWeight: '700', color: '#1e40af', margin: 0 }}>
                      Correct Cash Amount (₹) <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontWeight: '700', color: '#64748b' }}>₹</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        className="form-control"
                        style={{ paddingLeft: '24px', fontWeight: '700', fontSize: '15px', color: '#1e40af', textAlign: 'right' }}
                        value={editPaymentFormData.newPaidAmount}
                        onChange={(e) => setEditPaymentFormData({ ...editPaymentFormData, newPaidAmount: e.target.value })}
                        placeholder="0.00"
                        autoFocus
                      />
                    </div>
                  </div>

                  {(() => {
                    const oldAmt = parseFloat(selectedTxForEdit.paidAmount || 0);
                    const newAmt = parseFloat(editPaymentFormData.newPaidAmount || 0);
                    const diff = newAmt - oldAmt;
                    const diffSign = diff >= 0 ? '+' : '';

                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: diff !== 0 ? (diff < 0 ? '#fff7ed' : '#f0fdf4') : '#f8fafc', padding: '10px 14px' }}>
                        <span style={{ fontSize: '12.5px', fontWeight: '600', color: diff < 0 ? '#c2410c' : (diff > 0 ? '#166534' : '#64748b') }}>
                          Payment Difference:
                        </span>
                        <span style={{ fontSize: '13.5px', fontWeight: '800', color: diff < 0 ? '#c2410c' : (diff > 0 ? '#166534' : '#64748b'), textAlign: 'right' }}>
                          {diffSign}₹{diff.toLocaleString('en-IN')}
                        </span>
                      </div>
                    );
                  })()}
                </div>

                {/* Reason Selection */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                    Reason for Payment Correction <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={editPaymentFormData.reasonPreset}
                    onChange={(e) => setEditPaymentFormData({ ...editPaymentFormData, reasonPreset: e.target.value })}
                    style={{ marginBottom: '8px', fontSize: '13px' }}
                  >
                    <option value="Amount entered incorrectly (Typo correction)">Amount entered incorrectly (Typo correction)</option>
                    <option value="Cash count correction by admin">Cash count correction by administration</option>
                    <option value="Wrong denomination entered">Wrong denomination recorded</option>
                    <option value="Adjustment / Partial refund on cash receipt">Adjustment / Partial refund on cash receipt</option>
                    <option value="Other">Other (Specify below)</option>
                  </select>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Additional notes / reference details..."
                    value={editPaymentFormData.reasonCustom}
                    onChange={(e) => setEditPaymentFormData({ ...editPaymentFormData, reasonCustom: e.target.value })}
                    style={{ fontSize: '12.5px' }}
                  />
                </div>

                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 12px', fontSize: '11.5px', color: '#1e40af', display: 'flex', gap: '8px' }}>
                  <span>ℹ️</span>
                  <span>Updating this cash amount will automatically adjust the student's total paid fees and outstanding pending balance. It is logged in the permanent audit history.</span>
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{ padding: '14px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => setShowEditPaymentModal(false)}
                  disabled={isEditingPayment}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveEditedPayment}
                  disabled={isEditingPayment || !editPaymentFormData.newPaidAmount}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600' }}
                >
                  {isEditingPayment ? '⏳ Updating Payment...' : '✓ Confirm & Update Cash Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 3. Auditable Concession Modal */}
      {showConcessionModal && (
        <div className="fs-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1050 }}>
          <div className="fs-modal-card" style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '400px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#166534' }}>Apply Concession / Discount</h3>
              <button onClick={() => setShowConcessionModal(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              setLoading(true);
              try {
                const payload = {
                  studentId: selectedStudentForCollection.studentId,
                  type: 'CONCESSION',
                  amount: parseFloat(concessionFormData.amount),
                  reason: concessionFormData.reason
                };
                const res = await feeService.applyAdjustment(payload);
                if (res.success) {
                  showNotification('success', 'Success', 'Concession applied successfully');
                  setShowConcessionModal(false);
                  
                  // Reload
                  const updatedFee = await feeService.getFeeByStudentAndOrg(selectedStudentForCollection.studentId, orgData?.orgId);
                  if (updatedFee && updatedFee.length > 0) {
                    setCollectionFeeRecord(updatedFee[0]);
                  }
                  loadFees();
                } else {
                  showNotification('error', 'Error', res.message || 'Failed to apply concession');
                }
              } catch (err) {
                console.error(err);
                showNotification('error', 'Error', 'Failed to apply concession');
              } finally {
                setLoading(false);
              }
            }}>
              <div className="form-group" style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Concession Amount (₹) *</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Enter discount amount..."
                  value={concessionFormData.amount}
                  onChange={(e) => setConcessionFormData({ ...concessionFormData, amount: e.target.value })}
                  required
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Concession / Discount Reason *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Academic scholarship, Siblings discount"
                  value={concessionFormData.reason}
                  onChange={(e) => setConcessionFormData({ ...concessionFormData, reason: e.target.value })}
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-light" onClick={() => setShowConcessionModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-success">Apply Discount</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 4. Auditable Penalty Modal */}
      {showPenaltyModal && (
        <div className="fs-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1050 }}>
          <div className="fs-modal-card" style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '400px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#991b1b' }}>Apply Fine / Penalty</h3>
              <button onClick={() => setShowPenaltyModal(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              setLoading(true);
              try {
                const payload = {
                  studentId: selectedStudentForCollection.studentId,
                  type: 'PENALTY',
                  amount: parseFloat(penaltyFormData.amount),
                  reason: penaltyFormData.reason
                };
                const res = await feeService.applyAdjustment(payload);
                if (res.success) {
                  showNotification('success', 'Success', 'Fine applied successfully');
                  setShowPenaltyModal(false);
                  
                  // Reload
                  const updatedFee = await feeService.getFeeByStudentAndOrg(selectedStudentForCollection.studentId, orgData?.orgId);
                  if (updatedFee && updatedFee.length > 0) {
                    setCollectionFeeRecord(updatedFee[0]);
                  }
                  loadFees();
                } else {
                  showNotification('error', 'Error', res.message || 'Failed to apply fine');
                }
              } catch (err) {
                console.error(err);
                showNotification('error', 'Error', 'Failed to apply fine');
              } finally {
                setLoading(false);
              }
            }}>
              <div className="form-group" style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Fine Amount (₹) *</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Enter penalty amount..."
                  value={penaltyFormData.amount}
                  onChange={(e) => setPenaltyFormData({ ...penaltyFormData, amount: e.target.value })}
                  required
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>Reason for Penalty *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Late fee submission, Library damaged book"
                  value={penaltyFormData.reason}
                  onChange={(e) => setPenaltyFormData({ ...penaltyFormData, reason: e.target.value })}
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-light" onClick={() => setShowPenaltyModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-danger">Apply Fine</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 5. Printable Professional Fee Receipt Modal */}
      {showReceiptModal && receiptData && (() => {
        const activeFeeRecord = (collectionFeeRecord && String(collectionFeeRecord.studentId) === String(receiptData.studentId))
          ? collectionFeeRecord
          : (fees && fees.find(f => String(f.studentId) === String(receiptData.studentId))) || null;

        const studentObj = (selectedStudentForCollection && String(selectedStudentForCollection.studentId) === String(receiptData.studentId))
          ? selectedStudentForCollection
          : (selectedStudentForHistory && String(selectedStudentForHistory.studentId) === String(receiptData.studentId))
            ? selectedStudentForHistory
            : (students && students.find(s => String(s.studentId) === String(receiptData.studentId))) || {
                name: receiptData.studentName || activeFeeRecord?.studentName || 'Student',
                studentId: receiptData.studentId,
                className: activeFeeRecord?.className || '',
                section: activeFeeRecord?.section || ''
              };

        const itemObj = receiptData.itemId ? activeFeeRecord?.items?.find(i => String(i.id) === String(receiptData.itemId)) : null;
        const instObj = receiptData.installmentId ? activeFeeRecord?.installments?.find(i => String(i.id) === String(receiptData.installmentId)) : null;

        const feeTargetName = itemObj ? itemObj.feeType : (receiptData.installmentName || 'Overall Outstanding');
        const installmentName = instObj ? instObj.installmentName : (itemObj ? 'Term 1' : 'General Realization');

        const feeAmount = itemObj ? (itemObj.netAmount || itemObj.originalAmount || 0) : (instObj ? (instObj.amount || 0) : (activeFeeRecord?.totalFees || activeFeeRecord?.totalAmount || 0));
        const paidNow = receiptData.paidAmount || 0;
        const fineAmt = receiptData.fineAmount || 0;
        const concessionAmt = receiptData.discount || activeFeeRecord?.concessionAmount || 0;
        const prevPaid = itemObj ? Math.max(0, (itemObj.paidAmount || 0) - paidNow) : (instObj ? Math.max(0, (instObj.paidAmount || 0) - paidNow) : Math.max(0, (activeFeeRecord?.paidFees || 0) - paidNow));
        const remainingBal = Math.max(0, feeAmount - prevPaid - paidNow);

        let storedBreakdownList = [];
        try {
          if (receiptData.breakdownJson) {
            storedBreakdownList = typeof receiptData.breakdownJson === 'string'
              ? JSON.parse(receiptData.breakdownJson)
              : receiptData.breakdownJson;
          }
        } catch (err) {
          console.error('Error parsing receipt breakdown:', err);
        }

        // Build comprehensive fee components list for this student
        const feeComponentsList = (() => {
          // If stored breakdown exists from backend
          if (storedBreakdownList && storedBreakdownList.length > 0) {
            return storedBreakdownList.map(b => {
              const compTotal = Number(b.totalFee || b.netAmount || b.originalAmount || 0);
              const compPaidNow = Number(b.paidNow != null ? b.paidNow : (b.paidAmount || 0));
              const compPrevPaid = Number(b.previouslyPaid != null ? b.previouslyPaid : Math.max(0, (b.totalPaidAfter || 0) - compPaidNow));
              const compBalDue = Number(b.balanceDue != null ? b.balanceDue : (b.remainingAmount != null ? b.remainingAmount : Math.max(0, compTotal - (compPrevPaid + compPaidNow))));
              return {
                feeType: b.feeType || 'Fee Component',
                totalFee: compTotal,
                previouslyPaid: compPrevPaid,
                paidNow: compPaidNow,
                balanceDue: compBalDue,
                discountAmount: Number(b.discountAmount || 0)
              };
            });
          }

          // Fallback: Reconstruct from active fee record items
          if (activeFeeRecord?.items && activeFeeRecord.items.length > 0) {
            if (itemObj) {
              return activeFeeRecord.items.map(item => {
                const isTarget = String(item.id) === String(itemObj.id);
                const compTotal = Number(item.netAmount || item.originalAmount || 0);
                const curPaid = Number(item.paidAmount || 0);
                const compPaidNow = isTarget ? paidNow : 0;
                const compPrevPaid = isTarget ? Math.max(0, curPaid - paidNow) : curPaid;
                const compBalDue = Math.max(0, compTotal - (compPrevPaid + compPaidNow));
                return {
                  feeType: item.feeType,
                  totalFee: compTotal,
                  previouslyPaid: compPrevPaid,
                  paidNow: compPaidNow,
                  balanceDue: compBalDue,
                  discountAmount: Number(item.discountAmount || 0)
                };
              });
            } else {
              let remToDistribute = paidNow;
              return activeFeeRecord.items.map(item => {
                const compTotal = Number(item.netAmount || item.originalAmount || 0);
                const curPaid = Number(item.paidAmount || 0);
                const compPaidNow = Math.min(remToDistribute, curPaid > 0 ? curPaid : compTotal);
                remToDistribute = Math.max(0, remToDistribute - compPaidNow);
                const compPrevPaid = Math.max(0, curPaid - compPaidNow);
                const compBalDue = Math.max(0, compTotal - (compPrevPaid + compPaidNow));
                return {
                  feeType: item.feeType,
                  totalFee: compTotal,
                  previouslyPaid: compPrevPaid,
                  paidNow: compPaidNow,
                  balanceDue: compBalDue,
                  discountAmount: Number(item.discountAmount || 0)
                };
              });
            }
          }

          // Single row fallback
          return [{
            feeType: feeTargetName,
            totalFee: feeAmount,
            previouslyPaid: prevPaid,
            paidNow: paidNow,
            balanceDue: remainingBal,
            discountAmount: concessionAmt
          }];
        })();

        // Calculate aggregate sums across the fee components
        const totalPrevPaidSum = feeComponentsList.reduce((acc, row) => acc + (row.previouslyPaid || 0), 0);
        const effectiveTotalRemaining = receiptData.remainingBalance != null ? receiptData.remainingBalance : remainingBal;

        const receiptNo = receiptData.receiptNumber || (receiptData.transactionId ? `REC-${receiptData.transactionId}` : `REC-SCH-${Date.now().toString().slice(-6)}`);
        const txDateObj = receiptData.paidDate ? new Date(receiptData.paidDate) : (receiptData.createdAt ? new Date(receiptData.createdAt) : new Date());
        const formattedDate = !isNaN(txDateObj.getTime())
          ? txDateObj.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
          : new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        const formattedTime = !isNaN(txDateObj.getTime())
          ? txDateObj.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
          : '';

        const trustName = brandingData?.trustName || orgData?.trustName || localStorage.getItem('trustName') || '';
        const schoolName = brandingData?.schoolName || orgData?.orgName || orgData?.name || localStorage.getItem('orgName') || 'School Management';
        const schoolAffiliation = brandingData?.affiliation || orgData?.affiliation || orgData?.tagline || 'Affiliated to State Board of Education';
        const schoolAddress = brandingData?.address || orgData?.address || '';
        const schoolPhone = brandingData?.phone || orgData?.phone || orgData?.contactNumber || orgData?.mobile || '';
        const schoolEmail = brandingData?.email || orgData?.email || '';
        const schoolWebsite = brandingData?.website || orgData?.website || '';
        const rawSchoolLogo = brandingData?.logoUrl || orgData?.logo || orgData?.schoolLogo || orgData?.logoUrl || orgData?.institutionLogo || localStorage.getItem('schoolLogo') || localStorage.getItem('orgLogo') || localStorage.getItem('logoUrl') || null;
        const resolvedSchoolLogo = rawSchoolLogo ? (rawSchoolLogo.startsWith('http') || rawSchoolLogo.startsWith('data:') || rawSchoolLogo.startsWith('blob:') ? rawSchoolLogo : `${API_BASE_URL}${rawSchoolLogo.startsWith('/') ? '' : '/'}${rawSchoolLogo}`) : null;

        const parentName = studentObj?.fatherName || studentObj?.parentName || studentObj?.guardianName || studentObj?.motherName || '';
        const parentMobile = studentObj?.fatherPhone || studentObj?.parentMobile || studentObj?.phone || studentObj?.motherPhone || studentObj?.guardianPhone || '';
        const studentAddress = studentObj?.address || [studentObj?.city, studentObj?.state, studentObj?.pincode].filter(Boolean).join(', ') || '';

        const utrNumber = receiptData.utrNumber || (receiptData.mode && receiptData.mode !== 'CASH' && receiptData.transactionId ? receiptData.transactionId : '');

        const handlePrintReceipt = () => {
          const receiptEl = document.getElementById('official-fee-receipt-card');
          if (!receiptEl) {
            window.print();
            return;
          }

          const existingFrame = document.getElementById('fee-receipt-print-iframe');
          if (existingFrame) existingFrame.remove();

          const iframe = document.createElement('iframe');
          iframe.id = 'fee-receipt-print-iframe';
          iframe.style.position = 'fixed';
          iframe.style.right = '0';
          iframe.style.bottom = '0';
          iframe.style.width = '0';
          iframe.style.height = '0';
          iframe.style.border = '0';
          iframe.style.opacity = '0';
          iframe.style.pointerEvents = 'none';
          document.body.appendChild(iframe);

          const iframeDoc = iframe.contentWindow.document;
          const clone = receiptEl.cloneNode(true);
          clone.querySelectorAll('.no-print').forEach(el => el.remove());

          iframeDoc.open();
          iframeDoc.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Receipt_${receiptNo || 'Payment'}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet">
  <style>
    @page {
      size: A4 portrait;
      margin: 5mm 8mm;
    }
    * {
      box-sizing: border-box;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    html, body {
      margin: 0;
      padding: 0;
      background: #ffffff;
      color: #0f172a;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      width: 100%;
      height: auto;
      overflow: visible;
    }
    .print-receipt-wrapper {
      width: 100%;
      max-width: 194mm;
      margin: 0 auto;
      page-break-after: avoid !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
      break-after: avoid !important;
    }
    .receipt-card-print {
      border: 1.5px solid #0f2942;
      border-radius: 4px;
      overflow: hidden;
      background: #ffffff;
    }
    .receipt-cut-line {
      margin-top: 8px;
      border-top: 1.5px dashed #94a3b8;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 8px;
      color: #64748b;
      letter-spacing: 2px;
      padding-top: 3px;
      text-transform: uppercase;
      font-weight: 700;
    }
    table {
      width: 100%;
      border-collapse: collapse;
    }
  </style>
</head>
<body>
  <div class="print-receipt-wrapper">
    <div class="receipt-card-print">
      ${clone.innerHTML}
    </div>
    <div class="receipt-cut-line">
      ✂ - - - - - - - - - - - - - - - - - - - - - - - - CUT HERE (HALF PAGE) - - - - - - - - - - - - - - - - - - - - - - - - ✂
    </div>
  </div>
</body>
</html>`);
          iframeDoc.close();

          setTimeout(() => {
            try {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();
            } catch (err) {
              console.error('Iframe print error, falling back:', err);
              window.print();
            }
          }, 350);
        };

        const handleDownloadReceiptPDF = async () => {
          const receiptEl = document.getElementById('official-fee-receipt-card');
          if (!receiptEl) {
            handlePrintReceipt();
            return;
          }
          try {
            setIsGeneratingReceiptPdf(true);
            const canvas = await html2canvas(receiptEl, {
              scale: 4,
              useCORS: true,
              allowTaint: true,
              logging: false,
              backgroundColor: '#ffffff',
              imageTimeout: 15000,
              onclone: (clonedDoc) => {
                const clonedEl = clonedDoc.getElementById('official-fee-receipt-card');
                if (clonedEl) {
                  clonedEl.style.transform = 'none';
                  clonedEl.style.boxShadow = 'none';
                  clonedEl.style.margin = '0';
                  clonedEl.style.borderRadius = '0';
                }
              },
              ignoreElements: (element) => element.classList && element.classList.contains('no-print')
            });

            const imgData = canvas.toDataURL('image/png', 1.0);
            const pdf = new jsPDF({
              orientation: 'portrait',
              unit: 'mm',
              format: 'a4',
              compress: true
            });
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            
            const margin = 8;
            const contentWidth = pdfWidth - (margin * 2);
            const contentHeight = (canvas.height * contentWidth) / canvas.width;
            
            pdf.addImage(imgData, 'PNG', margin, margin, contentWidth, Math.min(contentHeight, pdfHeight - (margin * 2)), undefined, 'FAST');
            const cleanStudentName = (studentObj?.name || receiptData.studentName || 'Student').replace(/[^a-zA-Z0-9]/g, '_');
            const cleanReceiptNo = (receiptNo || 'Receipt').replace(/[^a-zA-Z0-9]/g, '_');
            pdf.save(`Fee_Receipt_${cleanReceiptNo}_${cleanStudentName}.pdf`);
          } catch (err) {
            console.error('Error generating PDF:', err);
            handlePrintReceipt();
          } finally {
            setIsGeneratingReceiptPdf(false);
          }
        };

        return (
          <div className="fs-modal-overlay printable-receipt-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1060, padding: '16px', overflowY: 'auto' }}>
            <div id="official-fee-receipt-card" className="fs-modal-card printable-receipt" style={{ background: '#ffffff', borderRadius: '6px', border: '1.5px solid #0f2942', width: '100%', maxWidth: '680px', boxShadow: '0 20px 45px -10px rgba(15,23,42,0.3)', overflow: 'hidden', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", color: '#0f172a', margin: 'auto' }}>
              
              {/* 1. RECEIPT HEADER */}
              <div style={{ borderBottom: '2px solid #0f2942', padding: '8px 14px 6px 14px', background: '#f8fafc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                  
                  {/* Top-Left: School Logo */}
                  {resolvedSchoolLogo ? (
                    <div style={{ width: '50px', height: '44px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                      <img 
                        src={resolvedSchoolLogo} 
                        alt="School Logo" 
                        crossOrigin="anonymous"
                        style={{ maxHeight: '42px', maxWidth: '50px', objectFit: 'contain' }} 
                        onError={(e) => { 
                          e.target.style.display = 'none';
                          const fallback = e.target.parentElement.querySelector('.receipt-logo-fallback');
                          if (fallback) fallback.style.display = 'flex';
                        }} 
                      />
                      <div className="receipt-logo-fallback" style={{ display: 'none', width: '36px', height: '36px', borderRadius: '4px', background: '#0f2942', color: '#ffffff', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: '900' }}>
                        {schoolName.charAt(0) || '🏫'}
                      </div>
                    </div>
                  ) : (
                    <div style={{ width: '36px', height: '36px', borderRadius: '4px', background: '#0f2942', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: '900', flexShrink: 0 }}>
                      {schoolName.charAt(0) || '🏫'}
                    </div>
                  )}

                  {/* Center: School Branding & Details */}
                  <div style={{ flex: 1, textAlign: 'center', padding: '0 4px' }}>
                    {trustName && (
                      <div style={{ fontSize: '9.5px', fontWeight: '800', color: '#475569', letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '1px' }}>
                        {trustName}
                      </div>
                    )}
                    <h1 style={{ margin: '0', fontSize: '15px', fontWeight: '900', color: '#0f2942', letterSpacing: '0.4px', textTransform: 'uppercase', lineHeight: '1.2' }}>
                      {schoolName}
                    </h1>
                    {schoolAffiliation && (
                      <div style={{ fontSize: '9.5px', color: '#475569', fontWeight: '600', marginTop: '1px' }}>
                        {schoolAffiliation}
                      </div>
                    )}
                    {schoolAddress && (
                      <div style={{ fontSize: '9px', color: '#64748b', marginTop: '1px', lineHeight: '1.2' }}>
                        {schoolAddress}
                      </div>
                    )}
                    {(schoolPhone || schoolEmail || schoolWebsite) && (
                      <div style={{ fontSize: '8.5px', color: '#64748b', marginTop: '1px', fontWeight: '500', display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '6px' }}>
                        {schoolPhone && <span>📞 {schoolPhone}</span>}
                        {schoolEmail && <span>✉️ {schoolEmail}</span>}
                        {schoolWebsite && <span>🌐 {schoolWebsite}</span>}
                      </div>
                    )}
                  </div>

                  {/* Top-Right: ORIGINAL Badge & Receipt Number */}
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ display: 'inline-block', background: '#0f2942', color: '#ffffff', fontSize: '8.5px', fontWeight: '800', letterSpacing: '0.8px', padding: '1px 6px', borderRadius: '3px', textTransform: 'uppercase', marginBottom: '2px' }}>
                      ORIGINAL
                    </div>
                    <div style={{ fontSize: '10px', fontWeight: '800', color: '#0f2942', fontFamily: 'monospace' }}>
                      {receiptNo}
                    </div>
                    <div style={{ fontSize: '9.5px', color: '#475569', fontWeight: '600', marginTop: '1px' }}>
                      {formattedDate}
                    </div>
                  </div>

                </div>
              </div>

              <div style={{ padding: '8px 14px' }}>
                
                {/* 2. RECEIPT TITLE */}
                <div style={{ textAlign: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '3px', marginBottom: '6px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '900', letterSpacing: '0.8px', color: '#0f2942', textTransform: 'uppercase', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                    <span>📄</span> OFFICIAL PAYMENT RECEIPT
                  </div>
                  <div style={{ fontSize: '9px', color: '#64748b', fontWeight: '500' }}>
                    Fee Payment Transaction Confirmation
                  </div>
                </div>

                {/* 3. STUDENT DETAILS */}
                <div style={{ border: '1px solid #cbd5e1', borderRadius: '4px', overflow: 'hidden', marginBottom: '6px', background: '#ffffff' }}>
                  <div style={{ background: '#f1f5f9', borderBottom: '1px solid #cbd5e1', padding: '2px 8px', fontSize: '9px', fontWeight: '800', color: '#0f2942', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    STUDENT DETAILS
                  </div>
                  <div style={{ padding: '4px 8px', display: 'grid', gridTemplateColumns: parentName || studentAddress ? '1fr 1fr' : '1fr', gap: '6px', fontSize: '9.5px' }}>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: '800', color: '#0f172a' }}>
                        {studentObj?.name || receiptData.studentName || 'Student'}
                      </div>
                      <div style={{ color: '#475569', marginTop: '1px', fontWeight: '600' }}>
                        <span style={{ color: '#64748b' }}>Student ID:</span> {studentObj?.studentId || receiptData.studentId}
                      </div>
                      <div style={{ color: '#475569', marginTop: '1px', fontWeight: '600' }}>
                        <span style={{ color: '#64748b' }}>Class & Sec:</span> {studentObj?.className || 'Class'} {studentObj?.section ? `(Sec ${studentObj.section})` : ''}
                      </div>
                    </div>

                    {(parentName || parentMobile || studentAddress) && (
                      <div style={{ borderLeft: '1px dashed #e2e8f0', paddingLeft: '8px' }}>
                        {parentName && (
                          <div style={{ color: '#334155', fontWeight: '600' }}>
                            <span style={{ color: '#64748b' }}>Parent/Guardian:</span> {parentName}
                          </div>
                        )}
                        {parentMobile && (
                          <div style={{ color: '#334155', marginTop: '1px', fontWeight: '600' }}>
                            <span style={{ color: '#64748b' }}>Mobile:</span> {parentMobile}
                          </div>
                        )}
                        {studentAddress && (
                          <div style={{ color: '#334155', marginTop: '1px', fontSize: '8.5px', lineHeight: '1.2' }}>
                            <span style={{ color: '#64748b' }}>Address:</span> {studentAddress}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* 4. PAYMENT DETAILS & FEE BREAKUP TABLE */}
                <div style={{ border: '1px solid #cbd5e1', borderRadius: '4px', overflow: 'hidden', marginBottom: '6px' }}>
                  <div style={{ background: '#f1f5f9', borderBottom: '1px solid #cbd5e1', padding: '2px 8px', fontSize: '9px', fontWeight: '800', color: '#0f2942', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    PAYMENT DETAILS & FEE BREAKUP
                  </div>
                  
                  <table style={{ width: '100%', fontSize: '9.5px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#0f2942', color: '#ffffff', textAlign: 'left' }}>
                        <th style={{ padding: '3px 6px', fontWeight: '700', fontSize: '9.5px' }}>Fee Component</th>
                        <th style={{ padding: '3px 6px', fontWeight: '700', textAlign: 'right', fontSize: '9.5px' }}>Total Fee (₹)</th>
                        <th style={{ padding: '3px 6px', fontWeight: '700', textAlign: 'right', fontSize: '9.5px' }}>Previously Paid (₹)</th>
                        <th style={{ padding: '3px 6px', fontWeight: '700', textAlign: 'right', fontSize: '9.5px', color: '#86efac' }}>Paid Now (₹)</th>
                        <th style={{ padding: '3px 6px', fontWeight: '700', textAlign: 'right', fontSize: '9.5px', color: '#fca5a5' }}>Balance Due (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feeComponentsList.map((row, rIdx) => {
                        const tot = Number(row.totalFee || 0);
                        const prev = Number(row.previouslyPaid || 0);
                        const now = Number(row.paidNow || 0);
                        const due = Number(row.balanceDue || 0);
                        return (
                          <tr key={rIdx} style={{ borderBottom: '1px solid #e2e8f0', background: rIdx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                            <td style={{ padding: '3px 6px', fontWeight: '700', color: '#0f172a' }}>{row.feeType}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', color: '#334155', fontWeight: '600' }}>₹{tot.toLocaleString('en-IN')}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', color: '#64748b' }}>₹{prev.toLocaleString('en-IN')}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: '800', color: now > 0 ? '#166534' : '#64748b' }}>
                              {now > 0 ? `₹${now.toLocaleString('en-IN')}` : '₹0'}
                            </td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: '800', color: due === 0 ? '#166534' : '#dc2626' }}>
                              {due === 0 ? '✓ Paid' : `₹${due.toLocaleString('en-IN')}`}
                            </td>
                          </tr>
                        );
                      })}

                      {/* Concession / Penalty row if applied */}
                      {(concessionAmt > 0 || fineAmt > 0) && (
                        <tr style={{ background: '#fffbeb', borderTop: '1px dashed #cbd5e1', fontSize: '9px' }}>
                          <td colSpan="3" style={{ padding: '2px 6px', fontWeight: '700', color: '#92400e' }}>
                            {concessionAmt > 0 && <span>🎁 Concession / Discount: -₹{Number(concessionAmt).toLocaleString('en-IN')} </span>}
                            {fineAmt > 0 && <span style={{ marginLeft: '6px', color: '#b91c1c' }}>⚠️ Penalty / Fine: +₹{Number(fineAmt).toLocaleString('en-IN')}</span>}
                          </td>
                          <td colSpan="2" style={{ padding: '2px 6px', textAlign: 'right', fontWeight: '700', color: '#92400e' }}>
                            Applied to Net Balance
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Overall Summary Row */}
                  <div style={{ background: '#f8fafc', borderTop: '1px solid #cbd5e1', padding: '4px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px', fontSize: '10px' }}>
                    <div style={{ fontWeight: '700', color: '#475569' }}>
                      <span>Total Previous Paid: </span>
                      <span style={{ fontWeight: '800', color: '#0f172a' }}>₹{totalPrevPaidSum.toLocaleString('en-IN')}</span>
                    </div>

                    <div style={{ fontWeight: '800', color: '#166534', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span>Total Amount Collected:</span>
                      <span style={{ fontSize: '11px', background: '#dcfce7', padding: '1px 5px', borderRadius: '3px', border: '1px solid #86efac' }}>
                        ₹{paidNow.toLocaleString('en-IN')}
                      </span>
                    </div>

                    <div style={{ fontWeight: '800', color: effectiveTotalRemaining > 0 ? '#dc2626' : '#166534', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span>Total Remaining Balance:</span>
                      <span style={{ fontSize: '11px', background: effectiveTotalRemaining > 0 ? '#fee2e2' : '#dcfce7', padding: '1px 5px', borderRadius: '3px', border: effectiveTotalRemaining > 0 ? '1px solid #fca5a5' : '1px solid #86efac' }}>
                        ₹{effectiveTotalRemaining.toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 5. TRANSACTION & VERIFICATION INFORMATION */}
                <div style={{ border: '1px solid #cbd5e1', borderRadius: '4px', overflow: 'hidden', marginBottom: '6px', background: '#ffffff' }}>
                  <div style={{ background: '#f1f5f9', borderBottom: '1px solid #cbd5e1', padding: '2px 8px', fontSize: '9px', fontWeight: '800', color: '#0f2942', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    PAYMENT & VERIFICATION INFORMATION
                  </div>
                  <div style={{ padding: '4px 8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '9.5px', color: '#334155' }}>
                    <div style={{ display: 'grid', gap: '2px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>Date & Time:</span>
                        <span style={{ fontWeight: '700', color: '#0f172a' }}>{formattedDate} {formattedTime ? `at ${formattedTime}` : ''}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>Payment Mode:</span>
                        <span style={{ fontWeight: '800', color: '#0369a1' }}>{receiptData.mode || 'CASH'}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>Collected By:</span>
                        <span style={{ fontWeight: '700', color: '#0f172a' }}>{receiptData.collectedBy || 'SCHOOL_ADMIN'}</span>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gap: '2px', borderLeft: '1px dashed #e2e8f0', paddingLeft: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>Txn Reference ID:</span>
                        <span style={{ fontWeight: '700', fontFamily: 'monospace', color: '#0f172a' }}>{receiptData.transactionId || 'TXN-GEN'}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>UTR / Ref No:</span>
                        <span style={{ fontWeight: '700', fontFamily: 'monospace', color: utrNumber ? '#0f172a' : '#94a3b8' }}>
                          {utrNumber || 'N/A'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#64748b' }}>Status:</span>
                        <span style={{ fontWeight: '800', color: '#166534', background: '#dcfce7', padding: '1px 5px', borderRadius: '3px', fontSize: '9px', border: '1px solid #86efac' }}>
                          ✓ Verified by School Administration
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 6. FOOTER & SIGNATORY */}
                <div style={{ borderTop: '1.5px solid #0f2942', paddingTop: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', fontSize: '9px' }}>
                  <div>
                    <div style={{ fontWeight: '800', color: '#0f2942' }}>Thank you for your payment!</div>
                    <div style={{ color: '#64748b', fontSize: '8px', marginTop: '1px' }}>
                      This is a computer generated receipt and does not require a physical signature.
                    </div>
                  </div>

                  <div style={{ textAlign: 'center', minWidth: '120px' }}>
                    <div style={{ height: '14px', borderBottom: '1px solid #94a3b8', width: '100px', margin: '0 auto 2px auto' }}></div>
                    <div style={{ fontWeight: '800', color: '#0f2942', fontSize: '9px' }}>Authorized Signatory</div>
                    <div style={{ color: '#64748b', fontSize: '8px' }}>{schoolName}</div>
                  </div>
                </div>

                {/* Bottom Disclaimer */}
                <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '8px', marginTop: '2px', borderTop: '1px dashed #e2e8f0', paddingTop: '2px' }}>
                  {schoolWebsite ? `${schoolWebsite} • ` : ''}This receipt is valid subject to realisation of payment.
                </div>

                {/* Cut indicator in modal preview */}
                <div className="no-print" style={{ marginTop: '8px', borderTop: '1px dashed #94a3b8', textAlign: 'center', fontSize: '8px', color: '#64748b', letterSpacing: '1.5px', paddingTop: '2px', textTransform: 'uppercase' }}>
                  ✂ - - - - - - - - - - - - - - - - - - - - CUT HERE (HALF PAGE PRINT PRESERVED) - - - - - - - - - - - - - - - - - - - - ✂
                </div>

                {/* 7. ACTION BUTTONS (Hidden during printing) */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }} className="no-print">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handlePrintReceipt}
                    style={{ flex: 1, padding: '8px', fontWeight: '800', borderRadius: '6px', fontSize: '13px', background: '#0f2942', borderColor: '#0f2942', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                  >
                    <span>🖨️</span> Print Receipt
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handleDownloadReceiptPDF}
                    disabled={isGeneratingReceiptPdf}
                    style={{ flex: 1, padding: '8px', fontWeight: '800', borderRadius: '6px', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', border: '1.5px solid #0f2942', color: '#0f2942' }}
                  >
                    <span>📥</span> {isGeneratingReceiptPdf ? 'Generating PDF...' : 'Download PDF'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => {
                      setShowReceiptModal(false);
                      setReceiptData(null);
                    }}
                    style={{ padding: '8px 18px', fontWeight: '700', borderRadius: '6px', fontSize: '13px', border: '1px solid #cbd5e1', color: '#334155' }}
                  >
                    Close
                  </button>
                </div>

              </div>
            </div>
          </div>
        );
      })()}

      {/* Non-blocking Fees Cleared Confirmation Card */}
      {clearedPaymentPrompt && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 1070,
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.08)',
            border: '2px solid #10b981',
            padding: '16px 20px',
            maxWidth: '380px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>🎉</span>
            <div>
              <div style={{ fontWeight: '800', fontSize: '14px', color: '#065f46' }}>Fees fully cleared</div>
              <div style={{ fontSize: '12px', color: '#047857' }}>
                All fee balance for <strong>{clearedPaymentPrompt.student?.name}</strong> has been cleared.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={() => setClearedPaymentPrompt(null)}
              style={{ fontSize: '11.5px', padding: '4px 10px', border: '1px solid #cbd5e1' }}
            >
              Close
            </button>
            <button
              type="button"
              className="btn btn-sm btn-success"
              onClick={() => {
                setAdminClearanceStudent(clearedPaymentPrompt.student);
                setAdminClearanceFee(clearedPaymentPrompt.fee);
                setShowAdminClearanceModal(true);
                setClearedPaymentPrompt(null);
              }}
              style={{
                fontSize: '11.5px',
                fontWeight: '700',
                padding: '4px 12px',
                backgroundColor: '#059669',
                borderColor: '#059669'
              }}
            >
              📜 Generate Clearance Certificate
            </button>
          </div>
        </div>
      )}

      {/* Admin Fee Clearance Certificate Modal */}
      {showAdminClearanceModal && adminClearanceStudent && (
        <FeeClearanceCertificateModal
          isOpen={showAdminClearanceModal}
          onClose={() => {
            setShowAdminClearanceModal(false);
            setAdminClearanceStudent(null);
            setAdminClearanceFee(null);
          }}
          student={adminClearanceStudent}
          fee={adminClearanceFee}
          academicYear={adminClearanceFee?.academicYear || orgData?.academicYear || '2026-2027'}
          orgData={orgData}
          onCertificateIssued={(cert) => {
            showNotification('success', 'Certificate Issued', `Official Fee Clearance Certificate #${cert.certificateNumber} generated.`);
          }}
        />
      )}

      {/* 5b. Printable Component-First Payment History Modal */}
      {showHistoryModal && selectedStudentForHistory && (
        <div className="fs-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1050 }}>
          <div className="fs-modal-card" style={{ background: '#fff', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '580px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '14px', marginBottom: '16px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>🧾 Payment History</h3>
                <div style={{ fontSize: '12.5px', color: '#475569', marginTop: '2px', fontWeight: '600' }}>
                  Student: <span style={{ color: '#0f172a', fontWeight: '700' }}>{selectedStudentForHistory.name}</span> ({selectedStudentForHistory.studentId} • Class {selectedStudentForHistory.className || '—'} {selectedStudentForHistory.section || ''})
                </div>
              </div>
              <button onClick={() => { setShowHistoryModal(false); setSelectedStudentForHistory(null); }} style={{ background: '#f1f5f9', border: 'none', width: '32px', height: '32px', borderRadius: '50%', fontSize: '16px', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>

            {(() => {
              const selectedStudentFeeRecord = fees.find(f => f.studentId === selectedStudentForHistory?.studentId);

              const normalizeCompName = (name) => {
                if (!name) return 'School Fee';
                const clean = String(name).trim();
                const lower = clean.toLowerCase();
                if (lower.includes('school')) return 'School Fee';
                if (lower.includes('tuition')) return 'Tuition Fee';
                if (lower.includes('dress') || lower.includes('uniform')) return 'Dress Fees';
                if (lower.includes('transport') || lower.includes('bus')) return 'Transport Fee';
                if (lower.includes('hostel')) return 'Hostel Fee';
                if (lower.includes('exam')) return 'Exam Fee';
                return clean.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
              };

              const parseTxDate = (dateVal) => {
                if (!dateVal) return null;
                if (Array.isArray(dateVal)) {
                  return new Date(dateVal[0], dateVal[1] - 1, dateVal[2] || 1, dateVal[3] || 0, dateVal[4] || 0);
                }
                const d = new Date(dateVal);
                return isNaN(d.getTime()) ? null : d;
              };

              const resolveTxCompName = (tx, feeRecord) => {
                if (tx.feeComponentName && tx.feeComponentName.trim() !== '') return normalizeCompName(tx.feeComponentName);
                if (tx.itemId && feeRecord?.items?.length > 0) {
                  const foundItem = feeRecord.items.find(i => String(i.id) === String(tx.itemId));
                  if (foundItem?.feeType) return normalizeCompName(foundItem.feeType);
                }
                if (tx.installmentName) {
                  const lower = tx.installmentName.toLowerCase();
                  if (lower.includes('school')) return 'School Fee';
                  if (lower.includes('tuition')) return 'Tuition Fee';
                  if (lower.includes('dress') || lower.includes('uniform')) return 'Dress Fees';
                  if (lower.includes('transport') || lower.includes('bus')) return 'Transport Fee';
                  if (lower.includes('hostel')) return 'Hostel Fee';
                  if (lower.includes('exam')) return 'Exam Fee';
                  if (feeRecord?.items?.length > 0) {
                    return normalizeCompName(feeRecord.items[0].feeType);
                  }
                  return 'School Fee';
                }
                if (feeRecord?.items?.length > 0) {
                  return normalizeCompName(feeRecord.items[0].feeType);
                }
                return 'School Fee';
              };

              const availableFilters = (() => {
                const filterMap = new Map();
                filterMap.set('ALL', 'All');
                if (selectedStudentFeeRecord?.items?.length > 0) {
                  selectedStudentFeeRecord.items.forEach(i => { if (i.feeType) filterMap.set(normalizeCompName(i.feeType), normalizeCompName(i.feeType)); });
                }
                if (historyPayments?.length > 0) {
                  historyPayments.forEach(tx => {
                    const res = resolveTxCompName(tx, selectedStudentFeeRecord);
                    if (res) filterMap.set(res, res);
                  });
                }
                if (filterMap.size === 1) return ['ALL', 'School Fee', 'Tuition Fee', 'Dress Fees', 'Transport Fee'];
                return Array.from(filterMap.keys());
              })();

              return (
                <>
                  {/* Filter Controls */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {availableFilters.map(comp => (
                        <button
                          key={comp}
                          type="button"
                          className={`btn btn-xs ${historyComponentFilter === comp ? 'btn-primary' : 'btn-light'}`}
                          onClick={() => setHistoryComponentFilter(comp)}
                          style={{ padding: '3px 9px', fontSize: '11.5px', borderRadius: '6px', fontWeight: '600' }}
                        >
                          {comp === 'ALL' ? 'All' : comp}
                        </button>
                      ))}
                    </div>

                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        type="button"
                        className={`btn btn-xs ${historyTimeFilter === 'ALL' ? 'btn-dark' : 'btn-outline-secondary'}`}
                        onClick={() => setHistoryTimeFilter('ALL')}
                        style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', fontWeight: '600' }}
                      >
                        📅 All Time
                      </button>
                      <button
                        type="button"
                        className={`btn btn-xs ${historyTimeFilter === 'THIS_MONTH' ? 'btn-dark' : 'btn-outline-secondary'}`}
                        onClick={() => setHistoryTimeFilter('THIS_MONTH')}
                        style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', fontWeight: '600' }}
                      >
                        🗓️ This Month
                      </button>
                    </div>
                  </div>

                  {/* Content List */}
                  <div style={{ maxHeight: '420px', overflowY: 'auto', marginBottom: '20px' }}>
                    {loadingPaymentHistory ? (
                      <div style={{ padding: '40px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>
                        🔄 Loading payment history log...
                      </div>
                    ) : (() => {
                      let filtered = [...historyPayments];

                      if (historyComponentFilter !== 'ALL') {
                        filtered = filtered.filter(tx => {
                          const resComp = resolveTxCompName(tx, selectedStudentFeeRecord);
                          const targetComp = normalizeCompName(historyComponentFilter);
                          return resComp === targetComp;
                        });
                      }

                      if (historyTimeFilter === 'THIS_MONTH') {
                        const now = new Date();
                        filtered = filtered.filter(tx => {
                          const d = parseTxDate(tx.paidDate || tx.createdAt);
                          if (!d) return true;
                          return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                        });
                      }

                      if (filtered.length === 0) {
                        return (
                          <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic', fontSize: '13px' }}>
                            No payment transactions found for this student under selected filter.
                          </div>
                        );
                      }

                      const grouped = {};
                      filtered.forEach(tx => {
                        const txDate = new Date(tx.paidDate);
                        const isToday = txDate.toDateString() === new Date().toDateString();
                        const dateKey = isToday ? 'TODAY' : txDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
                        if (!grouped[dateKey]) grouped[dateKey] = [];
                        grouped[dateKey].push(tx);
                      });

                      return (
                        <div style={{ display: 'grid', gap: '16px' }}>
                          {Object.entries(grouped).map(([dateLabel, txList]) => (
                            <div key={dateLabel}>
                              <div style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px', borderBottom: '1px solid #f1f5f9', paddingBottom: '4px' }}>
                                📅 {dateLabel}
                              </div>

                              <div style={{ display: 'grid', gap: '8px' }}>
                                {txList.map(tx => {
                                  const compName = resolveTxCompName(tx, selectedStudentFeeRecord);
                                  const instName = tx.installmentName || 'Term 1';
                                  const txDateObj = tx.paidDate ? new Date(tx.paidDate) : (tx.createdAt ? new Date(tx.createdAt) : null);
                                  const timeStr = txDateObj && !isNaN(txDateObj.getTime())
                                    ? txDateObj.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
                                    : '';
                                  
                                  let icon = '🏷️';
                                  const lower = compName.toLowerCase();
                                  if (lower.includes('school')) icon = '🏫';
                                  else if (lower.includes('tuition')) icon = '📚';
                                  else if (lower.includes('dress') || lower.includes('uniform')) icon = '👕';
                                  else if (lower.includes('transport') || lower.includes('bus')) icon = '🚌';

                                  const rem = tx.remainingBalance != null ? tx.remainingBalance : 0;

                                  return (
                                    <div key={tx.transactionId} style={{ background: '#f8fafc', borderRadius: '10px', padding: '14px', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ fontSize: '24px' }}>{icon}</div>
                                        <div>
                                          <div style={{ fontWeight: '800', fontSize: '14.5px', color: '#0f172a' }}>
                                            {compName}
                                          </div>
                                          <div style={{ fontSize: '12px', color: '#475569', marginTop: '2px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                            <span>{instName}</span>
                                            <span>•</span>
                                            <span style={{ color: '#0284c7', fontWeight: '700' }}>{tx.mode || 'CASH'}</span>
                                            {timeStr && (
                                              <>
                                                <span>•</span>
                                                <span style={{ color: '#64748b', fontWeight: '700', background: '#f1f5f9', padding: '1px 6px', borderRadius: '4px' }}>
                                                  ⏰ {timeStr}
                                                </span>
                                              </>
                                            )}
                                          </div>

                                          {/* Itemized Fee Breakdown / Fee Types */}
                                          {(() => {
                                            let bList = [];
                                            try {
                                              if (tx.breakdownJson) {
                                                bList = typeof tx.breakdownJson === 'string' ? JSON.parse(tx.breakdownJson) : tx.breakdownJson;
                                              }
                                            } catch (e) {}

                                            if (bList && bList.length > 0) {
                                              return (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '6px' }}>
                                                  {bList.map((b, bi) => (
                                                    <span key={bi} style={{ background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: '5px', padding: '2px 7px', fontSize: '11px', fontWeight: '700' }}>
                                                      🏷️ {b.feeType}: ₹{Number(b.paidAmount || 0).toLocaleString('en-IN')}
                                                    </span>
                                                  ))}
                                                </div>
                                              );
                                            } else {
                                              return (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '6px' }}>
                                                  <span style={{ background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: '5px', padding: '2px 7px', fontSize: '11px', fontWeight: '700' }}>
                                                    🏷️ {compName}: ₹{(tx.paidAmount || 0).toLocaleString('en-IN')}
                                                  </span>
                                                </div>
                                              );
                                            }
                                          })()}

                                          <div style={{ fontSize: '11.5px', marginTop: '4px', fontWeight: '700', color: rem > 0 ? '#b91c1c' : '#166534' }}>
                                            Remaining: ₹{rem.toLocaleString('en-IN')}
                                          </div>
                                        </div>
                                      </div>

                                      <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                                        <div style={{ fontSize: '16px', fontWeight: '900', color: '#166534' }}>
                                          ₹{tx.paidAmount?.toLocaleString('en-IN')}
                                        </div>
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => {
                                              setSelectedStudentForCollection(selectedStudentForHistory);
                                              setCollectionFeeRecord(selectedStudentFeeRecord);
                                              setReceiptData(tx);
                                              setShowReceiptModal(true);
                                            }}
                                            style={{ padding: '4px 12px', fontSize: '12px', fontWeight: '700', borderRadius: '6px' }}
                                          >
                                            👁️ View Receipt
                                          </button>
                                          {(() => {
                                            const userRoleStr = (userRole || '').toUpperCase();
                                            const isAdmin = userRoleStr === 'SCHOOL_ADMIN' || userRoleStr === 'SUPER_ADMIN' || userRoleStr === 'COLLEGE_ADMIN' || userRoleStr === 'ADMIN' || !userRole;
                                            if (!isAdmin) return null;
                                            return (
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-warning"
                                                onClick={() => handleOpenEditPaymentModal(tx, selectedStudentForHistory)}
                                                style={{ padding: '4px 10px', fontSize: '12px', fontWeight: '700', borderRadius: '6px', color: '#b45309', borderColor: '#fde68a', background: '#fffbeb' }}
                                                title="Admin: Correct manual cash payment amount"
                                              >
                                                ✏️ Edit Amount
                                              </button>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </>
              );
            })()}

            <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e2e8f0', paddingTop: '14px' }}>
              <button type="button" className="btn btn-secondary" onClick={() => { setShowHistoryModal(false); setSelectedStudentForHistory(null); }} style={{ padding: '6px 18px', fontSize: '13px', fontWeight: '600' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Promote Student Modal */}
      {showPromoteModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '400px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: '1px solid #e5e7eb',
              paddingBottom: '12px'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                📈 Promote Student
              </h3>
              <button
                onClick={() => {
                  setShowPromoteModal(false);
                  setPromoteStudent(null);
                  setPromoteToClass('');
                }}
                style={{
                  background: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  width: '32px',
                  height: '32px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#e5e7eb';
                  e.target.style.borderColor = '#d1d5db';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#f3f4f6';
                  e.target.style.borderColor = '#e5e7eb';
                }}
              >
                ×
              </button>
            </div>

            {promoteStudent && (
              <div style={{ marginBottom: '20px' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6b7280' }}>
                  <strong>Student:</strong> {promoteStudent.name}
                </p>
                <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6b7280' }}>
                  <strong>Current Class:</strong> {promoteStudent.class || promoteStudent.className || 'N/A'}
                </p>

                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#1f2937' }}>
                  Select Class to Promote *
                </label>
                <select
                  value={promoteToClass}
                  onChange={(e) => setPromoteToClass(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                >
                  <option value="">-- Select Class --</option>
                  {classOptions && classOptions.map(cls => (
                    <option key={cls.id} value={cls.id}>
                      {cls.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end',
              marginTop: '24px'
            }}>
              <button
                onClick={() => {
                  setShowPromoteModal(false);
                  setPromoteStudent(null);
                  setPromoteToClass('');
                }}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#f3f4f6'}
              >
                Cancel
              </button>
              <button
                onClick={handlePromoteStudent}
                disabled={loading || !promoteToClass}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: promoteToClass ? '#10b981' : '#d1d5db',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: promoteToClass ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  if (promoteToClass) e.target.style.backgroundColor = '#059669';
                }}
                onMouseOut={(e) => {
                  if (promoteToClass) e.target.style.backgroundColor = '#10b981';
                }}
              >
                {loading ? 'Promoting...' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fee Reminder Modal */}
      {showReminderModal && (
        <div className="modal" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-content" style={{ width: '90%', maxWidth: '500px' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>Send Fee Reminder</h3>
              <button 
                className="close-btn" 
                onClick={() => setShowReminderModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSendReminder}>
              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
                  Class (Optional)
                </label>
                <select
                  value={reminderFormData.class}
                  onChange={(e) => setReminderFormData({...reminderFormData, class: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontFamily: 'inherit'
                  }}
                >
                  <option value="">All Classes</option>
                  {classOptions.map(cls => (
                    <option key={cls.id} value={cls.label}>{cls.label}</option>
                  ))}
                </select>
                <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Leave empty to send to all classes
                </small>
              </div>

              {reminderFormData.class && (
                <div className="form-group" style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
                    Section (Optional)
                  </label>
                  <select
                    value={reminderFormData.section}
                    onChange={(e) => setReminderFormData({...reminderFormData, section: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '13px',
                      fontFamily: 'inherit'
                    }}
                  >
                    <option value="">All Sections</option>
                    {sectionOptions.map(section => (
                      <option key={section} value={section}>Section {section}</option>
                    ))}
                  </select>
                </div>
              )}

              <div style={{
                background: '#e0f2fe',
                border: '1px solid #0ea5e9',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '15px',
                fontSize: '13px',
                color: '#0369a1'
              }}>
                📱 Reminders will be sent as mobile app notifications to all students with pending fees
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowReminderModal(false)}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    background: '#f3f4f6',
                    color: '#374151',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={reminderLoading}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '4px',
                    background: '#f59e0b',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: reminderLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {reminderLoading ? '⏳ Sending...' : '🔔 Send Reminders'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Hostel Management */}
      {activeTab === 'hostel' && (
        <div style={{ padding: '20px 0', width: '100%', boxSizing: 'border-box', display: 'block' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
            <div>
              <h2 className="section-title" style={{ marginBottom: '4px', fontSize: '22px' }}>🏨 Hostel Management</h2>
              <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>Manage hostels, rooms and student allocations for your school.</p>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                className="btn btn-primary"
                onClick={() => setShowHostelModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', borderRadius: '999px', padding: '8px 16px' }}
              >
                <span>➕</span>
                <span>Add Hostel</span>
              </button>
              <button
                className="btn btn-info"
                onClick={() => setShowRoomModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', borderRadius: '999px', padding: '8px 16px', background: '#0ea5e9', borderColor: '#0ea5e9' }}
              >
                <span>🚪</span>
                <span>Add Room</span>
              </button>
              <button
                className="btn btn-warning"
                onClick={() => setShowAllocationModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', borderRadius: '999px', padding: '8px 16px', background: '#f97316', borderColor: '#f97316' }}
              >
                <span>👤</span>
                <span>Allocate Student</span>
              </button>
              <button
                className="btn btn-danger"
                onClick={() => setShowMaintenanceModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', borderRadius: '999px', padding: '8px 16px', background: '#ef4444', borderColor: '#ef4444' }}
              >
                <span>🔧</span>
                <span>Add Maintenance</span>
              </button>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              padding: '16px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #eef2ff, #ffffff)',
              border: '1px solid #e5e7eb',
              boxShadow: '0 10px 25px rgba(15,23,42,0.06)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280' }}>Total Hostels</div>
                <div style={{ fontSize: '26px', fontWeight: '700', color: '#1f2937' }}>{hostels.length}</div>
              </div>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '999px',
                background: '#e0e7ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>🏢</div>
            </div>

            <div style={{
              padding: '16px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #ecfdf3, #ffffff)',
              border: '1px solid #e5e7eb',
              boxShadow: '0 10px 25px rgba(15,23,42,0.06)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280' }}>Students Assigned</div>
                <div style={{ fontSize: '26px', fontWeight: '700', color: '#16a34a' }}>{hostelAllocations.length}</div>
              </div>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '999px',
                background: '#bbf7d0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>👥</div>
            </div>

            <div style={{
              padding: '16px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #fffbeb, #ffffff)',
              border: '1px solid #e5e7eb',
              boxShadow: '0 10px 25px rgba(15,23,42,0.06)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280' }}>Total Capacity</div>
                <div style={{ fontSize: '26px', fontWeight: '700', color: '#d97706' }}>
                  {hostels.reduce((sum, h) => {
                    const cap = h.capacityPerRoom != null ? h.capacityPerRoom : h.capacity;
                    const n = parseInt(cap, 10);
                    return sum + (Number.isNaN(n) ? 0 : n);
                  }, 0)}
                </div>
              </div>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '999px',
                background: '#fef3c7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>🛏️</div>
            </div>
          </div>

          <div style={{ marginTop: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>Hostels</h3>
              {hostels.length > 0 && (
                <span style={{ fontSize: '12px', color: '#6b7280' }}>{hostels.length} hostel{hostels.length > 1 ? 's' : ''}</span>
              )}
            </div>
            {hostelLoading ? (
              <div style={{ textAlign: 'center', padding: '32px', borderRadius: '12px', background: 'white', border: '1px dashed #e5e7eb', color: '#6b7280' }}>Loading hostels...</div>
            ) : hostels.length === 0 ? (
              <div style={{
                padding: '32px',
                background: 'white',
                borderRadius: '12px',
                textAlign: 'center',
                color: '#6b7280',
                border: '1px dashed #e5e7eb'
              }}>
                <p style={{ marginBottom: '8px', fontWeight: '500' }}>No hostels created yet</p>
                <p style={{ marginBottom: '16px', fontSize: '13px' }}>Start by adding your first hostel to manage rooms and allocations.</p>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowHostelModal(true)}
                  style={{ padding: '8px 16px', borderRadius: '999px' }}
                >
                  ➕ Add Hostel
                </button>
              </div>
            ) : (
              <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 10px 30px rgba(15,23,42,0.06)', overflowX: 'auto' }}>
                <table className="table table-sm table-hover" style={{ width: '100%', marginBottom: 0, fontSize: '12px' }}>
                  <thead style={{ backgroundColor: '#f9fafb' }}>
                    <tr>
                      <th style={{ fontSize: '12px', fontWeight: 600 }}>Name</th>
                      <th style={{ fontSize: '12px', fontWeight: 600 }}>Address</th>
                      <th style={{ fontSize: '12px', fontWeight: 600 }}>Capacity</th>
                      <th style={{ fontSize: '12px', fontWeight: 600 }}>Rooms</th>
                      <th style={{ fontSize: '12px', fontWeight: 600 }}>Warden</th>
                      <th style={{ fontSize: '12px', fontWeight: 600 }}>Phone</th>
                      <th style={{ fontSize: '12px', fontWeight: 600 }}>Email</th>
                      <th style={{ fontSize: '12px', fontWeight: 600, textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hostels.map((hostel) => (
                      <tr key={hostel.id} style={{ fontSize: '12px', lineHeight: 1.2 }}>
                        <td>{hostel.hostelName}</td>
                        <td>
                          {(hostel.address || hostel.city || hostel.state || hostel.pincode)
                            ? `${hostel.address || ''}${hostel.city ? `${hostel.address ? ', ' : ''}${hostel.city}` : ''}${hostel.state ? `${hostel.address || hostel.city ? ', ' : ''}${hostel.state}` : ''}${hostel.pincode ? `${hostel.address || hostel.city || hostel.state ? ' - ' : ''}${hostel.pincode}` : ''}`
                            : (hostel.location || 'N/A')}
                        </td>
                        <td>{hostel.capacityPerRoom != null ? hostel.capacityPerRoom : (hostel.capacity || 0)}</td>
                        <td>{hostel.totalRooms || 'N/A'}</td>
                        <td>{hostel.warden || 'N/A'}</td>
                        <td>{hostel.wardenPhone || hostel.phone || 'N/A'}</td>
                        <td>{hostel.wardenEmail || hostel.email || 'N/A'}</td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary rounded-pill"
                              style={{ padding: '2px 8px', fontSize: '11px' }}
                              onClick={() => handleEditHostel(hostel)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger rounded-pill"
                              style={{ padding: '2px 8px', fontSize: '11px' }}
                              onClick={() => handleDeleteHostel(hostel)}
                            >
                              Delete
                            </button>
                            <button
                              className="btn btn-sm btn-primary rounded-pill"
                              style={{ padding: '2px 8px', fontSize: '11px' }}
                              onClick={() => handleViewHostel(hostel)}
                            >
                              View Details
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {selectedHostel && !showRoomModal && (
            <div className="modal" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
              <div className="modal-content" style={{ width: '90%', maxWidth: '600px', background: 'white', borderRadius: '12px', padding: '30px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '2px solid #e2e8f0', paddingBottom: '15px' }}>
                  <h2 style={{ margin: '0', fontSize: '22px', fontWeight: '700', color: '#1a202c' }}>Hostel Details</h2>
                  <button onClick={() => setSelectedHostel(null)} style={{ background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#718096' }}>×</button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <p style={{ color: '#718096', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>HOSTEL NAME</p>
                    <p style={{ fontSize: '16px', fontWeight: '700', color: '#1a202c', margin: '0' }}>{selectedHostel.hostelName}</p>
                  </div>
                  <div>
                    <p style={{ color: '#718096', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>ADDRESS</p>
                    <p style={{ fontSize: '16px', fontWeight: '700', color: '#1a202c', margin: '0' }}>
                      {(selectedHostel.address || selectedHostel.city || selectedHostel.state || selectedHostel.pincode)
                        ? `${selectedHostel.address || ''}${selectedHostel.city ? `${selectedHostel.address ? ', ' : ''}${selectedHostel.city}` : ''}${selectedHostel.state ? `${selectedHostel.address || selectedHostel.city ? ', ' : ''}${selectedHostel.state}` : ''}${selectedHostel.pincode ? `${selectedHostel.address || selectedHostel.city || selectedHostel.state ? ' - ' : ''}${selectedHostel.pincode}` : ''}`
                        : (selectedHostel.location || 'N/A')}
                    </p>
                  </div>
                  <div>
                    <p style={{ color: '#718096', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>CAPACITY</p>
                    <p style={{ fontSize: '16px', fontWeight: '700', color: '#1a202c', margin: '0' }}>
                      {selectedHostel.capacityPerRoom != null ? selectedHostel.capacityPerRoom : selectedHostel.capacity}
                    </p>
                  </div>
                  <div>
                    <p style={{ color: '#718096', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>TOTAL ROOMS</p>
                    <p style={{ fontSize: '16px', fontWeight: '700', color: '#1a202c', margin: '0' }}>{selectedHostel.totalRooms}</p>
                  </div>
                  <div>
                    <p style={{ color: '#718096', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>BEDS PER ROOM</p>
                    <p style={{ fontSize: '16px', fontWeight: '700', color: '#1a202c', margin: '0' }}>{selectedHostel.bedsPerRoom}</p>
                  </div>
                  <div>
                    <p style={{ color: '#718096', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>WARDEN</p>
                    <p style={{ fontSize: '16px', fontWeight: '700', color: '#1a202c', margin: '0' }}>{selectedHostel.warden}</p>
                  </div>
                  <div>
                    <p style={{ color: '#718096', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>PHONE</p>
                    <p style={{ fontSize: '16px', fontWeight: '700', color: '#1a202c', margin: '0' }}>{selectedHostel.wardenPhone || selectedHostel.phone}</p>
                  </div>
                  <div>
                    <p style={{ color: '#718096', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>EMAIL</p>
                    <p style={{ fontSize: '16px', fontWeight: '700', color: '#1a202c', margin: '0' }}>{selectedHostel.wardenEmail || selectedHostel.email}</p>
                  </div>
                </div>

                <div style={{ marginTop: '24px' }}>
                  <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: '600', color: '#1f2933' }}>Rooms</h3>
                  {hostelRoomsLoading ? (
                    <p style={{ fontSize: '13px', color: '#6b7280' }}>Loading rooms...</p>
                  ) : selectedHostelRooms.length === 0 ? (
                    <p style={{ fontSize: '13px', color: '#6b7280' }}>No rooms found for this hostel.</p>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginTop: '4px' }}>
                        <thead>
                          <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                            <th style={{ padding: '8px', textAlign: 'left' }}>Room</th>
                            <th style={{ padding: '8px', textAlign: 'left' }}>Capacity</th>
                            <th style={{ padding: '8px', textAlign: 'left' }}>Occupied</th>
                            <th style={{ padding: '8px', textAlign: 'left' }}>Status</th>
                            <th style={{ padding: '8px', textAlign: 'right' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedHostelRooms.map((room) => (
                            <tr key={room.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                              <td style={{ padding: '8px' }}>{room.roomNumber}</td>
                              <td style={{ padding: '8px' }}>{room.capacity}</td>
                              <td style={{ padding: '8px' }}>{room.occupied}</td>
                              <td style={{ padding: '8px' }}>{room.active ? 'Active' : 'Inactive'}</td>
                              <td style={{ padding: '8px', textAlign: 'right' }}>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary rounded-pill"
                                  style={{ padding: '2px 8px', fontSize: '11px', marginRight: '6px' }}
                                  onClick={() => handleEditRoom(room)}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger rounded-pill"
                                  style={{ padding: '2px 8px', fontSize: '11px' }}
                                  onClick={() => handleDeleteRoom(room)}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '25px', paddingTop: '15px', borderTop: '1px solid #e2e8f0' }}>
                  <button onClick={() => setSelectedHostel(null)} style={{ padding: '10px 20px', background: '#e2e8f0', color: '#1a202c', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Close</button>
                </div>
              </div>
            </div>
          )}

          {/* Allocations List */}
          <div style={{ marginTop: '30px' }}>
            <h3>Student Allocations</h3>
            {hostelAllocations.length === 0 ? (
              <div style={{
                padding: '20px',
                background: '#f8f9fa',
                borderRadius: '8px',
                textAlign: 'center',
                color: '#666'
              }}>
                No allocations found.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  background: 'white'
                }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #ddd' }}>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Student ID</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Room</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Allocation Date</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Checkout Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hostelAllocations.map((alloc, idx) => {
                      const allocationDate = alloc.allocationDate ? new Date(alloc.allocationDate) : null;
                      const checkoutDate = alloc.checkoutDate ? new Date(alloc.checkoutDate) : null;
                      const isValidAllocationDate = allocationDate && !isNaN(allocationDate.getTime());
                      const isValidCheckoutDate = checkoutDate && !isNaN(checkoutDate.getTime());
                      
                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '10px' }}>{alloc.studentId}</td>
                          <td style={{ padding: '10px' }}>{alloc.roomId}</td>
                          <td style={{ padding: '10px' }}>{isValidAllocationDate ? allocationDate.toLocaleDateString() : 'N/A'}</td>
                          <td style={{ padding: '10px' }}>{isValidCheckoutDate ? checkoutDate.toLocaleDateString() : 'N/A'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

           {/* Add Hostel Modal */}
          {showHostelModal && (
            <div className="modal" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className="modal-content" style={{ width: '90%', maxWidth: '500px' }}>
                <div className="modal-header" style={{ marginBottom: '20px' }}>
                  <h3>{editingHostel ? 'Edit Hostel' : 'Add Hostel'}</h3>
                  <button onClick={() => { setShowHostelModal(false); setEditingHostel(null); }} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
                </div>
                <form onSubmit={handleAddHostel}>
                  <input type="text" placeholder="Hostel Name *" value={hostelFormData.hostelName} onChange={(e) => setHostelFormData({...hostelFormData, hostelName: e.target.value})} required style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  <input type="text" placeholder="Address *" value={hostelFormData.address} onChange={(e) => setHostelFormData({...hostelFormData, address: e.target.value})} required style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  <input type="text" placeholder="City *" value={hostelFormData.city} onChange={(e) => setHostelFormData({...hostelFormData, city: e.target.value})} required style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  <input type="text" placeholder="State *" value={hostelFormData.state} onChange={(e) => setHostelFormData({...hostelFormData, state: e.target.value})} required style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  <input type="text" placeholder="Pincode *" value={hostelFormData.pincode} onChange={(e) => setHostelFormData({...hostelFormData, pincode: e.target.value})} required style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  <input type="number" placeholder="Capacity (Total Students) *" value={hostelFormData.capacity} onChange={(e) => setHostelFormData({...hostelFormData, capacity: e.target.value})} required min="1" style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  <input type="number" placeholder="Total Rooms *" value={hostelFormData.totalRooms} onChange={(e) => setHostelFormData({...hostelFormData, totalRooms: e.target.value})} required min="1" style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  <input type="number" placeholder="Beds Per Room *" value={hostelFormData.bedsPerRoom} onChange={(e) => setHostelFormData({...hostelFormData, bedsPerRoom: e.target.value})} required min="1" style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  <input type="text" placeholder="Warden Name" value={hostelFormData.warden} onChange={(e) => setHostelFormData({...hostelFormData, warden: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  <input type="tel" placeholder="Phone" value={hostelFormData.phone} onChange={(e) => setHostelFormData({...hostelFormData, phone: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  <input type="email" placeholder="Email" value={hostelFormData.email} onChange={(e) => setHostelFormData({...hostelFormData, email: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button type="button" onClick={() => { setShowHostelModal(false); setEditingHostel(null); }} className="btn btn-secondary">Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={hostelLoading}>{editingHostel ? 'Update Hostel' : 'Add Hostel'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Add Room Modal */}
          {showRoomModal && (
            <div className="modal" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className="modal-content" style={{ width: '90%', maxWidth: '500px' }}>
                <div className="modal-header" style={{ marginBottom: '20px' }}>
                  <h3>{editingRoom ? 'Edit Room' : 'Add Room'}</h3>
                  <button onClick={() => { setShowRoomModal(false); setEditingRoom(null); setRoomFormData({ roomNumber: '', capacity: '', type: 'shared' }); }} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
                </div>
                <form onSubmit={handleAddRoom}>
                  <select
                    value={selectedHostel ? String(selectedHostel.id) : ''}
                    onChange={(e) => {
                      const hostel = hostels.find(h => String(h.id) === e.target.value);
                      setSelectedHostel(hostel || null);
                    }}
                    required
                    style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                  >
                    <option value="">Select Hostel</option>
                    {hostels.map(h => (
                      <option key={h.id} value={String(h.id)}>{h.hostelName}</option>
                    ))}
                  </select>
                  <input type="text" placeholder="Room Number" value={roomFormData.roomNumber} onChange={(e) => setRoomFormData({...roomFormData, roomNumber: e.target.value})} required style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  <input type="number" placeholder="Capacity" value={roomFormData.capacity} onChange={(e) => setRoomFormData({...roomFormData, capacity: e.target.value})} required style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  <select value={roomFormData.type} onChange={(e) => setRoomFormData({...roomFormData, type: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }}>
                    <option value="shared">Shared</option>
                    <option value="single">Single</option>
                  </select>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => { setShowRoomModal(false); setEditingRoom(null); setRoomFormData({ roomNumber: '', capacity: '', type: 'shared' }); }}
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={hostelLoading}>
                      {editingRoom ? 'Update Room' : 'Add Room'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Allocate Student Modal */}
          {showAllocationModal && (
            <div className="modal" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className="modal-content" style={{ width: '90%', maxWidth: '500px' }}>
                <div className="modal-header" style={{ marginBottom: '20px' }}>
                  <h3>Allocate Student</h3>
                  <button onClick={() => setShowAllocationModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
                </div>
                <form onSubmit={handleAllocateStudent}>
                  <input type="text" placeholder="Student ID" value={allocationFormData.studentId} onChange={(e) => setAllocationFormData({...allocationFormData, studentId: e.target.value})} required style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  <input type="text" placeholder="Room ID" value={allocationFormData.roomId} onChange={(e) => setAllocationFormData({...allocationFormData, roomId: e.target.value})} required style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  <input type="date" value={allocationFormData.allocationDate} onChange={(e) => setAllocationFormData({...allocationFormData, allocationDate: e.target.value})} required style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  <input type="date" value={allocationFormData.checkoutDate} onChange={(e) => setAllocationFormData({...allocationFormData, checkoutDate: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button type="button" onClick={() => setShowAllocationModal(false)} className="btn btn-secondary">Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={hostelLoading}>Allocate</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Add Maintenance Modal */}
          {showMaintenanceModal && (
            <div className="modal" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className="modal-content" style={{ width: '90%', maxWidth: '500px' }}>
                <div className="modal-header" style={{ marginBottom: '20px' }}>
                  <h3>Add Maintenance Record</h3>
                  <button onClick={() => setShowMaintenanceModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
                </div>
                <form onSubmit={handleAddMaintenance}>
                  <select
                    value={selectedHostel ? String(selectedHostel.id) : ''}
                    onChange={(e) => {
                      const hostel = hostels.find(h => String(h.id) === e.target.value);
                      setSelectedHostel(hostel || null);
                    }}
                    required
                    style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                  >
                    <option value="">Select Hostel</option>
                    {hostels.map(h => (
                      <option key={h.id} value={String(h.id)}>{h.hostelName}</option>
                    ))}
                  </select>
                  <textarea placeholder="Description" value={maintenanceFormData.description} onChange={(e) => setMaintenanceFormData({...maintenanceFormData, description: e.target.value})} required style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px', minHeight: '80px' }} />
                  <select value={maintenanceFormData.type} onChange={(e) => setMaintenanceFormData({...maintenanceFormData, type: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }}>
                    <option value="repair">Repair</option>
                    <option value="cleaning">Cleaning</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                  <select value={maintenanceFormData.priority} onChange={(e) => setMaintenanceFormData({...maintenanceFormData, priority: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }}>
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                  </select>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button type="button" onClick={() => setShowMaintenanceModal(false)} className="btn btn-secondary">Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={hostelLoading}>Add Record</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vehicle Management */}
      {activeTab === 'vehicle' && (
        <VehicleManagement orgData={orgData} students={students} showNotification={showNotification} />
      )}

      {/* Marks Cards & Grading Subsystem */}
      {activeTab === 'marks-cards' && (
        <Examinations
          orgData={orgData}
          students={students}
          classes={classes}
          teachers={teachers}
          showSuccessNotification={(title, msg) => showNotification('success', title, msg)}
          showErrorNotification={(title, msg) => showNotification('error', title, msg)}
        />
      )}

      {/* School Certificates & Document Issuance */}
      {activeTab === 'certificates' && (
        <SchoolCertificatesManager
          orgData={orgData}
          students={students}
          classes={classes}
          brandingData={brandingData}
          showSuccessNotification={(title, msg) => showNotification('success', title, msg)}
          showErrorNotification={(title, msg) => showNotification('error', title, msg)}
        />
      )}

      {/* Report Card Preview Modal */}
      {showRCPreviewModal && selectedReportCardForView && (
        <div className="modal" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-content" style={{ width: '90%', maxWidth: '850px', maxHeight: '90vh', overflowY: 'auto', background: 'white', padding: '24px', borderRadius: '12px' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e3a8a' }}>🔍 Academic Transcript Preview</h3>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => handleDownloadRCPDF(selectedReportCardForView, rcEntriesForView)}
                  className="btn btn-primary"
                  style={{ padding: '6px 12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  📥 Download PDF
                </button>
                <button
                  onClick={() => {
                    setShowRCPreviewModal(false);
                    setSelectedReportCardForView(null);
                    setRcEntriesForView([]);
                  }}
                  style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}
                >
                  ×
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', background: '#f8fafc', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', width: '100%', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Select Layout Theme:</span>
                {['classic', 'modern', 'royal', 'prestige', 'international', 'glass', 'transcript_plus'].map(t => (
                  <button
                    key={t}
                    onClick={() => setRcTemplate(t)}
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      borderRadius: '6px',
                      border: '1px solid',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      backgroundColor: rcTemplate === t ? '#1e3a8a' : 'white',
                      color: rcTemplate === t ? 'white' : '#475569',
                      borderColor: rcTemplate === t ? '#1e3a8a' : '#cbd5e1',
                    }}
                  >
                    {t.replace('_', ' ')}
                  </button>
                ))}
              </div>
              <span style={{ fontSize: '11px', color: '#64748b', fontStyle: 'italic' }}>
                Real-time styling template
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', padding: '10px', backgroundColor: '#f1f5f9', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }}>
              <MarksCardRenderer 
                reportCard={selectedReportCardForView} 
                entries={rcEntriesForView} 
                branding={rcBrandingForView}
                institutionName={rcOrgInfoForView?.orgName || orgData?.orgName}
                institutionAddress={rcOrgInfoForView?.address || orgData?.address}
                template={rcTemplate}
                gradingScale={rcGradingScaleForView}
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Remarks Modal */}
      {showRCEditRemarksModal && editingRC && (
        <div className="modal" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-content" style={{ width: '90%', maxWidth: '500px', background: 'white', padding: '24px', borderRadius: '12px' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e3a8a' }}>✍️ Edit Report Card Remarks</h3>
              <button onClick={() => { setShowRCEditRemarksModal(false); setEditingRC(null); }} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Class Teacher Remarks</label>
              <textarea
                value={rcClassRemarksInput}
                onChange={(e) => setRcClassRemarksInput(e.target.value)}
                placeholder="Enter remarks from class teacher..."
                rows={3}
                style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Principal Remarks</label>
              <textarea
                value={rcPrincipalRemarksInput}
                onChange={(e) => setRcPrincipalRemarksInput(e.target.value)}
                placeholder="Enter remarks from principal..."
                rows={3}
                style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowRCEditRemarksModal(false); setEditingRC(null); }} className="btn btn-secondary">Cancel</button>
              <button onClick={handleUpdateRCRemarks} className="btn btn-primary">Save Remarks</button>
            </div>
          </div>
        </div>
      )}

      {/* Download Modal */}
      {showDownloadModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '0',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
          }}>
            <div className="modal-header" style={{
              padding: '20px',
              borderBottom: '1px solid #dee2e6',
              background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
              color: 'white',
              flexShrink: 0
            }}>
              <h5 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                📥 Download {downloadConfig.dataType.charAt(0).toUpperCase() + downloadConfig.dataType.slice(1)}
              </h5>
            </div>
            
            <div className="modal-body" style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
              {/* Timetable export options */}
              {downloadConfig.dataType === 'timetable' ? (
                <div>
                  <p style={{ marginBottom: '15px', fontSize: '14px', color: '#495057' }}>
                    Export the current timetable for <strong>{selectedTimetableClass}</strong> - Section <strong>{selectedTimetableSection}</strong>.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => {
                        exportTimetableToPDF();
                        setShowDownloadModal(false);
                      }}
                    >
                      📄 Export Timetable as PDF
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        exportTimetableToWord();
                        setShowDownloadModal(false);
                      }}
                    >
                      📝 Export Timetable as Word
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Format Selection - For Students and Teachers */}
                  {(downloadConfig.dataType === 'students' || downloadConfig.dataType === 'teachers') && (
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        File Format
                      </label>
                      <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="format"
                            value="excel"
                            checked={downloadConfig.format === 'excel'}
                            onChange={(e) => setDownloadConfig({...downloadConfig, format: e.target.value})}
                          />
                          📊 Excel File {downloadConfig.dataType === 'students' ? '(Class-wise)' : ''}
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Filters */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      Filters
                    </label>
                    
                    {/* Class Filter */}
                    {(downloadConfig.dataType === 'students' || downloadConfig.dataType === 'classes') && (
                      <div style={{ marginBottom: '10px' }}>
                        <select
                          className="form-control"
                          value={downloadConfig.filters.class || ''}
                          onChange={(e) => setDownloadConfig({
                            ...downloadConfig,
                            filters: {...downloadConfig.filters, class: e.target.value}
                          })}
                          style={{ fontSize: '14px' }}
                        >
                          <option value="">All Classes</option>
                          {getUniqueValues('class').map(cls => (
                            <option key={cls} value={cls}>Class {cls}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Section Filter */}
                    {downloadConfig.dataType === 'students' && (
                      <div style={{ marginBottom: '10px' }}>
                        <select
                          className="form-control"
                          value={downloadConfig.filters.section || ''}
                          onChange={(e) => setDownloadConfig({
                            ...downloadConfig,
                            filters: {...downloadConfig.filters, section: e.target.value}
                          })}
                          style={{ fontSize: '14px' }}
                        >
                          <option value="">All Sections</option>
                          {getUniqueValues('section').map(section => (
                            <option key={section} value={section}>Section {section}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Year Filter */}
                    {downloadConfig.dataType === 'students' && (
                      <div style={{ marginBottom: '10px' }}>
                        <select
                          className="form-control"
                          value={downloadConfig.filters.year || ''}
                          onChange={(e) => setDownloadConfig({
                            ...downloadConfig,
                            filters: {...downloadConfig.filters, year: e.target.value}
                          })}
                          style={{ fontSize: '14px' }}
                        >
                          <option value="">All Years</option>
                          {getUniqueValues('admissionYear').map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Preview Count */}
                  <div style={{ 
                    padding: '10px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '6px',
                    marginBottom: '20px',
                    fontSize: '14px',
                    color: '#495057'
                  }}>
                    📊 Records to download: <strong>
                      {getFilteredData(downloadConfig.dataType, downloadConfig.filters).length}
                    </strong>
                  </div>
                </>
              )}
            </div>
            
            <div className="modal-footer" style={{
              padding: '15px 20px',
              borderTop: '1px solid #dee2e6',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              flexShrink: 0,
              backgroundColor: '#f8f9fa'
            }}>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setShowDownloadModal(false)}
              >
                {downloadConfig.dataType === 'timetable' ? 'Close' : 'Cancel'}
              </button>
              {downloadConfig.dataType !== 'timetable' && (
                <button 
                  type="button" 
                  className="btn btn-success"
                  onClick={handleDownload}
                  disabled={getFilteredData(downloadConfig.dataType, downloadConfig.filters).length === 0}
                >
                  📥 Download ({getFilteredData(downloadConfig.dataType, downloadConfig.filters).length})
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* 🎓 BULK STUDENT PROMOTION MODAL */}
      {showPromotionModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#ffffff', borderRadius: '16px', width: '100%', maxWidth: '850px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0' }}>
            
            {/* Header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  🎓 End-of-Year Bulk Student Promotion Dashboard
                </h2>
                <p style={{ fontSize: '12.5px', color: '#64748b', margin: '4px 0 0 0' }}>
                  Transition students from their current academic session to the target session with full history preservation.
                </p>
              </div>
              <button onClick={() => setShowPromotionModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#64748b' }}>×</button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              
              {/* Transition Selection Bar */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px', background: '#f1f5f9', padding: '16px', borderRadius: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', marginBottom: '4px' }}>Source Class</label>
                  <select value={promotionSourceClass} onChange={e => { setPromotionSourceClass(e.target.value); fetchPromotionPreview(e.target.value); }} style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', fontWeight: '600' }}>
                    <option value="LKG">LKG</option>
                    <option value="UKG">UKG</option>
                    <option value="1st Standard">1st Standard</option>
                    <option value="2nd Standard">2nd Standard</option>
                    <option value="3rd Standard">3rd Standard</option>
                    <option value="4th Standard">4th Standard</option>
                    <option value="5th Standard">5th Standard</option>
                    <option value="6th Standard">6th Standard</option>
                    <option value="7th Standard">7th Standard</option>
                    <option value="8th Standard">8th Standard</option>
                    <option value="9th Standard">9th Standard</option>
                    <option value="10th Standard">10th Standard</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', marginBottom: '4px' }}>Target Class</label>
                  <select value={promotionTargetClass} onChange={e => setPromotionTargetClass(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', fontWeight: '600' }}>
                    <option value="UKG">UKG</option>
                    <option value="1st Standard">1st Standard</option>
                    <option value="2nd Standard">2nd Standard</option>
                    <option value="3rd Standard">3rd Standard</option>
                    <option value="4th Standard">4th Standard</option>
                    <option value="5th Standard">5th Standard</option>
                    <option value="6th Standard">6th Standard</option>
                    <option value="7th Standard">7th Standard</option>
                    <option value="8th Standard">8th Standard</option>
                    <option value="9th Standard">9th Standard</option>
                    <option value="10th Standard">10th Standard</option>
                    <option value="GRADUATED">Graduated / Alumni</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', marginBottom: '4px' }}>Source Session</label>
                  <input type="text" value={promotionSourceYear} disabled style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', background: '#e2e8f0' }} />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', marginBottom: '4px' }}>Target Session</label>
                  <select value={promotionTargetYear} onChange={e => setPromotionTargetYear(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', fontWeight: '600' }}>
                    <option value="2027-28">AY 2027-28</option>
                    <option value="2026-27">AY 2026-27</option>
                  </select>
                </div>
              </div>

              {/* KPI Summary */}
              {promotionPreviewData && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#1e40af' }}>TOTAL STUDENTS</div>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#2563eb' }}>{promotionPreviewData.totalCount || 0}</div>
                  </div>

                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#166534' }}>ELIGIBLE</div>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#16a34a' }}>{promotionPreviewData.eligibleCount || 0}</div>
                  </div>

                  <div style={{ background: '#fefce8', border: '1px solid #fef08a', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#854d0e' }}>BLOCKED / REVIEW</div>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#ca8a04' }}>{promotionPreviewData.blockedCount || 0}</div>
                  </div>
                </div>
              )}

              {/* Remarks / Override Reason Box */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#334155', marginBottom: '4px' }}>Administrator Remarks / Override Reason</label>
                <input type="text" placeholder="e.g. End of year session promotion approved by Principal" value={promotionRemarks} onChange={e => setPromotionRemarks(e.target.value)} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
              </div>

            </div>

            {/* Footer Actions */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px', background: '#f8fafc' }}>
              <button onClick={() => setShowPromotionModal(false)} style={{ padding: '9px 18px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
              <button onClick={executePromotion} disabled={promotionExecuting || (promotionPreviewData?.eligibleCount === 0 && !promotionRemarks)} style={{ padding: '9px 24px', background: '#059669', color: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                🚀 {promotionExecuting ? 'Promoting...' : `Execute Bulk Promotion (${promotionPreviewData?.eligibleCount || 0} Eligible)`}
              </button>
            </div>

          </div>
        </div>
      )}


{/* Teacher Details Modal */}
      {showTeacherDetailsModal && editingTeacher && (
        <div className="modal" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-content" style={{ width: '90%', maxWidth: '700px', maxHeight: '80vh', overflowY: 'auto' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #dee2e6' }}>
              <h3 style={{ margin: 0, color: '#333' }}>👨‍🏫 Teacher Details</h3>
              <button 
                className="close-btn" 
                onClick={() => {
                  setShowTeacherDetailsModal(false);
                  setEditingTeacher(null);
                }}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}
              >
                ×
              </button>
            </div>

            <div className="modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {/* Left Column */}
              <div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Teacher ID</label>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', fontWeight: '500', color: '#333' }}>{editingTeacher.teacherId || 'N/A'}</p>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Full Name</label>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', fontWeight: '500', color: '#333' }}>{editingTeacher.teacherName || 'N/A'}</p>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email</label>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', fontWeight: '500', color: '#333', wordBreak: 'break-all' }}>{editingTeacher.gmail || editingTeacher.email || 'N/A'}</p>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Phone</label>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', fontWeight: '500', color: '#333' }}>{editingTeacher.phoneNumber || 'N/A'}</p>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date of Birth</label>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', fontWeight: '500', color: '#333' }}>{editingTeacher.dob || 'N/A'}</p>
                </div>
              </div>

              {/* Right Column */}
              <div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Designation</label>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', fontWeight: '500', color: '#333' }}>{editingTeacher.designation || 'N/A'}</p>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Experience</label>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', fontWeight: '500', color: '#333' }}>{editingTeacher.experience || 0} years</p>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Degree</label>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', fontWeight: '500', color: '#333' }}>{editingTeacher.degree || 'N/A'}</p>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</label>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    <span className={`status-badge ${editingTeacher.status === 'ACTIVE' ? 'active' : 'inactive'}`}>
                      {editingTeacher.status || 'ACTIVE'}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            {(editingTeacher.specialization || editingTeacher.qualifications || editingTeacher.address) && (
              <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #dee2e6' }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '14px', fontWeight: '600' }}>📚 Additional Information</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  {editingTeacher.specialization && (
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Specialization</label>
                      <p style={{ margin: '4px 0 0 0', fontSize: '14px', fontWeight: '500', color: '#333' }}>{editingTeacher.specialization}</p>
                    </div>
                  )}
                  {editingTeacher.qualifications && (
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Qualifications</label>
                      <p style={{ margin: '4px 0 0 0', fontSize: '14px', fontWeight: '500', color: '#333' }}>{editingTeacher.qualifications}</p>
                    </div>
                  )}
                </div>
                {editingTeacher.address && (
                  <div style={{ marginTop: '15px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Address</label>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#555', lineHeight: '1.5' }}>{editingTeacher.address}</p>
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => {
                  setShowTeacherDetailsModal(false);
                  setEditingTeacher(null);
                }}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  background: '#6c757d',
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowTeacherDetailsModal(false);
                  setShowTeacherModal(true);
                }}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  background: '#007bff',
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Edit Teacher
              </button>
            </div>
          </div>
        </div>
      )}

      {showFinancePasswordModal && (
        <div className="modal" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-content" style={{ width: '90%', maxWidth: '400px', maxHeight: '80vh', overflowY: 'auto' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '10px', borderBottom: '1px solid #dee2e6' }}>
              <h3 style={{ margin: 0, fontSize: '18px', color: '#111827' }}>Finance Dashboard Access</h3>
              <button
                onClick={handleCloseFinancePasswordModal}
                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#6b7280' }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleFinancePasswordSubmit}>
              <div className="modal-body">
                <p style={{ fontSize: '13px', color: '#4b5563', marginBottom: '12px' }}>
                  Enter your finance password to open the Finance Dashboard.
                </p>
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#4b5563', marginBottom: '4px', display: 'block' }}>Finance Password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={financePassword}
                    onChange={(e) => setFinancePassword(e.target.value)}
                    placeholder="Enter password"
                    required
                    style={{ fontSize: '13px' }}
                  />
                </div>
              </div>
              <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseFinancePasswordModal}
                  style={{ fontSize: '13px', padding: '6px 12px' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{
                    fontSize: '13px',
                    padding: '6px 12px'
                  }}
                >
                  Continue
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Secure Account Settings Modal */}
      {showSecureAccountSettings && (
        <SecureAccountSettings 
          orgData={orgData}
          onClose={() => setShowSecureAccountSettings(false)}
        />
      )}
      {/* Smart Alerts */}
      {activeTab === 'smart-alerts' && (
        <SmartAlertsManagement orgData={orgData} />
      )}

      {/* ID Cards */}
      {activeTab === 'id-cards' && (
          <IDCardManagement
            orgData={orgData}
            students={students}
            teachers={teachers}
            brandingData={brandingData}
            loadBrandingData={loadBrandingData}
            onTeacherUpdated={(updatedTeacher) => {
              setTeachers(prev => prev.map(t => (t.teacherId === updatedTeacher.teacherId ? { ...t, ...updatedTeacher } : t)));
            }}
            onStudentUpdated={(updatedStudent) => {
              setStudents(prev => prev.map(s => (s.studentId === updatedStudent.studentId ? { ...s, ...updatedStudent } : s)));
            }}
            showSuccessNotification={(msg) => setNotification({ type: 'success', message: msg })}
            showErrorNotification={(msg) => setNotification({ type: 'error', message: msg })}
          />
      )}

      {/* Library */}
      {activeTab === 'library' && (
        <LibraryManagement 
          orgData={orgData} 
          externalActiveTab={libraryActiveTab}
          setExternalActiveTab={setLibraryActiveTab}
        />
      )}

      {/* Reports & Analytics */}
      {activeTab === 'reports' && (
        <div style={{ padding: '24px', background: '#f8fafc', minHeight: 'calc(100vh - 64px)' }}>
          {reportsSubTab === 'analytics' && (
            <AnalyticsManagement orgData={orgData} students={students} teachers={teachers} classes={classes} fees={fees} showNotification={showNotification} />
          )}

          {(reportsSubTab === 'admission_register' || reportsSubTab === 'new_admissions' || reportsSubTab === 'admission_cancelled') && (
            <AdmissionReports 
              orgData={orgData} 
              students={students} 
              classes={classes} 
              showNotification={showNotification} 
            />
          )}

          {reportsSubTab === 'migration_report' && (
            <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, fontSize: '20px', color: '#1e293b' }}>🛫 Student Migration Report</h2>
                <button className="btn btn-primary" style={{ padding: '8px 16px', fontSize: '13px' }}>Generate Certificates</button>
              </div>
              <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '20px' }}>Details of students migrating out with Transfer Certificate (TC) tracking.</p>
              
              <div style={{ padding: '40px', textAlign: 'center', background: '#f1f5f9', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                <span style={{ fontSize: '32px', display: 'block', marginBottom: '12px' }}>✈️</span>
                <h3 style={{ fontSize: '16px', color: '#334155', margin: '0 0 8px 0' }}>No Pending Migrations</h3>
                <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>There are no active TC requests or processed migrations.</p>
              </div>
            </div>
          )}

          {reportsSubTab === 'master_list' && (
            <StudentMasterList 
              orgData={orgData} 
              students={students} 
              classes={classes} 
              showNotification={showNotification} 
            />
          )}
        </div>
      )}

      {/* Settings */}
      {activeTab === 'settings' && ((orgData?.userType === 'SUB_USER' && !userPermissions.includes('settings') && !userPermissions.includes('settings.view')) ? (
        <div style={{ padding: '40px', textAlign: 'center', background: '#fff', borderRadius: '12px', margin: '20px', border: '1px solid #e2e8f0' }}>
          <h3 style={{ color: '#ef4444', marginBottom: '8px', fontSize: '18px', fontWeight: '700' }}>Access Denied</h3>
          <p style={{ color: '#64748b', fontSize: '14px' }}>You do not have permission to access Settings.</p>
        </div>
      ) : (
        <SettingsManagement 
          orgData={orgData} 
          onLogout={onLogout} 
          showNotification={showNotification} 
        />
      ))}

      {/* Media & Storage Management */}
      {activeTab === 'storage' && (
        <StorageManagement 
          orgData={orgData} 
          showNotification={showNotification} 
        />
      )}
        </div>
      </div>
    </div>

    {/* Big Screen Photo Lightbox Preview Modal */}
    {previewPhotoModal.isOpen && (
      <div 
        className="photo-preview-overlay"
        onClick={() => setPreviewPhotoModal({ isOpen: false, imageUrl: '', title: '', subtitle: '', tag: '', initials: '' })}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          padding: '20px'
        }}
      >
        <div 
          className="photo-preview-card"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#ffffff',
            borderRadius: '16px',
            maxWidth: '520px',
            width: '100%',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            overflow: 'hidden',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Header */}
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#f8fafc'
          }}>
            <div>
              <div style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>👤</span> {previewPhotoModal.title || 'Student Profile Photo'}
              </div>
              {previewPhotoModal.subtitle && (
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px', fontWeight: '500' }}>
                  {previewPhotoModal.subtitle}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setPreviewPhotoModal({ isOpen: false, imageUrl: '', title: '', subtitle: '', tag: '', initials: '' })}
              style={{
                background: '#e2e8f0',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                color: '#475569',
                cursor: 'pointer',
                fontWeight: '700',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#cbd5e1'; e.currentTarget.style.color = '#0f172a'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#475569'; }}
              title="Close (Esc)"
            >
              ✕
            </button>
          </div>

          {/* Image Display */}
          <div style={{
            padding: '24px',
            background: '#0f172a',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '340px',
            maxHeight: '68vh',
            overflow: 'hidden'
          }}>
            {previewPhotoModal.imageUrl ? (
              <img
                src={previewPhotoModal.imageUrl}
                alt={previewPhotoModal.title || 'Profile Photo'}
                style={{
                  maxWidth: '100%',
                  maxHeight: '62vh',
                  objectFit: 'contain',
                  borderRadius: '10px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                  border: '2px solid rgba(255,255,255,0.1)'
                }}
              />
            ) : (
              <div style={{
                width: '140px',
                height: '140px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: '#ffffff',
                fontWeight: '800',
                fontSize: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '4px solid rgba(255,255,255,0.2)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)'
              }}>
                {previewPhotoModal.initials || 'S'}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '14px 20px',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: '#f8fafc'
          }}>
            {previewPhotoModal.tag ? (
              <span style={{
                fontSize: '12px',
                fontWeight: '700',
                padding: '4px 10px',
                borderRadius: '20px',
                background: previewPhotoModal.tag === 'Active' ? '#dcfce7' : '#fee2e2',
                color: previewPhotoModal.tag === 'Active' ? '#15803d' : '#b91c1c'
              }}>
                ● {previewPhotoModal.tag}
              </span>
            ) : <div />}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => setPreviewPhotoModal({ isOpen: false, imageUrl: '', title: '', subtitle: '', tag: '', initials: '' })}
                style={{ padding: '6px 16px', borderRadius: '6px', fontSize: '12.5px', fontWeight: '700', background: '#0f2942', borderColor: '#0f2942' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

      {/* 🔑 Reset Student Password Modal */}
      {resetPasswordStudent && (
        <ResetStudentPasswordModal
          isOpen={!!resetPasswordStudent}
          student={resetPasswordStudent}
          onClose={() => setResetPasswordStudent(null)}
          showNotification={showNotification}
        />
      )}
    </div>
  );
};

export default SchoolAdmin;
